import { useState, useEffect, useRef } from "react";
import * as React from "react";
import { createPortal } from "react-dom";

// ─── Design Tokens — "Dark Atelier" (coach) ──────────────────────────────────
// Brand fonts: serif for brand moments/titles, sans for UI, mono for data.
const F_SERIF = "'Playfair Display', Didot, Georgia, 'Times New Roman', serif";
const F_SANS  = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const C = {
  bg:            "#090806",
  surface:       "#14110E",
  surfaceRaised: "#1D1914",
  surfaceMid:    "#241E16",
  overlay:       "rgba(4,3,2,0.74)",
  border:        "#2C2418",
  borderInput:   "#3A2D1F",
  borderSubtle:  "#1C1712",
  accent:        "#B08A4A",
  accentBg:      "rgba(176,138,74,0.10)",
  accentBorder:  "rgba(176,138,74,0.30)",
  textPrimary:   "#EEE7D8",
  textSecondary: "#B7AD9D",
  textMuted:     "#8A8175",
  textDim:       "#675F52",
  textOnAccent:  "#14110E",
  danger:        "#C76B58",
  dangerBg:      "rgba(155,63,50,0.14)",
  dangerBorder:  "rgba(155,63,50,0.36)",
  gold:          "#CBA35C",
  goldBg:        "rgba(203,163,92,0.10)",
  goldBorder:    "rgba(203,163,92,0.24)",
  // "indigo" slot holds the lapis navy family — informational/interactive layer
  // (links, info banners, Call/Intake badges, secondary chart series). Never buttons.
  indigo:        "#8FA6C9",
  indigoBg:      "rgba(143,166,201,0.10)",
  indigoBorder:  "rgba(143,166,201,0.28)",
  indigoDeep:    "#2C3E66",
  success:       "#8CA57F",
  successBg:     "rgba(81,107,74,0.16)",
  successBorder: "rgba(81,107,74,0.42)",
};
// ─── "Ivory Metaphysique" (client-facing pages) — same keys as C so shared
// components can read either palette from ThemeCtx. Gold is linework-only on
// ivory (~3:1); text-level accents use deep bronze, primary buttons use ink.
const IV: typeof C = {
  bg:            "#F3EFE6",
  surface:       "#FFFDF8",
  surfaceRaised: "#FAF6ED",
  surfaceMid:    "#EFE9DC",
  overlay:       "rgba(28,24,20,0.45)",
  border:        "#D8CFC0",
  borderInput:   "#C9BEAC",
  borderSubtle:  "#E4DCCE",
  accent:        "#876737",
  accentBg:      "rgba(176,138,74,0.10)",
  accentBorder:  "rgba(176,138,74,0.40)",
  textPrimary:   "#1C1814",
  textSecondary: "#4F463D",
  textMuted:     "#8A8175",
  textDim:       "#9A9184",
  textOnAccent:  "#FFFDF8",
  danger:        "#9B3F32",
  dangerBg:      "rgba(155,63,50,0.08)",
  dangerBorder:  "rgba(155,63,50,0.32)",
  gold:          "#B08A4A",
  goldBg:        "rgba(176,138,74,0.08)",
  goldBorder:    "rgba(176,138,74,0.32)",
  indigo:        "#33477A",
  indigoBg:      "rgba(35,50,79,0.08)",
  indigoBorder:  "rgba(51,71,122,0.32)",
  indigoDeep:    "#23324F",
  success:       "#516B4A",
  successBg:     "rgba(81,107,74,0.10)",
  successBorder: "rgba(81,107,74,0.36)",
};
// Components shared between the dark dashboard and ivory client pages read
// their palette from here; default is Dark Atelier.
const ThemeCtx = React.createContext(C);
const SP = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 };
const R  = { sm: 8, md: 10, lg: 14, xl: 16 };
const TY = {
  pageTitle:    { fontFamily: F_SERIF, fontSize: 26, fontWeight: 600, letterSpacing: 0.4, color: C.textPrimary } as React.CSSProperties,
  sectionTitle: { fontFamily: F_SERIF, fontSize: 19, fontWeight: 600, letterSpacing: 0.3, color: C.textPrimary } as React.CSSProperties,
  cardTitle:    { fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: C.textMuted } as React.CSSProperties,
  caption:      { fontSize: 11, color: C.textMuted, letterSpacing: 0.5 } as React.CSSProperties,
  mono:         { fontFamily: "'JetBrains Mono', monospace" } as React.CSSProperties,
};
// ─── Metaphysique mark — official logo: fluted pillar breaking through a ──────
// Official Metaphysique mark (Ionic pillar inside the Greek-key ring), drawn from
// brand/metaphysique-logo-master.png. Rendered as a CSS mask over a solid fill so the one
// artwork still recolors per theme — gold-on-ivory, gold-on-espresso, green on the forms.
const META_MARK = "/metaphysique-mark.png";
function MetaLogo({ size = 26, color, style }: { size?: number; color?: string; style?: React.CSSProperties }) {
  const mask: React.CSSProperties = {
    WebkitMaskImage: `url(${META_MARK})`,
    maskImage: `url(${META_MARK})`,
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  };
  return (
    <div
      role="img"
      aria-label="Metaphysique"
      style={{ width: size, height: size, background: color ?? C.accent, flexShrink: 0, display: "block", ...mask, ...style }}
    />
  );
}
const dsCard: React.CSSProperties = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, padding: SP.lg, marginBottom: SP.md };
const inputStyle: React.CSSProperties = { width: "100%", background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "10px 12px", color: C.textPrimary, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "'Inter', sans-serif" };
const ivInputStyle: React.CSSProperties = { ...inputStyle, background: IV.surface, border: `1px solid ${IV.borderInput}`, color: IV.textPrimary };
const dsBadge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "2px 8px", borderRadius: R.sm, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", background: `${color}18`, color, border: `1px solid ${color}35` });
type BtnVariant = "primary" | "secondary" | "ghost" | "danger";
const dsBtn = (variant: BtnVariant = "ghost", P: typeof C = C): React.CSSProperties => {
  const base: React.CSSProperties = { padding: "8px 16px", borderRadius: R.md, fontSize: 13, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, transition: "opacity 0.15s", whiteSpace: "nowrap", flexShrink: 0, border: "none", outline: "none", lineHeight: 1 };
  const v: Record<BtnVariant, React.CSSProperties> = {
    // On ivory, gold is linework-only — primary buttons are ink with paper text
    primary:   P === IV ? { ...base, background: P.textPrimary, color: P.surface, fontWeight: 600 }
                        : { ...base, background: P.accent,    color: P.textOnAccent, fontWeight: 700 },
    secondary: { ...base, background: "transparent", color: P.textSecondary, border: `1px solid ${P.border}` },
    ghost:     { ...base, background: "transparent", color: P.textMuted,   border: "1px solid transparent" },
    danger:    { ...base, background: P.dangerBg,  color: P.danger,        border: `1px solid ${P.dangerBorder}` },
  };
  return v[variant];
};
// ─── Btn component — hover + disabled states ──────────────────────────────────
function Btn({ variant = "ghost", onClick, disabled, children, style, type, title }: {
  variant?: BtnVariant;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
  type?: "button" | "submit" | "reset";
  title?: string;
}) {
  const T = React.useContext(ThemeCtx);
  const [hov, setHov] = React.useState(false);
  const hoverMap: Record<BtnVariant, React.CSSProperties> = {
    primary:   { opacity: 0.84 },
    secondary: { background: T.surfaceRaised, borderColor: T.borderInput, color: T.textPrimary },
    ghost:     { background: T === IV ? "rgba(28,24,20,0.05)" : "rgba(255,255,255,0.06)", color: T.textSecondary },
    danger:    { background: T.dangerBg, borderColor: T.danger },
  };
  return (
    <button
      type={type ?? "button"}
      disabled={disabled}
      title={title}
      onClick={onClick}
      onMouseEnter={() => { if (!disabled) setHov(true); }}
      onMouseLeave={() => setHov(false)}
      style={{
        ...dsBtn(variant, T),
        ...(hov && !disabled ? hoverMap[variant] : {}),
        ...(disabled ? { opacity: 0.36, cursor: "not-allowed" } : {}),
        ...style,
      }}
    >{children}</button>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
type MenuItem = { label: React.ReactNode; onClick: () => void; danger?: boolean; hidden?: boolean };
function KebabMenu({ items }: { items: MenuItem[] }) {
  const T = React.useContext(ThemeCtx);
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const [sheet, setSheet] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  // Portaled to <body> so no parent can clip it. Phones get a bottom sheet;
  // larger screens get a popover aligned to the button, clamped inside the viewport.
  const place = React.useCallback(() => {
    const vw = window.innerWidth, vh = window.innerHeight, pad = 8;
    if (vw < 640) { setSheet(true); return; }
    setSheet(false);
    const b = ref.current?.getBoundingClientRect();
    if (!b) return;
    const w = menuRef.current?.offsetWidth || 220;
    const h = menuRef.current?.offsetHeight || 0;
    let left = b.right - w;                         // prefer right-aligned to the button
    if (left < pad) left = b.left;                  // not enough room → left-align
    left = Math.max(pad, Math.min(left, vw - w - pad));
    let top = b.bottom + 6;
    if (h && top + h > vh - pad) top = b.top - 6 - h >= pad ? b.top - 6 - h : Math.max(pad, vh - h - pad);
    setPos({ top, left });
  }, []);
  React.useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    place();
    requestAnimationFrame(place);                   // re-measure once the menu has its real size
  }, [open, place]);
  React.useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", key);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", key);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);
  const visible = items.filter(i => !i.hidden);
  const hoverBg = T === IV ? "rgba(28,24,20,0.05)" : "rgba(255,255,255,0.06)";
  const list = visible.map((it, i) => (
    <button
      key={i}
      onClick={() => { setOpen(false); it.onClick(); }}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%",
        textAlign: "left" as const, padding: sheet ? "14px 16px" : "9px 11px", background: "transparent",
        border: "none", cursor: "pointer", fontSize: sheet ? 15 : 13, borderRadius: R.sm,
        color: it.danger ? T.danger : T.textSecondary, whiteSpace: "nowrap" as const,
        fontFamily: "inherit",
      }}
      onMouseEnter={e => (e.currentTarget.style.background = hoverBg)}
      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
    >{it.label}</button>
  ));
  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <Btn variant="ghost" onClick={() => setOpen(o => !o)} title="More actions" style={{ padding: "0 12px", fontSize: 20, lineHeight: 1, letterSpacing: 2 }}>⋯</Btn>
      {open && createPortal(
        sheet ? (
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "flex-end" }}>
            <div ref={menuRef} onClick={e => e.stopPropagation()} style={{
              width: "100%", background: T.surfaceRaised, borderTop: `1px solid ${T.border}`,
              borderRadius: `${R.lg}px ${R.lg}px 0 0`, boxShadow: "0 -10px 32px rgba(0,0,0,0.35)",
              padding: "8px 8px calc(12px + env(safe-area-inset-bottom))", maxHeight: "80vh", overflowY: "auto",
            }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: T.border, margin: "4px auto 8px" }} />
              {list}
              <button onClick={() => setOpen(false)} style={{
                width: "100%", marginTop: 6, padding: "13px 16px", fontSize: 15, fontFamily: "inherit",
                background: "transparent", border: `1px solid ${T.border}`, borderRadius: R.md,
                color: T.textSecondary, cursor: "pointer",
              }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div ref={menuRef} style={{
            position: "fixed", top: pos?.top ?? 0, left: pos?.left ?? 0,
            visibility: pos ? "visible" : "hidden",
            background: T.surfaceRaised, border: `1px solid ${T.border}`,
            borderRadius: R.md, boxShadow: "0 10px 32px rgba(0,0,0,0.35)",
            zIndex: 1000, minWidth: 220, maxWidth: "calc(100vw - 16px)",
            maxHeight: "calc(100vh - 16px)", overflowY: "auto", padding: 4,
          }}>{list}</div>
        ),
        document.body
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
const dsTab = (active: boolean): React.CSSProperties => ({ padding: "8px 16px", borderRadius: R.md, cursor: "pointer", background: active ? C.accentBg : "transparent", color: active ? C.accent : C.textDim, border: active ? `1px solid ${C.accentBorder}` : "1px solid transparent", fontSize: 11, fontWeight: active ? 600 : 400, letterSpacing: 0.8, textTransform: "uppercase" as const, transition: "color 0.12s, background 0.12s", whiteSpace: "nowrap" as const, flexShrink: 0, outline: "none", lineHeight: 1 });
// ─────────────────────────────────────────────────────────────────────────────

interface InBodyResult {
  date: string; weight: number; muscleMass: number; bodyFat: number; bmi: number; visceralFat: number; notes: string;
}
interface Session {
  id: string; date: string; time: string; type: string; notes: string; reminded: boolean; eventId?: string;
}

function matchClientInTitle(title: string, clients: Client[]): Client | undefined {
  const t = title.toLowerCase();
  return clients.find(c => {
    const parts = c.name.toLowerCase().split(" ").filter(p => p.length > 1);
    return parts.length > 0 && parts.every(p => t.includes(p));
  });
}
function eventLocalDateTime(ev: any): { date: string | null; time: string | null } {
  if (ev?.start?.date) return { date: ev.start.date, time: null };
  if (!ev?.start?.dateTime) return { date: null, time: null };
  const d = new Date(ev.start.dateTime);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return { date, time };
}
// Weekly PT memberships accrue credits by REAL calendar weeks elapsed, not by
// month×4. A month×4 model under-counts (4×12 = 48 credits vs ~52.14 weeks/yr),
// so the pocket drifts ~one week's sessions negative per year and 5-week months
// only credit 4. Instead: grant weekly-rate credits for the current (in-progress)
// week from day one, then add weekly-rate for each completed week since the
// membership start. Unused sessions carry over; a 5-week month naturally grants 5.
// Sessions dated before the membership start (trial/intake era) don't count.
// `monthly` is still returned as the ~monthly allotment (weekly-rate × 4) purely
// for display. Returns null for block packages / non-weekly memberships.
function calcPocketSessions(pkg: string | undefined, sessions: { date: string }[] | undefined, membershipStart: string | undefined, adjustment: number = 0): { pocket: number; monthly: number } | null {
  const m = pkg?.match(/(\d+)\s*session/i);
  if (!m || !/\bweek\b/i.test(pkg || '')) return null;
  const weeklyRate = parseInt(m[1]);
  const monthly = weeklyRate * 4;
  const startStr = (membershipStart || '').split('T')[0];
  let start = startStr ? new Date(startStr + 'T00:00:00') : new Date();
  if (isNaN(start.getTime())) start = new Date();
  const now = new Date();
  const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
  // +1 so the current, in-progress week is credited up front (client can book from day one).
  const weeksElapsed = Math.max(0, Math.floor((now.getTime() - start.getTime()) / MS_PER_WEEK)) + 1;
  const used = (sessions || []).filter(s => !startStr || s.date >= startStr).length;
  return { pocket: weeksElapsed * weeklyRate - used + adjustment, monthly };
}
// Block packages: sessions used within the CURRENT package. packageStartSessions marks how many
// sessions were already consumed by PREVIOUS packages, so a newly purchased package restarts the
// count (carrying any overflow from the finished one). Undefined = legacy cumulative (counts all).
function blockUsedSessions(sessions: { date: string }[] | undefined, packageStartSessions: number | undefined): number {
  return (sessions?.length || 0) - (packageStartSessions || 0);
}
// Keep session credits in sync when a calendar event is deleted: remove the linked session
function removeSessionForEvent(ev: any, clients?: Client[], onClientUpdate?: (c: Client) => void) {
  if (!clients || !onClientUpdate) return;
  const byId = clients.find(c => (c.sessions || []).some(s => s.eventId === ev.id));
  if (byId) {
    onClientUpdate({ ...byId, sessions: byId.sessions.filter(s => s.eventId !== ev.id) });
    return;
  }
  // Legacy sessions created before event linking: match credits-tagged events by client + date + time
  const cName = ev?.extendedProperties?.private?.clientName;
  if (!cName) return;
  const owner = clients.find(c => c.name === cName);
  if (!owner) return;
  const { date, time } = eventLocalDateTime(ev);
  if (!date) return;
  const idx = (owner.sessions || []).findIndex(s => !s.eventId && s.date === date && (!time || s.time === time));
  if (idx < 0) return;
  const sessions = [...owner.sessions];
  sessions.splice(idx, 1);
  onClientUpdate({ ...owner, sessions });
}
// Keep session records in sync when a calendar event is rescheduled
function updateSessionForEvent(eventId: string, date: string, time: string, clients?: Client[], onClientUpdate?: (c: Client) => void) {
  if (!clients || !onClientUpdate) return;
  const owner = clients.find(c => (c.sessions || []).some(s => s.eventId === eventId));
  if (!owner) return;
  onClientUpdate({ ...owner, sessions: owner.sessions.map(s => s.eventId === eventId ? { ...s, date, time } : s) });
}
interface Message {
  id: string; from: "client" | "coach"; text: string; date: string; time: string;
}
interface Client {
  id: string; name: string; email: string; phone: string; whatsapp: string; goal: string; goalSummary?: string; package: string;
  status: "active" | "inactive"; joinDate: string; membershipStart?: string; notes: string; coachNotes?: string; trainingScheme: string; trainingNotes: string; diet: string;
  inbody: InBodyResult[]; sessions: Session[]; messages: Message[]; payments?: any[];
  portalToken?: string; category?: string;
  isTrial?: boolean; trialEndDate?: string; schemeUpdatedAt?: string; rev?: number;
  vetStatus?: "pending" | "approved" | "declined"; vettedAt?: string;
  leadSource?: { heardFrom?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string; ref?: string; fbclid?: string; gclid?: string; referrer?: string; landingPage?: string; capturedAt?: string };
  creditAdjustment?: number;
  packageStartSessions?: number;
  frozen?: boolean; freezeStart?: string; freezeReason?: string; frozenPlan?: any;
  mollieCustomerId?: string; mollieSubscriptionId?: string | null;
  programStart?: string; programEnd?: string;
  weeklyCheckIns?: WeeklyCheckIn[];
  trainingQuestionnaires?: TrainingQuestionnaireEntry[];
  nutritionIntakes?: NutritionIntakeEntry[];
  intakeAnswers?: Record<string, string>;
  intakeSubmittedAt?: string;
}
interface WeeklyCheckIn {
  id: string;
  submittedAt: string;
  weekStart: string;
  overallFeel: number;
  energyRecovery: number;
  sessionsCompleted: "all" | "most" | "some" | "none";
  hardOrEasy: string;
  notesForCoach: string;
}
interface TrainingQuestionnaireEntry {
  id: string;
  submittedAt: string;
  answers: Record<string, string>;
  label?: string;
}
interface NutritionIntakeEntry {
  id: string;
  submittedAt: string;
  answers: Record<string, string>; // question label → answer, section order preserved
}

const CHAT_KEY      = "coachagent_chat_v2";
const API           = `https://coachagent-server-production.up.railway.app`;
const AUTH_TOKEN_KEY = "coach_jwt";

function getToken(): string | null { return localStorage.getItem(AUTH_TOKEN_KEY); }
function setToken(t: string)       { localStorage.setItem(AUTH_TOKEN_KEY, t); }
function clearToken()              { localStorage.removeItem(AUTH_TOKEN_KEY); }

// Client-portal session (separate from the coach JWT above)
const PORTAL_JWT_KEY = "portal_jwt";
function getPortalJwt(): string | null { try { return localStorage.getItem(PORTAL_JWT_KEY); } catch { return null; } }
function setPortalJwt(t: string)       { try { localStorage.setItem(PORTAL_JWT_KEY, t); } catch {} }
function clearPortalJwt()              { try { localStorage.removeItem(PORTAL_JWT_KEY); } catch {} }
// The clientId baked into a portal session JWT — used to check a stored session
// against the client a /client/<token> link is actually for.
function portalJwtClientId(t: string | null): string | null {
  if (!t) return null;
  try { const v = JSON.parse(atob(t.split(".")[1])).clientId; return v == null ? null : String(v); } catch { return null; }
}

function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const existing = options.headers as Record<string, string> | undefined;
  const headers: Record<string, string> = { "Content-Type": "application/json", ...existing };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}

async function loadClientsFromServer(): Promise<Client[] | null> {
  try {
    const res = await authFetch(`${API}/api/clients`);
    if (res.status === 401) return null;
    if (!res.ok) return [];
    const clients = await res.json();
    for (const c of clients) if (typeof c?.rev === "number") latestRev[c.id] = c.rev;
    return clients;
  } catch { return []; }
}
// Server bumps a rev counter on every save and rejects writes based on an older
// rev with 409 — that means another tab/device saved this client since we loaded it.
const latestRev: Record<string, number> = {};
const saveQueue: Record<string, Promise<void>> = {};
async function saveClientToServer(client: Client): Promise<Client | "conflict" | null> {
  try {
    const payload = latestRev[client.id] !== undefined ? { ...client, rev: latestRev[client.id] } : client;
    const res = await authFetch(`${API}/api/clients/${client.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.status === 409) return "conflict";
    if (!res.ok) { console.error("Failed to save client:", res.status); return null; }
    const saved = await res.json();
    if (typeof saved?.rev === "number") latestRev[client.id] = saved.rev;
    return saved;
  } catch { console.error("Failed to save client"); return null; }
}
// AI-assistant saves change one field of one client, often long after the app loaded —
// meanwhile webhooks, calendar reconcile and training patches bump the server rev, so the
// tab's copy is stale. Re-read the latest record, apply just that change on top, and save:
// a stale tab then neither 409s nor overwrites newer server data. Retries once on a race.
// Falls back to the local copy for a client created in this same reply (not on server yet).
async function saveClientChangeFresh(local: Client, change: (base: Client) => Partial<Client>): Promise<{ status: number; saved?: Client }> {
  let status = 0;
  for (let attempt = 0; attempt < 2; attempt++) {
    const all = await loadClientsFromServer();
    const base = all?.find(c => c.id === local.id) ?? local;
    const res = await authFetch(`${API}/api/clients/${local.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...base, ...change(base) }),
    });
    status = res.status;
    if (res.ok) {
      const saved = await res.json();
      if (typeof saved?.rev === "number") latestRev[local.id] = saved.rev;
      return { status, saved };
    }
    if (status !== 409) break;
  }
  return { status };
}
async function createClientOnServer(client: Client): Promise<void> {
  try {
    await authFetch(`${API}/api/clients`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(client),
    });
  } catch { console.error("Failed to create client"); }
}
async function deleteClientFromServer(id: string): Promise<void> {
  try {
    await authFetch(`${API}/api/clients/${id}`, { method: "DELETE" });
  } catch { console.error("Failed to delete client"); }
}
async function bulkSaveClientsToServer(clients: Client[]): Promise<void> {
  try {
    await authFetch(`${API}/api/clients/bulk`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(clients),
    });
  } catch { console.error("Failed to bulk save clients"); }
}
function newClient(name: string): Client {
  return { id: Date.now().toString(), name, email: "", phone: "", whatsapp: "", goal: "general fitness",
    package: "", status: "active", joinDate: new Date().toISOString().split("T")[0], notes: "",
    trainingScheme: "", trainingNotes: "", diet: "", inbody: [], sessions: [], messages: [], portalToken: Math.random().toString(36).slice(2, 10) };
}

// All Claude calls go through Railway — API key lives only in Railway env vars.
// Server streams SSE with X-Accel-Buffering:no to bypass Railway's nginx proxy buffer.
async function callClaude(
  messages: { role: string; content: any }[],
  opts: { system?: string; maxTokens?: number; meta?: { stopReason?: string } } = {}
): Promise<string> {
  try {
    const body: any = { model: "claude-sonnet-4-5", max_tokens: opts.maxTokens ?? 4000, messages };
    if (opts.system) body.system = opts.system;
    const res = await authFetch(`${API}/api/claude`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return `⚠️ Server error: ${res.status}`;
    const reader = res.body?.getReader();
    if (!reader) return "⚠️ No response body";
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const evt = JSON.parse(line.slice(6).trim());
          if (evt.content?.[0]?.text !== undefined) {
            if (opts.meta) opts.meta.stopReason = evt.stop_reason || undefined;
            return evt.content[0].text;
          }
          if (evt.error) return `⚠️ API Error: ${evt.error}`;
        } catch {}
      }
    }
    return "⚠️ No response received.";
  } catch (e: any) { return `⚠️ Network error: ${e.message}`; }
}

async function askClaude(messages: { role: string; content: any }[], system: string, maxTokens = 4000, meta?: { stopReason?: string }): Promise<string> {
  return callClaude(messages, { system, maxTokens, meta });
}

const S = {
  // ── Layout ──────────────────────────────────────────────────────────────────
  app:           { display: "flex", height: "100vh", background: C.bg, color: C.textPrimary, fontFamily: "'Inter', sans-serif", overflow: "hidden" } as React.CSSProperties,
  main:          { flex: 1, display: "flex", flexDirection: "column" as const, overflow: "hidden" },
  content:       { flex: 1, overflowY: "auto" as const, padding: `${SP.lg}px ${SP.xl}px` },
  topbar:        { padding: `${SP.md}px ${SP.xl}px`, borderBottom: `1px solid ${C.borderSubtle}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: C.bg },
  pageTitle:     { ...TY.pageTitle },

  // ── Sidebar ─────────────────────────────────────────────────────────────────
  sidebar:       { width: 260, background: C.surface, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column" as const, flexShrink: 0, overflow: "hidden" },
  sidebarHeader: { padding: "28px 20px 20px", borderBottom: `1px solid ${C.border}` },
  logo:          { fontFamily: F_SERIF, fontSize: 22, color: C.accent, letterSpacing: 2, lineHeight: 1 },
  logoSub:       { fontSize: 10, color: C.textDim, letterSpacing: 3, textTransform: "uppercase" as const, marginTop: 4 },
  navSection:    { padding: "16px 12px 8px" },
  navLabel:      { fontSize: 10, color: C.textDim, letterSpacing: 2, textTransform: "uppercase" as const, padding: "0 8px", marginBottom: 6 },
  navItem:       (active: boolean) => ({ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: R.md, cursor: "pointer", background: active ? C.accentBg : "transparent", color: active ? C.accent : C.textMuted, fontSize: 13, fontWeight: active ? 600 : 400, border: active ? `1px solid ${C.accentBorder}` : "1px solid transparent", transition: "all 0.15s", marginBottom: 2 }),
  clientList:    { flex: 1, overflowY: "auto" as const, padding: `${SP.sm}px 12px` },
  clientItem:    (active: boolean) => ({ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: R.md, cursor: "pointer", background: active ? C.accentBg : "transparent", border: active ? `1px solid ${C.accentBorder}` : "1px solid transparent", marginBottom: 2, transition: "all 0.15s" }),
  avatar:        (size: number) => ({ width: size, height: size, borderRadius: "50%", background: C.accentBg, border: `1.5px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F_SERIF, fontSize: size * 0.38, color: C.accent, flexShrink: 0 }),

  // ── Primitives ───────────────────────────────────────────────────────────────
  btn:       (variant: "primary" | "secondary" | "ghost" | "danger" = "ghost") => dsBtn(variant),
  card:      { ...dsCard } as React.CSSProperties,
  cardTitle: { ...TY.cardTitle, marginBottom: SP.md } as React.CSSProperties,
  input:     { ...inputStyle } as React.CSSProperties,
  textarea:  { ...inputStyle, resize: "vertical" as const, minHeight: 100 } as React.CSSProperties,
  label:     { ...TY.caption, letterSpacing: 1, textTransform: "uppercase" as const, marginBottom: SP.xs, display: "block", fontWeight: 600 } as React.CSSProperties,
  tab:       (active: boolean) => dsTab(active),
  badge:     (color: string) => dsBadge(color),
  modal:     { position: "fixed" as const, inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 },
  modalBox:  { background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.xl, padding: SP.lg, width: 480, maxWidth: "90vw" },

  // ── Composed ─────────────────────────────────────────────────────────────────
  grid2:     { display: "grid", gridTemplateColumns: "1fr 1fr", gap: SP.md },
  statCard:  { background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, padding: SP.lg, flex: 1 },
  statNum:   { fontFamily: F_SERIF, fontSize: 42, color: C.accent, lineHeight: 1 },
  statLabel: { ...TY.cardTitle, marginTop: SP.xs } as React.CSSProperties,
  chatBubble:(isUser: boolean) => ({ maxWidth: "75%", alignSelf: isUser ? "flex-end" : "flex-start", background: isUser ? C.accent : C.surfaceMid, color: isUser ? C.textOnAccent : C.textPrimary, padding: "10px 14px", borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px", fontSize: 13, lineHeight: 1.6, border: isUser ? "none" : `1px solid ${C.border}`, whiteSpace: "pre-wrap" as const }),
  inbodyRow: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: SP.md, padding: "12px 0", borderBottom: `1px solid ${C.borderSubtle}`, fontSize: 13 },
};

function Dashboard({ clients, isMobile, onSelectClient }: { clients: Client[], isMobile?: boolean, onSelectClient: (id: string) => void }) {
  const active      = clients.filter(c => c.status === "active").length;
  const withInBody  = clients.filter(c => (c.inbody || []).length > 0).length;
  const totalSessions = clients.reduce((sum, c) => sum + (c.sessions || []).length, 0);

  const [calWeekEvents, setCalWeekEvents] = useState<any[]>([]);
  const [calUpcoming, setCalUpcoming] = useState<any[]>([]);
  // Dashboard client sections are collapsed by default; a key is present only when expanded
  const [expandedDashCats, setExpandedDashCats] = useState<Set<string>>(new Set());
  const toggleDashCat = (key: string) => setExpandedDashCats(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  // Payment Renewals controls
  const [renewalFilter, setRenewalFilter] = useState<"all" | "overdue" | "7" | "30">("all");
  const [renewalSort, setRenewalSort] = useState<"urgency" | "date" | "amount" | "client">("urgency");
  const [collapsedRenewalGroups, setCollapsedRenewalGroups] = useState<Set<string>>(new Set(["soon", "later"]));
  const toggleRenewalGroup = (key: string) => setCollapsedRenewalGroups(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  const [showWeekModal, setShowWeekModal] = useState(false);
  const [showCollected, setShowCollected] = useState(false);
  const [showTodayModal, setShowTodayModal] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [highlightVisible, setHighlightVisible] = useState(true);
  const highlightsRef = React.useRef<typeof highlights>([] as any);
  useEffect(() => {
    const now = new Date();
    const dow = now.getDay();
    const monday = new Date(now); monday.setDate(now.getDate() - ((dow + 6) % 7)); monday.setHours(0,0,0,0);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); sunday.setHours(23,59,59,999);
    authFetch(`${API}/api/calendar/events?timeMin=${encodeURIComponent(monday.toISOString())}&timeMax=${encodeURIComponent(sunday.toISOString())}`)
      .then(r => r.json())
      .then(d => setCalWeekEvents((d.events || []).sort((a: any, b: any) => new Date(a.start?.dateTime || a.start?.date).getTime() - new Date(b.start?.dateTime || b.start?.date).getTime())))
      .catch(() => {});
    const twoWeeksOut = new Date(now); twoWeeksOut.setDate(now.getDate() + 14); twoWeeksOut.setHours(23,59,59,999);
    authFetch(`${API}/api/calendar/events?timeMin=${encodeURIComponent(now.toISOString())}&timeMax=${encodeURIComponent(twoWeeksOut.toISOString())}`)
      .then(r => r.json())
      .then(d => setCalUpcoming((d.events || []).sort((a: any, b: any) => new Date(a.start?.dateTime || a.start?.date).getTime() - new Date(b.start?.dateTime || b.start?.date).getTime()).slice(0, 5)))
      .catch(() => {});
  }, []);
  const upcoming    = clients
    .flatMap(c => c.sessions.map(s => ({ ...s, clientName: c.name, clientId: c.id })))
    .filter(s => new Date(s.date) >= new Date())
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 5);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const packageValidityMonths: Record<string, number> = {
    "8 sessions": 3, "12 sessions": 5, "24 sessions": 9,
  };
  const getValidityMonths = (description: string): number | null => {
    const lower = (description || "").toLowerCase();
    for (const [key, months] of Object.entries(packageValidityMonths)) {
      if (lower.includes(key)) return months;
    }
    return null;
  };
  const paymentDue = clients
    .filter(c => c.status === "active" && !c.frozen)
    .map(c => {
      const paid = (c.payments || []).filter((p: any) => p.status === "paid" && p.date);
      if (paid.length === 0) return null;
      const lastDate = paid.reduce((latest: any, p: any) => new Date(p.date) > new Date(latest.date) ? p : latest);
      const base = new Date(lastDate.date);
      const validityMonths = getValidityMonths(lastDate.description || "");
      const due = new Date(base);
      const isPackage = validityMonths !== null;
      if (isPackage) due.setMonth(due.getMonth() + validityMonths!);
      else { const day = due.getDate(); due.setMonth(due.getMonth() + 1); if (due.getDate() !== day) due.setDate(0); }
      const daysUntil = Math.round((due.getTime() - today.getTime()) / 86400000);
      const pkgLabel = (lastDate?.description || c.package || "").trim();
      return { clientId: c.id, clientName: c.name, due, daysUntil, lastAmount: lastDate?.amount || null, isPackage, pkgLabel };
    })
    .filter(Boolean)
    .sort((a: any, b: any) => a.daysUntil - b.daysUntil) as { clientId: string; clientName: string; due: Date; daysUntil: number; lastAmount: string | null; isPackage: boolean; pkgLabel: string }[];

  // ── local style helpers ──
  const secLabel: React.CSSProperties = {
    fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase",
    color: C.textDim, marginBottom: SP.sm,
  };
  const kpiCard = (accent: boolean): React.CSSProperties => ({
    background: C.surfaceRaised,
    border: `1px solid ${accent ? C.accentBorder : C.border}`,
    borderTop: `2px solid ${accent ? C.accent : C.border}`,
    borderRadius: R.lg,
    padding: isMobile ? "10px 12px" : "12px 14px",
  });
  const rowDivider = (last: boolean): React.CSSProperties => ({
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 0",
    borderBottom: last ? "none" : `1px solid ${C.border}`,
  });

  // ── Highlights feed ──────────────────────────────────────────────────────
  const highlights = (() => {
    type Item = { id: string; text: string; type: "danger" | "warning" | "success" | "info"; clientId?: string };
    const items: Item[] = [];
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const in7 = new Date(today); in7.setDate(today.getDate() + 7);
    const ago14 = new Date(today); ago14.setDate(today.getDate() - 14);
    const ago7 = new Date(today); ago7.setDate(today.getDate() - 7);

    clients.filter(c => c.status === "active").forEach(c => {
      const paid = (c.payments || []).filter((p: any) => p.status === "paid" && p.date);
      const recent = (c.payments || []).filter((p: any) => p.status === "paid" && p.date && new Date(p.date) >= ago7);
      if (recent.length > 0) {
        const p = recent[recent.length - 1];
        items.push({ id: `pay-recv-${c.id}`, text: `${c.name} paid €${p.amount}`, type: "success", clientId: c.id });
      }
      const futureSessions = c.sessions.filter(s => {
        const d = new Date(s.date);
        return d.toDateString() === today.toDateString() || d.toDateString() === tomorrow.toDateString();
      });
      futureSessions.forEach(s => {
        const isToday = new Date(s.date).toDateString() === today.toDateString();
        items.push({ id: `sess-${c.id}-${s.id}`, text: `${c.name} — session ${isToday ? "today" : "tomorrow"} at ${s.time || "TBD"}`, type: "info", clientId: c.id });
      });
      const allSessions = c.sessions.sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const lastSession = allSessions[0];
      if (lastSession && new Date(lastSession.date) < ago14 && new Date(lastSession.date) < today) {
        const days = Math.floor((today.getTime() - new Date(lastSession.date).getTime()) / 86400000);
        items.push({ id: `inactive-${c.id}`, text: `${c.name} — no session in ${days} days`, type: "warning", clientId: c.id });
      }
      if (paid.length === 0 && c.sessions.length > 0) {
        items.push({ id: `nopay-${c.id}`, text: `${c.name} — no payment on record`, type: "danger", clientId: c.id });
      }
    });

    const priority = { danger: 0, warning: 1, info: 2, success: 3 };
    return items.sort((a, b) => priority[a.type] - priority[b.type]).slice(0, 12);
  })();

  highlightsRef.current = highlights;
  useEffect(() => {
    if (highlightsRef.current.length <= 1) return;
    const tick = setInterval(() => {
      setHighlightVisible(false);
      setTimeout(() => {
        setHighlightIdx(i => (i + 1) % highlightsRef.current.length);
        setHighlightVisible(true);
      }, 300);
    }, 4000);
    return () => clearInterval(tick);
  }, []);

  const thisWeekSessions = (() => {
    const now = new Date();
    const dow = now.getDay();
    const monday = new Date(now); monday.setDate(now.getDate() - ((dow + 6) % 7)); monday.setHours(0,0,0,0);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); sunday.setHours(23,59,59,999);
    return clients.flatMap(c => c.sessions).filter(s => {
      const d = new Date(s.date);
      return d >= monday && d <= sunday;
    }).length;
  })();

  const weekNumber = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const week1 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  })();

  const kpiIcons: Record<string, React.ReactNode> = {
    "Collected": <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 7a6 6 0 1 0 0 10"/><line x1="4" y1="10" x2="13" y2="10"/><line x1="4" y1="14" x2="13" y2="14"/></svg>,
    "Today": <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>,
    "Total Clients": <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    "Active":        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    "This Week":     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  };

  // ── Appointments this week: calendar is the source of truth, stored sessions the fallback ──
  type Appt = { key: string; when: Date; time: string; title: string; clientId?: string };
  const weekAppts: Appt[] = (() => {
    if (calWeekEvents.length > 0) {
      return calWeekEvents.map((ev: any, i: number) => {
        const d = new Date(ev.start?.dateTime || ev.start?.date);
        const title: string = ev.summary || "Session";
        const matched = clients.find(c => c.name && title.toLowerCase().includes(c.name.split(" ")[0].toLowerCase()));
        return { key: ev.id || `cal-${i}`, when: d, time: ev.start?.dateTime ? d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) : "", title, clientId: matched?.id };
      });
    }
    const dow = new Date().getDay();
    const monday = new Date(); monday.setDate(monday.getDate() - ((dow + 6) % 7)); monday.setHours(0,0,0,0);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); sunday.setHours(23,59,59,999);
    return clients
      .flatMap(c => c.sessions.map(s => ({ key: `s-${c.id}-${s.id}`, when: new Date(`${String(s.date).slice(0, 10)}T${s.time || "00:00"}`), time: s.time || "", title: c.name, clientId: c.id })))
      .filter(a => a.when >= monday && a.when <= sunday)
      .sort((a, b) => a.when.getTime() - b.when.getTime());
  })();
  const todayAppts = weekAppts.filter(a => a.when.toDateString() === new Date().toDateString());
  const nextAppt = todayAppts.find(a => a.when.getTime() > Date.now());

  const activeUnfrozen = clients.filter(c => c.status === "active" && !c.frozen).length;
  const frozenCount = clients.filter(c => c.status === "active" && c.frozen).length;
  const monthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const collectedThisMonth = clients.reduce((t, c) => t + (c.payments || [])
    .filter((p: any) => p.status === "paid" && String(p.date || "").slice(0, 7) === monthKey)
    .reduce((s: number, p: any) => s + (parseFloat(String(p.amount ?? "").replace(/[^0-9.]/g, "")) || 0), 0), 0);

  const kpis = [
    { n: todayAppts.length,                     l: "Today",         accent: true,  sub: todayAppts.length === 1 ? "session" : "sessions" },
    { n: weekAppts.length || thisWeekSessions,  l: "This Week",     accent: false, sub: `Week ${weekNumber}` },
    { n: `€${Math.round(collectedThisMonth).toLocaleString("en-GB")}`, l: "Collected", accent: false, sub: new Date().toLocaleDateString("en-US", { month: "long" }) },
    { n: activeUnfrozen,                        l: "Active",        accent: false, sub: frozenCount ? `+${frozenCount} frozen` : null },
  ];

  const weekDays = (() => {
    const today = new Date();
    const dow = today.getDay(); // 0=Sun
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((dow + 6) % 7));
    const sessionDates = new Set(
      clients.flatMap(c => c.sessions.map(s => new Date(s.date).toDateString()))
    );
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return { d, label: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i], isToday: d.toDateString() === today.toDateString(), hasSession: sessionDates.has(d.toDateString()) };
    });
  })();

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SP.xl, maxWidth: 1240 }}>

      {/* ── Week Strip ── */}
      <section>
        <div style={{ marginBottom: SP.md }}>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 2 }}>{greeting}</div>
          <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 22 : 26, letterSpacing: 0.4, color: C.textPrimary, lineHeight: 1 }}>
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
        </div>
      </section>

      {/* ── KPI Strip ── */}
      <section>
        <div style={secLabel}>Overview</div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: SP.sm }}>
          {kpis.map(({ n, l, accent, sub }) => {
            const isCollected = l === "Collected";
            const clickable = l === "This Week" || l === "Today" || isCollected;
            return (
              <div key={l} title={isCollected ? (showCollected ? "Click to hide" : "Click to show") : undefined}
                onClick={clickable ? () => (isCollected ? setShowCollected(v => !v) : l === "Today" ? setShowTodayModal(true) : setShowWeekModal(true)) : undefined}
                style={{ ...kpiCard(accent), position: "relative", cursor: clickable ? "pointer" : "default" }}>
                <div style={{ position: "absolute", top: 10, right: 10, color: accent ? C.accent : C.textDim, opacity: 0.5 }}>
                  {kpiIcons[l]}
                </div>
                <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 28 : 30, color: accent ? C.accent : C.textPrimary, lineHeight: 1, marginBottom: 4 }}>
                  {isCollected && !showCollected ? "€ ••••" : n}
                </div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.8, textTransform: "uppercase" as const, color: C.textDim }}>
                  {l}
                </div>
                {sub && <div style={{ fontSize: 9, color: C.textDim, marginTop: 2, letterSpacing: 1 }}>{sub}</div>}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── This Week Modal ── */}
      {showWeekModal && (
        <div onClick={() => setShowWeekModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, width: "100%", maxWidth: 440, maxHeight: "80vh", display: "flex", flexDirection: "column" as const }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontFamily: F_SERIF, fontSize: 18, letterSpacing: 0.5, color: C.textPrimary }}>WEEK {weekNumber}</div>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginTop: 1 }}>{calWeekEvents.length} SESSION{calWeekEvents.length !== 1 ? "S" : ""}</div>
              </div>
              <button onClick={() => setShowWeekModal(false)} style={{ background: "none", border: "none", color: C.textDim, fontSize: 20, cursor: "pointer", lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            {/* Session list */}
            <div style={{ overflowY: "auto" as const, flex: 1, padding: "0 18px" }}>
              {calWeekEvents.length === 0 ? (
                <div style={{ padding: "32px 0", textAlign: "center" as const, color: C.textDim, fontSize: 13 }}>No sessions this week.</div>
              ) : calWeekEvents.map((ev: any, i: number) => {
                const dt = ev.start?.dateTime || ev.start?.date;
                const d = dt ? new Date(dt) : null;
                const timeStr = d && ev.start?.dateTime ? d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) : "";
                const dayStr = d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "";
                const title: string = ev.summary || "Session";
                const matched = clients.find((c: Client) => title.toLowerCase().includes(c.name.split(" ")[0].toLowerCase()));
                return (
                  <div key={ev.id || i} onClick={() => { if (matched) { onSelectClient(matched.id); setShowWeekModal(false); } }}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < calWeekEvents.length - 1 ? `1px solid ${C.border}` : "none", cursor: matched ? "pointer" : "default" }}>
                    <div style={{ minWidth: 44, textAlign: "center" as const, flexShrink: 0 }}>
                      <div style={{ fontFamily: F_SERIF, fontSize: 16, color: C.accent, lineHeight: 1 }}>{timeStr || "—"}</div>
                      <div style={{ fontSize: 9, color: C.textDim, letterSpacing: 0.5, marginTop: 1 }}>{dayStr}</div>
                    </div>
                    <div style={{ width: 1, height: 28, background: C.border, flexShrink: 0 }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                      <div style={S.avatar(28)}>{title.slice(0, 2).toUpperCase()}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Today Modal ── */}
      {showTodayModal && (
        <div onClick={() => setShowTodayModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, width: "100%", maxWidth: 440, maxHeight: "80vh", display: "flex", flexDirection: "column" as const }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontFamily: F_SERIF, fontSize: 18, letterSpacing: 0.5, color: C.textPrimary }}>TODAY</div>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 1, marginTop: 1 }}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }).toUpperCase()} · {todayAppts.length} SESSION{todayAppts.length !== 1 ? "S" : ""}</div>
              </div>
              <button onClick={() => setShowTodayModal(false)} style={{ background: "none", border: "none", color: C.textDim, fontSize: 20, cursor: "pointer", lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto" as const, flex: 1, padding: "0 18px" }}>
                {todayAppts.length === 0 ? (
                  <div style={{ padding: "32px 0", textAlign: "center" as const, color: C.textDim, fontSize: 13 }}>No sessions today.</div>
                ) : todayAppts.map((a, i) => {
                  const past = a.when.getTime() + 60 * 60 * 1000 < Date.now();
                  const [apptName, ...apptRest] = a.title.split(" — ");
                  const apptType = apptRest.join(" — ");
                  const isNext = a.key === nextAppt?.key;
                  const minsAway = Math.round((a.when.getTime() - Date.now()) / 60000);
                  const awayLabel = minsAway <= 0 ? "now" : minsAway < 60 ? `in ${minsAway}m` : `in ${Math.floor(minsAway / 60)}h${minsAway % 60 ? ` ${minsAway % 60}m` : ""}`;
                  return (
                    <div key={a.key} onClick={() => { if (a.clientId) { onSelectClient(a.clientId); setShowTodayModal(false); } }}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: i === todayAppts.length - 1 ? "none" : `1px solid ${C.border}`, cursor: a.clientId ? "pointer" : "default", opacity: past ? 0.45 : 1 }}>
                      <div style={{ fontFamily: F_SERIF, fontSize: 16, color: C.accent, minWidth: 48, flexShrink: 0 }}>{a.time || "—"}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" }}>{apptName}</div>
                        {apptType && <div style={{ fontSize: 11, color: C.textDim, marginTop: 1, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" }}>{apptType}</div>}
                      </div>
                      {past && <span style={{ fontSize: 10, color: C.textDim, flexShrink: 0 }}>done</span>}
                      {isNext && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" as const, color: C.accent, background: C.accentBg, border: `1px solid ${C.accentBorder}`, borderRadius: 4, padding: "2px 8px", flexShrink: 0, whiteSpace: "nowrap" as const }}>Next · {awayLabel}</span>}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
      {/* ── Payment Renewals (full width) ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 440px), 1fr))", gap: SP.xl, alignItems: "start" }}>

      {/* ── Payment Renewals ── */}
      {paymentDue.length > 0 && (() => {
        const AMBER = "#D89A46";                         // due-soon status only (gold stays reserved for money)
        const money = (s: string | null) => { const n = parseFloat(String(s ?? "").replace(/[^0-9.]/g, "")); return isNaN(n) ? 0 : n; };
        const euro0 = (n: number) => `€${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
        const fmtDate = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
        const plural = (n: number) => n === 1 ? "" : "s";
        const relLabel = (p: any) => {
          const n = Math.abs(p.daysUntil);
          if (p.daysUntil < -45 && !p.isPackage) return `Lapsed ${n} days — still a client?`;
          if (p.daysUntil < 0)  return p.isPackage ? `Expired ${n} day${plural(n)} ago` : `${n} day${plural(n)} overdue`;
          if (p.daysUntil === 0) return p.isPackage ? "Expires today" : "Due today";
          return p.isPackage ? `Expires in ${n} day${plural(n)}` : `Renews in ${n} day${plural(n)}`;
        };
        const typeLabel = (p: any) => p.pkgLabel || (p.isPackage ? "Session package" : "Monthly membership");
        // Packages expire on a date but don't bill on it — only memberships count as expected money
        const sum = (arr: any[]) => arr.filter(p => !p.isPackage).reduce((t, p) => t + money(p.lastAmount), 0);

        // Always-on summary reflects the full dataset (not the active filter)
        const overdueItems = paymentDue.filter(p => !p.isPackage && p.daysUntil < 0);
        const weekItems    = paymentDue.filter(p => !p.isPackage && p.daysUntil >= 0 && p.daysUntil <= 7);
        const in30Items    = paymentDue.filter(p => !p.isPackage && p.daysUntil >= 0 && p.daysUntil <= 30);

        // Filter narrows the rows; grouping + sort applied within
        const filtered = paymentDue.filter(p =>
          renewalFilter === "all"     ? true
          : renewalFilter === "overdue" ? p.daysUntil < 0
          : renewalFilter === "7"       ? p.daysUntil <= 7
          :                               p.daysUntil <= 30);
        const bucketOf = (p: any) => p.daysUntil < 0 ? "overdue" : p.daysUntil <= 7 ? "week" : p.daysUntil <= 30 ? "soon" : "later";
        const sortFn = (a: any, b: any) =>
          renewalSort === "amount" ? money(b.lastAmount) - money(a.lastAmount)
          : renewalSort === "client" ? a.clientName.localeCompare(b.clientName)
          : renewalSort === "date"   ? a.due.getTime() - b.due.getTime()
          :                            a.daysUntil - b.daysUntil;
        const groupDefs = [
          { key: "overdue", label: "Overdue",          color: C.danger,    stripe: C.danger },
          { key: "week",    label: "Due this week",    color: AMBER,       stripe: AMBER },
          { key: "soon",    label: "Due in 8–30 days", color: C.textMuted, stripe: "transparent" },
          { key: "later",   label: "Later",            color: C.textMuted, stripe: "transparent" },
        ];
        const groups = groupDefs
          .map(g => ({ ...g, items: filtered.filter(p => bucketOf(p) === g.key).sort(sortFn) }))
          .filter(g => g.items.length > 0);

        const chip = (key: any, label: string) => {
          const on = renewalFilter === key;
          return (
            <div key={key} onClick={() => setRenewalFilter(key)} style={{
              padding: "5px 11px", borderRadius: R.sm, cursor: "pointer", fontSize: 11, fontWeight: 600,
              letterSpacing: 0.5, textTransform: "uppercase" as const, userSelect: "none" as const, transition: "all 0.12s",
              background: on ? C.indigoBg : "transparent", color: on ? C.indigo : C.textDim,
              border: `1px solid ${on ? C.indigoBorder : C.border}`,
            }}>{label}</div>
          );
        };

        const summaryCard = (frame: string, title: string, big: React.ReactNode, sub: string, moneyLine: string) => (
          <div style={{ background: C.surfaceRaised, border: `1px solid ${C.border}`, borderTop: `2px solid ${frame}`, borderRadius: R.lg, padding: "10px 14px" }}>
            <div style={{ fontSize: 9, letterSpacing: 2, textTransform: "uppercase" as const, color: frame, fontWeight: 700 }}>{title}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginTop: 6 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary, lineHeight: 1 }}>{big}</span>
              <span style={{ fontSize: 12, color: C.textMuted }}>{sub}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.gold, marginTop: 4 }}>{moneyLine}</div>
          </div>
        );

        const renderRow = (p: any, last: boolean, stripe: string, statusColor: string) => (
          <div
            key={p.clientId}
            onClick={() => onSelectClient(p.clientId)}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "14px 16px 14px 13px", borderLeft: `3px solid ${stripe}`,
              borderBottom: last ? "none" : `1px solid ${C.border}`,
              cursor: "pointer", flexWrap: isMobile ? "wrap" as const : "nowrap" as const,
            }}
          >
            <div style={S.avatar(36)}>{p.clientName.slice(0, 2).toUpperCase()}</div>
            {/* Client + package (priority 2 + 5) */}
            <div style={{ flex: 1, minWidth: isMobile ? 120 : 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" }}>{p.clientName}</div>
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 2, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" }}>{typeLabel(p)}</div>
            </div>
            {/* Amount (priority 3, gold) + date/status (priority 1 + 4) */}
            <div style={{ textAlign: "right" as const, flexShrink: 0, minWidth: isMobile ? "auto" : 130 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: p.lastAmount && !p.isPackage ? C.gold : C.textDim }}>{p.lastAmount ? `€${money(p.lastAmount).toFixed(2)}` : "—"}</div>
              <div style={{ fontSize: 11, marginTop: 2, whiteSpace: "nowrap" as const }}>
                <span style={{ color: C.textMuted }}>Due {fmtDate(p.due)}</span>
                <span style={{ color: C.textDim }}> · </span>
                <span style={{ color: statusColor, fontWeight: 600 }}>{relLabel(p)}</span>
              </div>
            </div>
            {/* Actions (priority 6) — isolated from the row click */}
            <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginLeft: isMobile ? "auto" : 0 }}>
              <Btn variant="secondary" onClick={() => onSelectClient(p.clientId)} style={{ padding: "7px 12px", fontSize: 12, color: C.indigo, borderColor: C.indigoBorder }}>Open client</Btn>
            </div>
          </div>
        );

        return (
          <section style={{ minWidth: 0 }}>
            {/* Toolbar: label · filters · sort */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" as const, marginBottom: SP.sm }}>
              <div style={{ ...secLabel, marginBottom: 0 }}>Payment Renewals</div>
            </div>

            {/* Summary strip */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: SP.sm, marginBottom: SP.sm }}>
              {summaryCard(C.danger, "Overdue", overdueItems.length, `payment${plural(overdueItems.length)}`, `${euro0(sum(overdueItems))} outstanding`)}
              {summaryCard(AMBER, "Due this week", weekItems.length, `renewal${plural(weekItems.length)}`, `${euro0(sum(weekItems))} expected`)}
              {summaryCard(C.gold, "Next 30 days", euro0(sum(in30Items)), `${in30Items.length} renewal${plural(in30Items.length)}`, "expected revenue")}
            </div>

            {/* Grouped renewals */}
            <div style={{ display: "flex", flexDirection: "column" as const, gap: SP.sm }}>
              {groups.length === 0 ? (
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, padding: "16px", fontSize: 12, color: C.textDim, fontStyle: "italic" as const }}>No renewals match this filter.</div>
              ) : groups.map(g => {
                const collapsed = collapsedRenewalGroups.has(g.key);
                return (
                  <div key={g.key} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
                    <div onClick={() => toggleRenewalGroup(g.key)} style={{ padding: "9px 16px", borderBottom: collapsed ? "none" : `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" as const }}>
                      <span style={{ fontSize: 9, color: C.textDim, width: 9, flexShrink: 0, transition: "transform 0.15s", transform: collapsed ? "none" : "rotate(90deg)" }}>▶</span>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: g.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.8, textTransform: "uppercase" as const, color: g.color }}>{g.label}</span>
                      <span style={{ fontSize: 10, color: C.textDim }}>{g.items.length}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.gold, marginLeft: "auto" }}>{euro0(sum(g.items))}</span>
                    </div>
                    {!collapsed && g.items.map((p, i) => renderRow(p, i === g.items.length - 1, g.stripe, g.color))}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}
      </div>

      {/* ── Program Review Reminders ── */}
      {(() => {
        // Program end date drives the reminder (≤7 days out or overdue); clients
        // without dates fall back to "scheme not edited in 4+ weeks"
        const reviewInfo = (c: Client): { label: string; urgency: number; overdue: boolean } | null => {
          if (!c.trainingScheme) return null;
          if (c.programEnd) {
            const daysLeft = Math.ceil((new Date(c.programEnd + "T00:00:00").getTime() - today.getTime()) / 86400000);
            if (daysLeft > 7) return null;
            const label = daysLeft > 0 ? `ends in ${daysLeft}d — update plan` : daysLeft === 0 ? "ends today — update plan" : `ended ${-daysLeft}d ago — update plan`;
            return { label, urgency: daysLeft, overdue: daysLeft <= 0 };
          }
          if (!c.schemeUpdatedAt) return null;
          const days = Math.floor((today.getTime() - new Date(c.schemeUpdatedAt).getTime()) / 86400000);
          if (days < 28) return null;
          return { label: `${Math.floor(days / 7)}w — adjust program`, urgency: 28 - days, overdue: false };
        };
        const allDue = clients
          .filter(c => c.status === "active" && !c.frozen)
          .map(c => ({ c, info: reviewInfo(c) }))
          .filter((x): x is { c: Client; info: { label: string; urgency: number; overdue: boolean } } => x.info !== null)
          .sort((a, b) => a.info.urgency - b.info.urgency);
        if (allDue.length === 0) return null;
        // Dated programs (ending/ended) always show; "not edited in 4+ weeks" is folded behind a toggle
        const dated = allDue.filter(x => !!x.c.programEnd);
        const stale = allDue.filter(x => !x.c.programEnd);
        const staleOpen = expandedDashCats.has("stale-programs");
        const due = staleOpen ? allDue : dated;
        return (
          <section>
            <div style={secLabel}>Program Review</div>
            <div style={{ background: C.surface, border: `1px solid ${C.goldBorder}`, borderTop: `2px solid ${C.gold}`, borderRadius: R.lg, padding: `0 ${SP.md}px` }}>
              {due.map(({ c, info }, i) => (
                <div key={c.id} onClick={() => onSelectClient(c.id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: i === due.length - 1 ? "none" : `1px solid ${C.border}`, cursor: "pointer", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                    <div style={S.avatar(24)}>{(c.name || '?').slice(0, 2).toUpperCase()}</div>
                    <span style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</span>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: info.overdue ? C.danger : C.gold, background: info.overdue ? C.dangerBg : C.goldBg, border: `1px solid ${info.overdue ? C.dangerBorder : C.goldBorder}`, borderRadius: 4, padding: "2px 8px", whiteSpace: "nowrap" as const, flexShrink: 0 }}>
                    {info.label}
                  </div>
                </div>
              ))}
              {stale.length > 0 && (
                <div onClick={() => toggleDashCat("stale-programs")} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: due.length > 0 ? `1px solid ${C.border}` : "none", cursor: "pointer", userSelect: "none" as const }}>
                  <span style={{ fontSize: 9, color: C.textDim, width: 9, flexShrink: 0, transition: "transform 0.15s", transform: staleOpen ? "rotate(90deg)" : "none" }}>▶</span>
                  <span style={{ fontSize: 12, color: C.textMuted }}>{stale.length} program{stale.length === 1 ? "" : "s"} not edited in 4+ weeks</span>
                </div>
              )}
            </div>
          </section>
        );
      })()}


    </div>
  );
}


function QuestionnaireEntryRow({ entry, qLabels, isLast }: { entry: TrainingQuestionnaireEntry & { label?: string }; qLabels: Record<string, string>; isLast: boolean }) {
  const [open, setOpen] = useState(false);
  const answered = Object.entries(entry.answers).filter(([, v]) => v);
  const isIntake = entry.id === "intake";
  return (
    <div style={{ borderBottom: isLast ? "none" : `1px solid ${C.border}` }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {(isIntake || entry.label) && (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" as const, background: C.accentBg, color: C.accent, border: `1px solid ${C.accentBorder}`, borderRadius: 4, padding: "2px 7px" }}>{entry.label || "Intake"}</span>
          )}
          <span style={{ fontSize: 11, color: C.textDim, fontWeight: 600 }}>
            {new Date(entry.submittedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
          </span>
          <span style={{ fontSize: 11, color: C.textDim }}>{answered.length} answered</span>
        </div>
        <span style={{ fontSize: 12, color: C.textDim }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ padding: "0 16px 16px" }}>
          {answered.map(([k, v]) => (
            <div key={k} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: C.accent, marginBottom: 4, textTransform: "uppercase" as const }}>{qLabels[k] || k}</div>
              <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.6, whiteSpace: "pre-wrap" as const }}>{v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function parseTrainingScheme(text: string) {
  const days: { day: string; exercises: { name: string; sets: string; reps: string; weight: string; rest: string; notes: string; isSuperset?: boolean; supersetWith?: string }[] }[] = [];
  if (!text) return days;
  // Clean markdown bold
  const cleaned = text.replace(/\*\*/g, "");
  const lines = cleaned.split("\n");
  let currentDay: typeof days[0] | null = null;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    // Match week/phase headers — covers: ## Week 1, # PHASE 2, ## Phase 2 — Week 1, # WEEK 1 — PHASE 2 etc.
    if (
      /^#{1,4}\s*week\s*\d/i.test(trimmed) ||
      /^#{1,4}\s*phase\s*\d/i.test(trimmed) ||
      /^#{1,4}\s*(block|mesocycle|cycle|program)\s*\d/i.test(trimmed)
    ) {
      const weekTitle = trimmed.replace(/^#+\s*/, "").trim();
      currentDay = { day: "WEEK:" + weekTitle, exercises: [] };
      days.push(currentDay);
    } else if (/^(day\s*\d|training\s*day\s*\d|workout\s*\d|session\s*\d|monday|tuesday|wednesday|thursday|friday|saturday|sunday|rest\s*day)/i.test(trimmed.replace(/^#+\s*/, "").replace(/:$/, ""))) {
      const strippedLine = trimmed.replace(/^#+\s*/, "");
      currentDay = { day: strippedLine, exercises: [] };
      days.push(currentDay);
    } else if (currentDay && trimmed.length > 2) {
      // Skip notes/headers lines first
      if (/^(notes|tip|rest)/i.test(trimmed) && !trimmed.includes("|")) continue;
      // All-caps section labels without pipes (PREP WORK, MAIN LIFTS, ACCESSORIES, etc.) → block header
      if (!trimmed.includes("|") && /^[A-Z][A-Z\s\/-]{3,38}$/.test(trimmed)) {
        currentDay.exercises.push({ name: "### " + trimmed, sets: "", reps: "", weight: "", rest: "", notes: "" });
        continue;
      }
      // Markdown heading lines (# BLOCK A: ..., ## COOL DOWN, etc.) that aren't week/phase → block header
      if (/^#{1,4}\s+/.test(trimmed) && !trimmed.includes("|")) {
        currentDay.exercises.push({ name: "### " + trimmed.replace(/^#+\s*/, ""), sets: "", reps: "", weight: "", rest: "", notes: "" });
        continue;
      }
      // Detect superset notation (2A., 2B., 3A., 3B. etc)
      const isSuperset = /^\d+[A-Z]\./i.test(trimmed) || /^superset/i.test(trimmed);
      const withoutNum = trimmed.replace(/^\d+\.\s*/, "");
      const parts = withoutNum.split("|").map(p => p.trim());
      if (parts.length >= 2) {
        // Detect if parts[4] is rest (contains min/sec/s/m pattern) or notes
        const p4 = parts[4] || "";
        const p5 = parts[5] || "";
        const isRestField = /\d+\s*(min|sec|s\b|m\b|'|")/i.test(p4) || /^rest/i.test(p4);
        // If parts[1] looks like reps (e.g. "8-10 reps each side") but not sets, shift fields
        const p1IsReps = /reps?/i.test(parts[1] || "") && !/sets?/i.test(parts[1] || "");
        currentDay.exercises.push({
          name: parts[0].replace(/^\d+[A-Za-z]?\.\s*/, ""),
          sets: p1IsReps ? "" : (parts[1] || ""),
          reps: p1IsReps ? parts[1] : (parts[2] || ""),
          weight: parts[3] || "",
          rest: isRestField ? p4 : p5.match(/\d+\s*(min|sec|s\b|m\b)/i) ? p5 : "",
          notes: isRestField ? p5 : p4,
          isSuperset
        });
      } else {
        // No pipe format - try to extract from text
        const setsMatch = trimmed.match(/(\d+)\s*sets?/i);
        const repsMatch = trimmed.match(/(\d+[-–]?\d*)\s*reps?/i);
        currentDay.exercises.push({
          name: trimmed.replace(/\|.*/,"").replace(/^[*\-]\s*/, "").trim(),
          sets: setsMatch ? setsMatch[1] + " sets" : "",
          reps: repsMatch ? repsMatch[1] + " reps" : "",
          weight: "",
          rest: "",
          notes: ""
        });
      }
    }
  }
  if (days.length === 0 && text.trim()) {
    return [{ day: "Training Plan", exercises: [{ name: text, sets: "", reps: "", weight: "", notes: "" }] }];
  }
  return days;
}

// Reorders training-day blocks within each week so weekdays run Monday→Sunday, regardless of
// the order the AI generated them (it tends to list training days first and dump rest/cardio/
// optional days at the end). "Day N" numbers are rewritten to match the new order. Blocks
// without a recognizable weekday keep their slots; weeks with duplicate weekdays, already in
// order, or with fewer than 2 weekday-tagged days are left byte-for-byte untouched.
const WEEKDAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
function normalizeWeekdayOrder(text: string): string {
  if (!text) return text;
  const isWeekHeader = (t: string) =>
    /^#{1,4}\s*week\s*\d/i.test(t) || /^#{1,4}\s*phase\s*\d/i.test(t) || /^#{1,4}\s*(block|mesocycle|cycle|program)\s*\d/i.test(t);
  const isDayHeader = (t: string) =>
    /^(day\s*\d|training\s*day\s*\d|workout\s*\d|session\s*\d|monday|tuesday|wednesday|thursday|friday|saturday|sunday|rest\s*day)/i.test(t.replace(/^#+\s*/, "").replace(/:$/, ""));
  const weekdayOf = (header: string): number => {
    const lower = header.toLowerCase();
    for (let w = 0; w < WEEKDAY_ORDER.length; w++) if (lower.includes(WEEKDAY_ORDER[w])) return w;
    return -1;
  };

  type Day = { lines: string[]; weekday: number };
  type Section = { intro: string[]; days: Day[]; outro: string[] };
  const sections: Section[] = [{ intro: [], days: [], outro: [] }];
  let sec = sections[0];
  let curDay: Day | null = null;
  let inOutro = false;
  for (const line of text.split("\n")) {
    const t = line.trim();
    if (isWeekHeader(t)) {
      sec = { intro: [line], days: [], outro: [] };
      sections.push(sec);
      curDay = null;
      inOutro = false;
    } else if (t && isDayHeader(t)) {
      curDay = { lines: [line], weekday: weekdayOf(t) };
      sec.days.push(curDay);
      inOutro = false;
    } else if (/^#{1,2}\s+/.test(t) && !t.includes("|")) {
      // A top-level heading that isn't a week or day (e.g. "## Program Notes") ends the day
      // blocks — keep it and everything after it anchored to the bottom of the section.
      inOutro = true;
      sec.outro.push(line);
      curDay = null;
    } else if (inOutro) {
      sec.outro.push(line);
    } else if (curDay) {
      curDay.lines.push(line);
    } else {
      sec.intro.push(line);
    }
  }

  const out: string[] = [];
  for (const s of sections) {
    out.push(...s.intro);
    const tagged = s.days.filter(d => d.weekday >= 0);
    const order = tagged.map(d => d.weekday);
    const alreadySorted = order.every((w, i) => i === 0 || order[i - 1] <= w);
    if (tagged.length < 2 || new Set(order).size !== order.length || alreadySorted) {
      for (const d of s.days) out.push(...d.lines);
    } else {
      const sorted = [...tagged].sort((a, b) => a.weekday - b.weekday);
      let k = 0;
      const reordered = s.days.map(d => (d.weekday >= 0 ? sorted[k++] : d));
      reordered.forEach((d, i) => {
        d.lines[0] = d.lines[0].replace(/day\s*\d+/i, "Day " + (i + 1));
      });
      for (const d of reordered) out.push(...d.lines);
    }
    out.push(...s.outro);
  }
  return out.join("\n");
}

function TrainingTab({ client, draft, setDraft, editing, setEditing, save, onUpdate, workoutLogs = [], prs = {} }: {
  client: Client; draft: Client; setDraft: (c: Client) => void;
  editing: boolean; setEditing: (b: boolean) => void; save: () => void; onUpdate: (c: Client) => void;
  workoutLogs?: any[]; prs?: {[ex: string]: {weight: number; reps: number; date: string; estimated1RM: number}};
}) {
  const [generating, setGenerating] = useState(false);
  const [viewMode, setViewMode] = useState<"structured" | "raw">("structured");
  const [supersetMode, setSupersetMode] = useState(false);
  const [selected, setSelected] = useState<{di: number, ei: number, name: string, isSuperset: boolean}[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyVersions, setHistoryVersions] = useState<{scheme: string; savedAt: string; archivedAt: string}[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySelected, setHistorySelected] = useState<number>(0);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadProgramPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await authFetch(`${API}/api/clients/${client.id}/program-pdf`);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.error || "Could not generate the PDF — please try again.");
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `Metaphysique_Program_${(client.name || "Client").replace(/[^\w]+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Could not generate the PDF — please try again."); }
    setPdfBusy(false);
  };
  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await authFetch(`${API}/api/clients/${client.id}/scheme-history`);
      const data = await res.json();
      setHistoryVersions(data);
      setHistorySelected(0);
    } catch { /* ignore */ }
    setHistoryLoading(false);
  };
  const parsedDays = parseTrainingScheme(client.trainingScheme);
  let _adminInProgRules = false;
  const parsedWeeks = parsedDays.reduce((acc: {week: string, days: typeof parsedDays}[], day) => {
    if (day.day.match(/^WEEK:/)) {
      const weekTitle = day.day.replace("WEEK:", "");
      _adminInProgRules = /progression\s*rules?/i.test(weekTitle);
      if (!_adminInProgRules) acc.push({week: day.day, days: []});
    } else {
      if (acc.length === 0) acc.push({week: "", days: []});
      if (_adminInProgRules) {
        const prev = acc[acc.length - 1].days;
        if (!prev.some((d: any) => d.day === day.day)) prev.push(day);
      } else {
        acc[acc.length - 1].days.push(day);
      }
    }
    return acc;
  }, []);
  const hasPhaseHeaders = parsedWeeks.some(w => /^phase\s*\d/i.test(w.week.replace("WEEK:", "").trim()));
  const parsedPhases: { label: string; endDate: string | null; weeks: typeof parsedWeeks }[] = hasPhaseHeaders ? (() => {
    const result: { label: string; endDate: string | null; weeks: typeof parsedWeeks }[] = [];
    let cur: (typeof result)[0] | null = null;
    let curPhaseNum = -1;
    for (const w of parsedWeeks) {
      const title = w.week.replace("WEEK:", "").trim();
      if (/^phase\s*\d/i.test(title)) {
        const phaseNumMatch = title.match(/^phase\s*(\d+)/i);
        const phaseNum = phaseNumMatch ? parseInt(phaseNumMatch[1]) : -1;
        const endMatch = title.match(/ends?\s+(.+)/i);
        if (phaseNum === curPhaseNum && cur) {
          // Same phase number — this is another week inside the current phase
          const weekLabel = title.replace(/^phase\s*\d+\W*/i, "") || title;
          if (w.days.length > 0) cur.weeks.push({ ...w, week: "WEEK:" + weekLabel });
        } else {
          // New phase number
          curPhaseNum = phaseNum;
          const phaseLabel = title
            .replace(/\W+week\s*\d.*/i, "")
            .replace(/\W+ends?.*/i, "")
            .trim();
          cur = { label: phaseLabel, endDate: endMatch ? endMatch[1].trim() : null, weeks: [] };
          result.push(cur);
          if (w.days.length > 0) {
            const weekLabel = title.replace(/^phase\s*\d+\W*/i, "") || title;
            cur.weeks.push({ ...w, week: "WEEK:" + weekLabel });
          }
        }
      } else {
        if (!cur) { cur = { label: "Phase 1", endDate: null, weeks: [] }; result.push(cur); curPhaseNum = 1; }
        if (w.days.length > 0) cur.weeks.push(w);
      }
    }
    return result;
  })() : [];
  const isEndDatePast = (dateStr: string): boolean => {
    const m = dateStr.match(/(\d+)\s+(\w{3})/i);
    if (!m) return false;
    const months: Record<string, number> = { jan:0, feb:1, mar:2, apr:3, may:4, jun:5, jul:6, aug:7, sep:8, oct:9, nov:10, dec:11 };
    const mo = months[(m[2] || "").toLowerCase()];
    if (mo === undefined) return false;
    const today = new Date();
    return new Date(today.getFullYear(), mo, parseInt(m[1])) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };
  const isCoachNoteLine = (ex: any) => !ex.sets && !ex.reps && !ex.weight && !/^###\s*/.test(ex.name) && !/same.?day\s+lighter\s+option/i.test(ex.name) && !/^-{2,}$/.test((ex.name||"").trim());
  const coachingNotesByDay = parsedDays.reduce((acc: {dayLabel: string; lines: string[]}[], day) => {
    const notes = day.exercises.filter(isCoachNoteLine).map(ex => ex.name);
    if (notes.length > 0) acc.push({ dayLabel: day.day.replace(/^Day\s*\d+\s*[-–]?\s*/i, "").trim() || day.day, lines: notes });
    return acc;
  }, []);
  const [collapsedWeeks, setCollapsedWeeks] = useState<Set<number>>(new Set([0,1,2,3,4,5,6,7,8,9,10,11,12]));
  const [notesOpen, setNotesOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [expandedDay, setExpandedDay] = useState<number | null>(null);
  const [chartExercise, setChartExercise] = useState<string | null>(null);
  const [inlineEditWi, setInlineEditWi] = useState<number | null>(null);
  const [inlineEditGdi, setInlineEditGdi] = useState<number | null>(null);
  const [inlineEditText, setInlineEditText] = useState("");
  const [inlineEditRange, setInlineEditRange] = useState<{ start: number; end: number } | null>(null);
  const [inlineSaving, setInlineSaving] = useState(false);

  const weekHeaderRe = /^#{1,4}\s*(week|phase|block)\s*\d/i;
  const dayHeaderRe = /^(day\s*\d+|training\s*day\s*\d|workout\s*\d|session\s*\d|monday|tuesday|wednesday|thursday|friday|saturday|sunday|rest\s*day)/i;

  const extractSegment = (ordinal: number, isWeek: boolean) => {
    const raw = client.trainingScheme || "";
    const rawLines = raw.split("\n");
    const cleanLines = raw.replace(/\*\*/g, "").split("\n");
    let count = -1;
    let startLine = -1;
    for (let i = 0; i < cleanLines.length; i++) {
      const t = cleanLines[i].trim().replace(/^#+\s*/, "");
      const hit = isWeek ? weekHeaderRe.test(cleanLines[i].trim()) : dayHeaderRe.test(t);
      if (hit) { count++; if (count === ordinal) { startLine = i; break; } }
    }
    if (startLine === -1) return null;
    let endLine = rawLines.length;
    for (let i = startLine + 1; i < cleanLines.length; i++) {
      const t = cleanLines[i].trim().replace(/^#+\s*/, "");
      const isEnd = isWeek ? weekHeaderRe.test(cleanLines[i].trim()) : (weekHeaderRe.test(cleanLines[i].trim()) || dayHeaderRe.test(t));
      if (isEnd) { endLine = i; break; }
    }
    return { text: rawLines.slice(startLine, endLine).join("\n").trimEnd(), start: startLine, end: endLine };
  };

  const openWeekEdit = (wi: number) => {
    const seg = extractSegment(wi, true);
    if (!seg) return;
    setInlineEditWi(wi); setInlineEditGdi(null);
    setInlineEditText(seg.text); setInlineEditRange({ start: seg.start, end: seg.end });
  };

  const openDayEdit = (gdi: number) => {
    const seg = extractSegment(gdi, false);
    if (!seg) return;
    setInlineEditGdi(gdi); setInlineEditWi(null);
    setInlineEditText(seg.text); setInlineEditRange({ start: seg.start, end: seg.end });
  };

  const saveInlineEdit = async () => {
    if (!inlineEditRange) return;
    setInlineSaving(true);
    const lines = (client.trainingScheme || "").split("\n");
    const newScheme = [
      ...lines.slice(0, inlineEditRange.start),
      ...inlineEditText.split("\n"),
      ...lines.slice(inlineEditRange.end),
    ].join("\n");
    const updated = { ...client, trainingScheme: newScheme };
    onUpdate(updated);
    try {
      await authFetch(`https://coachagent-server-production.up.railway.app/api/clients/${client.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(updated),
      });
    } catch { }
    setInlineEditWi(null); setInlineEditGdi(null); setInlineEditRange(null);
    setInlineSaving(false);
  };

  const cancelInlineEdit = () => { setInlineEditWi(null); setInlineEditGdi(null); setInlineEditRange(null); };

  const toggleWeek = (wi: number) => setCollapsedWeeks(prev => { const s = new Set(prev); s.has(wi) ? s.delete(wi) : s.add(wi); return s; });
  const getExerciseHistory = (exName: string) => workoutLogs
    .filter((log: any) => log.exercises?.some((ex: any) => ex.name === exName))
    .slice(-10)
    .map((log: any) => {
      const ex = log.exercises.find((e: any) => e.name === exName);
      const allSets = ex?.sets || [];
      const topSet = allSets.reduce((best: any, s: any) => (!best || parseFloat(s.weight) > parseFloat(best.weight)) ? s : best, null);
      return { date: log.date, allSets, topSet };
    });

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [genProgramType, setGenProgramType] = useState("Hypertrophy");
  const [genSFSubType, setGenSFSubType] = useState("Easy Strength Classic 2.0");
  const [genDays, setGenDays] = useState("4");
  const [genWeeks, setGenWeeks] = useState("4");
  const [genEquipment, setGenEquipment] = useState("Full gym");
  const [genFocus, setGenFocus] = useState("");

  const generateTraining = async () => {
    setShowGenerateModal(false);
    setGenerating(true);
    const latest = client.inbody[client.inbody.length - 1];

    const blockDefs: Record<string, string> = {
      "General Fitness": "full-body sessions every training day, 10-15 reps, moderate load, 2-3 sets per exercise — cover all fitness components: strength, cardio, mobility, and coordination. Ideal for general population training 1-3x/week. Keep sessions under 60 min, approachable for all levels",
      Strength: "3-5 reps, 85-90% 1RM, 4-5 sets, 3-5 min rest — focus on maximal force production",
      Hypertrophy: "8-12 reps, 65-75% 1RM, 3-4 sets, 60-90s rest — focus on muscle growth and time under tension",
      Power: "1-3 reps, 90%+ 1RM, 3-5 sets, 4-6 min rest — explosive intent on every rep",
      Deload: "same movement patterns, 50-60% 1RM, 2-3 sets, cut volume by 40% — active recovery",
      Athletic: "mix of strength, speed, and conditioning — vary rep ranges 3-8, include jumps/throws/carries",
    };
    const sfDefs: Record<string, string> = {
      "Easy Strength Classic 2.0": "2 sets x 5 reps at 70-80% 1RM, 5 days/week, always leave reps in reserve, rotate 5 core exercises, submaximal never to failure. Follow Easy Strength Classic 2.0 template exactly.",
      "Easy Strength → Easy Power": "Start with Easy Strength (2x5 @ 70-80%), then transition to Easy Power in later weeks by adding explosive/ballistic work at lower percentages. Keep volume low, quality high.",
      "Plan Strong Tactical": "Use Plan Strong Tactical template: tonnage-based programming with specific volume/intensity ratios. Use precise %1RM, vary rep ladders (e.g. 1-2-3 ladders), cycle intensity across the week.",
      "Plan Strong 1118D": "Use Plan Strong Algorithm 1118D: structured tonnage calculation, specific load distribution across the week and mesocycle. Prescribe %1RM precisely, use wave-like intensity variation per the 1118D algorithm.",
      "Linear Cycling": "Build a 4-6 week mesocycle where every consecutive main session is harder than the prior week, with weight and reps peaking in the final week. Classic SF linear progression — add load or reps each session systematically.",
      "Wave Cycling": "Build a 6-week mesocycle of 2 ascending waves (3 weeks up, brief reset, 3 weeks up again). Each wave ends harder than the last. Undulating intensity within each week, overall upward trend across waves.",
      "Step Cycling": "Use step loading: start light (low RPE), hold load constant for 2-3 weeks to accumulate volume, then step up. Good for beginners and early training blocks. Reverse RPE dynamics vs. linear.",
      "SF Hypertrophy": "StrongFirst hypertrophy principles: higher volume, moderate intensity (70-80% 1RM), 8-12 rep ranges, submaximal effort, never to failure. Structured mesocycles with clear volume and intensity cycling.",
    };
    const isSF = genProgramType === "StrongFirst";
    const effectiveProgramType = isSF ? `StrongFirst — ${genSFSubType}` : genProgramType;
    const effectiveBlockDef = isSF ? sfDefs[genSFSubType] : (blockDefs[genProgramType] || genProgramType);

    const prompt = `Create a complete ${genWeeks}-week training program for this client.

PROGRAM SPECIFICATIONS:
- Program type: ${effectiveProgramType}
- Block definition: ${effectiveBlockDef}
- Training days per week: ${genDays}
- Number of weeks: ${genWeeks}
- Equipment: ${genEquipment}
${genFocus ? `- Special focus / extra notes: ${genFocus}` : ""}

CLIENT INFO:
- Name: ${client.name}
- Goal: ${client.goal}
- Package: ${client.package}
- Notes: ${client.notes}
${latest ? `- Latest InBody: Weight ${latest.weight}kg, Muscle ${latest.muscleMass}kg, Body Fat ${latest.bodyFat}%` : ""}

FORMAT RULES — follow exactly, no deviations:
- Always open with a phase header: ## Phase 1, then each week inside it: ## Week 1, ## Week 2, etc.
- If the program has multiple phases (e.g. hypertrophy block then strength block), start each phase with ## Phase 2, ## Phase 3, etc. followed by its own ## Week 1, ## Week 2 sequence
- Start each training day with: Day 1 - Monday (Focus), Day 2 - Wednesday (Focus), etc.
- Days MUST appear in calendar order Monday → Sunday. Rest, cardio, and optional days go in their natural weekday position (e.g. a Wednesday rest day comes between Tuesday and Thursday) — NEVER grouped at the end of the week.
- Each exercise on its own line: Exercise Name | X sets | X-X reps | intensity | coaching note
- Rest days: Rest Day | — | — | — | Active recovery / mobility
- Write ALL ${genWeeks} weeks in full — never say "repeat week 1"
- Show clear progression week to week (reps, load, or volume)
- Progressive overload: increase reps first, then weight once top of range is reached`;

    try {
      // 16k tokens — a full multi-week program must never hit the cap (a truncated plan saves half-finished)
      const genMeta: { stopReason?: string } = {};
      const plan = await callClaude([{ role: "user", content: prompt }], { maxTokens: 16000, meta: genMeta });
      if (genMeta.stopReason === "max_tokens") { alert("The generated program was cut off before it finished — not saved. Try fewer weeks per generation."); setGenerating(false); return; }
      const updated = { ...client, trainingScheme: normalizeWeekdayOrder(plan), schemeUpdatedAt: new Date().toISOString().split("T")[0] };
      onUpdate(updated);
      // Auto-save to server
      await authFetch(`https://coachagent-server-production.up.railway.app/api/clients/${client.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated)
      });
    } catch { alert("Could not generate training plan."); }
    setGenerating(false);
  };

  // ── Inline helpers ────────────────────────────────────────────────────────
  const addRestTimes = async () => {
    const getRestTime = (repsStr: string): string => {
      const m = repsStr.match(/(\d+)/);
      if (!m) return "2 min";
      const r = parseInt(m[1]);
      if (r <= 5) return "3 min";
      if (r <= 8) return "2 min";
      if (r <= 12) return "90 sec";
      return "60 sec";
    };
    const newScheme = client.trainingScheme.split("\n").map(line => {
      const trimmed = line.trim();
      if (!trimmed) return line;
      const parts = trimmed.split("|").map((p: string) => p.trim());
      if (parts.length !== 5) return line;
      const isAlreadyRest = /\d+\s*(min|sec|s\b|m\b|'|")/i.test(parts[4]) || /^rest/i.test(parts[4]);
      if (isAlreadyRest) return line;
      const rest = getRestTime(parts[2]);
      const prefix = line.match(/^(\d+[A-Za-z]?\.\s*)/)?.[1] || "";
      const baseName = parts[0].replace(/^\d+[A-Za-z]?\.\s*/, "");
      return `${prefix}${baseName} | ${parts[1]} | ${parts[2]} | ${parts[3]} | ${rest} | ${parts[4]}`;
    }).join("\n");
    const updated = { ...client, trainingScheme: newScheme };
    onUpdate(updated);
    try {
      await authFetch(`https://coachagent-server-production.up.railway.app/api/clients/${client.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(updated),
      });
    } catch { alert("Save failed — please try again."); }
  };

  const applySupersetChange = async () => {
    if (selected.length !== 2) return;
    const isDecoupling = selected.every(s => s.isSuperset);
    const cleanA = selected[0].name.replace(/^\d+[A-Za-z]?\.\s*/, "").trim();
    const cleanB = selected[1].name.replace(/^\d+[A-Za-z]?\.\s*/, "").trim();
    const newScheme = client.trainingScheme.split("\n").map(line => {
      const clean = line.replace(/^\d+[A-Za-z]?\.\s*/, "").trim();
      const cl = clean.toLowerCase();
      const matchesA = cl.startsWith(cleanA.toLowerCase() + " |") || cl.startsWith(cleanA.toLowerCase() + "|");
      const matchesB = cl.startsWith(cleanB.toLowerCase() + " |") || cl.startsWith(cleanB.toLowerCase() + "|");
      if (isDecoupling) { if (matchesA || matchesB) return clean; }
      else { if (matchesA) return "1A. " + clean; if (matchesB) return "1B. " + clean; }
      return line;
    }).join("\n");
    const updated = { ...client, trainingScheme: newScheme };
    onUpdate(updated);
    try {
      await authFetch(`https://coachagent-server-production.up.railway.app/api/clients/${client.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(updated),
      });
    } catch { alert("Save failed — please try again."); }
    setSupersetMode(false);
    setSelected([]);
  };

  const isDecoupling = selected.length === 2 && selected.every(s => s.isSuperset);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        {/* View toggle */}
        <div style={{ display: "flex", gap: 2 }}>
          <button onClick={() => setViewMode("structured")} style={dsTab(viewMode === "structured")}>Structured</button>
          <button onClick={() => setViewMode("raw")} style={dsTab(viewMode === "raw")}>Edit Text</button>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {viewMode === "structured" && <>
            <Btn variant="ghost" onClick={addRestTimes}>⏱ Rest</Btn>
            <Btn variant={supersetMode ? "secondary" : "ghost"} onClick={() => { setSupersetMode(!supersetMode); setSelected([]); }}>
              {supersetMode ? "✕ Cancel" : "🔗 Superset"}
            </Btn>
            {supersetMode && selected.length === 2 && (
              <Btn variant={isDecoupling ? "danger" : "primary"} onClick={applySupersetChange}>
                {isDecoupling ? "✕ Decouple" : "Apply Superset"}
              </Btn>
            )}
          </>}
          {viewMode === "raw" && (
            !editing
              ? <Btn variant="ghost" onClick={() => setEditing(true)}>Edit</Btn>
              : <Btn variant="primary" onClick={save}>Save</Btn>
          )}
          {client.trainingScheme && (
            <Btn variant="ghost" onClick={downloadProgramPdf} disabled={pdfBusy}>
              {pdfBusy ? "Preparing…" : "📄 PDF"}
            </Btn>
          )}
          {client.trainingScheme && (
            <Btn variant="ghost" onClick={openHistory}>🕐 History</Btn>
          )}
          <Btn variant="primary" onClick={() => setShowGenerateModal(true)} disabled={generating}>
            {generating ? "Generating…" : "✨ Generate"}
          </Btn>
        </div>
      </div>

      {/* ── Program dates ───────────────────────────────────────────────── */}
      {client.trainingScheme && (() => {
        const weekCount = parsedWeeks.filter(w => w.days.length > 0).length;
        const suggestEnd = (startStr: string) => {
          const d = new Date(startStr + "T12:00:00");
          d.setDate(d.getDate() + Math.max(weekCount, 1) * 7 - 1);
          return d.toISOString().split("T")[0];
        };
        const now = new Date(); now.setHours(0, 0, 0, 0);
        const daysLeft = client.programEnd ? Math.ceil((new Date(client.programEnd + "T00:00:00").getTime() - now.getTime()) / 86400000) : null;
        const dateInput: React.CSSProperties = { background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "5px 9px", color: C.textPrimary, fontSize: 12, outline: "none", colorScheme: "dark", fontFamily: "'Inter', sans-serif" };
        const chipColors = daysLeft === null ? null
          : daysLeft > 7 ? { color: C.accent, bg: C.accentBg, border: C.accentBorder }
          : daysLeft >= 0 ? { color: C.gold, bg: C.goldBg, border: C.goldBorder }
          : { color: C.danger, bg: C.dangerBg, border: C.dangerBorder };
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" as const, background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, padding: "10px 16px" }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: C.textDim }}>Program Dates</span>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMuted }}>
              Start
              <input type="date" value={client.programStart || ""} style={dateInput} onChange={e => {
                const v = e.target.value;
                const updated: Client = { ...client, programStart: v };
                if (v && !client.programEnd) updated.programEnd = suggestEnd(v);
                onUpdate(updated);
              }} />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMuted }}>
              End
              <input type="date" value={client.programEnd || ""} style={dateInput} onChange={e => onUpdate({ ...client, programEnd: e.target.value })} />
            </label>
            {daysLeft !== null && chipColors && (
              <span style={{ fontSize: 10, fontWeight: 700, color: chipColors.color, background: chipColors.bg, border: `1px solid ${chipColors.border}`, borderRadius: R.sm, padding: "3px 9px", marginLeft: "auto" }}>
                {daysLeft > 0 ? `Ends in ${daysLeft}d` : daysLeft === 0 ? "Ends today" : `Ended ${-daysLeft}d ago`}
              </span>
            )}
          </div>
        );
      })()}

      {/* ── Superset mode hint ──────────────────────────────────────────── */}
      {supersetMode && (
        <div style={{ background: C.accentBg, border: `1px solid ${C.accentBorder}`, borderRadius: R.md, padding: "10px 16px", fontSize: 12, color: C.accent, display: "flex", alignItems: "center", gap: 8 }}>
          <span>🔗</span>
          <span>Open a day and click 2 exercises to pair them.{selected.length > 0 ? ` ${selected.length}/2 selected.` : ""}</span>
        </div>
      )}

      {/* ── Raw edit ────────────────────────────────────────────────────── */}
      {viewMode === "raw" ? (
        <div style={dsCard}>
          {editing
            ? <textarea style={{ ...inputStyle, resize: "vertical" as const, minHeight: 400 }} value={draft.trainingScheme} onChange={e => setDraft({ ...draft, trainingScheme: e.target.value })} placeholder={"Enter training plan...\nDay 1 - Monday\nBench Press | 4 sets | 6-8 reps | 70% 1RM"} />
            : <div style={{ fontSize: 13, color: client.trainingScheme ? C.textPrimary : C.textDim, lineHeight: 1.8, whiteSpace: "pre-wrap" as const }}>{client.trainingScheme || "No training plan yet. Click Generate!"}</div>
          }
        </div>

      ) : (
        /* ── Structured view ──────────────────────────────────────────── */
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {parsedDays.length === 0 ? (

            /* Empty state */
            <div style={{ ...dsCard, textAlign: "center" as const, padding: 40 }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>💪</div>
              <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 16 }}>No training plan yet.</div>
              <Btn variant="primary" onClick={() => setShowGenerateModal(true)} disabled={generating}>
                {generating ? "Generating…" : "✨ Generate Plan"}
              </Btn>
            </div>

          ) : (() => {
            const flatWeeks = parsedWeeks.filter(w => w.days.length > 0);
            const renderWeekBlock = (week: any, wi: number, weekStartDi: number) => {
              const isCollapsed = collapsedWeeks.has(wi);
              return (
              <div key={wi} style={{ marginBottom: hasPhaseHeaders ? 4 : 0 }}>

                {/* Week header */}
                <div
                  onClick={() => { if (inlineEditWi === wi) return; toggleWeek(wi); }}
                  style={{
                    cursor: "pointer", userSelect: "none" as const,
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "13px 18px",
                    background: inlineEditWi === wi ? C.surfaceMid : C.surfaceRaised,
                    border: `1px solid ${inlineEditWi === wi ? C.accentBorder : C.border}`,
                    borderRadius: (isCollapsed && inlineEditWi !== wi) ? R.lg : `${R.lg}px ${R.lg}px 0 0`,
                  }}
                >
                  <span style={{ color: C.accent, fontSize: 9, opacity: 0.8, width: 10, flexShrink: 0 }}>{isCollapsed && inlineEditWi !== wi ? "▶" : "▼"}</span>
                  <span style={{ fontFamily: F_SANS, fontSize: 13, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textPrimary }}>
                    {week.week.replace("WEEK:", "").trim() || "Training Plan"}
                  </span>
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: C.textDim, textTransform: "uppercase" as const }}>{week.days.length} days</span>
                    <button
                      onClick={e => { e.stopPropagation(); inlineEditWi === wi ? cancelInlineEdit() : openWeekEdit(wi); }}
                      title={inlineEditWi === wi ? "Cancel edit" : "Edit week"}
                      style={{ background: inlineEditWi === wi ? C.dangerBg : "none", border: inlineEditWi === wi ? `1px solid ${C.dangerBorder}` : "none", color: inlineEditWi === wi ? C.danger : C.textDim, cursor: "pointer", fontSize: 11, padding: "2px 7px", borderRadius: R.sm, lineHeight: 1 }}
                    >{inlineEditWi === wi ? "✕" : "✎"}</button>
                  </div>
                </div>

                {/* Inline week editor */}
                {inlineEditWi === wi && (
                  <div style={{ border: `1px solid ${C.accentBorder}`, borderTop: "none", borderRadius: `0 0 ${R.lg}px ${R.lg}px`, background: C.surfaceMid, padding: 14 }}>
                    <textarea
                      value={inlineEditText}
                      onChange={e => setInlineEditText(e.target.value)}
                      style={{ width: "100%", minHeight: 320, background: C.bg, border: `1px solid ${C.borderInput}`, borderRadius: R.md, color: C.textPrimary, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", padding: "10px 12px", outline: "none", resize: "vertical" as const, boxSizing: "border-box" as const, lineHeight: 1.6 }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <Btn variant="ghost" onClick={cancelInlineEdit} style={{ fontSize: 12 }}>Cancel</Btn>
                      <Btn variant="primary" onClick={saveInlineEdit} disabled={inlineSaving} style={{ fontSize: 12 }}>{inlineSaving ? "Saving…" : "Save Week"}</Btn>
                    </div>
                  </div>
                )}

                {/* Days */}
                {!isCollapsed && inlineEditWi !== wi && (
                  <div style={{ border: `1px solid ${C.border}`, borderTop: "none", borderRadius: `0 0 ${R.lg}px ${R.lg}px`, overflow: "hidden" }}>
                    {week.days.map((day: any, di: number) => {
                      const gdi = weekStartDi + di;
                      const isOpen = expandedDay === gdi;
                      const isDayEditing = inlineEditGdi === gdi;
                      return (
                        <div key={di} style={{ borderBottom: di < week.days.length - 1 ? `1px solid ${C.border}` : "none" }}>

                          {/* Day header */}
                          <div
                            onClick={() => { if (isDayEditing) return; setExpandedDay(isOpen ? null : gdi); }}
                            style={{
                              background: isDayEditing ? C.surfaceMid : isOpen ? C.surfaceMid : C.surface,
                              padding: "13px 18px",
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              cursor: "pointer", transition: "background 0.1s",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, color: C.textDim, textTransform: "uppercase" as const, flexShrink: 0 }}>Day {di + 1}</span>
                              {(() => { const m = day.day?.match(/\(([^)]+)\)/); const label = m ? m[1] : day.day?.replace(/^Day\s+\d+\s*[-–]\s*\w+\s*/i, '').trim(); return label ? <span style={{ fontSize: 11, color: C.textMuted, fontWeight: 500 }}>{label}</span> : null; })()}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {(() => { const c = day.exercises.filter((e: any) => (e.sets || e.reps || e.weight) && !/^###\s*/.test(e.name)).length; return c > 0 ? <span style={{ fontSize: 10, color: C.textDim }}>{c} ex</span> : null; })()}
                              <button
                                onClick={e => { e.stopPropagation(); isDayEditing ? cancelInlineEdit() : (openDayEdit(gdi), setExpandedDay(gdi)); }}
                                title={isDayEditing ? "Cancel edit" : "Edit day"}
                                style={{ background: isDayEditing ? C.dangerBg : "none", border: isDayEditing ? `1px solid ${C.dangerBorder}` : "none", color: isDayEditing ? C.danger : C.textDim, cursor: "pointer", fontSize: 11, padding: "2px 6px", borderRadius: R.sm, lineHeight: 1 }}
                              >{isDayEditing ? "✕" : "✎"}</button>
                              <span style={{ fontSize: 10, color: C.textDim }}>{(isOpen || isDayEditing) ? "▲" : "▼"}</span>
                            </div>
                          </div>

                          {/* Inline day editor */}
                          {isDayEditing && (
                            <div style={{ padding: "12px 14px 14px", background: C.surfaceMid, borderTop: `1px solid ${C.border}` }}>
                              <textarea
                                value={inlineEditText}
                                onChange={e => setInlineEditText(e.target.value)}
                                style={{ width: "100%", minHeight: 200, background: C.bg, border: `1px solid ${C.accentBorder}`, borderRadius: R.md, color: C.textPrimary, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", padding: "10px 12px", outline: "none", resize: "vertical" as const, boxSizing: "border-box" as const, lineHeight: 1.6 }}
                              />
                              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                                <Btn variant="ghost" onClick={cancelInlineEdit} style={{ fontSize: 12 }}>Cancel</Btn>
                                <Btn variant="primary" onClick={saveInlineEdit} disabled={inlineSaving} style={{ fontSize: 12 }}>{inlineSaving ? "Saving…" : "Save Day"}</Btn>
                              </div>
                            </div>
                          )}

                          {/* Exercise list */}
                          {isOpen && !isDayEditing && (
                            <div style={{ padding: "12px 14px 14px", background: C.bg, display: "flex", flexDirection: "column", gap: 6 }}>
                              {day.exercises.length === 0 && (
                                <div style={{ color: C.textDim, fontSize: 13, padding: "6px 0" }}>Rest day — active recovery</div>
                              )}
                              {day.exercises.map((ex: any, ei: number) => {
                                if (isCoachNoteLine(ex) || /^-{2,}$/.test((ex.name||"").trim())) return null;
                                const isBlockHeader = /^###\s*/.test(ex.name);
                                const isSameDayOption = /same.?day\s+lighter\s+option/i.test(ex.name);
                                if (isBlockHeader) {
                                  return (
                                    <div key={ei} style={{ paddingTop: ei > 0 ? 10 : 4, paddingBottom: 4 }}>
                                      <span style={{ fontFamily: F_SANS, fontSize: 12, fontWeight: 600, letterSpacing: 2, color: C.accent, textTransform: "uppercase" as const }}>
                                        {ex.name.replace(/^###\s*/, "")}
                                      </span>
                                    </div>
                                  );
                                }
                                if (isSameDayOption) {
                                  return (
                                    <div key={ei} style={{ padding: "8px 14px", background: C.indigoBg, border: `1px dashed ${C.indigoBorder}`, borderRadius: R.md, marginTop: 6 }}>
                                      <span style={{ fontSize: 11, fontStyle: "italic", color: C.indigo, letterSpacing: 0.5 }}>{ex.name}</span>
                                    </div>
                                  );
                                }
                                const isSelected = selected.some(s => s.di === gdi && s.ei === ei);
                                const history = getExerciseHistory(ex.name);
                                const hasHistory = history.length >= 2;
                                const prescParts = [ex.sets, ex.reps, ex.weight, ex.rest ? `Rest ${ex.rest}` : ""].filter(Boolean);
                                const presc = prescParts.join("  ·  ");

                                return (
                                  <div
                                    key={ei}
                                    onClick={() => {
                                      if (!supersetMode) return;
                                      if (isSelected) { setSelected(selected.filter(s => !(s.di === gdi && s.ei === ei))); return; }
                                      if (selected.length < 2) setSelected([...selected, { di: gdi, ei, name: ex.name, isSuperset: !!ex.isSuperset }]);
                                    }}
                                    style={{
                                      padding: "12px 14px",
                                      background: isSelected ? C.accentBg : C.surfaceRaised,
                                      border: `1px solid ${isSelected ? C.accentBorder : C.border}`,
                                      borderRadius: R.md,
                                      cursor: supersetMode ? "pointer" : "default",
                                    }}
                                  >
                                    {/* Row 1: Name + badges */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: presc || ex.notes ? 5 : 0 }}>
                                      {supersetMode && (
                                        <span style={{ width: 14, height: 14, borderRadius: 3, border: isSelected ? "none" : `1.5px solid ${C.border}`, background: isSelected ? C.accent : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: C.textOnAccent, flexShrink: 0 }}>
                                          {isSelected ? "✓" : ""}
                                        </span>
                                      )}
                                      <span style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, flex: 1, lineHeight: 1.3 }}>{ex.name}</span>
                                      {ex.isSuperset && <span style={dsBadge(C.accent)}>SS</span>}
                                      {prs[ex.name] && <span style={{ ...dsBadge(C.gold), whiteSpace: "nowrap" as const }}>🏆 {prs[ex.name].weight}kg×{prs[ex.name].reps}</span>}
                                    </div>

                                    {/* Row 2: Prescription line */}
                                    {presc && (
                                      <div style={{ fontSize: 12, color: C.textSecondary, fontFamily: "'JetBrains Mono', monospace", marginBottom: ex.notes ? 4 : hasHistory ? 6 : 0, lineHeight: 1.4 }}>
                                        {presc}
                                      </div>
                                    )}

                                    {/* Row 3: Coaching note */}
                                    {ex.notes && (
                                      <div style={{ fontSize: 11, color: C.textMuted, fontStyle: "italic", lineHeight: 1.5, marginBottom: hasHistory ? 6 : 0 }}>
                                        {ex.notes}
                                      </div>
                                    )}

                                    {/* Row 4: Progress toggle + chart */}
                                    {hasHistory && (
                                      <div>
                                        <button
                                          onClick={e => { e.stopPropagation(); setChartExercise(chartExercise === ex.name ? null : ex.name); }}
                                          style={{ fontSize: 11, color: chartExercise === ex.name ? C.accent : C.textDim, background: "none", border: "none", cursor: "pointer", padding: 0, letterSpacing: 0.3 }}
                                        >
                                          {chartExercise === ex.name ? "▲ hide" : "📈 progress"}
                                        </button>
                                        {chartExercise === ex.name && (() => {
                                          const weights = history.map((h: any) => parseFloat(h.topSet?.weight || "0")).filter((w: number) => w > 0);
                                          if (weights.length < 2) return null;
                                          const min = Math.min(...weights), max = Math.max(...weights), range = max - min || 1;
                                          const W = 280, H = 56, PL = 30, PR = 8, PT = 6, PB = 16;
                                          const cW = W - PL - PR, cH = H - PT - PB;
                                          const pts = weights.map((w: number, i: number) => `${PL + (i / (weights.length - 1)) * cW},${PT + (1 - (w - min) / range) * cH}`).join(" ");
                                          return (
                                            <div style={{ marginTop: 8, background: C.bg, borderRadius: R.sm, padding: "6px 8px" }}>
                                              <svg width={W} height={H} style={{ display: "block", overflow: "visible" }}>
                                                <polyline points={pts} fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                                                {weights.map((w: number, i: number) => (
                                                  <g key={i}>
                                                    <circle cx={PL + (i / (weights.length - 1)) * cW} cy={PT + (1 - (w - min) / range) * cH} r="2.5" fill={C.accent} />
                                                    <text x={PL + (i / (weights.length - 1)) * cW} y={H - 2} textAnchor="middle" fontSize="9" fill={C.textDim}>{history[i].date.slice(5)}</text>
                                                  </g>
                                                ))}
                                                <text x={2} y={PT + 5} fontSize="9" fill={C.textMuted}>{max}kg</text>
                                                <text x={2} y={PT + cH + 4} fontSize="9" fill={C.textMuted}>{min}kg</text>
                                              </svg>
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
            };
            if (hasPhaseHeaders) {
              let runningWi = 0;
              let runningDi = 0;
              return parsedPhases.map((phase, pi) => {
                const phaseContent = (
                  <div key={pi} style={{ marginBottom: 8 }}>
                    {/* Phase header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0 8px" }}>
                      <span style={{ fontFamily: F_SANS, fontSize: 11, fontWeight: 600, letterSpacing: 2, color: C.accent, textTransform: "uppercase" as const }}>
                        {phase.label}
                      </span>
                      {phase.endDate && (
                        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, background: C.surfaceRaised, border: `1px solid ${isEndDatePast(phase.endDate) ? C.border : C.accentBorder}`, borderRadius: 4, padding: "2px 8px", textTransform: "uppercase" as const, color: isEndDatePast(phase.endDate) ? C.textDim : C.accent }}>
                          {isEndDatePast(phase.endDate) ? "Ended" : "Ends"} {phase.endDate}
                        </span>
                      )}
                      <div style={{ flex: 1, height: 1, background: C.border }} />
                    </div>
                    {/* Phase weeks */}
                    {phase.weeks.map((week, wj) => {
                      const wi = runningWi++;
                      const startDi = runningDi;
                      runningDi += week.days.length;
                      return renderWeekBlock(week, wi, startDi);
                    })}
                  </div>
                );
                return phaseContent;
              });
            }
            return flatWeeks.map((week, wi) => {
              const weekStartDi = flatWeeks.slice(0, wi).reduce((acc: number, w: any) => acc + w.days.length, 0);
              return renderWeekBlock(week, wi, weekStartDi);
            });
          })()}

          {/* ── Program Notes ──────────────────────────────────────────── */}
          {parsedDays.length > 0 && (
            <div style={{ marginTop: 8, background: C.surfaceRaised, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
              <div
                onClick={() => setNotesOpen(!notesOpen)}
                style={{ padding: `10px ${SP.md}px`, borderBottom: notesOpen ? `1px solid ${C.border}` : "none", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" as const }}
              >
                <span style={{ color: C.textDim, fontSize: 9 }}>{notesOpen ? "▼" : "▶"}</span>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textMuted }}>Program Notes</span>
                {notesOpen && !editingNotes && (
                  <Btn variant="ghost" onClick={e => { e.stopPropagation(); setEditingNotes(true); setNotesDraft(client.trainingNotes || ""); }} style={{ marginLeft: "auto", fontSize: 11, padding: "3px 10px" }}>Edit</Btn>
                )}
                {notesOpen && editingNotes && (
                  <Btn variant="primary" onClick={e => { e.stopPropagation(); onUpdate({ ...client, trainingNotes: notesDraft }); setEditingNotes(false); }} style={{ marginLeft: "auto", fontSize: 11, padding: "3px 10px" }}>Save</Btn>
                )}
              </div>
              {notesOpen && (
                <div style={{ padding: SP.md, display: "flex", flexDirection: "column" as const, gap: 16 }}>
                  {editingNotes ? (
                    <textarea style={{ ...inputStyle, resize: "vertical" as const, minHeight: 150 }} value={notesDraft} onChange={e => setNotesDraft(e.target.value)} placeholder="Add program notes, guidelines, progressions..." />
                  ) : (
                    <>
                      {client.trainingNotes ? (
                        <div style={{ display: "flex", flexDirection: "column" as const, gap: 6 }}>
                          {(() => {
                            let tn = 0;
                            return client.trainingNotes.split("\n").filter((l: string) => l.trim()).map((line: string, i: number) => {
                              const bm = line.trim().match(/^\*{1,2}([^*]+)\*{1,2}:?\s*(.*)/);
                              const nm = !bm && line.trim().match(/^(\d+)[\.\)]\s*(.*)/);
                              if (bm || nm) {
                                tn++;
                                const title = bm ? bm[1].trim() : (nm ? nm[2] : "");
                                const sub = bm && bm[2] ? ": " + bm[2].trim() : "";
                                return (
                                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginTop: i > 0 ? 14 : 0 }}>
                                    <span style={{ color: C.accent, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, minWidth: 24, flexShrink: 0, lineHeight: 1.6 }}>{tn}.</span>
                                    <span style={{ fontSize: 14, color: C.textPrimary, fontWeight: 700, lineHeight: 1.6 }}>{title}<span style={{ color: C.textSecondary, fontWeight: 400, fontSize: 13 }}>{sub}</span></span>
                                  </div>
                                );
                              }
                              return (
                                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, paddingLeft: 36 }}>
                                  <span style={{ color: C.accent, fontSize: 6, marginTop: 9, flexShrink: 0 }}>●</span>
                                  <span style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.7 }}>{line.replace(/^[-•*]\s*/, "").replace(/\*{1,2}/g, "")}</span>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, color: C.textDim }}>No program notes yet. Click Edit to add guidelines.</div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Weekly Check-ins ───────────────────────────────────────── */}
          {(client.weeklyCheckIns && client.weeklyCheckIns.length > 0) && (() => {
            const checkIns = [...(client.weeklyCheckIns || [])].reverse();
            const feelColor = (n: number) => n >= 8 ? C.accent : n >= 5 ? "#f0c060" : "#e05a5a";
            const sessionLabel: Record<string, string> = { all: "All sessions ✓", most: "Most sessions", some: "Some sessions", none: "None" };
            const sessionColor: Record<string, string> = { all: C.accent, most: "#b8e060", some: "#f0c060", none: "#e05a5a" };
            return (
              <div style={{ marginTop: 8, background: C.surfaceRaised, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
                <div style={{ padding: `10px 16px`, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textMuted }}>Weekly Check-ins</span>
                  <span style={{ fontSize: 11, color: C.textDim, marginLeft: 4 }}>{checkIns.length} total</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column" as const, gap: 0 }}>
                  {checkIns.map((ci, i) => (
                    <div key={ci.id} style={{ padding: "14px 16px", borderBottom: i < checkIns.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <span style={{ fontSize: 11, color: C.textDim, fontWeight: 600 }}>Week of {new Date(ci.weekStart + "T12:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                        <span style={{ fontSize: 11, color: C.textDim }}>{new Date(ci.submittedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const, marginBottom: ci.hardOrEasy || ci.notesForCoach ? 10 : 0 }}>
                        <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 20, background: "rgba(255,255,255,0.04)", border: `1px solid ${feelColor(ci.overallFeel)}`, color: feelColor(ci.overallFeel) }}>Feel {ci.overallFeel}/10</span>
                        <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 20, background: "rgba(255,255,255,0.04)", border: `1px solid ${feelColor(ci.energyRecovery * 2)}`, color: feelColor(ci.energyRecovery * 2) }}>Energy {ci.energyRecovery}/5</span>
                        <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 20, background: "rgba(255,255,255,0.04)", border: `1px solid ${sessionColor[ci.sessionsCompleted] || C.border}`, color: sessionColor[ci.sessionsCompleted] || C.textSecondary }}>{sessionLabel[ci.sessionsCompleted] || ci.sessionsCompleted}</span>
                      </div>
                      {ci.hardOrEasy && <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 4 }}><span style={{ color: C.textDim, marginRight: 6 }}>Hard/Easy:</span>{ci.hardOrEasy}</div>}
                      {ci.notesForCoach && <div style={{ fontSize: 12, color: C.textSecondary }}><span style={{ color: C.textDim, marginRight: 6 }}>Note:</span>{ci.notesForCoach}</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── Assessments (Intake + Questionnaires) ────────────────── */}
          {(() => {
            const qLabels: Record<string, string> = { q1:"Goal", q2:"Current Training", q3:"Injuries", q4:"Strong/Weak", q5:"Stress/Sleep", q6:"Training Days", q7:"Enjoyment", q8:"Limitations", q9:"Intensity Response", q10:"Goal Athlete" };
            const entries: (TrainingQuestionnaireEntry & { label?: string })[] = [];
            if (client.intakeAnswers && Object.values(client.intakeAnswers).some(Boolean)) {
              entries.push({ id: "intake", submittedAt: client.intakeSubmittedAt || client.joinDate + "T00:00:00Z", answers: client.intakeAnswers, label: "Initial Intake" });
            }
            (client.trainingQuestionnaires || []).forEach(q => entries.push(q));
            (client.nutritionIntakes || []).forEach(q => entries.push({ ...q, id: "nutrition-" + q.id, label: "Nutrition" }));
            const sorted = [...entries].sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
            return (
              <div style={{ marginTop: 8, background: C.surfaceRaised, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
                <div style={{ padding: `10px 16px`, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textMuted }}>Assessments</span>
                    <span style={{ fontSize: 11, color: C.textDim }}>{sorted.length} total</span>
                  </div>
                </div>
                {sorted.length === 0 ? (
                  <div style={{ padding: "20px 16px", textAlign: "center" as const }}>
                    <div style={{ fontSize: 12, color: C.textDim, marginBottom: 6 }}>No assessments yet</div>
                    <div style={{ fontSize: 11, color: C.textDim, opacity: 0.6 }}>Send the Training Questionnaire link via Send Forms to collect responses</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" as const, gap: 0 }}>
                    {sorted.map((entry, i) => (
                      <QuestionnaireEntryRow key={entry.id} entry={entry} qLabels={qLabels} isLast={i === sorted.length - 1} />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Danger zone ────────────────────────────────────────────── */}
          {client.trainingScheme && (
            <div style={{ marginTop: 4, paddingTop: 16, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end" }}>
              <Btn variant="ghost" style={{ fontSize: 11, color: C.textDim, padding: "4px 10px" }}
                onClick={() => { if (window.confirm("Delete training plan for " + client.name + "?")) { onUpdate({ ...client, trainingScheme: "" }); } }}>
                Delete training plan
              </Btn>
            </div>
          )}
        </div>
      )}

      {showHistory && (
        <div style={S.modal} onClick={() => setShowHistory(false)}>
          <div style={{ ...S.modalBox, width: 700, maxHeight: "80vh", display: "flex", flexDirection: "column" as const }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: F_SERIF, fontSize: 24, marginBottom: 4, letterSpacing: 0.4 }}>Training Scheme History</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>{client.name} — past versions of the training scheme</div>
            {historyLoading ? (
              <div style={{ color: C.textMuted, fontSize: 13, padding: 24, textAlign: "center" as const }}>Loading history…</div>
            ) : historyVersions.length === 0 ? (
              <div style={{ color: C.textMuted, fontSize: 13, padding: 24, textAlign: "center" as const }}>No history yet. Past versions will be saved here whenever the scheme is updated.</div>
            ) : (
              <div style={{ display: "flex", gap: 16, flex: 1, minHeight: 0 }}>
                <div style={{ width: 180, flexShrink: 0, overflowY: "auto" as const, borderRight: `1px solid ${C.borderInput}`, paddingRight: 12 }}>
                  {historyVersions.map((v, i) => (
                    <div key={i} onClick={() => setHistorySelected(i)} style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", marginBottom: 4, background: historySelected === i ? C.accentBg : "transparent", border: historySelected === i ? `1px solid ${C.accentBorder}` : "1px solid transparent" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: historySelected === i ? C.accent : C.textPrimary }}>{v.savedAt || "Unknown date"}</div>
                      <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>Replaced {new Date(v.archivedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                    </div>
                  ))}
                </div>
                <div style={{ flex: 1, overflowY: "auto" as const }}>
                  <pre style={{ fontSize: 12, color: C.textSecondary, whiteSpace: "pre-wrap" as const, lineHeight: 1.7, margin: 0 }}>
                    {historyVersions[historySelected]?.scheme || ""}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showGenerateModal && (
        <div style={S.modal} onClick={() => setShowGenerateModal(false)}>
          <div style={{ ...S.modalBox, width: 520 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: F_SERIF, fontSize: 24, marginBottom: 4, letterSpacing: 0.4 }}>AI Generate Training Plan</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 24 }}>Answer a few questions to get a tailored program for {client.name}.</div>

            <div style={{ marginBottom: 18 }}>
              <label style={S.label}>Program Type</label>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                {["General Fitness", "Hypertrophy", "Strength", "Power", "Athletic", "Deload", "StrongFirst"].map(t => (
                  <button key={t} onClick={() => setGenProgramType(t)} style={{ padding: "7px 14px", borderRadius: 8, cursor: "pointer", background: genProgramType === t ? C.accentBg : "transparent", color: genProgramType === t ? C.accent : C.textMuted, fontSize: 12, fontWeight: genProgramType === t ? 600 : 400, border: genProgramType === t ? `1px solid ${C.accentBorder}` : `1px solid ${C.borderInput}`, transition: "all 0.15s" }}>
                    {t}{t === "StrongFirst" ? " ▾" : ""}
                  </button>
                ))}
                {genProgramType === "StrongFirst" && (
                  <div style={{ width: "100%", marginTop: 10, padding: "14px 16px", background: C.surfaceRaised, border: `1px solid ${C.accentBorder}`, borderRadius: 8 }}>
                    <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 2, textTransform: "uppercase" as const, marginBottom: 10, fontWeight: 600 }}>StrongFirst Method</div>
                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                      {["Easy Strength Classic 2.0", "Easy Strength → Easy Power", "Plan Strong Tactical", "Plan Strong 1118D", "Linear Cycling", "Wave Cycling", "Step Cycling", "SF Hypertrophy"].map(sf => (
                        <button key={sf} onClick={() => setGenSFSubType(sf)} style={{ padding: "6px 12px", borderRadius: 6, cursor: "pointer", background: genSFSubType === sf ? C.accentBg : "rgba(255,255,255,0.03)", color: genSFSubType === sf ? C.accent : C.textMuted, fontSize: 11, fontWeight: genSFSubType === sf ? 600 : 400, border: genSFSubType === sf ? `1px solid ${C.accentBorder}` : `1px solid ${C.borderInput}`, transition: "all 0.15s" }}>{sf}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={S.grid2}>
              <div>
                <label style={S.label}>Training Days / Week</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {["1", "2", "3", "4", "5", "6"].map(d => (
                    <button key={d} onClick={() => setGenDays(d)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, cursor: "pointer", background: genDays === d ? C.accentBg : "transparent", color: genDays === d ? C.accent : C.textMuted, fontSize: 13, fontWeight: genDays === d ? 700 : 400, border: genDays === d ? `1px solid ${C.accentBorder}` : `1px solid ${C.borderInput}`, transition: "all 0.15s" }}>{d}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={S.label}>Number of Weeks</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {["2", "4", "6", "8", "12"].map(w => (
                    <button key={w} onClick={() => setGenWeeks(w)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, cursor: "pointer", background: genWeeks === w ? C.accentBg : "transparent", color: genWeeks === w ? C.accent : C.textMuted, fontSize: 13, fontWeight: genWeeks === w ? 700 : 400, border: genWeeks === w ? `1px solid ${C.accentBorder}` : `1px solid ${C.borderInput}`, transition: "all 0.15s" }}>{w}</button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <label style={S.label}>Equipment Available</label>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                {["Full gym", "Barbells only", "Dumbbells only", "Kettlebells", "Bodyweight", "Home gym"].map(eq => (
                  <button key={eq} onClick={() => setGenEquipment(eq)} style={{ padding: "7px 14px", borderRadius: 8, cursor: "pointer", background: genEquipment === eq ? C.accentBg : "transparent", color: genEquipment === eq ? C.accent : C.textMuted, fontSize: 12, fontWeight: genEquipment === eq ? 600 : 400, border: genEquipment === eq ? `1px solid ${C.accentBorder}` : `1px solid ${C.borderInput}`, transition: "all 0.15s" }}>{eq}</button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <label style={S.label}>Special Focus or Notes <span style={{ color: C.textDim, fontWeight: 400, textTransform: "none" as const, letterSpacing: 0 }}>(optional)</span></label>
              <input style={S.input} value={genFocus} onChange={e => setGenFocus(e.target.value)} placeholder="e.g. weak point: posterior chain, injured left shoulder, peaking for competition..." />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 24 }}>
              <Btn variant="ghost" onClick={() => setShowGenerateModal(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={generateTraining}>✨ Generate Plan</Btn>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function ReplyBox({ client, onUpdate }: { client: any; onUpdate: (c: any) => void }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const now = new Date();
      const message = {
        id: Date.now().toString(),
        from: "coach",
        text: reply,
        date: now.toISOString().split("T")[0],
        time: now.toTimeString().slice(0, 5),
      };
      await authFetch(`https://coachagent-server-production.up.railway.app/api/clients/${client.id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(message),
      });
      if (client.email) {
        await authFetch(`https://coachagent-server-production.up.railway.app/api/send-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: client.email,
            subject: "Message from Joey Rouhana",
            body: reply,
          }),
        });
      }
      const updatedMessages = [...(client.messages || []), message];
      onUpdate({ ...client, messages: updatedMessages });
      setReply("");
      setSent(true);
      setTimeout(() => setSent(false), 2500);
    } catch { alert("Could not send reply."); }
    setSending(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <textarea
        style={{
          width: "100%", background: C.surfaceRaised, border: `1px solid ${C.borderInput}`,
          borderRadius: R.md, padding: "12px 14px", color: C.textPrimary, fontSize: 13,
          outline: "none", resize: "vertical" as const, minHeight: 96,
          boxSizing: "border-box" as const, fontFamily: "'Inter', sans-serif", lineHeight: 1.6,
        }}
        value={reply}
        onChange={e => setReply(e.target.value)}
        placeholder="Type your message…"
      />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {client.email
          ? <span style={{ fontSize: 11, color: C.textDim }}>Sends via email to {client.email}</span>
          : <span style={{ fontSize: 11, color: C.textDim }}>No email on file</span>
        }
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {sent && <span style={{ fontSize: 11, color: C.accent }}>✓ Sent</span>}
          <button style={dsBtn("primary")} onClick={sendReply} disabled={sending || !reply.trim()}>
            {sending ? "Sending…" : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CollapsibleCard({ title, children, defaultCollapsed = false }: { title: string; children: React.ReactNode; defaultCollapsed?: boolean }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
      <div onClick={() => setCollapsed(c => !c)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 18px", cursor: "pointer", userSelect: "none" as const }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textMuted }}>{title}</div>
        <span style={{ color: C.textDim, fontSize: 9 }}>{collapsed ? "▶" : "▼"}</span>
      </div>
      {!collapsed && <div style={{ padding: "0 18px 16px" }}>{children}</div>}
    </div>
  );
}

// ─── Profile Tab ─────────────────────────────────────────────────────────────
const PROFILE_TAB_LABELS: Record<string, string> = {
  overview:    "Overview",
  training:    "Training",
  diet:        "Diet",
  inbody:      "InBody",
  sessions:    "Sessions",
  messages:    "Messages",
  payments:    "Payments",
  workoutlogs: "Logs",
  shop:        "Shop",
};

function ProfileTab({ tabKey, active, onClick }: { tabKey: string; active: boolean; onClick: () => void }) {
  const T = React.useContext(ThemeCtx);
  const [hovered, setHovered] = React.useState(false);
  const style: React.CSSProperties = {
    padding:         "8px 16px",
    borderRadius:    R.md,
    cursor:          "pointer",
    background:      active  ? T.accentBg
                   : hovered ? (T === IV ? "rgba(28,24,20,0.04)" : "rgba(255,255,255,0.04)")
                   :           "transparent",
    color:           active  ? T.accent
                   : hovered ? T.textSecondary
                   :           T.textDim,
    border:          active ? `1px solid ${T.accentBorder}` : "1px solid transparent",
    fontSize:        11,
    fontWeight:      active ? 600 : 400,
    letterSpacing:   0.8,
    textTransform:   "uppercase",
    transition:      "color 0.12s, background 0.12s",
    whiteSpace:      "nowrap",
    flexShrink:      0,
    outline:         "none",
    lineHeight:      1,
  };
  return (
    <button
      style={style}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {PROFILE_TAB_LABELS[tabKey] ?? tabKey}
    </button>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

function ClientProfile({ client, onUpdate, onDelete, isMobile, allCategories = [] }: { client: Client; onUpdate: (c: Client) => void; onDelete: (id: string) => void; isMobile?: boolean; allCategories?: string[] }) {
  const [tab, setTab] = useState<"overview" | "training" | "diet" | "inbody" | "sessions" | "messages" | "payments" | "workoutlogs">("overview");
  const [clientWorkoutLogs, setClientWorkoutLogs] = useState<any[]>([]);
  const [clientPrs, setClientPrs] = useState<{[ex: string]: {weight: number; reps: number; date: string; estimated1RM: number}}>({});
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [collapsedLogWeeks, setCollapsedLogWeeks] = useState<Set<string>>(new Set());
  const [collapsedLogDays, setCollapsedLogDays] = useState<Set<string>>(new Set());
  const [logCalCursor, setLogCalCursor] = useState<string | null>(null); // "YYYY-MM" of visible month; null = latest logged month
  const [selectedLogDate, setSelectedLogDate] = useState<string | null>(null); // "YYYY-MM-DD" of the day shown below the grid
  const [lastEmail, setLastEmail] = useState<{ status: string; subject?: string; date?: string } | null>(null);

  // Load PRs + logs on mount (used in overview progress + training tab charts)
  useEffect(() => {
    authFetch(`${API}/api/clients/${client.id}/prs`).then(r => r.json()).then(setClientPrs).catch(() => {});
    authFetch(`${API}/api/clients/${client.id}/workout-logs`).then(r => r.json()).then(setClientWorkoutLogs).catch(() => {});
  }, [client.id]);

  // Last email we sent this client + delivery status (for the header chip)
  useEffect(() => {
    let cancelled = false;
    setLastEmail(null);
    authFetch(`${API}/api/clients/${client.id}/last-email`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d) setLastEmail(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [client.id]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(client);
  const [showAddInBody, setShowAddInBody] = useState(false);
  const [uploadingInBody, setUploadingInBody] = useState(false);
  const [generatingDiet, setGeneratingDiet] = useState(false);
  const [showDietModal, setShowDietModal] = useState(false);
  const [genDietType, setGenDietType] = useState("Balanced Diet");
  const [genDietCalories, setGenDietCalories] = useState("2000");
  const [showAddSession, setShowAddSession] = useState(false);
  const today0 = new Date().toISOString().split("T")[0];
  const [showAssignPkg, setShowAssignPkg] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignDraft, setAssignDraft] = useState({
    packageType: "block" as "keep" | "block" | "weekly" | "custom",
    count: "10",
    customPackage: "",
    membershipStart: today0,
    recordPayment: true,
    amount: "",
    paymentDesc: "",
    paymentDate: today0,
  });
  const [inbodyDraft, setInbodyDraft] = useState<InBodyResult>({ date: new Date().toISOString().split("T")[0], weight: 0, muscleMass: 0, bodyFat: 0, bmi: 0, visceralFat: 0, notes: "" });
  const [sessionDraft, setSessionDraft] = useState<Session>({ id: "", date: new Date().toISOString().split("T")[0], time: "09:00", type: "PT Membership", notes: "", reminded: false });
  // Open the Schedule Session modal, defaulting the type to match the client's billing:
  // a fixed-count bundle (e.g. "24 sessions block") → "PT Package"; otherwise "PT Membership".
  const openScheduleSession = () => {
    const pkg = client.package || "";
    const isBlockPkg = /(\d+)\s*session/i.test(pkg) && !/\bweek\b/i.test(pkg);
    setSessionDraft(d => ({ ...d, type: isBlockPkg ? "PT Package" : "PT Membership" }));
    setShowAddSession(true);
  };
  const [waMessage, setWaMessage] = useState("");
  const [waSending, setWaSending] = useState(false);
  const [waStatus, setWaStatus] = useState<"idle" | "sent" | "error">("idle");
  const [reportModal, setReportModal] = useState(false);
  const [reportMonth, setReportMonth] = useState(() => { const n = new Date(); return String(n.getMonth() + 1); });
  const [reportYear, setReportYear] = useState(() => String(new Date().getFullYear()));
  const [reportNote, setReportNote] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [reportPreview, setReportPreview] = useState("");
  const [reportSent, setReportSent] = useState(false);
  useEffect(() => { setDraft(client); }, [client]);
  const save = () => {
    const schemeChanged = draft.trainingScheme !== client.trainingScheme;
    onUpdate(schemeChanged ? { ...draft, schemeUpdatedAt: new Date().toISOString().split("T")[0] } : draft);
    setEditing(false);
  };
  const [freezeBusy, setFreezeBusy] = useState(false);
  const [unfreezeModal, setUnfreezeModal] = useState(false);
  const oneMonthOut = () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return d.toISOString().split("T")[0]; };
  const [unfreezeDate, setUnfreezeDate] = useState(oneMonthOut);
  const freezeMembership = async () => {
    if (!window.confirm(`Freeze ${client.name}'s membership?\n\nThis cancels their Mollie subscription so they won't be billed. Their spot and history are kept, and you can unfreeze anytime without them re-entering card details. They'll get an email letting them know.`)) return;
    setFreezeBusy(true);
    try {
      const r = await authFetch(`${API}/api/clients/${client.id}/freeze`, { method: "POST", body: JSON.stringify({}) });
      const data = await r.json().catch(() => ({} as any));
      if (r.ok && data.client) {
        latestRev[client.id] = data.client.rev;
        onUpdate(data.client);
        alert(`${client.name}'s membership is frozen. They've been notified by email.`);
      } else {
        alert(data.error || "Could not freeze the membership.");
      }
    } catch { alert("Could not freeze the membership."); }
    finally { setFreezeBusy(false); }
  };
  const unfreezeMembership = async (startDate: string) => {
    setFreezeBusy(true);
    try {
      const r = await authFetch(`${API}/api/clients/${client.id}/unfreeze`, { method: "POST", body: JSON.stringify({ startDate }) });
      const data = await r.json().catch(() => ({} as any));
      if (r.ok && data.client) {
        latestRev[client.id] = data.client.rev;
        onUpdate(data.client);
        setUnfreezeModal(false);
        alert(`${client.name}'s membership is active again. Billing resumes ${data.nextCharge}.`);
      } else if (data.needsReauth) {
        alert(`${client.name} no longer has a valid payment mandate (their card likely expired during the freeze).\n\nSend them a new checkout link to resume — the membership stays frozen until they re-authorise.`);
      } else {
        alert(data.error || "Could not unfreeze the membership.");
      }
    } catch { alert("Could not unfreeze the membership."); }
    finally { setFreezeBusy(false); }
  };
  const sendWhatsApp = async () => {
    if (!waMessage.trim() || !client.whatsapp) return;
    setWaSending(true); setWaStatus("idle");
    try {
      const res = await authFetch(`${API}/api/whatsapp/send`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: client.whatsapp, message: waMessage })
      });
      const data = await res.json();
      if (data.success) { setWaStatus("sent"); setWaMessage(""); }
      else { setWaStatus("error"); }
    } catch { setWaStatus("error"); }
    setWaSending(false);
    setTimeout(() => setWaStatus("idle"), 4000);
  };
  const addInBody = () => { onUpdate({ ...client, inbody: [...client.inbody, inbodyDraft] }); setShowAddInBody(false); };
  // Build the package string in the exact format the credit engine parses:
  // block → "N sessions block"; weekly → "N sessions/week" (calcPocketSessions needs /session/ + /week/).
  const assignPackageString = (): string | null => {
    const n = parseInt(assignDraft.count);
    if (assignDraft.packageType === "keep") return null;
    if (assignDraft.packageType === "custom") return assignDraft.customPackage.trim() || null;
    if (!n || n <= 0) return null;
    return assignDraft.packageType === "weekly" ? `${n} sessions/week` : `${n} sessions block`;
  };
  const applyAssignPackage = async () => {
    const pkg = assignPackageString();
    const changingPkg = assignDraft.packageType !== "keep";
    if (!changingPkg && !assignDraft.recordPayment) {
      setAssignError("Nothing to save — pick a package/membership, or tick “Also record a cash payment.”"); return;
    }
    if (changingPkg && !pkg) { setAssignError("Enter a valid number of sessions (or a custom package name)."); return; }
    if (assignDraft.recordPayment && (!assignDraft.amount || isNaN(parseFloat(assignDraft.amount)) || parseFloat(assignDraft.amount) <= 0)) {
      setAssignError("Enter a cash amount above, or untick “Also record a cash payment.”"); return;
    }
    setAssignError("");
    setAssignSaving(true);
    // Save on top of the freshest server copy so the write carries the current rev
    // (the backend rejects stale writes with 409) and never clobbers other fields.
    let base: Client = client;
    try {
      const all = await loadClientsFromServer();
      const fresh = all?.find(c => c.id === client.id);
      if (fresh) base = fresh;
    } catch { /* fall back to in-memory client */ }
    let updated: Client = { ...base };
    if (changingPkg && pkg) {
      // Blocks restart the per-package count on a new package, carrying any overflow from the
      // finished one. packageStartSessions = sessions consumed by all PREVIOUS packages, so
      // remaining = newSize − (totalSessions − packageStartSessions). Preserves the current
      // remaining and adds the new package's size on top.
      const isBlockPkg = /(\d+)\s*session/i.test(pkg) && !/\bweek\b/i.test(pkg);
      let newPackageStart: number | undefined = undefined;
      if (isBlockPkg) {
        const oldM = base.package?.match(/(\d+)\s*session/i);
        const oldIsBlock = !!oldM && !/\bweek\b/i.test(base.package || "");
        // For block→block: prior boundary + finished package size + any manual adjustment
        // (= totalSessions + old remaining). For a fresh block: start from current session count.
        newPackageStart = oldIsBlock
          ? parseInt(oldM![1]) + (base.packageStartSessions || 0) + (base.creditAdjustment || 0)
          : (base.sessions?.length || 0);
      }
      updated = { ...updated, package: pkg, membershipStart: assignDraft.membershipStart, creditAdjustment: 0, packageStartSessions: newPackageStart };
    }
    if (assignDraft.recordPayment && assignDraft.amount) {
      const payment = {
        id: `manual-${Date.now()}`,
        amount: parseFloat(assignDraft.amount).toFixed(2),
        date: assignDraft.paymentDate || today0,
        description: assignDraft.paymentDesc.trim() || pkg || base.package || "Cash payment",
        status: "paid",
        method: "cash",
      };
      updated = { ...updated, payments: [...(base.payments || []), payment] };
    }
    const saved = await saveClientToServer(updated);
    setAssignSaving(false);
    if (saved === "conflict") {
      setAssignError("This client changed on another device while saving. Please close and try once more.");
      return;
    }
    if (!saved) {
      setAssignError("Couldn’t save — the server was unreachable or rejected the change. Nothing was saved. Check your connection and try again.");
      return;
    }
    onUpdate(saved); // sync local state with the server’s saved copy (fresh rev)
    setShowAssignPkg(false);
  };
  const addSession = async () => {
    const newSession: Session = { ...sessionDraft, id: Date.now().toString() };
    setShowAddSession(false);
    // Mirror to Google Calendar first so the session can store its event id (tagged as credits)
    try {
      const res = await authFetch(`${API}/api/calendar/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `${client.name} — ${newSession.type}`,
          date: newSession.date,
          time: newSession.time,
          duration: 60,
          description: newSession.notes || '',
          source: 'credits',
          clientName: client.name,
        }),
      });
      const data = await res.json();
      if (data.eventId) newSession.eventId = data.eventId;
    } catch { /* calendar sync is best-effort */ }
    onUpdate({ ...client, sessions: [...client.sessions, newSession] });
  };
  const latestInBody = client.inbody[client.inbody.length - 1];
  return (
    <div>
      {/* ── Free-signup vetting banner ── */}
      {client.vetStatus === "pending" && (
        <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" as const : "row" as const, gap: 12, padding: "12px 16px", marginBottom: SP.md, background: C.accentBg, border: `1px solid ${C.accentBorder}`, borderRadius: R.lg }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.accent }}>Free signup · to vet</div>
            <div style={{ fontSize: 13, color: C.textMuted, marginTop: 3 }}>Signed up for the free program. Approve to move them into your client list, or decline.</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <Btn variant="primary" onClick={() => onUpdate({ ...client, vetStatus: "approved", vettedAt: new Date().toISOString(), category: client.category || "Free Program" })}>Approve</Btn>
            <Btn onClick={() => { if (window.confirm(`Decline ${client.name}? They move to the Declined group and are set to inactive. Their portal access is not removed.`)) onUpdate({ ...client, vetStatus: "declined", vettedAt: new Date().toISOString(), status: "inactive" }); }}>Decline</Btn>
          </div>
        </div>
      )}
      {client.vetStatus === "declined" && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", marginBottom: SP.md, border: `1px solid ${C.border}`, borderRadius: R.lg, fontSize: 13, color: C.textMuted }}>
          <span style={{ flex: 1 }}>Declined free signup{client.vettedAt ? ` · ${new Date(client.vettedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}</span>
          <Btn onClick={() => onUpdate({ ...client, vetStatus: "pending", status: "active" })}>Move back to vetting</Btn>
        </div>
      )}
      {/* ── Client Identity Header ── */}
      <div style={{
        display: "flex",
        alignItems: isMobile ? "flex-start" : "center",
        gap: isMobile ? 12 : SP.lg,
        marginBottom: SP.lg,
        flexDirection: isMobile ? "column" as const : "row" as const,
        padding: `${SP.md}px 0`,
      }}>
        {/* Avatar */}
        <div style={{
          ...S.avatar(isMobile ? 52 : 64),
          fontSize: isMobile ? 18 : 22,
          boxShadow: "0 0 0 2px rgba(176,138,74,0.28), 0 0 20px rgba(176,138,74,0.10)",
          flexShrink: 0,
        }}>
          {(client.name || '?').slice(0, 2).toUpperCase()}
        </div>

        {/* Identity block */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" as const, gap: 6 }}>
          {/* Name + status badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" as const }}>
            <span style={{
              fontFamily: F_SERIF,
              fontSize: isMobile ? 26 : 34,
              letterSpacing: 0.4,
              color: C.textPrimary,
              lineHeight: 1,
            }}>
              {client.name}
            </span>
            <div style={S.badge(client.frozen ? "#6BA3C9" : client.status === "active" ? C.accent : C.textMuted)}>
              {client.frozen ? "❄ frozen" : client.status}
            </div>
          </div>

          {/* Meta row */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap" as const,
            color: C.textMuted,
            fontSize: 12,
            lineHeight: 1,
          }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.5 }}><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              {client.email}
            </span>
            {client.phone && <>
              <span style={{ color: C.textDim, fontSize: 14, userSelect: "none" as const }}>·</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.5 }}><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                {client.phone}
              </span>
            </>}
            {client.joinDate && <>
              <span style={{ color: C.textDim, fontSize: 14, userSelect: "none" as const }}>·</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.5 }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Since {new Date(client.joinDate).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
              </span>
            </>}
            {client.leadSource && (() => {
              const ls = client.leadSource;
              let refHost = "";
              try { refHost = ls.referrer ? new URL(ls.referrer).hostname.replace(/^www\./, "") : ""; } catch {}
              const label = ls.heardFrom
                || (ls.utm_source ? ls.utm_source : "")
                || (ls.fbclid ? "Meta ad" : ls.gclid ? "Google ad" : "")
                || refHost || "Direct";
              const detail = [
                ls.heardFrom ? `Said: ${ls.heardFrom}` : "",
                ls.utm_source ? `Link: ${[ls.utm_source, ls.utm_medium, ls.utm_campaign].filter(Boolean).join(" / ")}` : "",
                ls.ref ? `Ref: ${ls.ref}` : "",
                ls.fbclid ? "Clicked a Facebook/Instagram ad" : "",
                ls.gclid ? "Clicked a Google ad" : "",
                ls.referrer ? `Came from: ${ls.referrer}` : "No referring site (typed/app/direct)",
              ].filter(Boolean).join("\n");
              return <>
                <span style={{ color: C.textDim, fontSize: 14, userSelect: "none" as const }}>·</span>
                <span title={detail} style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "help" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.5 }}><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
                  Via {label}
                </span>
              </>;
            })()}
            {lastEmail && lastEmail.status !== "unknown" && (() => {
              const s = lastEmail.status;
              const bad = s === "none" || s === "failed" || s === "bounced";
              const col = bad ? C.danger : (s === "opened" || s === "delivered") ? C.accent : C.textMuted;
              const label =
                s === "none" ? "No email on file"
                : s === "failed" ? "Last email failed"
                : s === "bounced" ? "Last email bounced"
                : (() => {
                    const when = lastEmail.date ? new Date(lastEmail.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
                    const verb = s === "opened" ? "Opened" : s === "delivered" ? "Delivered" : "Sent";
                    return `${verb}${when ? " " + when : ""}`;
                  })();
              return <>
                <span style={{ color: C.textDim, fontSize: 14, userSelect: "none" as const }}>·</span>
                <span title={lastEmail.subject ? `Last email: ${lastEmail.subject}` : "No email on file for this client"}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, color: col }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: bad ? 0.9 : 0.55 }}>
                    {bad
                      ? <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>
                      : <><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8"/><rect x="3" y="5" width="18" height="14" rx="2"/></>}
                  </svg>
                  {label}
                </span>
              </>;
            })()}
          </div>
        </div>

        {/* Actions */}
        <div style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap" as const,
          alignItems: "center",
          flexShrink: 0,
        }}>
          <Btn variant="ghost" onClick={() => setEditing(!editing)}>{editing ? "Cancel" : "Edit"}</Btn>
          {editing && <Btn variant="primary" onClick={save}>Save</Btn>}
          <KebabMenu items={[
            {
              label: <>🔗 Copy portal link</>,
              onClick: () => {
                const url = "https://app.joeyrouhana.com/client/" + client.portalToken;
                navigator.clipboard.writeText(url);
                alert("Portal link copied! Share with: " + client.name);
              },
            },
            {
              label: <>👁 View portal</>,
              onClick: async () => {
                try {
                  const r = await authFetch(`${API}/api/clients/${client.id}/portal-preview`, { method: "POST" });
                  const data = await r.json().catch(() => ({} as any));
                  if (r.ok && data.token) window.open(`/portal?preview=${data.token}`, "_blank");
                  else alert(data.error || "Could not open portal preview.");
                } catch { alert("Could not open portal preview."); }
              },
            },
            {
              label: <>📊 Send report</>,
              onClick: () => { setReportModal(true); setReportPreview(""); setReportSent(false); },
            },
            {
              label: freezeBusy ? <>⏳ Working…</> : client.frozen ? <>☀️ Unfreeze membership</> : <>❄️ Freeze membership</>,
              onClick: () => { if (!freezeBusy) (client.frozen ? (setUnfreezeDate(oneMonthOut()), setUnfreezeModal(true)) : freezeMembership()); },
            },
            {
              label: <>🗑 Delete client</>,
              danger: true,
              onClick: () => { if (window.confirm(`Delete ${client.name}? This cannot be undone.`)) onDelete(client.id); },
            },
          ]} />
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{
        display: "flex", gap: 2, marginBottom: 24,
        overflowX: "auto", paddingBottom: 2,
        WebkitOverflowScrolling: "touch" as any,
        borderBottom: `1px solid ${C.border}`, paddingBottom: 8,
        msOverflowStyle: "none", scrollbarWidth: "none" as any,
      }}>
        {(["overview", "training", "diet", "inbody", "sessions", "messages", "payments", "workoutlogs"] as const).map(t => (
          <ProfileTab
            key={t}
            tabKey={t}
            active={tab === t}
            onClick={() => {
              setTab(t);
              if (t === "workoutlogs" && clientWorkoutLogs.length === 0) {
                setLoadingLogs(true);
                Promise.all([
                  authFetch(`${API}/api/clients/${client.id}/workout-logs`).then(r => r.json()),
                  authFetch(`${API}/api/clients/${client.id}/prs`).then(r => r.json()),
                ]).then(([logs, prs]) => { setClientWorkoutLogs(logs); setClientPrs(prs); setLoadingLogs(false); }).catch(() => setLoadingLogs(false));
              }
            }}
          />
        ))}
      </div>
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: SP.lg }}>

          {/* ── Hero Summary ─────────────────────────────────────────── */}
          {(() => {
            const nextSession = [...client.sessions]
              .filter(s => new Date(s.date) >= new Date())
              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
            const latest = client.inbody[client.inbody.length - 1];
            const first  = client.inbody[0];
            const bodyMetrics = latest ? [
              { label: "Weight",   value: latest.weight,    unit: "kg", delta: first ? parseFloat((latest.weight    - first.weight).toFixed(1))    : null, lowerIsBetter: true  },
              { label: "Body Fat", value: latest.bodyFat,   unit: "%",  delta: first ? parseFloat((latest.bodyFat   - first.bodyFat).toFixed(1))   : null, lowerIsBetter: true  },
              { label: "Muscle",   value: latest.muscleMass,unit: "kg", delta: first ? parseFloat((latest.muscleMass- first.muscleMass).toFixed(1)): null, lowerIsBetter: false },
            ] : [];
            const secCaption: React.CSSProperties = { fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: SP.sm };
            return (
              <div style={{ background: C.surfaceRaised, border: `1px solid ${C.border}`, borderRadius: R.xl, padding: `${SP.lg}px ${SP.xl}px` }}>
                <div style={{ display: "flex", gap: SP.xl, flexWrap: "wrap" as const, alignItems: "flex-start" }}>

                  {/* Body metrics */}
                  <div style={{ flex: 2, minWidth: 200 }}>
                    <div style={{ ...secCaption, marginBottom: 16 }}>{latest ? `Latest InBody · ${latest.date}` : "Body Metrics"}</div>
                    {latest ? (
                      <div style={{ display: "flex", gap: SP.lg, flexWrap: "wrap" as const }}>
                        {bodyMetrics.map(({ label, value, unit, delta, lowerIsBetter }) => {
                          const dColor = delta === null || delta === 0 ? C.textDim
                            : delta > 0 ? C.accent : C.danger;
                          return (
                            <div key={label}>
                              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 6 }}>{label}</div>
                              <div style={{ fontFamily: F_SERIF, fontSize: 34, color: C.textPrimary, lineHeight: 1 }}>
                                {value}<span style={{ fontSize: 13, color: C.textMuted, marginLeft: 2 }}>{unit}</span>
                              </div>
                              {delta !== null && (
                                <div style={{ fontSize: 11, fontWeight: 600, color: dColor, marginTop: 5 }}>
                                  {delta > 0 ? "+" : ""}{delta}{unit} vs prev scan
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: C.textDim }}>No InBody data yet.</div>
                    )}
                  </div>

                  {/* Vertical divider */}
                  <div style={{ width: 1, background: C.border, alignSelf: "stretch", flexShrink: 0 }} />

                  {/* Next session + plan status */}
                  <div style={{ flex: 1, minWidth: 160, display: "flex", flexDirection: "column" as const, gap: SP.lg }}>
                    <div>
                      <div style={secCaption}>Next Session</div>
                      {nextSession ? (
                        <>
                          <div style={{ fontSize: 18, fontWeight: 700, color: C.textPrimary, lineHeight: 1.2 }}>{nextSession.date}</div>
                          <div style={{ fontSize: 14, color: C.accent, marginTop: 4, fontWeight: 600 }}>{nextSession.time}</div>
                          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>{nextSession.type}</div>
                        </>
                      ) : (
                        <div style={{ fontSize: 13, color: C.textDim }}>No upcoming sessions</div>
                      )}
                    </div>
                    <div>
                      <div style={secCaption}>Plan Status</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
                        {[{ label: "Training", has: !!client.trainingScheme }, { label: "Diet", has: !!client.diet }].map(({ label, has }) => (
                          <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, background: has ? C.accentBg : C.surfaceMid, border: `1px solid ${has ? C.accentBorder : C.border}`, borderRadius: R.sm, padding: "4px 10px" }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: has ? C.accent : C.textDim, flexShrink: 0, display: "inline-block" }} />
                            <span style={{ fontSize: 11, color: has ? C.accent : C.textDim }}>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {(() => {
                      const pkgMatch = client.package?.match(/(\d+)\s*session/i);
                      if (!pkgMatch) return null;
                      const adjustment = client.creditAdjustment || 0;
                      const pocketInfo = calcPocketSessions(client.package, client.sessions, client.membershipStart || client.joinDate, adjustment);
                      const total = pocketInfo ? pocketInfo.monthly : parseInt(pkgMatch[1]);
                      const remaining = pocketInfo ? pocketInfo.pocket : parseInt(pkgMatch[1]) - blockUsedSessions(client.sessions, client.packageStartSessions) + adjustment;
                      const label = pocketInfo ? "in pocket / monthly" : "remaining";
                      const low = remaining <= 2;
                      const leftPill: React.CSSProperties = low
                        ? { color: C.danger, background: "rgba(239,68,68,0.1)", border: `1px solid ${C.danger}` }
                        : pocketInfo
                          ? { color: C.textOnAccent, background: C.accent, border: `1px solid ${C.accent}` }
                          : { color: C.accent, background: C.accentBg, border: `1px solid ${C.accentBorder}` };
                      const stepBtn: React.CSSProperties = { width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, lineHeight: 1, color: C.textPrimary, background: C.surfaceMid, border: `1px solid ${C.border}`, borderRadius: R.sm, cursor: "pointer", userSelect: "none" as const, padding: 0 };
                      const setAdjustment = (next: number) => onUpdate({ ...client, creditAdjustment: next });
                      return (
                        <div>
                          <div style={secCaption}>Session Credits</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 18, fontWeight: 700, ...leftPill, borderRadius: R.sm, padding: "3px 12px", fontFamily: "'JetBrains Mono', monospace" }}>{remaining}</span>
                            <span style={{ fontSize: 13, color: C.textDim }}>/</span>
                            <span style={{ fontSize: 18, fontWeight: 700, color: C.textDim, background: C.surfaceMid, border: `1px solid ${C.border}`, borderRadius: R.sm, padding: "3px 12px", fontFamily: "'JetBrains Mono', monospace" }}>{total}</span>
                            <span style={{ fontSize: 11, color: C.textDim }}>{label}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textDim }}>Manual adjust</span>
                            <button type="button" title="Remove one banked session" style={stepBtn} onClick={() => setAdjustment(adjustment - 1)}>−</button>
                            <span style={{ fontSize: 14, fontWeight: 700, minWidth: 26, textAlign: "center" as const, color: adjustment === 0 ? C.textDim : adjustment > 0 ? C.accent : C.danger, fontFamily: "'JetBrains Mono', monospace" }}>{adjustment > 0 ? `+${adjustment}` : adjustment}</span>
                            <button type="button" title="Add one banked session" style={stepBtn} onClick={() => setAdjustment(adjustment + 1)}>+</button>
                            {adjustment !== 0 && (
                              <button type="button" style={{ ...stepBtn, width: "auto", padding: "0 8px", fontSize: 11, fontWeight: 600, color: C.textDim }} onClick={() => setAdjustment(0)}>Reset</button>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Progress & Performance ────────────────────────────────── */}
          {(() => {
            const first  = client.inbody[0];
            const latest = client.inbody[client.inbody.length - 1];
            const hasInBodyProgress = client.inbody.length >= 2;
            const prEntries = Object.entries(clientPrs).sort((a, b) => b[1].estimated1RM - a[1].estimated1RM).slice(0, 5);
            const totalSessions = client.sessions?.length ?? 0;
            const joinDate = client.joinDate ? new Date(client.joinDate) : null;
            const daysActive = joinDate ? Math.floor((Date.now() - joinDate.getTime()) / 86400000) : null;
            const sessionsThisMonth = client.sessions?.filter(s => {
              const d = new Date(s.date); const now = new Date();
              return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            }).length ?? 0;
            const pkgMatch = client.package?.match(/(\d+)\s*session/i);
            const pkgTotal = pkgMatch ? parseInt(pkgMatch[1]) : null;
            const pkgRemaining = pkgTotal !== null ? pkgTotal - totalSessions : null;
            const pCard: React.CSSProperties = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, padding: SP.lg };
            if (!hasInBodyProgress && prEntries.length === 0) return null;
            return (
              <section>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: SP.sm }}>Progress & Performance</div>
                <div style={{ display: "flex", gap: SP.md, flexWrap: "wrap" as const }}>

                  {/* Body Composition */}
                  {hasInBodyProgress && (
                    <div style={{ ...pCard, flex: 2, minWidth: 240 }}>
                      <div style={{ ...TY.cardTitle, marginBottom: 16 }}>Body Composition</div>
                      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" as const }}>
                        {[
                          { label: "Weight",   cur: latest.weight,     start: first.weight,     unit: "kg", lob: true  },
                          { label: "Body Fat", cur: latest.bodyFat,    start: first.bodyFat,    unit: "%",  lob: true  },
                          { label: "Muscle",   cur: latest.muscleMass, start: first.muscleMass, unit: "kg", lob: false },
                        ].map(({ label, cur, start, unit, lob }) => {
                          const diff = parseFloat((cur - start).toFixed(1));
                          const dColor = diff === 0 ? C.textDim : (lob ? diff < 0 : diff > 0) ? C.accent : C.danger;
                          return (
                            <div key={label}>
                              <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>{label}</div>
                              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: C.textPrimary }}>
                                {cur}<span style={{ fontSize: 10, color: C.textMuted, marginLeft: 2 }}>{unit}</span>
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: dColor, marginTop: 3 }}>{diff > 0 ? "+" : ""}{diff}{unit}</div>
                              <div style={{ fontSize: 10, color: C.textDim, marginTop: 1 }}>from {start}{unit}</div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: SP.md, fontSize: 10, color: C.textDim }}>{client.inbody.length} scans · {first.date} → {latest.date}</div>
                    </div>
                  )}

                  {/* Personal Records */}
                  {prEntries.length > 0 && (
                    <div style={{ ...pCard, flex: 2, minWidth: 220 }}>
                      <div style={{ ...TY.cardTitle, marginBottom: 16 }}>Personal Records</div>
                      <div style={{ display: "flex", flexDirection: "column" as const, gap: SP.sm }}>
                        {prEntries.map(([name, pr]) => (
                          <div key={name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: 12, color: C.textSecondary, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{name}</div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: C.gold, fontWeight: 700 }}>{pr.weight}kg × {pr.reps}</span>
                              <span style={{ fontSize: 10, color: C.textDim }}>~{Math.round(pr.estimated1RM)}kg 1RM</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Activity */}
                  <div style={{ ...pCard, minWidth: 130 }}>
                    <div style={{ ...TY.cardTitle, marginBottom: 16 }}>Activity</div>
                    <div style={{ display: "flex", flexDirection: "column" as const, gap: 16 }}>
                      <div>
                        <div style={{ fontFamily: F_SERIF, fontSize: 38, color: C.accent, lineHeight: 1 }}>{totalSessions}</div>
                        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: C.textDim, textTransform: "uppercase" as const, marginTop: 3 }}>Total Sessions</div>
                      </div>
                      <div>
                        <div style={{ fontFamily: F_SERIF, fontSize: 38, color: C.textPrimary, lineHeight: 1 }}>{sessionsThisMonth}</div>
                        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: C.textDim, textTransform: "uppercase" as const, marginTop: 3 }}>This Month</div>
                      </div>
                      {pkgTotal !== null && (
                        <div>
                          <div style={{ fontFamily: F_SERIF, fontSize: 38, color: pkgRemaining! <= 2 ? C.danger : C.gold, lineHeight: 1 }}>{pkgRemaining}</div>
                          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: C.textDim, textTransform: "uppercase" as const, marginTop: 3 }}>Sessions Left</div>
                          <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: C.border, overflow: "hidden" }}>
                            <div style={{ height: "100%", borderRadius: 2, background: pkgRemaining! <= 2 ? C.danger : C.gold, width: `${Math.min(100, (totalSessions / pkgTotal) * 100)}%`, transition: "width 0.3s" }} />
                          </div>
                          <div style={{ fontSize: 10, color: C.textDim, marginTop: 3 }}>{totalSessions} / {pkgTotal} used</div>
                        </div>
                      )}
                      {daysActive !== null && <div style={{ fontSize: 10, color: C.textDim }}>Day {daysActive} of journey</div>}
                    </div>
                  </div>

                </div>
              </section>
            );
          })()}

          {/* ── Client Details ────────────────────────────────────────── */}
          <section>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: SP.sm }}>Client Details</div>
            <div style={S.grid2}>
              <CollapsibleCard title="Personal Info" defaultCollapsed>
                {[{ label: "Name", key: "name" }, { label: "Email", key: "email" }, { label: "Phone", key: "phone" }, { label: "Goal", key: "goal" }, { label: "Package", key: "package" }].map(({ label, key }) => (
                  <div key={key} style={{ marginBottom: SP.md }}>
                    <label style={S.label}>{label}</label>
                    {editing
                      ? <input style={S.input} value={(draft as any)[key]} onChange={e => setDraft({ ...draft, [key]: e.target.value })} />
                      : <div style={{ fontSize: 13, color: (client as any)[key] ? C.textPrimary : C.textDim }}>{(client as any)[key] || "—"}</div>}
                  </div>
                ))}
                <div style={{ marginBottom: SP.md }}>
                  <label style={S.label}>Membership Start</label>
                  {editing ? (
                    <input
                      type="date"
                      style={S.input}
                      value={draft.membershipStart || ""}
                      onChange={e => setDraft({ ...draft, membershipStart: e.target.value })}
                    />
                  ) : (
                    <div style={{ fontSize: 13, color: client.membershipStart ? C.textPrimary : C.textDim }}>
                      {client.membershipStart || `— (using join date ${client.joinDate || "today"})`}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: SP.md }}>
                  <label style={S.label}>Category</label>
                  {editing ? (
                    <input
                      style={S.input}
                      list="category-options"
                      value={draft.category || ""}
                      onChange={e => setDraft({ ...draft, category: e.target.value })}
                      placeholder="e.g. Online, 1 on 1…"
                    />
                  ) : (
                    <div style={{ fontSize: 13, color: client.category ? C.textPrimary : C.textDim }}>{client.category || "—"}</div>
                  )}
                </div>
              </CollapsibleCard>
              <CollapsibleCard title="Health & Medical" defaultCollapsed>
                {editing
                  ? <textarea style={S.textarea} value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} />
                  : <div style={{ fontSize: 13, color: client.notes ? C.textPrimary : C.textDim, lineHeight: 1.6, whiteSpace: "pre-wrap" as const }}>{client.notes || "No notes yet."}</div>}
              </CollapsibleCard>
              <CollapsibleCard title="Coach Notes" defaultCollapsed>
                <div style={{ fontSize: 13, color: client.coachNotes ? C.textPrimary : C.textDim, lineHeight: 1.8, whiteSpace: "pre-wrap" as const }}>{client.coachNotes || "No coach notes yet. Ask the AI assistant to add notes."}</div>
              </CollapsibleCard>
            </div>
          </section>

          <datalist id="category-options">
            {allCategories.map(cat => <option key={cat} value={cat} />)}
          </datalist>

        </div>
      )}
      {tab === "training" && (
        <TrainingTab client={client} draft={draft} setDraft={setDraft} editing={editing} setEditing={setEditing} save={save} onUpdate={onUpdate} workoutLogs={clientWorkoutLogs} prs={clientPrs} />
      )}
      {tab === "diet" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Toolbar — Edit is the primary action; regeneration is secondary and explained */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
            <Btn variant="secondary" onClick={() => setShowDietModal(true)} disabled={generatingDiet}
              title="Generates a fresh AI plan from this client's data. Review it before saving — it replaces the current plan.">
              {generatingDiet ? "Generating…" : "✨ Regenerate"}
            </Btn>
            {!editing
              ? <Btn variant="primary" onClick={() => setEditing(true)}>Edit plan</Btn>
              : <Btn variant="primary" onClick={save}>Save</Btn>}
          </div>

          {/* Content */}
          {editing ? (
            <div style={dsCard}>
              <textarea style={{ ...inputStyle, resize: "vertical" as const, minHeight: 320 }} value={draft.diet} onChange={e => setDraft({ ...draft, diet: e.target.value })} />
            </div>
          ) : client.diet ? (
            <DietPlanView diet={client.diet} />
          ) : (
            <div style={{ ...dsCard, textAlign: "center" as const, padding: 40 }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>🥗</div>
              <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 16 }}>No diet plan yet.</div>
              <Btn variant="primary" onClick={() => setShowDietModal(true)}>✨ Generate Plan</Btn>
            </div>
          )}

          {/* Danger zone */}
          {client.diet && !editing && (
            <div style={{ paddingTop: 16, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end" }}>
              <Btn variant="ghost" style={{ fontSize: 11, color: C.textDim, padding: "4px 10px" }}
                onClick={() => { if (window.confirm("Delete diet plan for " + client.name + "?")) { onUpdate({ ...client, diet: "" }); } }}>
                Delete diet plan
              </Btn>
            </div>
          )}
        </div>
      )}
      {tab === "inbody" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Toolbar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim }}>Body Composition</div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {uploadingInBody && <span style={{ fontSize: 11, color: C.accent }}>Reading PDF…</span>}
              <label style={{ ...dsBtn("ghost"), cursor: "pointer" }}>
                Upload PDF
                <input type="file" accept=".pdf,image/*" style={{ display: "none" }} onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = async () => {
                    const base64 = (reader.result as string).split(",")[1];
                    setUploadingInBody(true);
                    try {
                      const text = await callClaude([{ role: "user", content: [
                            { type: "image", source: { type: "base64", media_type: "image/jpeg", data: base64 } },
                            { type: "text", text: "Extract InBody scan data. Return ONLY valid JSON no markdown: {\"date\": \"YYYY-MM-DD\", \"weight\": 0, \"muscleMass\": 0, \"bodyFat\": 0, \"bmi\": 0, \"visceralFat\": 0, \"notes\": \"\"}. IMPORTANT: bodyFat must be the fat PERCENTAGE (vetpercentage in Dutch, shown as % on the report e.g. 31.0), NOT the fat mass in kg (vetmassa). muscleMass should be SSM skeletal muscle mass in kg. visceralFat should be the VVG (Visceral Vet Gebied) value in cm2 shown as a number on the report (e.g. 55.1), NOT the scale maximum of 200." }
                          ]}], { maxTokens: 500 });
                      const clean = text.replace(/```json|```/g, "").trim();
                      const parsed = JSON.parse(clean);
                      onUpdate({ ...client, inbody: [...client.inbody, { date: parsed.date || new Date().toISOString().split("T")[0], weight: parsed.weight || 0, muscleMass: parsed.muscleMass || 0, bodyFat: parsed.bodyFat || 0, bmi: parsed.bmi || 0, visceralFat: parsed.visceralFat || 0, notes: parsed.notes || "Uploaded from PDF" }] });
                    } catch(err: any) { alert("Error: " + (err?.message || JSON.stringify(err))); }
                    setUploadingInBody(false);
                  };
                  reader.readAsDataURL(file);
                }} />
              </label>
              <button style={dsBtn("secondary")} onClick={() => setShowAddInBody(true)}>+ Add Scan</button>
            </div>
          </div>

          {/* Latest metrics strip */}
          {client.inbody.length > 0 && (() => {
            const latest = [...client.inbody].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
            const metrics = [
              { label: "Weight", value: latest.weight + " kg" },
              { label: "Muscle", value: latest.muscleMass + " kg" },
              { label: "Body Fat", value: latest.bodyFat + "%" },
              { label: "BMI", value: latest.bmi },
              { label: "Visceral", value: latest.visceralFat ?? "—" },
            ];
            return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: SP.sm, background: C.surfaceRaised, border: `1px solid ${C.border}`, borderRadius: R.lg, padding: "18px 20px" }}>
                {metrics.map((m, i) => (
                  <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: C.textDim }}>{m.label}</div>
                    <div style={{ fontFamily: F_SERIF, fontSize: 28, lineHeight: 1, color: i === 0 ? C.accent : C.textPrimary }}>{m.value}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Trend charts */}
          {client.inbody.length > 1 && <InBodyChart inbody={client.inbody} />}

          {/* History table */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr repeat(4, 1fr) 32px", gap: SP.md, padding: `10px ${SP.md}px`, borderBottom: `1px solid ${C.border}` }}>
              {["Date", "Weight (kg)", "Muscle (kg)", "Body Fat %", "BMI"].map(h => (
                <div key={h} style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: C.textDim }}>{h}</div>
              ))}
              <div />
            </div>
            {client.inbody.length === 0 ? (
              <div style={{ padding: "24px 18px", color: C.textMuted, fontSize: 13 }}>No scans recorded yet.</div>
            ) : (
              [...client.inbody].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((r, i, arr) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1.4fr repeat(4, 1fr) 32px", gap: SP.md, padding: `10px ${SP.md}px`, borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none", alignItems: "center" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.accent }}>{r.date}</div>
                  <div style={{ fontSize: 13, color: C.textPrimary }}>{r.weight}</div>
                  <div style={{ fontSize: 13, color: C.textPrimary }}>{r.muscleMass}</div>
                  <div style={{ fontSize: 13, color: C.textPrimary }}>{r.bodyFat}%</div>
                  <div style={{ fontSize: 13, color: C.textPrimary }}>{r.bmi}</div>
                  <button
                    title="Delete this scan"
                    onClick={() => {
                      if (!window.confirm(`Delete the InBody scan from ${r.date}? This cannot be undone.`)) return;
                      onUpdate({ ...client, inbody: client.inbody.filter(x => x !== r) });
                    }}
                    style={{ justifySelf: "center", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: R.sm, border: `1px solid ${C.dangerBorder}`, background: "transparent", color: C.danger, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0 }}
                  >×</button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {tab === "sessions" && (() => {
        const today = new Date().toISOString().split("T")[0];
        const sorted = [...client.sessions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const upcoming = sorted.filter(s => s.date >= today);
        const past = [...sorted.filter(s => s.date < today)].reverse();
        const spkgMatch = client.package?.match(/(\d+)\s*session/i);
        const spkgNum = spkgMatch ? parseInt(spkgMatch[1]) : null;
        const isWeeklyMembership = spkgNum !== null && /\bweek\b/i.test(client.package || '');
        const getWeekBounds = () => {
          const now = new Date();
          const day = now.getDay() || 7;
          const mon = new Date(now); mon.setDate(now.getDate() - (day - 1)); mon.setHours(0,0,0,0);
          const sun = new Date(mon); sun.setDate(mon.getDate() + 6); sun.setHours(23,59,59,999);
          return { mon: mon.toISOString().split('T')[0], sun: sun.toISOString().split('T')[0] };
        };
        const { mon: weekMon, sun: weekSun } = getWeekBounds();
        const sessionsThisWeek = client.sessions.filter(s => s.date >= weekMon && s.date <= weekSun).length;
        // Pocket = same monthly-renewal model as the sidebar/Overview pills
        const creditAdj = client.creditAdjustment || 0;
        const pocketInfo = calcPocketSessions(client.package, client.sessions, client.membershipStart || client.joinDate, creditAdj);
        const accumulatedCredits = pocketInfo ? pocketInfo.pocket : 0;
        // Block package: count within the current package (overflow from finished packages excluded)
        const spkgTotal = isWeeklyMembership ? null : spkgNum;
        const spkgUsed = blockUsedSessions(client.sessions, client.packageStartSessions);
        const spkgRemaining = spkgTotal !== null ? spkgTotal - spkgUsed + creditAdj : null;
        const weekRemaining = isWeeklyMembership ? spkgNum! - sessionsThisWeek : null;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Package usage banner */}
            {(spkgTotal !== null || isWeeklyMembership) && (
              <div style={{ background: C.surface, border: `1px solid ${(isWeeklyMembership ? weekRemaining! <= 0 : spkgRemaining! <= 2) ? C.danger : C.border}`, borderRadius: R.lg, padding: `${SP.md}px ${SP.lg}px`, display: "flex", alignItems: "center", gap: SP.lg, flexWrap: "wrap" as const }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.textSecondary }}>{client.package}</span>
                    {isWeeklyMembership
                      ? <span style={{ fontSize: 11, fontWeight: 700, color: weekRemaining! <= 0 ? C.danger : C.textPrimary }}>{weekRemaining} session{weekRemaining !== 1 ? 's' : ''} left this week</span>
                      : <span style={{ fontSize: 11, fontWeight: 700, color: spkgRemaining! <= 2 ? C.danger : C.textPrimary }}>{spkgRemaining} sessions remaining</span>
                    }
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: "hidden" }}>
                    {isWeeklyMembership
                      ? <div style={{ height: "100%", borderRadius: 3, background: weekRemaining! <= 0 ? C.danger : C.accent, width: `${Math.min(100, (sessionsThisWeek / spkgNum!) * 100)}%`, transition: "width 0.3s" }} />
                      : <div style={{ height: "100%", borderRadius: 3, background: spkgRemaining! <= 2 ? C.danger : C.accent, width: `${Math.min(100, (spkgUsed / spkgTotal!) * 100)}%`, transition: "width 0.3s" }} />
                    }
                  </div>
                  <div style={{ fontSize: 10, color: C.textDim, marginTop: 4, display: "flex", justifyContent: "space-between" }}>
                    {isWeeklyMembership
                      ? <span>{sessionsThisWeek} of {spkgNum} sessions scheduled this week</span>
                      : <span>{spkgUsed} of {spkgTotal} sessions scheduled</span>
                    }
                    {isWeeklyMembership && accumulatedCredits !== 0 && (
                      <span style={{ color: accumulatedCredits > 0 ? C.accent : C.danger, fontWeight: 600 }}>
                        {accumulatedCredits > 0 ? `${accumulatedCredits} in pocket` : `${accumulatedCredits} over`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Toolbar */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim }}>Sessions</div>
              <button style={dsBtn("secondary")} onClick={openScheduleSession}>+ Schedule Session</button>
            </div>

            {/* Empty state */}
            {client.sessions.length === 0 && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "64px 0", color: C.textMuted }}>
                <div style={{ fontSize: 32, opacity: 0.4 }}>📅</div>
                <div style={{ fontSize: 13, color: C.textMuted }}>No sessions scheduled yet.</div>
                <button style={dsBtn("primary")} onClick={openScheduleSession}>+ Schedule First Session</button>
              </div>
            )}

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
                <div style={{ padding: `10px ${SP.md}px`, borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim }}>Upcoming</span>
                </div>
                {upcoming.map((s, i) => (
                  <div key={s.id} style={{ borderBottom: i < upcoming.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <SessionRow session={s} />
                  </div>
                ))}
              </div>
            )}

            {/* Past */}
            {past.length > 0 && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
                <div style={{ padding: `10px ${SP.md}px`, borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim }}>Past</span>
                </div>
                {past.map((s, i) => (
                  <div key={s.id} style={{ borderBottom: i < past.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <SessionRow session={s} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
      {tab === "payments" && (
        <div style={{ display: "flex", flexDirection: "column", gap: SP.lg, paddingBottom: SP.xl }}>

          {/* Toolbar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SP.sm, flexWrap: "wrap" as const }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim }}>Payment History</div>
            <div style={{ display: "flex", gap: SP.sm }}>
              <button style={dsBtn("primary")} onClick={() => {
                const isWeekly = /\bweek\b/i.test(client.package || "");
                const existing = client.package?.match(/(\d+)\s*session/i);
                setAssignError("");
                setAssignSaving(false);
                setAssignDraft({
                  packageType: existing ? (isWeekly ? "weekly" : "block") : "block",
                  count: existing ? existing[1] : "10",
                  customPackage: "",
                  membershipStart: today0,
                  recordPayment: true,
                  amount: "",
                  paymentDesc: "",
                  paymentDate: today0,
                });
                setShowAssignPkg(true);
              }}>💶 Cash / Assign Package</button>
              <button style={dsBtn("secondary")} onClick={async () => {
                const amount = prompt("Amount in EUR (e.g. 150.00):");
                const description = prompt("Description (e.g. Monthly training package):");
                if (!amount || !description) return;
                const res = await authFetch(`https://coachagent-server-production.up.railway.app/api/payments/create`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ amount, description, clientId: client.id, clientName: client.name }),
                });
                const data = await res.json();
                if (data.paymentUrl) {
                  try { await navigator.clipboard.writeText(data.paymentUrl); } catch {}
                  alert("Payment link for " + client.name + ":\n\n" + data.paymentUrl + "\n\nLink also copied to clipboard!");
                }
              }}>+ Generate Payment Link</button>
            </div>
          </div>

          {/* Payment list */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
            {/* Column header */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 16, padding: `10px ${SP.md}px`, borderBottom: `1px solid ${C.border}`, alignItems: "center" }}>
              {["Description", "Amount", "Status", ""].map(h => (
                <div key={h} style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: C.textDim, textAlign: h === "Amount" ? "right" as const : "left" as const }}>{h}</div>
              ))}
            </div>

            {(!client.payments || client.payments.length === 0) ? (
              <div style={{ padding: "48px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 28, opacity: 0.35 }}>💳</div>
                <div style={{ fontSize: 13, color: C.textMuted }}>No payments recorded yet.</div>
              </div>
            ) : [...client.payments].reverse().map((p: any, i: number, arr: any[]) => (
              <div key={i} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <PaymentRow p={p} />
              </div>
            ))}
          </div>

        </div>
      )}
      {tab === "workoutlogs" && (() => {
        const secLabel: React.CSSProperties = { fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", color: C.textDim };
        const prEntries = (Object.entries(clientPrs) as [string, any][])
          .filter(([, pr]) => pr.weight > 0)
          .sort((a, b) => b[1].estimated1RM - a[1].estimated1RM)
          .slice(0, 12);
        const reversedLogs = [...clientWorkoutLogs].reverse();

        // Group chronologically by date; within a date, logs with the same day label merge into one row
        type SessionGroup = { date: string; dayName: string; logs: any[]; firstIdx: number };
        type DateGroup = { date: string; sessions: SessionGroup[] };
        const dateGroups: DateGroup[] = [];
        reversedLogs.forEach((log, idx) => {
          const date = log.date || "";
          let group = dateGroups.find(g => g.date === date);
          if (!group) { group = { date, sessions: [] }; dateGroups.push(group); }
          const dayName = log.dayName || "Session";
          let sess = group.sessions.find(s => s.dayName === dayName);
          if (!sess) { sess = { date, dayName, logs: [], firstIdx: idx }; group.sessions.push(sess); }
          sess.logs.push(log);
        });
        // Newest date first
        dateGroups.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

        const LOG_WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
        // "Day 1 — Lower Body Strength + Core (Monday)" → { dayNum: "DAY 1", descriptor: "Lower Body Strength + Core" }
        const parseDayLabel = (name: string) => {
          const numM = name.match(/day\s*(\d+)/i);
          const parenM = name.match(/\(([^)]+)\)\s*$/);
          const paren = parenM ? parenM[1].trim() : "";
          let descriptor = paren && !LOG_WEEKDAYS.includes(paren.toLowerCase()) ? paren : "";
          if (!descriptor) descriptor = name.replace(/^\s*day\s*\d+\s*[—–:-]*\s*/i, "").replace(/\s*\([^)]*\)\s*$/, "").trim();
          return { dayNum: numM ? `DAY ${numM[1]}` : null, descriptor: descriptor || name };
        };
        const LOG_DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const LOG_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const fmtLogDate = (d: string) => {
          const dt = new Date(d + "T00:00:00");
          if (isNaN(dt.getTime())) return d;
          return `${LOG_DOW[dt.getDay()]} ${dt.getDate()} ${LOG_MONTHS[dt.getMonth()]}`;
        };
        const fmtLogHeaderDate = (d: string) => {
          const dt = new Date(d + "T00:00:00");
          if (isNaN(dt.getTime())) return d || "Undated";
          return `${LOG_DOW[dt.getDay()]} ${dt.getDate()} ${LOG_MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
        };

        // ── Calendar model ──
        const LOG_FULL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const LOG_WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        const pad2 = (n: number) => String(n).padStart(2, "0");
        const dateGroupMap: Record<string, DateGroup> = {};
        dateGroups.forEach(g => { if (g.date) dateGroupMap[g.date] = g; });
        const loggedDates = dateGroups.map(g => g.date).filter(Boolean).sort(); // ascending
        const latestLoggedDate = loggedDates.length ? loggedDates[loggedDates.length - 1] : null;

        const defaultCalMonth = latestLoggedDate ? latestLoggedDate.slice(0, 7) : new Date().toISOString().slice(0, 7);
        const visMonth = logCalCursor || defaultCalMonth;
        const visY = parseInt(visMonth.slice(0, 4), 10);
        const visM = parseInt(visMonth.slice(5, 7), 10); // 1-12
        const selDate = selectedLogDate && dateGroupMap[selectedLogDate] ? selectedLogDate : latestLoggedDate;
        const selGroup = selDate ? dateGroupMap[selDate] : null;

        // 6×7 grid, weeks start Monday
        const firstOfMonth = new Date(visY, visM - 1, 1);
        const mondayOffset = (firstOfMonth.getDay() + 6) % 7; // days before the 1st
        const daysInMonth = new Date(visY, visM, 0).getDate();
        const calCells: Array<{ date: string | null; day: number | null }> = [];
        for (let i = 0; i < mondayOffset; i++) calCells.push({ date: null, day: null });
        for (let d = 1; d <= daysInMonth; d++) calCells.push({ date: `${visY}-${pad2(visM)}-${pad2(d)}`, day: d });
        while (calCells.length % 7 !== 0) calCells.push({ date: null, day: null });

        const gotoLogMonth = (delta: number) => {
          const base = new Date(visY, visM - 1 + delta, 1);
          setLogCalCursor(`${base.getFullYear()}-${pad2(base.getMonth() + 1)}`);
        };
        const todayStr = new Date().toISOString().slice(0, 10);

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 24, paddingBottom: 32 }}>

            {/* ── Toolbar ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={secLabel}>Performance History</div>
              <button style={dsBtn("ghost")} onClick={async () => {
                const res = await authFetch(`${API}/api/workout-summary/send`, { method: "POST" });
                const d = await res.json();
                alert(d.success ? `Weekly summary sent! (${d.totalSessions} sessions)` : "Error: " + d.error);
              }}>Email Weekly Summary</button>
            </div>

            {/* ── Personal Records ── */}
            {prEntries.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: SP.xs }}>
                <div style={secLabel}>Personal Records</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 6 }}>
                  {prEntries.map(([ex, pr]) => (
                    <div key={ex} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                      padding: "8px 14px", background: C.surfaceRaised, border: `1px solid ${C.border}`, borderRadius: R.md,
                    }}>
                      <div style={{ fontSize: 12, color: C.textSecondary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{ex}</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 4, flexShrink: 0 }}>
                        <span style={{ fontFamily: F_SERIF, fontSize: 16, color: C.gold, letterSpacing: 0.5 }}>{pr.weight}kg</span>
                        <span style={{ fontSize: 11, color: C.textDim }}>×{pr.reps}</span>
                        <span style={{ fontSize: 10, color: C.textDim, marginLeft: 8 }}>{pr.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Session Logs ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: SP.sm }}>
              <div style={secLabel}>Session Log</div>

              {loadingLogs ? (
                <div style={{ color: C.textMuted, fontSize: 13 }}>Loading…</div>
              ) : clientWorkoutLogs.length === 0 ? (
                <div style={{ padding: "48px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 28, opacity: 0.35 }}>🏋️</div>
                  <div style={{ fontSize: 13, color: C.textMuted }}>No sessions logged yet.</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: SP.md }}>

                  {/* ── Calendar ── */}
                  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, padding: SP.sm, width: "100%", maxWidth: 360 }}>
                    {/* Month nav */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP.sm }}>
                      <button onClick={() => gotoLogMonth(-1)} style={{ ...dsBtn("ghost"), padding: "2px 10px", fontSize: 14, lineHeight: 1 }}>‹</button>
                      <div style={{ fontFamily: F_SERIF, fontSize: 15, letterSpacing: 0.3, color: C.textPrimary }}>{LOG_FULL_MONTHS[visM - 1]} {visY}</div>
                      <button onClick={() => gotoLogMonth(1)} style={{ ...dsBtn("ghost"), padding: "2px 10px", fontSize: 14, lineHeight: 1 }}>›</button>
                    </div>
                    {/* Weekday header */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, marginBottom: 3 }}>
                      {LOG_WEEKDAY_LABELS.map(w => (
                        <div key={w} style={{ textAlign: "center" as const, fontSize: 8, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" as const, color: C.textDim, padding: "1px 0" }}>{w[0]}</div>
                      ))}
                    </div>
                    {/* Day grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
                      {calCells.map((cell, ci) => {
                        if (!cell.date) return <div key={ci} />;
                        const g = dateGroupMap[cell.date];
                        const has = !!g;
                        const isSel = cell.date === selDate;
                        const isToday = cell.date === todayStr;
                        const cellDate = cell.date;
                        return (
                          <div
                            key={ci}
                            onClick={has ? () => setSelectedLogDate(cellDate) : undefined}
                            style={{
                              position: "relative" as const, aspectRatio: "1 / 1", minHeight: 30,
                              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1,
                              borderRadius: R.sm, cursor: has ? "pointer" : "default", userSelect: "none" as const,
                              background: isSel ? C.accentBg : has ? C.surfaceRaised : "transparent",
                              border: `1px solid ${isSel ? C.accentBorder : has ? C.border : "transparent"}`,
                              transition: "all 0.12s",
                            }}
                          >
                            <span style={{ fontSize: 11, fontWeight: has ? 700 : 400, color: isSel ? C.accent : has ? C.textPrimary : C.textDim, fontFamily: "'JetBrains Mono', monospace" }}>{cell.day}</span>
                            {has && <span style={{ width: 4, height: 4, borderRadius: "50%", background: isSel ? C.accent : C.accentBorder }} />}
                            {isToday && !isSel && <span style={{ position: "absolute" as const, top: 3, right: 4, width: 3, height: 3, borderRadius: "50%", background: C.accent }} />}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Selected day detail ── */}
                  {selGroup ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: SP.sm }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textMuted, padding: "0 4px" }}>
                        {fmtLogHeaderDate(selGroup.date)}
                        <span style={{ fontSize: 9, color: C.textDim, opacity: 0.5, marginLeft: 8 }}>{selGroup.sessions.length} session{selGroup.sessions.length !== 1 ? "s" : ""}</span>
                      </div>
                      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
                        {selGroup.sessions.map((sess, li) => {
                          const idx = sess.firstIdx;
                          const isExpanded = expandedLogs.has(idx);
                          const exercises = sess.logs.reduce((acc: any[], l: any) => acc.concat(l.exercises || []), []);
                          const notes = sess.logs.map((l: any) => l.sessionNote).filter(Boolean);
                          const rpe = sess.logs.map((l: any) => l.sessionRPE).find(Boolean);
                          const weekName = sess.logs.map((l: any) => l.weekName).find(Boolean);
                          const { dayNum, descriptor } = parseDayLabel(sess.dayName);
                          return (
                            <div key={idx} style={{ borderBottom: li < selGroup.sessions.length - 1 ? `1px solid ${C.border}` : "none" }}>
                              {/* Session row */}
                              <div
                                onClick={() => setExpandedLogs(prev => { const s = new Set(prev); s.has(idx) ? s.delete(idx) : s.add(idx); return s; })}
                                style={{ display: "flex", alignItems: "center", gap: SP.sm, padding: `13px ${SP.md}px`, cursor: "pointer", userSelect: "none" as const, background: isExpanded ? C.surfaceRaised : "transparent" }}
                              >
                                <span style={{ color: C.accent, fontSize: 8, width: 10, textAlign: "center" as const, flexShrink: 0 }}>{isExpanded ? "▼" : "▶"}</span>
                                {/* Focus name (headline) */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                                  {dayNum && <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textDim }}>{dayNum}</span>}
                                  <span style={{ fontFamily: F_SERIF, fontSize: 15, letterSpacing: 0.2, color: C.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{descriptor}</span>
                                </div>
                                {/* Meta */}
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                  {weekName && <span style={{ fontSize: 9, color: C.textDim, letterSpacing: 0.5, textTransform: "uppercase" as const }}>{weekName}</span>}
                                  {rpe && <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, background: C.accentBg, border: `1px solid ${C.accentBorder}`, borderRadius: R.sm, padding: "2px 7px" }}>RPE {rpe}</span>}
                                  <span style={{ fontSize: 11, color: C.textDim }}>{exercises.length} ex</span>
                                </div>
                              </div>
                              {/* Expanded exercises */}
                              {isExpanded && (
                                <div style={{ padding: `${SP.sm}px ${SP.md}px ${SP.md}px 34px`, display: "flex", flexDirection: "column", gap: SP.sm, borderTop: `1px solid ${C.border}`, background: C.bg }}>
                                  {exercises.map((ex: any, ei: number) => (
                                    <div key={ei} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                      <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary }}>{ex.name}</div>
                                      <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 4 }}>
                                        {(ex.sets || []).map((s: any, si2: number) => (
                                          <span key={si2} style={{ background: C.surfaceRaised, border: `1px solid ${C.border}`, borderRadius: R.sm, padding: "3px 9px", fontSize: 11, color: C.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                                            {s.weight ? `${s.weight}kg` : "—"}×{s.reps || "—"}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                  {notes.map((note: string, ni: number) => (
                                    <div key={ni} style={{ fontSize: 11, color: C.textSecondary, fontStyle: "italic" as const, borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>"{note}"</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: "24px 0", textAlign: "center" as const, fontSize: 12, color: C.textDim }}>Select a highlighted day to see its sessions.</div>
                  )}

                </div>
              )}
            </div>

          </div>
        );
      })()}
      {tab === "messages" && (
        <div style={{ display: "flex", flexDirection: "column", gap: SP.lg, paddingBottom: SP.xl }}>

          {/* ── Section label ── */}
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim }}>Message History</div>

          {/* ── Message thread ── */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
            {(!client.messages || client.messages.length === 0) ? (
              <div style={{ padding: "40px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 28, opacity: 0.35 }}>✉️</div>
                <div style={{ fontSize: 13, color: C.textMuted }}>No messages yet.</div>
              </div>
            ) : [...client.messages]
                .sort((a, b) => new Date(a.date + " " + a.time).getTime() - new Date(b.date + " " + b.time).getTime())
                .map((m, i, arr) => {
                  const isCoach = m.from === "coach";
                  return (
                    <div key={m.id} style={{
                      display: "flex", flexDirection: "column",
                      alignItems: isCoach ? "flex-end" : "flex-start",
                      padding: `12px ${SP.md}px`,
                      borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none",
                    }}>
                      {/* Sender + timestamp */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: isCoach ? C.textDim : C.accent }}>
                          {isCoach ? "You" : client.name.split(" ")[0]}
                        </span>
                        <span style={{ fontSize: 10, color: C.textDim }}>{m.date} · {m.time}</span>
                      </div>
                      {/* Bubble */}
                      <div style={{
                        maxWidth: "78%", padding: "10px 14px", borderRadius: R.md,
                        background: isCoach ? C.surfaceMid : C.accentBg,
                        border: `1px solid ${isCoach ? C.border : C.accentBorder}`,
                        fontSize: 13, color: isCoach ? C.textSecondary : C.textPrimary,
                        lineHeight: 1.65,
                      }}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
          </div>

          {/* ── Reply section ── */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
            <div style={{ padding: `10px ${SP.md}px`, borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim }}>Reply</span>
            </div>
            <div style={{ padding: SP.md }}>
              <ReplyBox client={client} onUpdate={onUpdate} />
            </div>
          </div>

          {/* ── WhatsApp section ── */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: "hidden" }}>
            <div style={{ padding: `10px ${SP.md}px`, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim }}>WhatsApp</span>
              </div>
              {client.whatsapp && (
                <span style={{ fontSize: 11, color: C.textDim, fontFamily: "'JetBrains Mono', monospace" }}>{client.whatsapp}</span>
              )}
            </div>
            <div style={{ padding: SP.md }}>
              {!client.whatsapp ? (
                <div style={{ fontSize: 13, color: C.textMuted }}>No WhatsApp number on file — add one in the Overview tab.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <textarea
                    style={{
                      width: "100%", background: C.surfaceRaised,
                      border: `1px solid ${waStatus === "error" ? C.dangerBorder : C.borderInput}`,
                      borderRadius: R.md, padding: "12px 14px", color: C.textPrimary,
                      fontSize: 13, outline: "none", resize: "vertical" as const, minHeight: 88,
                      boxSizing: "border-box" as const, fontFamily: "'Inter', sans-serif", lineHeight: 1.6,
                    }}
                    value={waMessage}
                    onChange={e => setWaMessage(e.target.value)}
                    placeholder={`Message ${client.name.split(" ")[0]} directly on WhatsApp…`}
                  />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: C.textDim }}>Sends via Twilio WhatsApp</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {waStatus === "sent" && <span style={{ fontSize: 11, color: "#25D366" }}>✓ Sent</span>}
                      {waStatus === "error" && <span style={{ fontSize: 11, color: C.danger }}>Failed — check number or Twilio sandbox</span>}
                      <button
                        style={{ ...dsBtn("ghost"), background: waSending ? C.surfaceMid : "rgba(37,211,102,0.10)", color: waSending ? C.textDim : "#25D366", border: "1px solid rgba(37,211,102,0.25)" }}
                        onClick={sendWhatsApp}
                        disabled={waSending || !waMessage.trim()}
                      >
                        {waSending ? "Sending…" : "Send on WhatsApp"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      )}
      {showDietModal && (
        <div style={S.modal} onClick={() => setShowDietModal(false)}>
          <div style={{ ...S.modalBox, width: 520 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: F_SERIF, fontSize: 24, marginBottom: 4, letterSpacing: 0.4 }}>AI Generate Diet Plan</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 24 }}>Set the diet parameters for {client.name}.</div>

            <div style={{ marginBottom: 18 }}>
              <label style={S.label}>Diet Type</label>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                {["Balanced Diet", "High-Protein Diet", "Low-Carb Diet", "Mediterranean Diet", "Keto Diet", "DASH Diet", "Intermittent Fasting", "Vegan Diet"].map(t => (
                  <button key={t} onClick={() => setGenDietType(t)} style={{ padding: "7px 14px", borderRadius: 8, cursor: "pointer", background: genDietType === t ? C.accentBg : "transparent", color: genDietType === t ? C.accent : C.textMuted, fontSize: 12, fontWeight: genDietType === t ? 600 : 400, border: genDietType === t ? `1px solid ${C.accentBorder}` : `1px solid ${C.borderInput}`, transition: "all 0.15s" }}>{t}</button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={S.label}>Daily Calorie Target</label>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginBottom: 10 }}>
                {["1500", "1750", "2000", "2250", "2500", "2750", "3000", "3500"].map(cal => (
                  <button key={cal} onClick={() => setGenDietCalories(cal)} style={{ padding: "7px 14px", borderRadius: 8, cursor: "pointer", background: genDietCalories === cal ? C.accentBg : "transparent", color: genDietCalories === cal ? C.accent : C.textMuted, fontSize: 12, fontWeight: genDietCalories === cal ? 600 : 400, border: genDietCalories === cal ? `1px solid ${C.accentBorder}` : `1px solid ${C.borderInput}`, transition: "all 0.15s" }}>{cal} kcal</button>
                ))}
              </div>
              <input style={{ ...S.input, marginTop: 4 }} type="number" value={genDietCalories} onChange={e => setGenDietCalories(e.target.value)} placeholder="Or type a custom value..." />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
              <Btn variant="ghost" onClick={() => setShowDietModal(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={async () => {
                setShowDietModal(false);
                setGeneratingDiet(true);
                const latest = client.inbody[client.inbody.length - 1];
                const dietDefs: Record<string, string> = {
                  "Balanced Diet": "well-rounded macros (~50% carbs, 25% protein, 25% fat), whole foods, varied meals",
                  "High-Protein Diet": "1.8-2.2g protein per kg bodyweight, moderate carbs, low fat — prioritize lean meats, eggs, dairy, legumes",
                  "Low-Carb Diet": "under 100g carbs/day, higher fat and protein, minimize grains/sugar/starchy veg",
                  "Mediterranean Diet": "olive oil, fish, legumes, whole grains, vegetables, moderate dairy and red wine — heart-healthy, anti-inflammatory",
                  "Keto Diet": "under 30g net carbs/day, 70-75% fat, 20-25% protein — strict ketogenic macros",
                  "DASH Diet": "low sodium (under 2300mg/day), high potassium/magnesium/calcium, fruits/veg/whole grains/lean protein",
                  "Intermittent Fasting": "16:8 fasting window (fast 16h, eat within 8h window). All meals within the eating window. Note fasting/eating times clearly for each day.",
                  "Vegan Diet": "100% plant-based — no meat, dairy, or eggs. Ensure complete protein sources (legumes + grains), B12, iron, omega-3 from plant sources",
                };
                const prompt = `Create a detailed personalized 7-day diet plan for this client.

DIET SPECIFICATIONS:
- Diet type: ${genDietType}
- Diet guidelines: ${dietDefs[genDietType] || genDietType}
- Daily calorie target: ${genDietCalories} kcal
- Distribute calories appropriately across meals

CLIENT INFO:
- Name: ${client.name}
- Goal: ${client.goal}
- Notes: ${client.notes}
${latest ? `- Latest InBody: Weight ${latest.weight}kg, Muscle ${latest.muscleMass}kg, Body Fat ${latest.bodyFat}%` : ""}

FORMAT RULES — follow exactly:
- Start with ## Nutrition Overview (include daily calorie target, macro breakdown, key guidelines for this diet type)
- Use ## for each day with ONLY the day name (e.g. ## Monday)
- Use ### for each meal (e.g. ### Breakfast, ### Lunch, ### Dinner, ### Snack)
- List 2-4 food items per meal with portion sizes and kcal (e.g. "- Grilled chicken breast (200g): 330 kcal")
- End each meal with a total including macros: **Meal total: ~XXX kcal | ~XXg protein | ~XXg fat | ~XXg carbs**
- Be specific with portions and calories`;
                try {
                  const plan = await callClaude([{ role: "user", content: prompt }], { maxTokens: 2500 });
                  setDraft({ ...draft, diet: plan });
                  setEditing(true);
                } catch { alert("Could not generate diet plan."); }
                setGeneratingDiet(false);
              }}>✨ Generate Plan</Btn>
            </div>
          </div>
        </div>
      )}
      {showAddInBody && (
        <div style={S.modal} onClick={() => setShowAddInBody(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: F_SERIF, fontSize: 22, marginBottom: SP.lg, letterSpacing: 0.4 }}>Add InBody Result</div>
            <div style={S.grid2}>
              {[{ label: "Date", key: "date", type: "date" }, { label: "Weight (kg)", key: "weight", type: "number" }, { label: "Muscle Mass (kg)", key: "muscleMass", type: "number" }, { label: "Body Fat %", key: "bodyFat", type: "number" }, { label: "BMI", key: "bmi", type: "number" }, { label: "Visceral Fat", key: "visceralFat", type: "number" }].map(({ label, key, type }) => (
                <div key={key}><label style={S.label}>{label}</label><input style={S.input} type={type} value={(inbodyDraft as any)[key]} onChange={e => setInbodyDraft({ ...inbodyDraft, [key]: type === "number" ? parseFloat(e.target.value) || 0 : e.target.value })} /></div>
              ))}
            </div>
            <div style={{ marginTop: SP.md }}><label style={S.label}>Notes</label><textarea style={S.textarea} value={inbodyDraft.notes} onChange={e => setInbodyDraft({ ...inbodyDraft, notes: e.target.value })} /></div>
            <div style={{ display: "flex", gap: SP.sm, justifyContent: "flex-end", marginTop: SP.lg }}>
              <Btn variant="ghost" onClick={() => setShowAddInBody(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={addInBody}>Save Result</Btn>
            </div>
          </div>
        </div>
      )}
      {showAddSession && (
        <div style={S.modal} onClick={() => setShowAddSession(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: F_SERIF, fontSize: 22, marginBottom: SP.lg, letterSpacing: 0.4 }}>Schedule Session</div>
            {[{ label: "Date", key: "date", type: "date" }, { label: "Time", key: "time", type: "time" }].map(({ label, key, type }) => (
              <div key={key} style={{ marginBottom: SP.md }}><label style={S.label}>{label}</label><input style={S.input} type={type} value={(sessionDraft as any)[key]} onChange={e => setSessionDraft({ ...sessionDraft, [key]: e.target.value })} /></div>
            ))}
            <div style={{ marginBottom: SP.md }}><label style={S.label}>Session Type</label>
              <select style={S.input} value={sessionDraft.type} onChange={e => setSessionDraft({ ...sessionDraft, type: e.target.value })}>
                {["Call", "Intake", "PT Membership", "PT Package"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Notes</label><textarea style={S.textarea} value={sessionDraft.notes} onChange={e => setSessionDraft({ ...sessionDraft, notes: e.target.value })} /></div>
            <div style={{ display: "flex", gap: SP.sm, justifyContent: "flex-end", marginTop: SP.lg }}>
              <Btn variant="ghost" onClick={() => setShowAddSession(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={addSession}>Schedule</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Cash Payment / Assign Package Modal ── */}
      {showAssignPkg && (() => {
        const pkgTypeBtn = (active: boolean): React.CSSProperties => ({
          flex: 1, padding: "9px 8px", borderRadius: R.sm, cursor: "pointer", textAlign: "center" as const,
          fontSize: 12, fontWeight: active ? 700 : 500,
          background: active ? C.accentBg : C.surfaceMid,
          border: `1px solid ${active ? C.accentBorder : C.border}`,
          color: active ? C.accent : C.textSecondary,
        });
        const pkgPreview = assignPackageString();
        const setA = (patch: Partial<typeof assignDraft>) => { if (assignError) setAssignError(""); setAssignDraft({ ...assignDraft, ...patch }); };
        return (
          <div style={S.modal} onClick={() => setShowAssignPkg(false)}>
            <div style={{ ...S.modalBox, maxHeight: "90vh", overflowY: "auto" as const }} onClick={e => e.stopPropagation()}>
              <div style={{ fontFamily: F_SERIF, fontSize: 22, marginBottom: 4, letterSpacing: 0.4 }}>Cash / Assign Package</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: SP.lg }}>For {client.name} — set their package or membership and log a cash payment.</div>

              {/* Package type */}
              <label style={S.label}>Package type</label>
              <div style={{ display: "flex", gap: 6, marginBottom: SP.md }}>
                {([["block", "Session block"], ["weekly", "Weekly membership"], ["custom", "Custom"], ["keep", "Don’t change"]] as const).map(([val, lbl]) => (
                  <div key={val} style={pkgTypeBtn(assignDraft.packageType === val)} onClick={() => setA({ packageType: val })}>{lbl}</div>
                ))}
              </div>

              {(assignDraft.packageType === "block" || assignDraft.packageType === "weekly") && (
                <div style={{ marginBottom: SP.md }}>
                  <label style={S.label}>{assignDraft.packageType === "weekly" ? "Sessions per week" : "Number of sessions"}</label>
                  <input style={S.input} type="number" min="1" value={assignDraft.count} onChange={e => setA({ count: e.target.value })} />
                </div>
              )}
              {assignDraft.packageType === "custom" && (
                <div style={{ marginBottom: SP.md }}>
                  <label style={S.label}>Package name</label>
                  <input style={S.input} value={assignDraft.customPackage} onChange={e => setA({ customPackage: e.target.value })} placeholder="e.g. 12 sessions block" />
                  <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>Tip: include “sessions” + a number so credits track. Add “/week” for a weekly membership.</div>
                </div>
              )}
              {assignDraft.packageType !== "keep" && (
                <div style={{ marginBottom: SP.md }}>
                  <label style={S.label}>Membership start</label>
                  <input style={S.input} type="date" value={assignDraft.membershipStart} onChange={e => setA({ membershipStart: e.target.value })} />
                </div>
              )}
              {pkgPreview && (
                <div style={{ fontSize: 12, color: C.textSecondary, background: C.surfaceMid, border: `1px solid ${C.border}`, borderRadius: R.sm, padding: "8px 10px", marginBottom: SP.md }}>
                  Package will be set to <strong style={{ color: C.accent, fontFamily: "'JetBrains Mono', monospace" }}>{pkgPreview}</strong>{assignDraft.packageType !== "keep" ? " (manual credit adjustment reset to 0)" : ""}.
                </div>
              )}

              {/* Cash payment */}
              <label style={{ ...S.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: SP.sm }}>
                <input type="checkbox" checked={assignDraft.recordPayment} onChange={e => setA({ recordPayment: e.target.checked })} />
                Also record a cash payment
              </label>
              {assignDraft.recordPayment && (
                <div style={{ display: "flex", flexDirection: "column" as const, gap: SP.md, marginTop: SP.sm, paddingLeft: 4, borderLeft: `2px solid ${C.border}`, paddingBlock: 2, marginLeft: 2, paddingInlineStart: 12 }}>
                  <div><label style={S.label}>Amount (EUR)</label><input style={S.input} type="number" min="0" step="0.01" value={assignDraft.amount} onChange={e => setA({ amount: e.target.value })} placeholder="e.g. 380.00" /></div>
                  <div><label style={S.label}>Description</label><input style={S.input} value={assignDraft.paymentDesc} onChange={e => setA({ paymentDesc: e.target.value })} placeholder={pkgPreview || "e.g. Cash — training package"} /></div>
                  <div><label style={S.label}>Payment date</label><input style={S.input} type="date" value={assignDraft.paymentDate} onChange={e => setA({ paymentDate: e.target.value })} /></div>
                </div>
              )}

              {assignError && (
                <div style={{ fontSize: 12, color: C.danger, background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, borderRadius: R.sm, padding: "8px 10px", marginTop: SP.md }}>
                  {assignError}
                </div>
              )}
              <div style={{ display: "flex", gap: SP.sm, justifyContent: "flex-end", marginTop: SP.lg }}>
                <Btn variant="ghost" onClick={() => setShowAssignPkg(false)} disabled={assignSaving}>Cancel</Btn>
                <Btn variant="primary" onClick={applyAssignPackage} disabled={assignSaving}>{assignSaving ? "Saving…" : "Save"}</Btn>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Monthly Report Modal ── */}
      {unfreezeModal && (() => {
        const today = new Date().toISOString().split("T")[0];
        const monthOut = oneMonthOut();
        const presetStyle = (active: boolean): React.CSSProperties => ({
          flex: 1, padding: "10px 12px", borderRadius: 8, cursor: "pointer", textAlign: "center" as const,
          fontSize: 13, fontWeight: active ? 600 : 500,
          background: active ? C.accentBg : C.inputBg,
          border: `1px solid ${active ? C.accentBorder : C.borderInput}`,
          color: active ? C.accent : C.textSecondary,
        });
        return (
          <div onClick={e => { if (e.target === e.currentTarget) setUnfreezeModal(false); }}
            style={{ position: "fixed" as const, inset: 0, background: "rgba(0,0,0,0.82)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.borderInput}`, borderRadius: 16, padding: 32, maxWidth: 460, width: "100%", boxShadow: "0 40px 80px rgba(0,0,0,0.7)", position: "relative" as const }}>
              <button onClick={() => setUnfreezeModal(false)} style={{ position: "absolute" as const, top: 16, right: 20, background: "none", border: "none", color: C.textMuted, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 6 }}>Unfreeze Membership</div>
              <div style={{ fontFamily: F_SERIF, fontSize: 26, letterSpacing: 0.4, color: C.textPrimary, marginBottom: 8 }}>{client.name}</div>
              <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.5, marginBottom: 22 }}>
                Recreates their monthly subscription on the card already on file. Choose when the next charge should happen.
              </p>
              <label style={{ display: "block", fontSize: 12, color: C.textMuted, marginBottom: 8, fontWeight: 500 }}>Next charge date</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <div style={presetStyle(unfreezeDate === today)} onClick={() => setUnfreezeDate(today)}>Today</div>
                <div style={presetStyle(unfreezeDate === monthOut)} onClick={() => setUnfreezeDate(monthOut)}>In 1 month</div>
              </div>
              <input type="date" min={today} value={unfreezeDate} onChange={e => setUnfreezeDate(e.target.value)}
                style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.borderInput}`, borderRadius: 8, padding: "10px 12px", color: C.textPrimary, fontSize: 14, boxSizing: "border-box" as const, marginBottom: 24 }} />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <Btn variant="ghost" onClick={() => setUnfreezeModal(false)}>Cancel</Btn>
                <Btn variant="primary" disabled={freezeBusy || !unfreezeDate} onClick={() => unfreezeMembership(unfreezeDate)}>
                  {freezeBusy ? "Working…" : "Unfreeze & resume billing"}
                </Btn>
              </div>
            </div>
          </div>
        );
      })()}
      {reportModal && (
        <div onClick={e => { if (e.target === e.currentTarget) { setReportModal(false); setReportPreview(""); } }}
          style={{ position: "fixed" as const, inset: 0, background: "rgba(0,0,0,0.82)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.borderInput}`, borderRadius: 16, padding: 32, maxWidth: 560, width: "100%", maxHeight: "85vh", overflowY: "auto" as const, boxShadow: "0 40px 80px rgba(0,0,0,0.7)", position: "relative" as const }}>
            <button onClick={() => { setReportModal(false); setReportPreview(""); }} style={{ position: "absolute" as const, top: 16, right: 20, background: "none", border: "none", color: C.textMuted, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 6 }}>Monthly Report</div>
            <div style={{ fontFamily: F_SERIF, fontSize: 26, letterSpacing: 0.4, color: C.textPrimary, marginBottom: 24 }}>{client.name}</div>

            {!reportPreview ? (
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 16 }}>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", fontSize: 12, color: C.textMuted, marginBottom: 6, fontWeight: 500 }}>Month</label>
                    <select value={reportMonth} onChange={e => setReportMonth(e.target.value)} style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.borderInput}`, borderRadius: 8, padding: "10px 12px", color: C.textPrimary, fontSize: 14 }}>
                      {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                        <option key={m} value={String(i + 1)}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", fontSize: 12, color: C.textMuted, marginBottom: 6, fontWeight: 500 }}>Year</label>
                    <select value={reportYear} onChange={e => setReportYear(e.target.value)} style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.borderInput}`, borderRadius: 8, padding: "10px 12px", color: C.textPrimary, fontSize: 14 }}>
                      {["2025","2026","2027"].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: C.textMuted, marginBottom: 6, fontWeight: 500 }}>Personal note to include (optional)</label>
                  <textarea
                    value={reportNote}
                    onChange={e => setReportNote(e.target.value)}
                    placeholder="e.g. Great progress this month, loved your commitment on leg day…"
                    rows={3}
                    style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.borderInput}`, borderRadius: 8, padding: "10px 12px", color: C.textPrimary, fontSize: 13, resize: "vertical" as const, boxSizing: "border-box" as const }}
                  />
                </div>
                <Btn variant="primary" disabled={reportLoading} onClick={async () => {
                  setReportLoading(true);
                  try {
                    const res = await authFetch(`${API}/api/clients/${client.id}/monthly-report`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ month: reportMonth, year: reportYear, coachNote: reportNote, previewOnly: true }),
                    });
                    const data = await res.json();
                    if (data.preview) setReportPreview(data.preview);
                    else alert("Error: " + (data.error || "Unknown error"));
                  } catch { alert("Could not connect to server."); }
                  setReportLoading(false);
                }} style={{ justifyContent: "center", fontSize: 14 }}>
                  {reportLoading ? "Generating…" : "Generate Preview →"}
                </Btn>
                <div style={{ fontSize: 11, color: C.textDim, textAlign: "center" as const }}>Claude writes the report · review before sending</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: reportSent ? C.accent : C.textDim, marginBottom: 12 }}>
                  {reportSent ? `✓ Sent to ${client.email}` : `Preview · will send to ${client.email}`}
                </div>
                <div style={{ background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, fontSize: 13, color: C.textMuted, lineHeight: 1.75, whiteSpace: "pre-wrap" as const }}>
                  {reportPreview}
                </div>
                {!reportSent ? (
                  <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                    <Btn variant="ghost" onClick={() => { setReportPreview(""); }} style={{ flex: 1, justifyContent: "center" }}>← Back</Btn>
                    <Btn variant="primary" disabled={reportLoading} onClick={async () => {
                      setReportLoading(true);
                      try {
                        const res = await authFetch(`${API}/api/clients/${client.id}/monthly-report`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ month: reportMonth, year: reportYear, coachNote: reportNote, previewOnly: false }),
                        });
                        const data = await res.json();
                        if (data.sent) setReportSent(true);
                        else alert("Error sending: " + (data.error || "Unknown error"));
                      } catch { alert("Could not connect to server."); }
                      setReportLoading(false);
                    }} style={{ flex: 2, justifyContent: "center", fontSize: 14 }}>
                      {reportLoading ? "Sending…" : `Send to ${client.email} →`}
                    </Btn>
                  </div>
                ) : (
                  <Btn variant="ghost" onClick={() => { setReportModal(false); setReportPreview(""); setReportSent(false); }} style={{ marginTop: 20, width: "100%", justifyContent: "center" }}>Done</Btn>
                )}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Calendar helpers ────────────────────────────────────────────────────────
const CAL_API = `${API}/api/calendar`;
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS  = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function calFmtTime(dt: string) {
  if (!dt) return "";
  return new Date(dt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function calFmtDate(dt: string) {
  if (!dt) return "";
  return new Date(dt).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}
function getEventTag(summary: string, ev?: any): { label: string; color: string; bg: string } {
  const isCredits = ev?.extendedProperties?.private?.source === 'credits';
  const s = (summary || "").toLowerCase();
  if (s.includes("intake"))    return { label: "Intake",   color: C.indigo, bg: C.indigoBg };
  if (s.includes("call"))      return { label: "Call",     color: C.indigo, bg: C.indigoBg };
  if (s.includes("check"))     return { label: "Check-in", color: C.gold,   bg: C.goldBg };
  if (s.includes("missed"))    return { label: "Missed",   color: C.danger, bg: C.dangerBg };
  if (isCredits)               return { label: "Credits",  color: C.indigo, bg: C.indigoBg };
  return { label: "PT", color: C.accent, bg: C.accentBg };
}

// ─── SessionBlock ─────────────────────────────────────────────────────────────
function SessionBlock({ ev, past }: { ev: any; past: boolean }) {
  const [hov, setHov] = React.useState(false);
  const tag = getEventTag(ev.summary, ev);
  const time = calFmtTime(ev.start?.dateTime);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: past ? "rgba(107,114,128,0.10)" : (hov ? C.accentBg : tag.bg),
        border: `1px solid ${past ? "rgba(107,114,128,0.15)" : (hov ? C.accentBorder : "transparent")}`,
        borderRadius: 5,
        padding: "2px 5px",
        marginBottom: 2,
        cursor: "pointer",
        transition: "all 0.15s",
        transform: hov ? "translateY(-1px)" : "none",
        overflow: "hidden",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, overflow: "hidden" }}>
        {time && <span style={{ fontSize: 9, color: past ? C.textDim : tag.color, fontWeight: 600, flexShrink: 0, fontFamily: "'JetBrains Mono', monospace" }}>{time}</span>}
        <span style={{ fontSize: 10, color: past ? C.textDim : C.textSecondary, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{ev.summary}</span>
      </div>
    </div>
  );
}

// ─── DayCell ──────────────────────────────────────────────────────────────────
function DayCell({ day, year, month, events, today, onSelect }: {
  day: number; year: number; month: number;
  events: any[]; today: Date; onSelect: (day: number) => void;
}) {
  const [hov, setHov] = React.useState(false);
  const isToday  = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const isPast   = new Date(year, month, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const hasEvents = events.length > 0;
  const MAX_VISIBLE = 2;
  const overflow = events.length - MAX_VISIBLE;

  return (
    <div
      onClick={() => onSelect(day)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: isToday
          ? "rgba(176,138,74,0.10)"
          : hov ? C.surfaceMid : C.surfaceRaised,
        border: isToday
          ? `1px solid ${C.accentBorder}`
          : hov ? `1px solid ${C.borderInput}` : `1px solid ${C.borderSubtle}`,
        borderRadius: R.md,
        minHeight: 0,
        padding: "5px 6px 4px",
        cursor: "pointer",
        transition: "all 0.15s",
        boxShadow: isToday ? "0 0 16px rgba(176,138,74,0.10)" : hov ? "0 4px 16px rgba(0,0,0,0.3)" : "none",
        position: "relative" as const,
        display: "flex",
        flexDirection: "column" as const,
      }}>
      {/* Date number */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <span style={{
          fontSize: 12, fontWeight: isToday ? 700 : 500,
          color: isToday ? C.accent : isPast ? C.textDim : C.textMuted,
          lineHeight: 1,
        }}>{day}</span>
        {hov && !hasEvents && (
          <span style={{ fontSize: 14, color: C.textDim, lineHeight: 1, opacity: 0.6 }}>+</span>
        )}
        {hasEvents && (
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: isPast ? C.textDim : C.accent, flexShrink: 0, marginTop: 2, opacity: 0.7 }} />
        )}
      </div>
      {/* Dots */}
      {hasEvents && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 }}>
          {events.slice(0, 6).map((ev, j) => {
            const tag = getEventTag(ev.summary, ev);
            return <span key={j} style={{ width: 6, height: 6, borderRadius: "50%", background: isPast ? C.textDim : tag.color, opacity: isPast ? 0.4 : 0.85, flexShrink: 0 }} />;
          })}
          {events.length > 6 && <span style={{ fontSize: 8, color: C.textDim, fontWeight: 700 }}>+{events.length - 6}</span>}
        </div>
      )}
    </div>
  );
}

// ─── DayPanel ─────────────────────────────────────────────────────────────────
function DayPanel({ day, year, month, events, onClose, onAdded, onChanged, clients, onClientUpdate }: {
  day: number; year: number; month: number;
  events: any[]; onClose: () => void; onAdded: () => void; onChanged: () => void;
  clients?: Client[]; onClientUpdate?: (c: Client) => void;
}) {
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState({ title: "", time: "10:00", duration: "60", clientId: "" });
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editForm, setEditForm] = React.useState({ title: "", time: "10:00", duration: "60" });
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | null>(null);

  const startEdit = (ev: any) => {
    const t = ev.start?.dateTime
      ? new Date(ev.start.dateTime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
      : "10:00";
    const dur = ev.start?.dateTime && ev.end?.dateTime
      ? String(Math.round((new Date(ev.end.dateTime).getTime() - new Date(ev.start.dateTime).getTime()) / 60000))
      : "60";
    setEditForm({ title: ev.summary || "", time: t, duration: dur });
    setEditingId(ev.id);
  };

  const handleDelete = async (ev: any) => {
    setActionLoading(ev.id);
    setConfirmDeleteId(null);
    try {
      const res = await authFetch(`${CAL_API}/events/${ev.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Server error");
      removeSessionForEvent(ev, clients, onClientUpdate);
      onChanged();
    } catch { setErr("Could not delete event."); }
    setActionLoading(null);
  };

  const handleEdit = async (ev: any) => {
    if (!editForm.title.trim()) return;
    setActionLoading(ev.id);
    try {
      await authFetch(`${CAL_API}/events/${ev.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: editForm.title, date: dateStr, time: editForm.time, duration: parseInt(editForm.duration) }),
      });
      updateSessionForEvent(ev.id, dateStr, editForm.time, clients, onClientUpdate);
      setEditingId(null);
      onChanged();
    } catch { alert("Could not update event."); }
    setActionLoading(null);
  };
  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const label = new Date(year, month, day).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  const handleAdd = async () => {
    if (!form.title.trim()) { setErr("Session type required."); return; }
    setSaving(true); setErr("");
    try {
      const explicitClient = clients?.find(c => c.id === form.clientId);
      const detectedClient = !explicitClient && clients ? matchClientInTitle(form.title, clients) : undefined;
      const selectedClient = explicitClient || detectedClient;
      if (!selectedClient && /^PT\b/i.test(form.title.trim())) {
        setErr("Select a client — PT sessions must deduct from a client's package.");
        setSaving(false);
        return;
      }
      const title = selectedClient ? `${selectedClient.name} — ${form.title}` : form.title;
      const res = await authFetch(`${CAL_API}/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, date: dateStr, time: form.time, duration: parseInt(form.duration), source: selectedClient ? "credits" : undefined, clientName: selectedClient?.name, clientEmail: selectedClient?.email || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        if (selectedClient && onClientUpdate) {
          const newSession: Session = { id: Date.now().toString(), date: dateStr, time: form.time, type: form.title, notes: "", reminded: false, eventId: data.eventId };
          onClientUpdate({ ...selectedClient, sessions: [...selectedClient.sessions, newSession] });
        }
        onAdded(); setAdding(false); setForm({ title: "", time: "10:00", duration: "60", clientId: "" });
      } else setErr(data.error || "Failed to add session.");
    } catch { setErr("Could not connect."); }
    setSaving(false);
  };

  const inp2: React.CSSProperties = { background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "9px 12px", color: C.textPrimary, fontSize: 13, fontFamily: "'Inter', sans-serif", outline: "none", width: "100%", boxSizing: "border-box" as const };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed" as const, inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "flex-end", padding: 0 }}>
      <div style={{
        width: 360, height: "100%", background: C.surface,
        borderLeft: `1px solid ${C.border}`,
        display: "flex", flexDirection: "column" as const,
        boxShadow: "-24px 0 64px rgba(0,0,0,0.5)",
        animation: "slideIn 0.2s ease",
      }}>
        {/* Panel header */}
        <div style={{ padding: "24px 24px 20px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 6 }}>
                {String(month + 1).padStart(2, "0")} / {year}
              </div>
              <div style={{ fontFamily: F_SERIF, fontSize: 28, letterSpacing: 0.4, color: C.textPrimary, lineHeight: 1 }}>{label}</div>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
          </div>
        </div>

        {/* Session list */}
        <div style={{ flex: 1, overflowY: "auto" as const, padding: "20px 24px" }}>
          {events.length === 0 && !adding && (
            <div style={{ textAlign: "center" as const, padding: "40px 0", color: C.textDim, fontSize: 13 }}>
              No sessions scheduled
            </div>
          )}
          {events.map((ev, i) => {
            const tag = getEventTag(ev.summary, ev);
            const isEditing = editingId === ev.id;
            const isLoading = actionLoading === ev.id;
            return (
              <div key={i} style={{ background: C.surfaceRaised, border: `1px solid ${isEditing ? C.accentBorder : C.border}`, borderRadius: R.lg, padding: "14px 16px", marginBottom: 10 }}>
                {isEditing ? (
                  <div style={{ display: "flex", flexDirection: "column" as const, gap: 8 }}>
                    <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} style={{ background: C.surfaceMid, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "8px 10px", color: C.textPrimary, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" as const }} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <input type="text" placeholder="09:00" value={editForm.time} onChange={e => setEditForm(f => ({ ...f, time: e.target.value }))} style={{ background: C.surfaceMid, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "8px 10px", color: C.textPrimary, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" as const, fontFamily: "'JetBrains Mono', monospace" }} />
                      <select value={editForm.duration} onChange={e => setEditForm(f => ({ ...f, duration: e.target.value }))} style={{ background: C.surfaceMid, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "8px 10px", color: C.textPrimary, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" as const }}>
                        {["30","45","60","90","120"].map(d => <option key={d} value={d}>{d} min</option>)}
                      </select>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Btn variant="ghost" onClick={() => setEditingId(null)} style={{ flex: 1, justifyContent: "center", fontSize: 12 }}>Cancel</Btn>
                      <Btn variant="primary" onClick={() => handleEdit(ev)} disabled={isLoading} style={{ flex: 2, justifyContent: "center", fontSize: 12 }}>{isLoading ? "Saving…" : "Save"}</Btn>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, flex: 1, marginRight: 8 }}>{ev.summary}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                        <span style={{ ...dsBadge(tag.color) }}>{tag.label}</span>
                        <button onClick={() => startEdit(ev)} title="Edit" disabled={isLoading} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 13, padding: "2px 6px", lineHeight: 1 }}>✎</button>
                        {confirmDeleteId === ev.id ? (
                          <>
                            <button onClick={() => handleDelete(ev)} disabled={isLoading} style={{ background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, borderRadius: 4, color: C.danger, cursor: "pointer", fontSize: 11, padding: "2px 7px", lineHeight: 1.4, fontWeight: 600 }}>{isLoading ? "…" : "Yes"}</button>
                            <button onClick={() => setConfirmDeleteId(null)} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 11, padding: "2px 6px", lineHeight: 1.4 }}>No</button>
                          </>
                        ) : (
                          <button onClick={() => setConfirmDeleteId(ev.id)} title="Delete" style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 13, padding: "2px 6px", lineHeight: 1 }}>✕</button>
                        )}
                      </div>
                    </div>
                    {ev.start?.dateTime && (
                      <div style={{ fontSize: 12, color: C.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
                        {calFmtTime(ev.start.dateTime)}{ev.end?.dateTime && ` → ${calFmtTime(ev.end.dateTime)}`}
                      </div>
                    )}
                    {ev.description && <div style={{ fontSize: 12, color: C.textDim, marginTop: 6 }}>{ev.description}</div>}
                  </>
                )}
              </div>
            );
          })}

          {/* Add form */}
          {adding && (
            <div style={{ background: C.surfaceMid, border: `1px solid ${C.accentBorder}`, borderRadius: R.lg, padding: "16px", marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: C.accent, marginBottom: 14 }}>New Session</div>
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 10 }}>
                {clients && clients.length > 0 && (
                  <select value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))} style={inp2}>
                    <option value="">— No client —</option>
                    {[...clients.filter(c => c.status === "active")].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
                <select value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inp2}>
                  <option value="">— Select type —</option>
                  {["Call", "Intake", "PT Membership", "PT Package"].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <input type="text" placeholder="09:00" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} style={{ ...inp2, fontFamily: "'JetBrains Mono', monospace" }} />
                  <select value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} style={inp2}>
                    <option value="30">30 min</option>
                    <option value="45">45 min</option>
                    <option value="60">60 min</option>
                    <option value="90">90 min</option>
                    <option value="120">2 hours</option>
                  </select>
                </div>
                {err && <div style={{ fontSize: 12, color: C.danger }}>{err}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn variant="ghost" onClick={() => { setAdding(false); setErr(""); }} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
                  <Btn variant="primary" onClick={handleAdd} disabled={saving} style={{ flex: 2, justifyContent: "center" }}>
                    {saving ? "Adding…" : "Add to Calendar"}
                  </Btn>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!adding && (
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${C.border}` }}>
            <Btn variant="primary" onClick={() => setAdding(true)} style={{ width: "100%", justifyContent: "center", fontSize: 14, padding: "12px 20px" }}>
              + Add Session
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DayView ──────────────────────────────────────────────────────────────────
function DayView({ date, onDateChange, calViewMode, setCalViewMode, clients, onClientUpdate }: {
  date: Date; onDateChange: (d: Date) => void;
  calViewMode: "month" | "day"; setCalViewMode: (m: "month" | "day") => void;
  clients?: Client[]; onClientUpdate?: (c: Client) => void;
}) {
  const [events, setEvents]                 = useState<any[]>([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState("");
  const [adding, setAdding]                 = useState(false);
  const [addForm, setAddForm]               = useState({ title: "", time: "10:00", duration: "60", clientId: "" });
  const [saving, setSaving]                 = useState(false);
  const [addErr, setAddErr]                 = useState("");
  const [editingId, setEditingId]           = useState<string | null>(null);
  const [editForm, setEditForm]             = useState({ title: "", time: "10:00", duration: "60" });
  const [actionLoading, setActionLoading]   = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const today  = new Date();
  const year   = date.getFullYear();
  const month  = date.getMonth();
  const day    = date.getDate();
  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const isToday = dateStr === `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;

  const HOUR_START = 6;
  const HOUR_END   = 22;
  const HOUR_H     = 60;

  const loadEvents = React.useCallback(async () => {
    setLoading(true); setError("");
    const timeMin = new Date(year, month, day, 0, 0, 0).toISOString();
    const timeMax = new Date(year, month, day, 23, 59, 59).toISOString();
    try {
      const res = await authFetch(`${CAL_API}/events?timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch { setError("Could not load events."); }
    setLoading(false);
  }, [year, month, day]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const goDay = (delta: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    onDateChange(d);
  };

  const handleAdd = async () => {
    if (!addForm.title.trim()) { setAddErr("Session type required."); return; }
    setSaving(true); setAddErr("");
    try {
      const explicitClient = clients?.find(c => c.id === addForm.clientId);
      const detectedClient = !explicitClient && clients ? matchClientInTitle(addForm.title, clients) : undefined;
      const selectedClient = explicitClient || detectedClient;
      if (!selectedClient && /^PT\b/i.test(addForm.title.trim())) {
        setAddErr("Select a client — PT sessions must deduct from a client's package.");
        setSaving(false);
        return;
      }
      const title = selectedClient ? `${selectedClient.name} — ${addForm.title}` : addForm.title;
      const res = await authFetch(`${CAL_API}/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, date: dateStr, time: addForm.time, duration: parseInt(addForm.duration), source: selectedClient ? "credits" : undefined, clientName: selectedClient?.name, clientEmail: selectedClient?.email || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        // Deduct session credit from client portfolio
        if (selectedClient && onClientUpdate) {
          const newSession: Session = { id: Date.now().toString(), date: dateStr, time: addForm.time, type: addForm.title, notes: "", reminded: false, eventId: data.eventId };
          onClientUpdate({ ...selectedClient, sessions: [...selectedClient.sessions, newSession] });
        }
        setAdding(false); setAddForm({ title: "", time: "10:00", duration: "60", clientId: "" }); loadEvents();
      } else setAddErr(data.error || "Failed.");
    } catch { setAddErr("Could not connect."); }
    setSaving(false);
  };

  const handleDelete = async (ev: any) => {
    setActionLoading(ev.id); setConfirmDeleteId(null);
    try {
      const res = await authFetch(`${CAL_API}/events/${ev.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      removeSessionForEvent(ev, clients, onClientUpdate);
      loadEvents();
    } catch { }
    setActionLoading(null);
  };

  const startEdit = (ev: any) => {
    const t = ev.start?.dateTime
      ? new Date(ev.start.dateTime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
      : "10:00";
    const dur = ev.start?.dateTime && ev.end?.dateTime
      ? String(Math.round((new Date(ev.end.dateTime).getTime() - new Date(ev.start.dateTime).getTime()) / 60000))
      : "60";
    setEditForm({ title: ev.summary || "", time: t, duration: dur });
    setEditingId(ev.id);
  };

  const handleEdit = async (ev: any) => {
    if (!editForm.title.trim()) return;
    setActionLoading(ev.id);
    try {
      await authFetch(`${CAL_API}/events/${ev.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: editForm.title, date: dateStr, time: editForm.time, duration: parseInt(editForm.duration) }),
      });
      updateSessionForEvent(ev.id, dateStr, editForm.time, clients, onClientUpdate);
      setEditingId(null);
      loadEvents();
    } catch { }
    setActionLoading(null);
  };

  const getEventPosition = (ev: any) => {
    if (!ev.start?.dateTime) return null;
    const start = new Date(ev.start.dateTime);
    const end   = ev.end?.dateTime ? new Date(ev.end.dateTime) : new Date(start.getTime() + 60 * 60000);
    const startH = start.getHours() + start.getMinutes() / 60;
    const endH   = end.getHours()   + end.getMinutes()   / 60;
    if (startH >= HOUR_END || endH <= HOUR_START) return null;
    return {
      top:    (Math.max(startH, HOUR_START) - HOUR_START) * HOUR_H,
      height: Math.max((Math.min(endH, HOUR_END) - Math.max(startH, HOUR_START)) * HOUR_H, 24),
    };
  };

  const hours       = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
  const totalHeight = (HOUR_END - HOUR_START) * HOUR_H;
  const dateLabel   = date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const inp2: React.CSSProperties = { background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "9px 12px", color: C.textPrimary, fontSize: 13, fontFamily: "'Inter', sans-serif", outline: "none", width: "100%", boxSizing: "border-box" as const };

  return (
    <div style={{ display: "flex", flexDirection: "column" as const, height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "24px 24px 20px", flexShrink: 0, flexWrap: "wrap" as const, gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" as const }}>
          <h2 style={{ fontFamily: F_SERIF, fontSize: 26, letterSpacing: 0.4, color: isToday ? C.accent : C.textPrimary, margin: 0 }}>{dateLabel}</h2>
          <div style={{ display: "flex", gap: 4 }}>
            <Btn variant="secondary" onClick={() => goDay(-1)} style={{ padding: "6px 12px" }}>←</Btn>
            <Btn variant="secondary" onClick={() => goDay(1)}  style={{ padding: "6px 12px" }}>→</Btn>
          </div>
          {!isToday && <Btn variant="ghost" onClick={() => onDateChange(new Date())} style={{ fontSize: 12, padding: "6px 12px", color: C.accent, border: `1px solid ${C.accentBorder}` }}>Today</Btn>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* View toggle */}
          <div style={{ display: "flex", background: C.surfaceRaised, border: `1px solid ${C.border}`, borderRadius: R.md, overflow: "hidden" }}>
            {(["month","day"] as const).map(m => (
              <button key={m} onClick={() => setCalViewMode(m)} style={{
                padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none", outline: "none",
                background: calViewMode === m ? C.accent : "transparent",
                color: calViewMode === m ? C.textOnAccent : C.textMuted,
                textTransform: "capitalize" as const,
                transition: "all 0.15s",
              }}>{m}</button>
            ))}
          </div>
          <Btn variant="ghost" onClick={loadEvents} style={{ fontSize: 12 }}>{loading ? "Loading…" : "↻ Refresh"}</Btn>
          <Btn variant="primary" onClick={() => setAdding(true)} style={{ fontSize: 12, padding: "7px 14px" }}>+ Add Session</Btn>
        </div>
      </div>

      {/* Inline add form */}
      {adding && (
        <div style={{ padding: "14px 24px", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surfaceMid, flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" as const }}>
            {clients && clients.length > 0 && (
              <div style={{ flex: 2, minWidth: 160 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.textDim, marginBottom: 5, textTransform: "uppercase" as const }}>Client</div>
                <select value={addForm.clientId} onChange={e => setAddForm(f => ({ ...f, clientId: e.target.value }))} style={inp2}>
                  <option value="">— No client —</option>
                  {[...clients.filter(c => c.status === "active")].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ flex: 2, minWidth: 160 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.textDim, marginBottom: 5, textTransform: "uppercase" as const }}>Session Type</div>
              <select value={addForm.title} onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))} style={inp2}>
                <option value="">— Select type —</option>
                {["Call", "Intake", "PT Membership", "PT Package"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 90 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.textDim, marginBottom: 5, textTransform: "uppercase" as const }}>Time</div>
              <input type="text" placeholder="09:00" value={addForm.time} onChange={e => setAddForm(f => ({ ...f, time: e.target.value }))} style={{ ...inp2, fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
            <div style={{ flex: 1, minWidth: 90 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.textDim, marginBottom: 5, textTransform: "uppercase" as const }}>Duration</div>
              <select value={addForm.duration} onChange={e => setAddForm(f => ({ ...f, duration: e.target.value }))} style={inp2}>
                {["30","45","60","90","120"].map(d => <option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 6, paddingBottom: 1 }}>
              <Btn variant="ghost" onClick={() => { setAdding(false); setAddErr(""); }}>Cancel</Btn>
              <Btn variant="primary" onClick={handleAdd} disabled={saving}>{saving ? "Adding…" : "Add"}</Btn>
            </div>
          </div>
          {addErr && <div style={{ fontSize: 12, color: C.danger, marginTop: 8 }}>{addErr}</div>}
        </div>
      )}

      {error && <div style={{ color: C.danger, fontSize: 13, padding: "10px 24px", flexShrink: 0 }}>{error}</div>}

      {/* Timeline */}
      <div style={{ flex: 1, overflowY: "auto" as const, padding: "0 24px 24px" }}>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: C.textDim, fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ position: "relative" as const, display: "flex", marginTop: 16 }}>
            {/* Time labels column */}
            <div style={{ width: 48, flexShrink: 0 }}>
              {hours.map(h => (
                <div key={h} style={{ height: HOUR_H, display: "flex", alignItems: "flex-start", paddingTop: 4 }}>
                  <span style={{ fontSize: 10, color: C.textDim, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
                    {h === 12 ? "12pm" : h > 12 ? `${h-12}pm` : `${h}am`}
                  </span>
                </div>
              ))}
            </div>
            {/* Event grid */}
            <div style={{ flex: 1, position: "relative" as const, height: totalHeight }}>
              {/* Hour lines */}
              {hours.map(h => (
                <div key={h} style={{ position: "absolute" as const, left: 0, right: 0, top: (h - HOUR_START) * HOUR_H, height: 1, background: C.border }} />
              ))}
              {/* Events */}
              {(() => {
                // Collision detection — lay overlapping events side by side
                const positioned = events.map((ev, i) => ({ ev, i, pos: getEventPosition(ev) })).filter(x => x.pos);
                const cols: number[] = new Array(positioned.length).fill(0);
                const colCounts: number[] = new Array(positioned.length).fill(1);
                for (let a = 0; a < positioned.length; a++) {
                  const posA = positioned[a].pos!;
                  let maxCol = 0;
                  for (let b = 0; b < a; b++) {
                    const posB = positioned[b].pos!;
                    if (posA.top < posB.top + posB.height && posA.top + posA.height > posB.top) {
                      cols[a] = Math.max(cols[a], cols[b] + 1);
                      maxCol = Math.max(maxCol, cols[b] + 1);
                    }
                  }
                  for (let b = 0; b < a; b++) {
                    const posB = positioned[b].pos!;
                    if (posA.top < posB.top + posB.height && posA.top + posA.height > posB.top) {
                      colCounts[b] = Math.max(colCounts[b], cols[a] + 1);
                      colCounts[a] = Math.max(colCounts[a], cols[a] + 1);
                    }
                  }
                }
                return positioned.map(({ ev, i, pos }) => {
                const tag      = getEventTag(ev.summary, ev);
                const isEditing = editingId === ev.id;
                const isLoading = actionLoading === ev.id;
                const isPast   = new Date(ev.start.dateTime) < today;
                const col      = cols[positioned.findIndex(x => x.i === i)];
                const total    = colCounts[positioned.findIndex(x => x.i === i)];
                const width    = `calc(${100 / total}% - 6px)`;
                const left     = `calc(${(col / total) * 100}% + 4px)`;
                return (
                  <div key={i} style={{
                    position: "absolute" as const,
                    top: pos!.top + 1, height: pos!.height - 2,
                    left, width,
                    background: isPast ? "rgba(107,114,128,0.10)" : tag.bg,
                    border: `1px solid ${isPast ? "rgba(107,114,128,0.20)" : isEditing ? C.accentBorder : "transparent"}`,
                    borderRadius: R.md,
                    padding: "6px 10px",
                    overflow: "hidden" as const,
                    zIndex: isEditing ? 10 : 1,
                  }}>
                    {isEditing ? (
                      <div style={{ display: "flex", flexDirection: "column" as const, gap: 6 }}>
                        <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} style={{ background: C.surfaceMid, border: `1px solid ${C.borderInput}`, borderRadius: R.sm, padding: "4px 8px", color: C.textPrimary, fontSize: 12, outline: "none", width: "100%", boxSizing: "border-box" as const }} />
                        <div style={{ display: "flex", gap: 6 }}>
                          <input type="text" value={editForm.time} onChange={e => setEditForm(f => ({ ...f, time: e.target.value }))} style={{ background: C.surfaceMid, border: `1px solid ${C.borderInput}`, borderRadius: R.sm, padding: "4px 8px", color: C.textPrimary, fontSize: 12, outline: "none", flex: 1, fontFamily: "'JetBrains Mono', monospace", boxSizing: "border-box" as const }} />
                          <select value={editForm.duration} onChange={e => setEditForm(f => ({ ...f, duration: e.target.value }))} style={{ background: C.surfaceMid, border: `1px solid ${C.borderInput}`, borderRadius: R.sm, padding: "4px 8px", color: C.textPrimary, fontSize: 12, outline: "none", flex: 1, boxSizing: "border-box" as const }}>
                            {["30","45","60","90","120"].map(d => <option key={d} value={d}>{d}m</option>)}
                          </select>
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          <Btn variant="ghost" onClick={() => setEditingId(null)} style={{ fontSize: 11, padding: "3px 8px" }}>Cancel</Btn>
                          <Btn variant="primary" onClick={() => handleEdit(ev)} disabled={isLoading} style={{ fontSize: 11, padding: "3px 8px" }}>{isLoading ? "…" : "Save"}</Btn>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", height: "100%" }}>
                        <div style={{ overflow: "hidden" as const, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: isPast ? C.textDim : C.textPrimary, whiteSpace: "nowrap" as const, overflow: "hidden" as const, textOverflow: "ellipsis" }}>{ev.summary}</div>
                          {pos.height > 32 && (
                            <div style={{ fontSize: 10, color: isPast ? C.textDim : tag.color, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
                              {calFmtTime(ev.start.dateTime)}{ev.end?.dateTime && ` → ${calFmtTime(ev.end.dateTime)}`}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 2, flexShrink: 0, marginLeft: 6 }}>
                          <button onClick={() => startEdit(ev)} title="Edit" style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 11, padding: "1px 4px", lineHeight: 1 }}>✎</button>
                          {confirmDeleteId === ev.id ? (
                            <>
                              <button onClick={() => handleDelete(ev)} disabled={isLoading} style={{ background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, borderRadius: 3, color: C.danger, cursor: "pointer", fontSize: 10, padding: "1px 5px", lineHeight: 1.4, fontWeight: 600 }}>{isLoading ? "…" : "Yes"}</button>
                              <button onClick={() => setConfirmDeleteId(null)} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 10, padding: "1px 4px", lineHeight: 1.4 }}>No</button>
                            </>
                          ) : (
                            <button onClick={() => setConfirmDeleteId(ev.id)} title="Delete" style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 11, padding: "1px 4px", lineHeight: 1 }}>✕</button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
              })()}
              {events.length === 0 && (
                <div style={{ position: "absolute" as const, top: "40%", left: 0, right: 0, textAlign: "center" as const, color: C.textDim, fontSize: 13 }}>
                  No sessions scheduled
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CalendarView ─────────────────────────────────────────────────────────────
function CalendarView({ clients, onClientUpdate }: { clients?: Client[]; onClientUpdate?: (c: Client) => void }) {
  const [events, setEvents]           = useState<any[]>([]);
  const [loading, setLoading]         = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [error, setError]             = useState("");
  const [calViewMode, setCalViewMode] = useState<"month" | "day">("day");
  const [dayViewDate, setDayViewDate] = useState(new Date());

  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const today = new Date();

  const loadEvents = React.useCallback(async () => {
    setLoading(true); setError("");
    const timeMin = new Date(year, month, 1).toISOString();
    const timeMax = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
    try {
      const res = await authFetch(`${CAL_API}/events?timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch { setError("Could not load calendar."); }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const getEventsForDay = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return events.filter(e => (e.start?.dateTime || e.start?.date || "").startsWith(dateStr));
  };

  const days: (number | null)[] = [];
  const firstDay = new Date(year, month, 1).getDay();
  for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) days.push(null);
  for (let i = 1; i <= new Date(year, month + 1, 0).getDate(); i++) days.push(i);
  const numRows = Math.ceil(days.length / 7);

  const upcomingEvents = events
    .filter(e => new Date(e.start?.dateTime || e.start?.date) >= today)
    .slice(0, 8);

  const goToday = () => setCurrentDate(new Date());
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const viewToggle = (
    <div style={{ display: "flex", background: C.surfaceRaised, border: `1px solid ${C.border}`, borderRadius: R.md, overflow: "hidden" }}>
      {(["month","day"] as const).map(m => (
        <button key={m} onClick={() => {
          if (m === "day" && calViewMode === "month") setDayViewDate(new Date());
          setCalViewMode(m);
        }} style={{
          padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none", outline: "none",
          background: calViewMode === m ? C.accent : "transparent",
          color: calViewMode === m ? C.textOnAccent : C.textMuted,
          textTransform: "capitalize" as const,
          transition: "all 0.15s",
        }}>{m}</button>
      ))}
    </div>
  );

  if (calViewMode === "day") {
    return (
      <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
        <style>{`@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
        <DayView date={dayViewDate} onDateChange={setDayViewDate} calViewMode={calViewMode} setCalViewMode={setCalViewMode} clients={clients} onClientUpdate={onClientUpdate} />
      </div>
    );
  }

  return (
    <div className="cal-root">
      <style>{`
        .cal-root { display: flex; width: 100%; height: 100%; min-height: 0; overflow: hidden; }
        .cal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; overflow-x: auto; overflow-y: hidden; padding: 24px 20px 20px 24px; }
        .cal-scroll-inner { min-width: 560px; flex: 1; display: flex; flex-direction: column; min-height: 0; }
        .cal-sidebar { display: none; }
        .cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); grid-template-rows: repeat(${numRows}, 1fr); gap: 4px; flex: 1; min-height: 0; }
        .cal-labels { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; margin-bottom: 6px; flex-shrink: 0; }
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @media (max-width: 768px) {
          .cal-root { flex-direction: column; overflow-y: auto; overflow-x: hidden; height: auto; }
          .cal-main { padding: 14px 12px 8px; overflow-x: auto; overflow-y: visible; }
          .cal-scroll-inner { min-width: 480px; flex: none; }
          .cal-sidebar { width: 100%; border-left: none; border-top: 1px solid ${C.border}; padding: 16px 12px 20px; }
          .cal-grid { grid-template-rows: repeat(${numRows}, 52px); flex: none; gap: 3px; }
          .cal-labels { gap: 3px; margin-bottom: 4px; }
        }
      `}</style>

      {/* ── Main ── */}
      <div className="cal-main">
      <div className="cal-scroll-inner">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexShrink: 0, flexWrap: "wrap" as const, gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" as const }}>
            <h2 style={{ fontFamily: F_SERIF, fontSize: 26, letterSpacing: 0.4, color: C.textPrimary, margin: 0 }}>
              {MONTH_NAMES[month]} <span style={{ color: C.accent }}>{year}</span>
            </h2>
            <div style={{ display: "flex", gap: 4 }}>
              <Btn variant="secondary" onClick={prevMonth} style={{ padding: "6px 12px" }}>←</Btn>
              <Btn variant="secondary" onClick={nextMonth} style={{ padding: "6px 12px" }}>→</Btn>
            </div>
            <Btn variant="ghost" onClick={goToday} style={{ fontSize: 12, padding: "6px 12px", color: C.accent, border: `1px solid ${C.accentBorder}` }}>Today</Btn>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {viewToggle}
            <Btn variant="ghost" onClick={loadEvents} style={{ fontSize: 12 }}>{loading ? "Loading…" : "↻ Refresh"}</Btn>
          </div>
        </div>
        {error && <div style={{ color: C.danger, fontSize: 13, marginBottom: 10 }}>{error}</div>}

        {/* Day labels */}
        <div className="cal-labels">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
            <div key={d} style={{ textAlign: "center" as const, fontSize: 10, fontWeight: 700, letterSpacing: 1, color: C.textDim, textTransform: "uppercase" as const, padding: "3px 0" }}>{d}</div>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, fontSize: 13 }}>Loading…</div>
        ) : (
          <div className="cal-grid">
            {days.map((day, i) =>
              day === null
                ? <div key={i} />
                : <DayCell key={i} day={day} year={year} month={month} events={getEventsForDay(day)} today={today} onSelect={d => setSelectedDay(d)} />
            )}
          </div>
        )}
      </div>
      </div>


      {selectedDay !== null && (
        <DayPanel day={selectedDay} year={year} month={month} events={getEventsForDay(selectedDay)} onClose={() => setSelectedDay(null)} onAdded={() => { loadEvents(); setSelectedDay(null); }} onChanged={loadEvents} clients={clients} onClientUpdate={onClientUpdate} />
      )}
    </div>
  );
}

function IntakeForm({ hideShareLink }: { hideShareLink?: boolean }) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", emergencyContact: "", package: "", referral: "" });
  const [questions, setQuestions] = useState({ q1: "", q2: "", q3: "", q4: "", q5: "", q6: "", q7: "", q8: "", q9: "", q10: "" });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setErrs(prev => { const { [k]: _, ...rest } = prev; return rest; }); };
  const setQ = (k: string, v: string) => { setQuestions(q => ({ ...q, [k]: v })); setErrs(prev => { const { [k]: _, ...rest } = prev; return rest; }); };

  const niSelect: React.CSSProperties = { ...niInput, WebkitAppearance: "menulist" as any, appearance: "auto" as any };

  const handleSubmit = async () => {
    const e: Record<string, string> = {};
    if (!form.firstName.trim()) e.firstName = "Required.";
    if (!form.lastName.trim()) e.lastName = "Required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.phone.trim()) e.phone = "Please enter your phone number.";
    TQ_QDATA.filter(q => q.required && !(questions as any)[q.key].trim()).forEach(q => { e[q.key] = "Please answer this question."; });
    if (Object.keys(e).length) { setErrs(e); window.scrollTo(0, 0); return; }
    setLoading(true);
    try {
      await fetch(`${API}/api/intake`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.firstName + " " + form.lastName, email: form.email, phone: form.phone, emergencyContact: form.emergencyContact, package: form.package, referral: form.referral, questions }),
      });
    } catch {}
    setLoading(false); setSubmitted(true);
  };

  if (submitted) return <NiDone title="Intake received" body="I'll review your answers and get in touch shortly. A personalised training analysis is being prepared based on what you've shared." />;

  return (
    <NiPage
      title="Client Intake"
      intro="Take your time with these. The more honest and specific your answers, the better your programming will be. There are no right or wrong answers."
      footer={niSubmitBtn(loading ? "Sending…" : "Submit intake", handleSubmit, loading)}
    >
      {!hideShareLink && (
        <div style={{ ...niCard, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 10.5, color: NP.inkMuted, letterSpacing: 1.5, textTransform: "uppercase" as const, fontWeight: 600, marginBottom: 3 }}>Shareable link</div>
            <div style={{ fontSize: 13.5, color: NP.green, fontWeight: 600 }}>app.joeyrouhana.com/intake</div>
          </div>
          <button type="button" onClick={() => { navigator.clipboard.writeText("https://app.joeyrouhana.com/intake"); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }}
            style={{ background: linkCopied ? NP.greenSoft : NP.green, color: linkCopied ? NP.green : NP.card, border: `1px solid ${NP.green}`, borderRadius: 10, padding: "9px 16px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: NP_SANS, whiteSpace: "nowrap" as const }}>
            {linkCopied ? "Copied ✓" : "Copy link"}
          </button>
        </div>
      )}

      {Object.keys(errs).length > 0 && (
        <div role="alert" style={{ fontSize: 13.5, color: NP.danger, background: "#F6ECE6", border: "1px solid #DFC5BB", borderRadius: 10, padding: "12px 14px", marginBottom: 16, lineHeight: 1.5 }}>
          A few fields are still needed — they're marked below.
        </div>
      )}

      <div style={niCard}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <NiQ label="First name" error={errs.firstName} id="in-first"><NiText id="in-first" value={form.firstName} onChange={v => set("firstName", v)} /></NiQ>
          <NiQ label="Last name" error={errs.lastName} id="in-last"><NiText id="in-last" value={form.lastName} onChange={v => set("lastName", v)} /></NiQ>
        </div>
        <NiQ label="Email" error={errs.email} id="in-email"><NiText id="in-email" type="email" value={form.email} onChange={v => set("email", v)} /></NiQ>
        <NiQ label="Phone" error={errs.phone} id="in-phone"><NiText id="in-phone" type="tel" value={form.phone} onChange={v => set("phone", v)} /></NiQ>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <NiQ label="Package interest" optional id="in-package">
            <select id="in-package" style={niSelect} value={form.package} onChange={e => set("package", e.target.value)}>
              <option value="">Select…</option>
              {["1x per week", "2x per week", "3x per week", "Online training", "Not sure yet"].map(o => <option key={o}>{o}</option>)}
            </select>
          </NiQ>
          <NiQ label="How did you find me?" optional id="in-referral">
            <select id="in-referral" style={niSelect} value={form.referral} onChange={e => set("referral", e.target.value)}>
              <option value="">Select…</option>
              {["Instagram", "Friend / referral", "Google", "Website", "Other"].map(o => <option key={o}>{o}</option>)}
            </select>
          </NiQ>
        </div>
      </div>

      <div style={niCard}>
        <div style={{ fontSize: 13, fontWeight: 700, color: NP.ink, marginBottom: 4 }}>Training analysis questions</div>
        <p style={{ fontSize: 13, color: NP.inkMuted, margin: "0 0 22px", lineHeight: 1.6 }}>These build your personalised training analysis before your first session. Answer honestly — this is for your benefit.</p>
        {TQ_QDATA.map((q, i) => (
          <NiQ key={q.key} label={`${i + 1}. ${q.label}`} help={q.hint} optional={!q.required} error={errs[q.key]} id={`in-${q.key}`}>
            <NiArea id={`in-${q.key}`} value={(questions as any)[q.key]} onChange={v => setQ(q.key, v)} tall={q.tall} />
          </NiQ>
        ))}
      </div>
      <p style={{ fontSize: 12.5, color: NP.inkMuted, lineHeight: 1.6, textAlign: "center" as const }}>Your answers are sent directly to your coach. A training analysis will be generated within minutes.</p>
    </NiPage>
  );
}

const NEWSLETTER_THEMES = [
  { id: "mixed", label: "Mixed" },
  { id: "fitness", label: "Fitness" },
  { id: "health", label: "Health" },
  { id: "philosophy", label: "Philosophy" },
] as const;
type NewsletterTheme = typeof NEWSLETTER_THEMES[number]["id"];

function Newsletter({ clients }: { clients: Client[] }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [theme, setTheme] = useState<NewsletterTheme>("mixed");
  const [topicIdea, setTopicIdea] = useState("");
  const [topics, setTopics] = useState<string[]>([]);
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [history, setHistory] = useState<{ subject: string; theme?: string; topics?: string[]; sentAt: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");
  const activeClients = clients.filter(c => c.status === "active" && c.email);
  const loadHistory = () => authFetch(`${API}/api/newsletter/history`).then(r => r.ok ? r.json() : { issues: [] }).then(d => setHistory(d.issues || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);
  const generateNewsletter = async () => {
    setGenerating(true); setError("");
    // No client data in the prompt — the newsletter goes to everyone and is about ideas, not client activity.
    const lead = theme === "mixed" ? "Give the three content sections roughly equal weight." : `This issue leans ${theme.toUpperCase()}: make the ${theme === "philosophy" ? "THE IDEA" : theme === "fitness" ? "TRAIN" : "FUEL & RECOVER"} section the longest and most developed; keep the other two short.`;
    const pastTopics = history.flatMap(h => [h.subject, ...(h.topics || [])]).filter(Boolean);
    const prompt = [
      "You are Joey Rouhana, personal trainer and founder of Metaphysique, a personal training studio in Amsterdam built on the classical Greek ideal of training body and mind together.",
      "Write this week's newsletter for your clients, in your own voice: first person, warm but direct, no hype, no emojis. Short paragraphs. 300–450 words in total. It is about fitness, health and philosophy ideas — never about specific clients or what anyone in the studio is doing.",
      // Joey's own writing style, distilled from his essay "The False Promise of Connectivity".
      "WRITING STYLE — write the way Joey writes:",
      "- Open wide, then narrow: start a section from a broad human, cultural or historical frame before getting specific.",
      "- Use runs of short declarative sentences, often with a repeated pattern (\"We would… We would…\", \"Sometimes it lands… Sometimes it lands…\").",
      "- Build a lens and apply it: name a simple model (e.g. the ideal meets reality and becomes something new) and test it on the topic. A recurring Joey move is the gap between the ideal plan and what survives contact with real life.",
      "- Refine metaphors as you use them: \"not a high-speed train crash, but a slow and confusing one.\"",
      "- Think out loud in the first person and show your reasoning (\"I find it useful to think of…\", \"I chose to start here because…\").",
      "- Be fair before being critical: give the other side its due, then name the cost.",
      "- Reason in cause-and-effect chains where each step follows from the last.",
      "- Ground abstract ideas in the body and lived experience (meditation, a face-to-face conversation, a heavy set).",
      "- Land paragraphs on a short, plain closing line or common phrase (\"Out of sight, out of mind.\", \"Talk becomes cheap.\").",
      "- Clean, polished sentences: no run-ons, no typos, no academic jargon, no motivational clichés.",
      "Here is an excerpt of Joey's writing. Imitate its rhythm and sentence construction, NOT its topic:",
      "\"\"\"At the start of this century, we were made a series of promises. We would reconnect with long-lost relatives. We would find our tribe. Alienation began to feel like a thing of the past, a condition on its way to extinction.",
      "Every promise meets the world eventually. I find it useful to think of that meeting as a clash between the ideal and the reality it becomes: not a high-speed train crash, but a slow and confusing one. We start with the best ideas and the best intentions. Sooner or later those ideas are exposed to the outside world, the world that exists without us, where they meet different and sometimes opposing forces. What comes out is a new variant. Sometimes it lands between the ideal and reality. Sometimes it lands on the far side, turned into its opposite.\"\"\"",
      "Use exactly these sections, each starting with its header on its own line in capitals:",
      "THE IDEA — a short philosophy piece (e.g. Stoicism: Seneca, Marcus Aurelius, Epictetus; Aristotle on habit and arete; other classical or modern thinkers) tied back to training or daily life.",
      "TRAIN — one fitness topic explained clearly and practically (technique, programming, strength, conditioning, mobility).",
      "FUEL & RECOVER — one health topic (nutrition, sleep, stress, recovery, daily movement), evidence-based, no fads.",
      "THIS WEEK'S PRACTICE — one concrete, small action for the coming week.",
      "Finish with one short, accurately attributed quote on its own line (only quotes you are confident are real; no song lyrics).",
      lead,
      topicIdea.trim() ? `Build the issue around this topic: "${topicIdea.trim()}".` : "Choose the topics yourself — fresh, specific angles rather than generic advice.",
      pastTopics.length ? `Do NOT repeat these topics from previous issues: ${pastTopics.slice(0, 40).join("; ")}.` : "",
      "Do not include a greeting or sign-off — those are added automatically.",
      'Return ONLY a JSON object, no markdown: {"subject": string (short, intriguing, no clickbait), "body": string (the full newsletter text with the section headers, using \\n line breaks), "topics": string[] (3–4 short labels for the topics covered, e.g. "Seneca on time", "RDL hinge", "sleep consistency"), "imagePrompt": string (one vivid visual scene, 1–2 sentences, for the issue\'s header image — a single concrete, evocative image that captures the main idea, e.g. a weathered marble statue mid-stride at dawn; describe only what is seen, no words or text in the image)}.',
    ].filter(Boolean).join("\n");
    try {
      const newsletterText = await callClaude([{ role: "user", content: prompt }], { maxTokens: 2500 });
      if (newsletterText.startsWith("⚠️")) throw new Error(newsletterText);
      const m = newsletterText.match(/\{[\s\S]*\}/);
      const parsed = JSON.parse(m ? m[0] : newsletterText);
      if (!parsed.subject || !parsed.body) throw new Error("Empty draft");
      setSubject(parsed.subject); setBody(parsed.body);
      setTopics(Array.isArray(parsed.topics) ? parsed.topics.map(String) : []);
      setPreviewHtml("");
      const scene = String(parsed.imagePrompt || parsed.subject);
      setImagePrompt(scene);
      setGenerating(false);
      generateImage(scene);
      return;
    } catch { setError("Failed to generate. Try again."); }
    setGenerating(false);
  };
  // Header image for the issue (AI-generated on the server, hosted there so emails can load it).
  const generateImage = async (scene = imagePrompt) => {
    if (!scene.trim() && !subject.trim()) return;
    setImageBusy(true); setImageError(""); setPreviewHtml("");
    try {
      const res = await authFetch(`${API}/api/newsletter/image`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ imagePrompt: scene, subject }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || `Server error ${res.status}`);
      setImageUrl(data.url);
    } catch (e: any) { setImageError("Image failed: " + (e?.message || "unknown error") + " — you can still send without one."); }
    setImageBusy(false);
  };
  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    if (!subject || !body) return;
    setTesting(true); setError(""); setSent("");
    try {
      const res = await authFetch(`${API}/api/newsletter/test`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ subject, body, imageUrl: imageUrl || undefined }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
      setSent(`Test sent to ${data.to} only — no clients received it.`);
    } catch (e: any) { setError("Test send failed: " + (e?.message || "unknown error")); }
    setTesting(false);
  };
  const showPreview = async () => {
    if (previewHtml) { setPreviewHtml(""); return; }
    try {
      const res = await authFetch(`${API}/api/newsletter/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ subject, body, imageUrl }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
      setPreviewHtml(data.html);
    } catch (e: any) { setError("Preview failed: " + (e?.message || "unknown error")); }
  };
  const sendNewsletter = async () => {
    if (!subject || !body) return;
    if (!window.confirm(`Send "${subject}" to ${activeClients.length} clients?`)) return;
    setLoading(true); setError(""); setSent("");
    try {
      const res = await authFetch(`${API}/api/newsletter/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ subject, body, imageUrl: imageUrl || undefined, theme, topics: topics.length ? topics : (topicIdea.trim() ? [topicIdea.trim()] : []), recipients: activeClients.map(c => ({ name: c.name, email: c.email })) }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
      const failed: { email: string; name?: string }[] = data.failed || [];
      setSent(`Newsletter sent to ${data.sent} of ${activeClients.length} clients.`);
      if (failed.length) setError(`Not delivered to: ${failed.map(f => f.name || f.email).join(", ")}`);
      else { setSubject(""); setBody(""); setTopics([]); setTopicIdea(""); setImageUrl(""); setImagePrompt(""); setPreviewHtml(""); }
      loadHistory();
    } catch (e: any) { setError("Failed to send: " + (e?.message || "unknown error")); }
    setLoading(false);
  };
  return (
    <div style={{ padding: 32, maxWidth: 700 }}>
      <h2 style={{ color: C.accent, fontFamily: F_SERIF, fontSize: 28, marginBottom: 8 }}>NEWSLETTER</h2>
      <p style={{ color: C.textMuted, marginBottom: 24 }}>{activeClients.length} active clients with email will receive this</p>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 6 }}>Theme</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const, marginBottom: 16 }}>
        {NEWSLETTER_THEMES.map(t => (
          <button key={t.id} onClick={() => setTheme(t.id)} style={{ padding: "6px 14px", borderRadius: 999, fontSize: 13, cursor: "pointer", border: `1px solid ${theme === t.id ? C.accent : C.borderInput}`, background: theme === t.id ? C.accent : "transparent", color: theme === t.id ? C.bg : C.textPrimary }}>{t.label}</button>
        ))}
      </div>
      <div style={{ marginBottom: 16 }}><div style={{ color: C.textMuted, fontSize: 13, marginBottom: 6 }}>Write about… (optional)</div><input value={topicIdea} onChange={e => setTopicIdea(e.target.value)} placeholder="e.g. discipline vs motivation — leave empty to let the AI pick" style={{ width: "100%", background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "10px 14px", color: C.textPrimary, fontSize: 14, boxSizing: "border-box" as const }} /></div>
      <Btn variant="secondary" onClick={generateNewsletter} disabled={generating} style={{ marginBottom: 24 }}>{generating ? "Generating…" : body ? "Regenerate Draft" : "AI Draft Newsletter"}</Btn>
      {topics.length > 0 && <div style={{ color: C.textMuted, fontSize: 12.5, marginTop: -12, marginBottom: 16 }}>Topics: {topics.join(" · ")}</div>}
      <div style={{ marginBottom: 16 }}><div style={{ color: C.textMuted, fontSize: 13, marginBottom: 6 }}>Subject</div><input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Weekly update" style={{ width: "100%", background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "10px 14px", color: C.textPrimary, fontSize: 14, boxSizing: "border-box" as const }} /></div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 6 }}>Header image</div>
        {imageUrl
          ? <img src={imageUrl} alt="" style={{ width: "100%", maxWidth: 600, aspectRatio: "3 / 2", objectFit: "cover" as const, borderRadius: R.md, display: "block", marginBottom: 8, background: C.surfaceRaised }} />
          : <div style={{ width: "100%", maxWidth: 600, aspectRatio: "3 / 2", borderRadius: R.md, border: `1px dashed ${C.borderInput}`, display: "flex", alignItems: "center", justifyContent: "center", color: C.textMuted, fontSize: 13, marginBottom: 8 }}>{imageBusy ? "Creating image… (up to a minute)" : "No image yet — draft the newsletter or describe one below"}</div>}
        <input value={imagePrompt} onChange={e => setImagePrompt(e.target.value)} placeholder="Image idea, e.g. a weathered marble statue mid-stride at dawn" style={{ width: "100%", background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "8px 12px", color: C.textPrimary, fontSize: 13, boxSizing: "border-box" as const, marginBottom: 8 }} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
          <Btn variant="secondary" onClick={() => generateImage()} disabled={imageBusy || (!imagePrompt.trim() && !subject.trim())}>{imageBusy ? "Creating…" : imageUrl ? "New image" : "Create image"}</Btn>
          {imageUrl && !imageBusy && <Btn variant="secondary" onClick={() => { setImageUrl(""); setPreviewHtml(""); }}>Remove image</Btn>}
        </div>
        {imageError && <div style={{ color: "#ff4444", fontSize: 13, marginTop: 8 }}>{imageError}</div>}
      </div>
      <div style={{ marginBottom: 24 }}><div style={{ color: C.textMuted, fontSize: 13, marginBottom: 6 }}>Body</div><textarea value={body} onChange={e => { setBody(e.target.value); setPreviewHtml(""); }} rows={12} style={{ width: "100%", background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "10px 14px", color: C.textPrimary, fontSize: 14, boxSizing: "border-box" as const, resize: "vertical" as const }} /></div>
      {error && <div style={{ color: "#ff4444", marginBottom: 16 }}>{error}</div>}
      {sent && <div style={{ color: C.accent, marginBottom: 16 }}>{sent}</div>}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" as const, alignItems: "center" }}>
      <Btn variant="secondary" onClick={showPreview} disabled={!subject || !body}>{previewHtml ? "Hide preview" : "Preview email"}</Btn>
      <Btn variant="secondary" onClick={sendTest} disabled={testing || imageBusy || !subject || !body}>{testing ? "Sending test…" : "Send test to me"}</Btn>
      <Btn variant="primary" onClick={sendNewsletter} disabled={loading || imageBusy || !subject || !body} style={{ padding: "12px 32px", fontSize: 16 }}>{loading ? "Sending…" : "Send to " + activeClients.length + " Clients"}</Btn>
      </div>
      {previewHtml && <iframe title="Newsletter preview" srcDoc={previewHtml} style={{ width: "100%", height: 900, border: `1px solid ${C.borderInput}`, borderRadius: R.md, marginTop: 16, background: "#ECE3D2" }} />}
      {history.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 8 }}>Past issues (the AI avoids these topics)</div>
          {history.slice(0, 8).map((h, i) => (
            <div key={i} style={{ fontSize: 13, color: C.textPrimary, padding: "6px 0", borderTop: `1px solid ${C.borderInput}` }}>
              <span style={{ color: C.textMuted }}>{new Date(h.sentAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span> · {h.subject}{h.topics?.length ? <span style={{ color: C.textMuted }}> — {h.topics.join(", ")}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AIAssistant({ clients, onClientsChange, chatMessages, setChatMessages }: { clients: Client[]; onClientsChange: (c: Client[]) => void; chatMessages: { role: string; content: string }[]; setChatMessages: (m: { role: string; content: string }[]) => void }) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [attachedImage, setAttachedImage] = useState<{ base64: string; mediaType: string; preview: string; isPDF?: boolean; fileName?: string } | null>(null);
  const [pinnedClientId, setPinnedClientId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages, loading]);

  const handleImageAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(",")[1];
      if (file.type === "application/pdf") {
        setAttachedImage({ base64, mediaType: "application/pdf", preview: "", isPDF: true, fileName: file.name });
      } else {
        const mediaType = file.type || "image/png";
        setAttachedImage({ base64, mediaType, preview: dataUrl });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // Build a lean client roster (no bulky training/diet text) for the system prompt
  const clientRoster = clients.map(c => ({
    id: c.id, name: c.name, email: c.email, phone: c.phone,
    goal: c.goal, package: c.package, status: c.status,
    joinDate: c.joinDate, portalToken: c.portalToken,
    hasTraining: !!c.trainingScheme, hasDiet: !!c.diet,
    inbodyCount: c.inbody?.length ?? 0, sessionCount: c.sessions?.length ?? 0,
  }));

  const system = `You are an AI assistant for Joey Rouhana, a personal trainer. Help manage client portfolios.

SAVE CONFIRMATION RULE — CRITICAL:
When you generate a training plan or diet plan, always follow this 2-step process:
STEP 1: Write the full plan and end with "Save this to [client name]'s portfolio?" — do NOT include any ACTION tag yet.
STEP 2: Only when the coach replies "yes", "save it", "go ahead", "add it", or similar confirmation, THEN include the ACTION tag to actually save it.
NEVER include a set_training, append_training, or set_diet ACTION tag in the same response as the plan. Always wait for confirmation first.
This rule also applies to the week-by-week building flow — show each week, ask to save, then save on confirmation before moving to the next week.
Current clients (summary only): ${JSON.stringify(clientRoster, null, 2)}

CLIENT MATCHING — CRITICAL:
Always match clients by FULL name (first + last). NEVER match on last name alone.
If two clients share a last name (e.g. "Shearer"), they are different people — always use the full name to tell them apart.
If the name given does not exactly match any existing client's full name, treat the request as creating a NEW client (id = "new") — do NOT assume it refers to a client with the same last name.

CLIENT DATA — when a client is mentioned, their FULL DATA is appended to the user message. This includes:
- workoutLogs: array of logged sessions with exercises, sets, weight, reps, date, and optional sessionNote
- personalRecords: best weight×reps per exercise with estimated 1RM and date achieved
- trainingNotes: coach notes on the training program
Use workoutLogs to see actual performance history, identify progress, and base progressive overload calculations on real numbers. Use personalRecords to know each exercise's current best. Use trainingNotes for program context.
Actions (include in response):
- <ACTION>upsert_client:{"id":"new_or_existing","name":"...","email":"...","phone":"...","goal":"...","package":"...","status":"active","notes":"..."}</ACTION>
CRITICAL — upsert_client: ONLY use this to CREATE a brand new client (id = "new") or update basic profile fields (name, email, phone, goal, package, status, notes) on an EXISTING client. ALWAYS use the client's exact id from [FULL DATA]. NEVER generate a new id for an existing client — this creates duplicates. For training/diet changes use patch_training, set_training, append_training, or set_diet instead.
SESSION CREDITS — adding/removing sessions from a client's package: The session count is embedded in the package field as a number (e.g. "24 sessions block"). To add or remove sessions, parse the current number from the package string, adjust it, and save the updated package string using upsert_client. Example: client has package "24 sessions block", coach says "add 2 sessions" → new package = "26 sessions block". Always confirm the new total in your response and include the upsert_client ACTION immediately (no confirmation needed for credit adjustments).
- <ACTION>patch_training:{"clientId":"...","find":"exact text to find","replace":"new text to replace it with"}</ACTION>
- <ACTION>set_training:{"clientId":"...","trainingScheme":"..."}</ACTION>
- <ACTION>append_training:{"clientId":"...","weeksToAdd":"...only the new weeks text..."}</ACTION>
- <ACTION>set_diet:{"clientId":"...","diet":"..."}</ACTION>
- <ACTION>add_note:{"clientId":"...","note":"..."}</ACTION>
NOTES: Use add_note to append to the client's Coach Notes section (separate from Health & Medical notes). The note will be timestamped automatically. Use this whenever the coach says things like "add a note", "note that", "remember that", "log that", "write down that", "remind me", or mentions payment reminders about a client. Never use upsert_client to update notes — always use add_note so existing notes are preserved. The health/medical notes field (client.notes) is managed manually by the coach and should never be modified by the AI.
- <ACTION>add_inbody:{"clientId":"...","date":"...","weight":0,"muscleMass":0,"bodyFat":0,"bmi":0,"visceralFat":0,"notes":"..."}</ACTION>
- <ACTION>delete_inbody:{"clientId":"...","date":"..."}</ACTION>
- <ACTION>add_session:{"clientId":"...","date":"...","time":"...","type":"...","notes":"..."}</ACTION>
CRITICAL: When adding a session, you MUST ALWAYS include BOTH the add_session ACTION and the add_calendar ACTION in the same response. Never add a session without also adding it to the calendar.
SESSION TYPES — CRITICAL: The "type" field of add_session MUST be EXACTLY one of these four values, with this exact spelling and capitalization: "Call", "Intake", "PT Membership", "PT Package". No other value is ever allowed — never write "Personal Training", "PT", "Training Session" or anything else. How to choose:
- "Call" = phone or video call (check-in call, consult call)
- "Intake" = first appointment with a new or prospective client (intake, kennismaking, trial session)
- "PT Membership" = regular training session for a client on a monthly membership — this is the DEFAULT when the coach doesn't specify
- "PT Package" = training session for a client on a session package/bundle (check the client's "package" field if unsure; if it mentions a bundle or fixed number of sessions, use "PT Package")
Use the same value in the add_calendar description field so the calendar and the client record always match.
- <ACTION>delete_session:{"clientId":"...","sessionId":"..."}</ACTION>
- <ACTION>delete_calendar:{"title":"...","date":"YYYY-MM-DD"}</ACTION>
- <ACTION>delete_client:{"clientId":"..."}</ACTION>
- <ACTION>send_email:{"to":"client@email.com","subject":"...","body":"..."}</ACTION>
- <ACTION>create_payment:{"clientId":"...","amount":"150.00","description":"Personal Training - [Month]"}</ACTION>
- <ACTION>record_payment:{"clientId":"...","amount":"380.00","date":"YYYY-MM-DD","description":"..."}</ACTION>
Use record_payment to log a payment that has already been made (e.g. a past Mollie purchase, bank transfer, or cash). This adds it directly to the client's payment history with status "paid". Amount must be a string with 2 decimal places. Date is YYYY-MM-DD (use today if not specified).
- <ACTION>add_calendar:{"title":"...","date":"YYYY-MM-DD","time":"HH:MM","duration":60,"description":"..."}</ACTION>
- <ACTION>add_session:{"clientId":"...","date":"YYYY-MM-DD","time":"HH:MM","type":"...","notes":"..."}</ACTION>
CRITICAL: When deleting a session, use delete_calendar (with the session title and date) to remove it from Google Calendar. Also use delete_session if you know the client and sessionId to remove it from their record.
- <ACTION>log_workout:{"clientId":"...","date":"YYYY-MM-DD","dayName":"Day 1 — Upper Body","weekName":"Week 1","sessionNote":"optional note","exercises":[{"name":"...","sets":[{"weight":"...","reps":"..."}]}]}</ACTION>
Use log_workout to save a training session to a client's workout log. Each exercise has a name and an array of sets, each with weight (in kg as a string, e.g. "60") and reps (as a string, e.g. "8"). Use "" for weight if not recorded.
REQUIRED: Always include dayName and weekName when you know them (look at the client's trainingScheme to match the session to the right day and week). dayName = the training day title (e.g. "Day 1 — Full Body Strength A"). weekName = the program week (e.g. "Week 1", "Week 3"). If unknown, use dayName:"Session" and omit weekName. sessionNote is optional — include it only if the coach provides a note for the session.

TRAINING NOTES IMPORT — when the coach pastes raw session notes (client names followed by exercises with sets/reps/weight):
STEP 1: Parse the notes. For each client block, identify the client from the known client list, parse each exercise line into sets/reps/weight. Show a clean formatted preview grouped by client — show exercise name, sets×reps @ weight. If you can match the exercises to a specific training day in the client's trainingScheme, note which day it matches (e.g. "Matches Day 1 — Full Body Strength A, Week 2"). End with "Save these sessions to workout logs?" — do NOT include ACTION tags yet.
STEP 2: Only when the coach confirms ("yes", "save", "go ahead", etc.), fire one log_workout ACTION per client. Use the date the coach mentions or ask if not specified. Always include dayName and weekName if you identified the matching day/week in Step 1.
Format rules: "3x8 @60kgs" → 3 sets of {weight:"60", reps:"8"}. "8,6,6 50,52.5,52.5" → [{weight:"50",reps:"8"},{weight:"52.5",reps:"6"},{weight:"52.5",reps:"6"}]. No weight → weight:"". Always use the recommended format: SETSxREPS @WEIGHTkgs.

EMAIL RULES — CRITICAL:
- ALWAYS follow this 2-step process for emails:
  STEP 1: When asked to send an email, write the full email draft and show it to the coach. End with "Send this?" — do NOT include the ACTION tag yet.
  STEP 2: Only when the coach replies "yes", "send it", "go ahead" or similar confirmation, THEN include the <ACTION>send_email:{"to":"...","subject":"...","body":"...full email text..."}</ACTION> tag to actually send it.
- NEVER include the send_email ACTION tag in the same response as the draft. Always wait for confirmation first.
- NEVER say "email sent" without including the ACTION tag — the tag is what actually sends it.
- Always use the client's actual email address from client data for the "to" field.
- The "body" field must contain the full email text.
- If the client has no email address, say so and do NOT send.

PAYMENTS: When asked to send a payment link or invoice to a client, use the create_payment ACTION. The amount must be a string with 2 decimal places (e.g. "150.00"). Always confirm the amount and description with the coach before sending if not specified. The payment link will be automatically emailed to the client via Mollie (iDEAL/credit card). Example: "Send Joey a payment link for €150 for November training" → create_payment with clientId, amount "150.00", description "Personal Training - November". After the action runs the client receives the payment link by email automatically.

PORTAL LINKS: Clients log in at https://app.joeyrouhana.com/portal with their email and password. For a client who has NOT set up their account yet, send their personal setup link instead: https://app.joeyrouhana.com/client/[portalToken] (the portalToken is included in each client's data) — that link lets them create their password, and afterwards redirects to login.
Be concise and professional. NEVER say something is saved without including the ACTION tag — the ACTION tag is what actually saves it. For all other data changes (inbody, sessions, payments, client info), include the ACTION tag immediately without asking for confirmation.

DIET FORMAT: Always write diet plans using ## headers. Keep diet plans concise - maximum 500 words. Always put the <ACTION> tag BEFORE any explanation text. Always write diet plans using ## headers for each section (e.g. ## Breakfast, ## Lunch, ## Dinner, ## Snacks, ## Nutrition Overview). Use - bullet points for items within each section. Example:
## Nutrition Overview
- Calories: 2500 kcal
- Protein: 200g
## Breakfast
- Oats with banana: 400 kcal | 30g protein

SAVING TRAINING — CRITICAL RULES:
- NEVER claim something is "saved", "done", or "added" in your own words. YOU cannot save anything — only the ACTION tag does, and the app appends a real "✅ ..." confirmation line to your message after the save succeeds (or a "⚠️ ..." line if it fails). If a previous message of yours has no ✅ confirmation line under it, that save NEVER HAPPENED — re-emit the full ACTION tag instead of insisting it worked. When emitting an action, phrase it as intent: "Saving Week 2 now:" followed by the ACTION tag.
- When making a SMALL EDIT to existing content (change a weight, fix a note, update a rep range, rename an exercise, remove a line): use patch_training with the exact text to find and the replacement. This is surgical — only the matched text changes, nothing else is touched.
- When ADDING weeks, phases, or blocks to an existing plan: use append_training with ONLY the new content. NEVER use set_training if trainingScheme already has content.
- When appending a new phase, start with a clear phase header on its own line, e.g. "## Phase 2 — Strength Foundation", followed by week headers like "## Week 1", "## Week 2". This ensures the structured view recognises it as a separate block.
- DAY ORDER: within each week, days MUST appear in calendar order Monday → Sunday. Rest, cardio, and optional days go in their natural weekday position (a Wednesday rest/cardio day comes between Tuesday and Thursday) — NEVER grouped at the end of the week.
- When CREATING a plan from scratch (trainingScheme is empty or blank): use set_training with the full trainingScheme.
- NEVER use set_training if the client already has a trainingScheme — use append_training or patch_training instead. Using set_training on an existing plan DESTROYS all previous weeks and phases.
- ALWAYS prefer patch_training over set_training for any edit that doesn't touch the whole plan. It is faster, safer, and cheaper.
- For patch_training, the "find" field must match the existing text EXACTLY (copy it character-for-character from the trainingScheme). If the text isn't found, the server will return an error.
- NEVER embed the entire existing plan inside an ACTION tag — only new content goes in append_training.
- NEVER "rebuild with clear headers" or restructure the whole plan — if the structure needs fixing, use patch_training to fix only what's wrong.
- ONE patch per response: NEVER include more than one patch_training ACTION in a single response. If you need multiple edits, do one at a time and wait for the result before the next.
- For append_training and set_training: ALL content (every day, every exercise, every week) MUST be included in a SINGLE ACTION tag. NEVER split days or weeks across multiple responses — if you are adding Day 1 and Day 2, both must appear together in one weeksToAdd or trainingScheme string. Saying "saved" and then only including one day is a critical error.
- If you are unsure what text to use as the "find" value, DO NOT guess — ask the coach first. Say what you want to change and ask them to confirm before you patch.
For PREP WORK / warm-up sections: ALWAYS write them as a ### PREP WORK section header INSIDE the training day, as the very first section before the main exercises. NEVER create a standalone "PREP WORK" day. Example:
Day 1 - Upper Body (Monday)
### PREP WORK
Halos | 2 sets | 10 reps | bodyweight | 30 sec |
Thoracic Spine Rotation | 2 sets | 10 reps | bodyweight | 30 sec |
### MAIN LIFTS
Bench Press | 4 sets | 6-8 reps | 70kg | 2 min | ...
### ACCESSORIES
...

CLARIFICATION RULE: If a request is ambiguous or you are not sure exactly what the coach wants, ask ONE short clarifying question before acting. Do not guess and do not proceed with assumptions.

WEEK-BY-WEEK BUILDING — MANDATORY for programs longer than 4 weeks:
- If the requested program is MORE than 4 weeks, do NOT generate all weeks at once.
- Instead: generate and save ONLY Week 1 using set_training, then end your response with: "Reply **continue** when you're ready for Week 2." (the app appends the real ✅ save confirmation itself)
- Each time the coach replies "continue", "next", "next week", or similar: generate and save the NEXT week using append_training, then prompt for the following week.
- Keep track of which week you're on from the conversation history.
- After the final week is saved, confirm the full program is complete.
- This applies even if the coach originally asked for "8 weeks" or "12 weeks" in one message.

RESUMING A PROGRAM IN A NEW SESSION — CRITICAL:
When the coach says something like "continue the program", "add the next week", "resume week-by-week", or "continue where we left off" for a client:
1. Look at the client's trainingScheme in [FULL DATA] and count how many weeks are already there (look for "## Week N" or "### Week N" headers).
2. The NEXT week to build is the one after the last saved week. State this clearly: "I can see [N] weeks already saved. I'll build Week [N+1] now."
3. Check workoutLogs to see which weeks have been completed (logged sessions). Use those logs to apply progressive overload per the PROGRESSIVE OVERLOAD FROM LOGS rule.
4. Then generate and save the next week using append_training, and prompt for the following week as usual.
5. If the trainingScheme already contains ALL the requested weeks, tell the coach the program is complete rather than adding more.

PROGRESSIVE OVERLOAD FROM LOGS — CRITICAL:
Before writing any week beyond Week 1, you MUST check the client's workoutLogs for actual performance in the previous week:
- For each exercise in the previous week, find the most recent logged set (highest weight lifted, or latest date if multiple entries).
- Use those logged weights as the baseline for the next week's progression — NOT the prescribed weights from the training scheme.
- If the client hit the top of the rep range (e.g. logged 8 reps when the prescription was 6-8), increase the weight by a small increment (typically 2.5–5kg for upper body, 5–10kg for lower body).
- If the client stayed in the middle of the rep range (e.g. logged 6 reps on a 6-8 prescription), keep the same weight.
- If the client struggled or logged below the bottom of the rep range, keep weight the same or reduce slightly.
- If no logs exist for an exercise, fall back to standard progressive overload from the prescribed weight.
- When you do this, briefly note at the top of the week: "Based on [client]'s logged performance from Week [N]..." so the coach can see you used real data.

TRAINING FORMAT: Always write training plans using this EXACT pipe format for each exercise:
Exercise Name | 4 sets | 6-8 reps | weight/intensity | rest time | coaching note
Example: Bench Press | 4 sets | 6-8 reps | 70kg | 2 min | Focus on controlled eccentric
Always include rest time as the 5th field (e.g. 90 sec, 2 min, 3 min). Never skip it.
Use **Day X - Title (Day of week)** for day headers. Each exercise MUST use the pipe | separator format. This is required for proper structured display in the app.
For SUPERSETS, use letter suffixes like: 2A. Exercise Name | sets | reps | weight | notes
When asked to merge or combine 2 exercises into a superset, find them in the existing training plan and rename them with letter suffixes (e.g. if exercises 3 and 4 should be a superset, rename to 3A. and 3B.). Always use set_training ACTION to save the updated plan.
2B. Exercise Name | sets | reps | weight | notes
This groups them visually as a superset in the app.

BLOCK PERIODIZATION - MANDATORY RULES:
When asked to create a brand new training plan from scratch, ask these before generating:
1. How many weeks? (default: 4)
2. What block type? (strength / hypertrophy / power / deload)
EXCEPTION — skip these questions when:
- You already have the client's trainingScheme loaded (extending or updating an existing plan). In that case, infer block type from the existing plan's rep ranges and continue with the same structure.
- The coach specifies weeks (e.g. "add week 3 to 6") — just generate using the existing plan's structure.
Block type definitions:
- Strength: 3-5 reps, 85-90% 1RM, 4-5 sets, 3-5 min rest
- Hypertrophy: 8-12 reps, 65-75% 1RM, 3-4 sets, 60-90s rest
- Power: 1-3 reps, 90%+ 1RM, 3-5 sets, 4-6 min rest, explosive intent
- Deload: same exercises, 50-60% 1RM, 2-3 sets, cut volume by 40%
Progressive overload: increase reps first, then weight once top of range is hit.
Always write ALL weeks in full. Never say repeat week 1. Show exact progression each week.

CONVERSATION CONTEXT: The client's full data (including trainingScheme, workoutLogs, personalRecords) is injected into the user message when a client is mentioned — either by name in the current message or inferred from recent conversation. When you see [FULL DATA for X], you have everything you need to act — do not ask "which client?" or request data you already have.


STRONGFIRST PROGRAMMING KNOWLEDGE:
When asked to use StrongFirst, Easy Strength, Plan Strong, or any SF methodology, apply these principles from the Programming Demystified manual:
- Easy Strength: 2 sets x 5 reps at 70-80% 1RM, 5 days/week, always leave reps in reserve, rotate 5 exercises
- Plan Strong: tonnage-based, specific volume/intensity ratios, uses 1RM percentages precisely
- Key principles: Specificity, Novelty, Volume, Intensity, Reps/Effort, Rest, Exercise Dynamics, Load Dynamics
- Stress-free adaptation: submaximal loads, never to failure, sustainable progress
- Cycling: Linear (add weight each session), Wave (undulating loads), Step (step loading with resets)
- Structure: Macrocycle > Mesocycle (4-6 weeks) > Microcycle (weekly)
- Training vs Peaking: long submaximal blocks then short peaking phases
- Hypertrophy: higher volume, moderate intensity, 8-12 rep ranges with SF principles
Always when using StrongFirst: specify exercise, sets, reps, %1RM or RPE, rest, weekly schedule.

PDF UPLOAD — TRAINING SCHEDULE OR DIET PLAN:
When the coach uploads a PDF file, follow these rules EXACTLY:

PRIORITY ORDER — CRITICAL:
- The PDF is the single source of truth for exercise selection, structure, sets, reps, day layout, and progression scheme. Do NOT alter or omit anything from the PDF.
- The client's existing app data (workoutLogs, personalRecords, trainingScheme) is used ONLY to fill in or adjust weights for exercises that already appear in the PDF AND have a logged history. Nothing else from the existing data bleeds into the PDF plan.
- NEVER merge, blend, or mix the PDF content with the client's existing trainingScheme. The PDF replaces or extends — it does not combine.

WEIGHT ADJUSTMENT RULE:
- For each exercise in the PDF that also appears in personalRecords or workoutLogs, substitute or suggest the weight based on the client's best logged performance (use 1RM estimate or last logged working weight).
- If the PDF already specifies a weight AND the client has a log for that exercise, use the logged weight if it differs — note the substitution inline (e.g. "adjusted to 90kg based on logged PR").
- If the PDF specifies a weight and there is NO log for that exercise, keep the PDF weight as-is.
- If the PDF has no weight for an exercise and the client has a log, fill in the weight from the log.
- If neither the PDF nor logs have a weight, leave it blank or write "TBD".

STEPS:
1. Read the full PDF content. Do not skip or summarize sections.
2. Determine whether it contains a training schedule, a diet plan, or both.
3. If a client is pinned (their [FULL DATA] is in the message), apply the plan to them. If no client is pinned, ask: "Which client should I apply this to?" before doing anything.
4. For a TRAINING SCHEDULE: reproduce the PDF structure faithfully in the pipe format (Exercise | sets | reps | weight | rest | notes), applying weight adjustments per the rule above. Show the full formatted plan and end with "Save this to [client name]'s portfolio?" — wait for confirmation before saving.
5. For a DIET PLAN: reproduce the PDF content faithfully using ## section headers and bullet points. Show it and wait for confirmation before saving.
6. If the PDF contains weeks or phases, respect the WEEK-BY-WEEK BUILDING rule for programs longer than 4 weeks.
7. If anything in the PDF is ambiguous (missing exercise names, unclear structure), note it after the plan.
8. After saving, confirm: "✅ Plan from PDF saved to [client name]'s profile."

CALENDAR SCREENSHOT PARSING:
When the coach shares a screenshot of their Magicline (or any gym software) calendar:
IMPORTANT: The calendar uses European week format — the week starts on MONDAY, not Sunday. The columns left to right are: Mon, Tue, Wed, Thu, Fri, Sat, Sun. Never treat the first column as Sunday.
1. Extract every appointment: client name, date (read the exact date number shown in each column header), start time, end time, session type.
2. Match each client name to [CLIENT ROSTER] by first or last name (fuzzy match is fine).
3. For each matched appointment, immediately emit both actions (no confirmation needed):
   <ACTION>add_session:{"clientId":"...","date":"YYYY-MM-DD","time":"HH:MM","type":"PT Membership","notes":"From Magicline"}</ACTION>
   <ACTION>add_calendar:{"title":"PT: [client name]","date":"YYYY-MM-DD","time":"HH:MM","duration":60,"description":"PT Membership"}</ACTION>
   (Use "PT Package" instead of "PT Membership" in both actions if the client's package field indicates a session bundle. The SESSION TYPES rule applies here too — only the four allowed values.)
4. After all actions, list what was saved and flag any names that had no match in the roster.
5. If no text accompanies the image, assume the coach wants all visible sessions saved.`;

  const send = async () => {
    if (!input.trim() && !attachedImage || loading) return;
    const displayText = attachedImage && !input.trim()
      ? (attachedImage.isPDF ? `📄 ${attachedImage.fileName || "PDF uploaded"}` : "📎 Calendar screenshot")
      : input;
    const userMsg = { role: "user", content: displayText };
    const newMessages = [...chatMessages, userMsg];
    setChatMessages(newMessages);
    setInput(""); setLoading(true);
    const imageSnapshot = attachedImage;
    setAttachedImage(null);

    // Determine active client — dropdown is the explicit control, always use it if set
    let mentionedClient = clients.find(c => c.id === pinnedClientId) || undefined;
    if (!pinnedClientId) {
      // No client selected — detect from message or recent user messages
      const searchText = input.toLowerCase();
      const recentUserHistory = chatMessages.slice(-6).filter(m => m.role === "user").map(m => m.content).join(" ").toLowerCase();

      // Levenshtein distance for fuzzy matching typos
      const levenshtein = (a: string, b: string): number => {
        const m = a.length, n = b.length;
        const dp: number[][] = Array.from({ length: m + 1 }, (_, i) => Array(n + 1).fill(0).map((_, j) => i === 0 ? j : j === 0 ? i : 0));
        for (let i = 1; i <= m; i++) for (let j = 1; j <= n; j++)
          dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
        return dp[m][n];
      };

      // Extract words from text and check if any word fuzzy-matches any name part
      const fuzzyMatchesText = (name: string, text: string) => {
        const nameParts = name.toLowerCase().split(" ").filter(p => p.length > 2);
        const textWords = text.split(/\s+/).filter(w => w.length > 2);
        return nameParts.some(part =>
          textWords.some(word => {
            if (text.includes(part)) return true; // exact match
            const maxDist = part.length <= 4 ? 1 : 2;
            return levenshtein(part, word) <= maxDist;
          })
        );
      };
      const exactMatchesAll = (name: string, text: string) => {
        const parts = name.toLowerCase().split(" ").filter(p => p.length > 2);
        return parts.length > 0 && parts.every(p => text.includes(p));
      };

      mentionedClient =
        clients.find(c => exactMatchesAll(c.name, searchText)) ||
        clients.find(c => exactMatchesAll(c.name, recentUserHistory)) ||
        clients.find(c => fuzzyMatchesText(c.name, searchText)) ||
        clients.find(c => fuzzyMatchesText(c.name, recentUserHistory));
      if (mentionedClient) setPinnedClientId(mentionedClient.id);
    }

    let workoutLogs: any[] = [];
    let prs: any = {};
    if (mentionedClient) {
      try {
        // Coach JWT — ?pt= access is rejected once a client has portal login enabled
        const logsUrl = `${API}/api/clients/${mentionedClient.id}/workout-logs`;
        const prsUrl  = `${API}/api/clients/${mentionedClient.id}/prs`;
        const safeJson = (r: Response) => r.json().then(d => d).catch(() => null);
        const doFetch = (url: string) => authFetch(url);
        [workoutLogs, prs] = await Promise.all([
          doFetch(logsUrl).then(safeJson).then(d => Array.isArray(d) ? d : []).catch(() => []),
          doFetch(prsUrl).then(safeJson).then(d => (d && !Array.isArray(d)) ? d : {}).catch(() => ({})),
        ]);
      } catch { /* non-fatal */ }
    }

    const clientContext = mentionedClient
      ? `\n\n[FULL DATA for ${mentionedClient.name}]: ${JSON.stringify({
          id: mentionedClient.id, name: mentionedClient.name, email: mentionedClient.email,
          phone: mentionedClient.phone, goal: mentionedClient.goal, package: mentionedClient.package,
          status: mentionedClient.status, notes: mentionedClient.notes, joinDate: mentionedClient.joinDate,
          portalToken: mentionedClient.portalToken, inbody: mentionedClient.inbody,
          sessions: mentionedClient.sessions,
          trainingScheme: mentionedClient.trainingScheme || "",
          trainingNotes: mentionedClient.trainingNotes || "",
          diet: mentionedClient.diet || "",
          workoutLogs,
          personalRecords: prs,
        })}`
      : "";

    // Send current message with injected client context + last 6 messages for conversation continuity
    // Truncate long history messages (e.g. large training plans) to keep request body manageable
    const history = chatMessages.slice(-6).map(m => {
      const c = m.content;
      if (typeof c === 'string' && c.length > 3000) {
        // Keep the tail too — the app appends ✅/⚠️ save-result lines at the END of assistant
        // messages, and the model must see them to know whether a save actually happened.
        return { role: m.role, content: c.slice(0, 2500) + '\n...[truncated for brevity]...\n' + c.slice(-500) };
      }
      return { role: m.role, content: c };
    });
    const defaultPrompt = imageSnapshot?.isPDF
      ? "Parse this PDF and apply the training schedule or diet plan to the pinned client."
      : "Parse this calendar screenshot and save all sessions.";
    const textContent = (input || defaultPrompt) + clientContext;
    const userContent: any = imageSnapshot
      ? imageSnapshot.isPDF
        ? [
            { type: "document", source: { type: "base64", media_type: "application/pdf", data: imageSnapshot.base64 } },
            { type: "text", text: textContent },
          ]
        : [
            { type: "image", source: { type: "base64", media_type: imageSnapshot.mediaType, data: imageSnapshot.base64 } },
            { type: "text", text: textContent },
          ]
      : textContent;
    const messagesToSend = [...history, { role: "user", content: userContent }];

    // 16k output tokens — a full multi-week program in one ACTION tag must never hit the cap.
    const replyMeta: { stopReason?: string } = {};
    const reply = await askClaude(messagesToSend, system, 16000, replyMeta);
    let updatedClients = [...clients];
    const emailResults: string[] = [];
    const actionRegex = /<ACTION>([\s\S]*?)<\/ACTION>/g;
    let match;
    let newlyCreatedClientId: string | null = null;
    // Fields an earlier action in this reply changed locally (e.g. upsert_client) but hasn't
    // saved yet — carried along when a later action saves the same client from a fresh copy.
    const pendingEdits = (uc: Client): Partial<Client> => {
      const original: any = clients.find(c => c.id === uc.id);
      if (!original) return uc;
      const edits: any = {};
      for (const k of Object.keys(uc)) {
        if (k !== "rev" && JSON.stringify((uc as any)[k]) !== JSON.stringify(original[k])) edits[k] = (uc as any)[k];
      }
      return edits;
    };
    // Clients already saved inside the loop, as saved — so the final pass doesn't PUT them again
    // (that second PUT bumped the server rev behind our back and made every later save 409).
    const savedSnapshots: Record<string, string> = {};
    const adoptSaved = (idx: number, saved: Client) => {
      updatedClients[idx] = { ...updatedClients[idx], ...saved };
      savedSnapshots[saved.id] = JSON.stringify(updatedClients[idx]);
    };
    while ((match = actionRegex.exec(reply)) !== null) {
      // Declared outside the try so the catch can name the failed action in the chat warning
      let actionType = "";
      try {
        const colonIdx = match[1].indexOf(":");
        actionType = match[1].slice(0, colonIdx).trim();
        const payload = JSON.parse(match[1].slice(colonIdx + 1).trim());
        if (actionType === "upsert_client") {
          const idx = updatedClients.findIndex(c => c.id === payload.id);
          if (idx >= 0) { updatedClients[idx] = { ...updatedClients[idx], ...payload }; }
          else {
            const freshId = Date.now().toString();
            updatedClients.push({ ...newClient(payload.name), ...payload, id: freshId });
            newlyCreatedClientId = freshId;
          }
        } else if (actionType === "delete_inbody") {
          const idx = updatedClients.findIndex((c: any) => c.id === payload.clientId);
          if (idx >= 0) {
            updatedClients[idx].inbody = updatedClients[idx].inbody.filter((r: any) => r.date !== payload.date);
          }
        } else if (actionType === "delete_session") {
          const idx = updatedClients.findIndex((c: any) => c.id === payload.clientId);
          if (idx >= 0) {
            // Also remove the linked Google Calendar event so the calendar stays in sync
            const target = updatedClients[idx].sessions.find((s: any) => s.id === payload.sessionId);
            if (target?.eventId) {
              authFetch(`${API}/api/calendar/events/${target.eventId}`, { method: "DELETE" }).catch(() => {});
            }
            updatedClients[idx].sessions = updatedClients[idx].sessions.filter((s: any) => s.id !== payload.sessionId);
          }
        } else if (actionType === "delete_client") {
          updatedClients = updatedClients.filter((c: any) => c.id !== payload.clientId);
        } else if (actionType === "patch_training") {
          const idx = updatedClients.findIndex(c => c.id === payload.clientId);
          if (idx >= 0) {
            const r = await authFetch(`${API}/api/clients/${payload.clientId}/training`, {
              method: "PATCH", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ find: payload.find, replace: payload.replace }),
            });
            if (r.ok) {
              const data = await r.json();
              updatedClients[idx].trainingScheme = data.trainingScheme;
            } else {
              const err = await r.json();
              emailResults.push(`⚠️ Patch failed: ${err.error}`);
            }
          } else { emailResults.push(`⚠️ Could not patch training — client not found.`); }
        } else if (actionType === "set_training") {
          const idx = updatedClients.findIndex(c => c.id === payload.clientId);
          if (idx >= 0) {
            const r = await saveClientChangeFresh(updatedClients[idx], () => ({
              ...pendingEdits(updatedClients[idx]),
              trainingScheme: normalizeWeekdayOrder(payload.trainingScheme),
              schemeUpdatedAt: new Date().toISOString().split("T")[0],
            }));
            if (r.saved) {
              adoptSaved(idx, r.saved);
              emailResults.push(`✅ Training saved to ${updatedClients[idx].name}'s portfolio.`);
            } else if (r.status === 409) {
              emailResults.push(`⚠️ Training NOT saved — ${updatedClients[idx].name} was updated on another device/tab. Refresh the app and try again.`);
            } else {
              emailResults.push(`⚠️ Training update failed (server error ${r.status}) — not saved.`);
            }
          } else { emailResults.push(`⚠️ Could not save training — client not found. Please recreate the client profile first.`); }
        } else if (actionType === "append_training") {
          const idx = updatedClients.findIndex(c => c.id === payload.clientId);
          if (idx >= 0) {
            const newWeeks = normalizeWeekdayOrder(payload.weeksToAdd);
            const r = await saveClientChangeFresh(updatedClients[idx], base => ({
              ...pendingEdits(updatedClients[idx]),
              trainingScheme: base.trainingScheme ? base.trainingScheme + "\n\n" + newWeeks : newWeeks,
              schemeUpdatedAt: new Date().toISOString().split("T")[0],
            }));
            if (r.saved) {
              adoptSaved(idx, r.saved);
              emailResults.push(`✅ Training week(s) appended to ${updatedClients[idx].name}'s portfolio.`);
            } else if (r.status === 409) {
              emailResults.push(`⚠️ Training NOT appended — ${updatedClients[idx].name} was updated on another device/tab. Refresh the app and try again.`);
            } else {
              emailResults.push(`⚠️ Append training failed (server error ${r.status}) — not saved.`);
            }
          } else { emailResults.push(`⚠️ Could not append training — client not found. Please recreate the client profile first.`); }
        } else if (actionType === "set_diet") {
          const idx = updatedClients.findIndex(c => c.id === payload.clientId);
          if (idx >= 0) {
            const r = await saveClientChangeFresh(updatedClients[idx], () => ({ ...pendingEdits(updatedClients[idx]), diet: payload.diet }));
            if (r.saved) {
              adoptSaved(idx, r.saved);
              emailResults.push(`✅ Diet saved to ${updatedClients[idx].name}'s portfolio.`);
            } else {
              emailResults.push(`⚠️ Diet update failed (server error ${r.status}) — not saved.`);
            }
          } else emailResults.push(`⚠️ Could not save diet — client not found. Please recreate the client profile first.`);
        } else if (actionType === "add_note") {
          const idx = updatedClients.findIndex(c => c.id === payload.clientId);
          if (idx >= 0) {
            const timestamp = new Date().toISOString().split("T")[0];
            const newEntry = `[${timestamp}] ${payload.note}`;
            const r = await saveClientChangeFresh(updatedClients[idx], base => ({
              ...pendingEdits(updatedClients[idx]),
              coachNotes: base.coachNotes ? base.coachNotes + "\n" + newEntry : newEntry,
            }));
            if (r.saved) {
              adoptSaved(idx, r.saved);
              emailResults.push(`✅ Note added to ${updatedClients[idx].name}'s portfolio.`);
            } else emailResults.push(`⚠️ Failed to save note (server error ${r.status}).`);
          } else emailResults.push(`⚠️ Could not add note — client not found.`);
        } else if (actionType === "add_inbody") {
          const idx = updatedClients.findIndex(c => c.id === payload.clientId);
          if (idx >= 0) { const { clientId, ...result } = payload; updatedClients[idx].inbody.push(result); }
        } else if (actionType === "send_email") {
          try {
            const emailRes = await authFetch(`https://coachagent-server-production.up.railway.app/api/send-email`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
            if (emailRes.ok) {
              emailResults.push(`✅ Email delivered to ${payload.to}`);
            } else {
              const err = await emailRes.json();
              emailResults.push(`❌ Email FAILED to ${payload.to}: ${err.error}`);
            }
          } catch(e: any) {
            emailResults.push(`❌ Email FAILED to ${payload.to}: ${e.message}`);
          }
        } else if (actionType === "delete_calendar") {
          const r = await authFetch("https://coachagent-server-production.up.railway.app/api/calendar/events/by-title", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
          if (r.ok) { const d = await r.json(); emailResults.push(`🗑️ Deleted ${d.deleted} calendar event(s) matching "${payload.title}" on ${payload.date}`); }
          else { const e = await r.json(); emailResults.push(`⚠️ Calendar delete failed: ${e.error}`); }
        } else if (actionType === "add_calendar") {
          const calClient = (payload.clientId ? updatedClients.find(c => c.id === payload.clientId) : undefined)
            || matchClientInTitle(payload.title || "", updatedClients);
          // Tag client events as credits so calendar reconcile keeps session counts in sync
          const calPayload = calClient
            ? { ...payload, source: "credits", clientName: calClient.name, ...(calClient.email ? { clientEmail: calClient.email } : {}) }
            : payload;
          await authFetch("https://coachagent-server-production.up.railway.app/api/calendar/add", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(calPayload) });
        } else if (actionType === "add_session") {
          const idx = updatedClients.findIndex(c => c.id === payload.clientId);
          if (idx >= 0) {
            const { clientId, ...session } = payload;
            updatedClients[idx].sessions.push({ ...session, id: Date.now().toString(), reminded: false });
            const client = updatedClients[idx];
            if (client.email) {
              // Detect package type
              const pkgStr = client.package || '';
              const pkgMatch = pkgStr.match(/(\d+)\s*session/i);
              const pkgTotal = pkgMatch ? parseInt(pkgMatch[1]) : null;
              const isWeeklyMembership = pkgTotal !== null && /\bweek\b/i.test(pkgStr);
              const isBlockPackage = pkgTotal !== null && !isWeeklyMembership;
              const spkgUsed = client.sessions.length; // after push, includes this session
              const spkgRemaining = isBlockPackage ? pkgTotal! - spkgUsed : null;

              // Format date nicely
              const [y, m, d] = session.date.split('-').map(Number);
              const dateObj = new Date(y, m - 1, d);
              const niceDate = dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

              const packageRow = isBlockPackage
                ? `<tr><td style="padding:6px 0;color:#888;font-size:13px;">Package</td><td style="padding:6px 0;font-size:13px;font-weight:600;text-align:right;">${pkgStr}</td></tr>
                   <tr><td style="padding:6px 0;color:#888;font-size:13px;">Sessions remaining</td><td style="padding:6px 0;font-size:13px;font-weight:700;text-align:right;color:${spkgRemaining! <= 2 ? '#e74c3c' : '#27ae60'};">${spkgRemaining} of ${pkgTotal}</td></tr>`
                : pkgStr
                  ? `<tr><td style="padding:6px 0;color:#888;font-size:13px;">Membership</td><td style="padding:6px 0;font-size:13px;font-weight:600;text-align:right;">${pkgStr}</td></tr>`
                  : '';

              const html = `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f5f5f5;font-family:'Helvetica Neue',Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f5;padding:32px 0;">
  <tr><td align="center">
    <table width="520" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
      <tr><td style="background:#111;padding:24px 32px;">
        <div style="font-family:'Helvetica Neue',Arial,sans-serif;font-size:20px;font-weight:700;color:#B08A4A;letter-spacing:2px;text-transform:uppercase;">JOEY ROUHANA PT</div>
      </td></tr>
      <tr><td style="padding:32px;">
        <p style="margin:0 0 8px;font-size:15px;color:#333;">Hi ${client.name.split(' ')[0]},</p>
        <p style="margin:0 0 24px;font-size:15px;color:#333;">Your session has been confirmed. Here are the details:</p>
        <table width="100%" cellpadding="0" cellspacing="0" style="border-top:1px solid #eee;">
          <tr><td style="padding:12px 0;color:#888;font-size:13px;">Date</td><td style="padding:12px 0;font-size:13px;font-weight:600;text-align:right;">${niceDate}</td></tr>
          <tr style="border-top:1px solid #f0f0f0;"><td style="padding:12px 0;color:#888;font-size:13px;">Time</td><td style="padding:12px 0;font-size:13px;font-weight:600;text-align:right;">${session.time}</td></tr>
          <tr style="border-top:1px solid #f0f0f0;"><td style="padding:12px 0;color:#888;font-size:13px;">Type</td><td style="padding:12px 0;font-size:13px;font-weight:600;text-align:right;">${session.type}</td></tr>
          ${session.notes ? `<tr style="border-top:1px solid #f0f0f0;"><td style="padding:12px 0;color:#888;font-size:13px;">Notes</td><td style="padding:12px 0;font-size:13px;text-align:right;">${session.notes}</td></tr>` : ''}
          ${packageRow ? `<tr style="border-top:1px solid #f0f0f0;">${packageRow}</tr>` : ''}
        </table>
        <p style="margin:28px 0 0;font-size:14px;color:#555;">See you then!<br/><strong>Joey Rouhana</strong></p>
      </td></tr>
    </table>
  </td></tr>
</table></body></html>`;

              const plainText = `Hi ${client.name.split(' ')[0]},\n\nYour session is confirmed:\n\nDate: ${niceDate}\nTime: ${session.time}\nType: ${session.type}${session.notes ? `\nNotes: ${session.notes}` : ''}${isBlockPackage ? `\n\nSessions remaining: ${spkgRemaining} of ${pkgTotal}` : ''}\n\nSee you then!\nJoey Rouhana`;

              await authFetch(`https://coachagent-server-production.up.railway.app/api/send-email`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ to: client.email, subject: `Session Confirmed — ${niceDate}`, body: plainText, html }),
              });
            }
          }
        } else if (actionType === "log_workout") {
          try {
            const r = await authFetch(`${API}/api/clients/${payload.clientId}/workout-logs`, {
              method: "POST",
              body: JSON.stringify({ date: payload.date, dayName: payload.dayName || "Session", weekName: payload.weekName, sessionNote: payload.sessionNote, exercises: payload.exercises }),
            });
            const idx = updatedClients.findIndex(c => c.id === payload.clientId);
            const name = idx >= 0 ? updatedClients[idx].name : payload.clientId;
            if (r.ok) emailResults.push(`✅ Workout logged for ${name} (${payload.date})`);
            else emailResults.push(`⚠️ Failed to log workout for ${name}`);
          } catch (e: any) {
            emailResults.push(`⚠️ Workout log error: ${e.message}`);
          }
        } else if (actionType === "create_payment") {
          const idx = updatedClients.findIndex(c => c.id === payload.clientId);
          if (idx >= 0) {
            const client = updatedClients[idx];
            try {
              const res = await authFetch(`https://coachagent-server-production.up.railway.app/api/payments/create`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ amount: payload.amount, description: payload.description, clientId: client.id, clientName: client.name }),
              });
              const data = await res.json();
              if (data.paymentUrl && client.email) {
                await authFetch(`https://coachagent-server-production.up.railway.app/api/send-email`, {
                  method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    to: client.email,
                    subject: `Payment Request — ${payload.description}`,
                    body: `Hi ${client.name},\n\nHere is your payment link for: ${payload.description}\nAmount: €${payload.amount}\n\nPay here: ${data.paymentUrl}\n\nBest,\nJoey Rouhana`,
                  }),
                });
              }
              // Store the paymentUrl in the reply context so Claude can mention it
              (payload as any)._resolvedUrl = data.paymentUrl || "created";
            } catch (e) { console.error("Payment creation error", e); }
          }
        } else if (actionType === "record_payment") {
          const idx = updatedClients.findIndex(c => c.id === payload.clientId);
          if (idx >= 0) {
            if (!updatedClients[idx].payments) updatedClients[idx].payments = [];
            updatedClients[idx].payments.push({
              id: `manual-${Date.now()}`,
              amount: payload.amount,
              date: payload.date || new Date().toISOString().split('T')[0],
              description: payload.description || '',
              status: 'paid',
            });
          }
        }
      } catch (e: any) {
        console.error("Action error", e);
        emailResults.push(`⚠️ ${actionType || "Action"} FAILED and was NOT saved (${e.message}). Ask me to retry.`);
      }
    }
    // A truncated reply or an <ACTION> missing its closing tag means the save never executed —
    // surface it loudly instead of letting the model's own "Done!" text stand.
    const completeActionCount = (reply.match(/<ACTION>[\s\S]*?<\/ACTION>/g) || []).length;
    const openActionCount = (reply.match(/<ACTION>/g) || []).length;
    if (openActionCount > completeActionCount) {
      emailResults.push(`⚠️ The response was CUT OFF mid-save — nothing from the incomplete action was saved. Say "retry" and I'll regenerate it.`);
    } else if (replyMeta.stopReason === "max_tokens") {
      emailResults.push(`⚠️ The response hit the length limit and may be incomplete. Double-check before trusting it.`);
    }
    // Save each modified client individually to Railway
    for (const uc of updatedClients) {
      const original = clients.find((c: any) => c.id === uc.id);
      if (JSON.stringify(uc) !== JSON.stringify(original) && JSON.stringify(uc) !== savedSnapshots[uc.id]) {
        // saveClientToServer sends the latest known rev and records the new one
        await saveClientToServer(uc);
      }
    }
    onClientsChange(updatedClients);
    if (newlyCreatedClientId) setPinnedClientId(newlyCreatedClientId);
    // Strip closed tags, then strip any unclosed <ACTION> tag that runs to end of string
    const cleanReply = reply
      .replace(/<ACTION>[\s\S]*?<\/ACTION>/g, "")
      .replace(/<ACTION>[\s\S]*$/g, "")
      .trim();
    const emailNote = emailResults.length > 0 ? "\n\n" + emailResults.join("\n") : "";
    setChatMessages([...newMessages, { role: "assistant", content: cleanReply + emailNote }]);
    setLoading(false);
  };

  const pinnedClient = clients.find(c => c.id === pinnedClientId);
  const activeClients = clients.filter(c => c.status === "active").sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div style={{ display: "flex", flexDirection: "column" as const, height: "100%", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 20px", borderBottom: "1px solid #1a1a1a", background: C.surface, flexShrink: 0 }}>
        <span style={{ fontSize: 11, color: C.textDim, letterSpacing: 0.8, textTransform: "uppercase" as const, whiteSpace: "nowrap" as const, fontWeight: 600 }}>Client</span>
        <select
          value={pinnedClientId || ""}
          onChange={e => setPinnedClientId(e.target.value || null)}
          style={{ flex: 1, background: pinnedClientId ? C.accentBg : C.surfaceRaised, border: `1px solid ${pinnedClientId ? C.accentBorder : C.borderInput}`, borderRadius: 8, color: pinnedClientId ? C.accent : C.textMuted, fontSize: 13, fontWeight: pinnedClientId ? 600 : 400, padding: "7px 12px", fontFamily: "'Inter', sans-serif", outline: "none", cursor: "pointer" }}
        >
          <option value="">— Select a client —</option>
          {activeClients.map(c => <option key={c.id} value={c.id} style={{ color: C.textPrimary, background: C.surface }}>{c.name}</option>)}
        </select>
        {pinnedClientId && (
          <button onClick={() => setPinnedClientId(null)} title="Clear client" style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px", flexShrink: 0 }}>×</button>
        )}
      </div>
      <div style={{ flex: 1, overflowY: "auto" as const, padding: "24px 28px", display: "flex", flexDirection: "column" as const, gap: 12 }}>
        {chatMessages.length === 0 && (
          <div style={S.chatBubble(false)}>Hey coach! I am your AI assistant. I can help you manage clients, schedule sessions, send emails, and more. What would you like to do?</div>
        )}
        {chatMessages.map((m, i) => <div key={i} style={S.chatBubble(m.role === "user")}>{m.content}</div>)}
        {loading && <div style={{ ...S.chatBubble(false), color: C.textMuted }}>Thinking...</div>}
        <div ref={bottomRef} />
      </div>
      {attachedImage && (
        <div style={{ padding: "8px 28px 0", display: "flex", alignItems: "center", gap: 10 }}>
          {attachedImage.isPDF
            ? <div style={{ height: 48, width: 40, borderRadius: 6, border: `1px solid ${C.borderInput}`, background: C.surfaceRaised, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>📄</div>
            : <img src={attachedImage.preview} alt="attachment" style={{ height: 48, borderRadius: 6, border: `1px solid ${C.borderInput}`, objectFit: "cover" }} />
          }
          <span style={{ fontSize: 12, color: C.textMuted, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>
            {attachedImage.isPDF ? (attachedImage.fileName || "PDF attached") : "Calendar screenshot attached"}
          </span>
          <button onClick={() => setAttachedImage(null)} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 16, lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>
      )}
      <div style={{ padding: "16px 28px", borderTop: "1px solid #1a1a1a", display: "flex", gap: 10 }}>
        <input ref={fileInputRef} type="file" accept="image/*,.pdf" style={{ display: "none" }} onChange={handleImageAttach} />
        <button onClick={() => fileInputRef.current?.click()} title="Attach image or PDF (training schedule, diet plan)" style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "0 12px", fontSize: 16, flexShrink: 0 }}>📎</button>
        <input style={{ ...S.input, flex: 1 }} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Ask me to add a client, set a training plan, log InBody results..." />
        <Btn variant="ghost" onClick={() => { setChatMessages([]); setPinnedClientId(null); }} title="Clear chat">✕</Btn>
        <Btn variant="primary" onClick={send} disabled={loading}>Send</Btn>
      </div>
    </div>
  );
}


// ── Diet day row — hover-aware ────────────────────────────────────────────
function PaymentRow({ p }: { p: any }) {
  const [hovered, setHovered] = React.useState(false);
  const statusColor  = p.status === "paid" ? C.accent   : p.status === "failed" || p.status === "expired" ? C.danger : C.textSecondary;
  const statusBg     = p.status === "paid" ? C.accentBg : p.status === "failed" || p.status === "expired" ? C.dangerBg : "rgba(255,255,255,0.04)";
  const statusBorder = p.status === "paid" ? C.accentBorder : p.status === "failed" || p.status === "expired" ? C.dangerBorder : C.border;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: "flex", alignItems: "center", gap: SP.md, padding: `12px ${SP.md}px`, background: hovered ? C.surfaceMid : "transparent", transition: "background 0.12s" }}
    >
      {/* Left: description + date */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary, marginBottom: 3 }}>{p.description}</div>
        <div style={{ fontSize: 11, color: C.textDim }}>{p.date}</div>
      </div>
      {/* Right: amount + status badge + invoice anchor */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <div style={{ fontFamily: F_SERIF, fontSize: 24, lineHeight: 1, color: C.textPrimary, letterSpacing: 0.5, minWidth: 64, textAlign: "right" as const }}>
          €{p.amount}
        </div>
        {p.method === "cash" && (
          <div style={{ padding: "3px 8px", borderRadius: R.sm, background: C.surfaceMid, border: `1px solid ${C.border}`, fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textSecondary, whiteSpace: "nowrap" as const }}>
            Cash
          </div>
        )}
        <div style={{ padding: "3px 8px", borderRadius: R.sm, background: statusBg, border: `1px solid ${statusBorder}`, fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: statusColor, whiteSpace: "nowrap" as const }}>
          {p.status || "pending"}
        </div>
        <div style={{ width: 64, display: "flex", justifyContent: "flex-end" }}>
          {p.status === "paid" && p.id ? (
            <a
              href={`${API}/api/invoices/${p.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...dsBtn("ghost"), fontSize: 11, padding: "4px 10px", textDecoration: "none", color: C.textSecondary }}
            >
              Invoice
            </a>
          ) : (
            <div style={{ width: 64 }} />
          )}
        </div>
      </div>
    </div>
  );
}

function SessionRow({ session }: { session: { id: string; date: string; time: string; type: string; notes?: string; reminded?: boolean } }) {
  const [hovered, setHovered] = React.useState(false);
  const today = new Date().toISOString().split("T")[0];
  const isPast = session.date < today;
  const isToday = session.date === today;
  const [yyyy, mm, dd] = session.date.split("-");
  const monthShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(mm, 10) - 1];
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: SP.md,
        padding: `12px ${SP.md}px`,
        background: hovered ? C.surfaceMid : "transparent",
        transition: "background 0.12s",
        cursor: "default",
      }}
    >
      {/* Date badge */}
      <div style={{
        flexShrink: 0, width: 44, textAlign: "center" as const,
        background: isToday ? C.accentBg : C.surfaceRaised,
        border: `1px solid ${isToday ? C.accentBorder : C.border}`,
        borderRadius: R.md, padding: "6px 0",
      }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: isToday ? C.accent : C.textDim }}>{monthShort}</div>
        <div style={{ fontFamily: F_SERIF, fontSize: 22, lineHeight: 1, color: isToday ? C.accent : isPast ? C.textMuted : C.textPrimary }}>{dd}</div>
      </div>
      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: isPast ? C.textSecondary : C.textPrimary, marginBottom: 2 }}>{session.type}</div>
        {session.notes && <div style={{ fontSize: 11, color: C.textMuted, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{session.notes}</div>}
      </div>
      {/* Time + status */}
      <div style={{ flexShrink: 0, textAlign: "right" as const }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: isToday ? C.accent : isPast ? C.textDim : C.textSecondary }}>{session.time}</div>
        {isToday && <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.accent, marginTop: 2 }}>Today</div>}
        {isPast && <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textDim, marginTop: 2 }}>Past</div>}
        {!isPast && !isToday && <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textSecondary, marginTop: 2 }}>Upcoming</div>}
      </div>
    </div>
  );
}

// ═══ Diet Plan — structured client-facing renderer ═══════════════════════════
// Parses free-form coach/AI-written plans (markdown or plain text) into a calm
// layout: target summary cards, coach notes, week/day/meal accordions, and
// flexibility/planning groups. Weeks, days, meals and long reference sections
// are collapsed by default so the page opens compact. The neon accent is
// reserved for key target values and active states.

type DpMacros = { kcal?: string; protein?: string; carbs?: string; fat?: string };
type DpOption = { items: string[]; total?: DpMacros };
type DpMeal = { title: string; time?: string; options: DpOption[] };
type DpDay = { title: string; meals: DpMeal[]; loose: string[]; total?: DpMacros };
type DpWeek = { title: string; days: DpDay[] };
type DpBlock = { title: string; lines: string[] };
type DpSection =
  | { kind: "meta"; pairs: [string, string][] }
  | { kind: "targets"; cards: { label: string; value: string; sub?: string }[]; extras: [string, string][]; blocks: DpBlock[] }
  | { kind: "days"; weeks: DpWeek[] }
  | { kind: "note"; title: string; lines: string[] }
  | { kind: "group"; group: "flex" | "plan" | "other"; title: string; blocks: DpBlock[] };

const dpParseMacros = (s: string): DpMacros => {
  const grab = (before: RegExp, after: RegExp) => {
    const m1 = s.match(before); if (m1) return m1[1];
    const m2 = s.match(after);  if (m2) return m2[1];
    return undefined;
  };
  return {
    kcal:    grab(/([\d,]+(?:\s*[-–]\s*[\d,]+)?)\s*(?:k?cal|calories)/i, /calories:?\s*~?([\d,]+(?:\s*[-–]\s*[\d,]+)?)/i),
    protein: grab(/([\d]+(?:\s*[-–]\s*[\d]+)?)\s*g?\s*protein/i,        /protein:?\s*~?([\d]+(?:\s*[-–]\s*[\d]+)?)/i),
    carbs:   grab(/([\d]+(?:\s*[-–]\s*[\d]+)?)\s*g?\s*carb/i,           /carbs?:?\s*~?([\d]+(?:\s*[-–]\s*[\d]+)?)/i),
    fat:     grab(/([\d]+(?:\s*[-–]\s*[\d]+)?)\s*g?\s*fat/i,            /fats?:?\s*~?([\d]+(?:\s*[-–]\s*[\d]+)?)/i),
  };
};
const dpMacroChips = (m: DpMacros): string[] => [
  m.kcal ? `${m.kcal} kcal` : null,
  m.protein ? `${m.protein}g protein` : null,
  m.carbs ? `${m.carbs}g carbs` : null,
  m.fat ? `${m.fat}g fat` : null,
].filter(Boolean) as string[];

const dpTitleCase = (s: string) => s.toLowerCase().replace(/(^|[\s/-])\w/g, c => c.toUpperCase());

function dpParse(diet: string): { title: string | null; sections: DpSection[] } {
  const sections: DpSection[] = [];
  let title: string | null = null;
  let sec: DpSection | null = null;
  let week: DpWeek | null = null;
  let day: DpDay | null = null;
  let meal: DpMeal | null = null;
  let block: DpBlock | null = null;

  const clean = (s: string) => s.replace(/\*\*/g, "").replace(/^\s*[-–•]\s*/, "").trim();
  const classify = (h: string): DpSection => {
    const t = h.toLowerCase();
    if (/meal plan|weekly plan|week plan|daily meals|menu/.test(t)) return { kind: "days", weeks: [] };
    if (/target|macro|nutrition(al)? goal|calorie/.test(t)) return { kind: "targets", cards: [], extras: [], blocks: [] };
    if (/overview|client info|about you/.test(t)) return { kind: "meta", pairs: [] };
    if (/substitut|swap|grocery|shopping|meal prep|planning|foods to|monitor/.test(t)) return { kind: "group", group: "plan", title: h, blocks: [] };
    if (/treat|flexib|training day|rest day|restaurant|eating out|weekend|alcohol|guideline|adjust/.test(t)) return { kind: "group", group: "flex", title: h, blocks: [] };
    if (/note|tip|strateg|hydration|supplement|remember|important|approach|principle/.test(t)) return { kind: "note", title: h, lines: [] };
    return { kind: "group", group: "other", title: h, blocks: [] };
  };
  const pushSec = (s: DpSection) => { sections.push(s); sec = s; week = null; day = null; meal = null; block = null; };
  const ensureDays = (): Extract<DpSection, { kind: "days" }> => {
    if (!sec || sec.kind !== "days") pushSec({ kind: "days", weeks: [] });
    return sec as Extract<DpSection, { kind: "days" }>;
  };
  const ensureWeek = (title = ""): DpWeek => {
    const d = ensureDays();
    if (!week || (title && week.title !== title)) { week = { title, days: [] }; d.weeks.push(week); day = null; meal = null; }
    return week;
  };
  const startDay = (h: string) => { ensureWeek(week?.title || ""); day = { title: h, meals: [], loose: [] }; week!.days.push(day); meal = null; };
  const startMeal = (mtitle: string, time?: string) => {
    if (!day) startDay("");
    meal = { title: mtitle, time, options: [{ items: [] }] };
    day!.meals.push(meal);
  };
  const isDayName = (h: string) => /^(monday|tuesday|wednesday|thursday|friday|saturday|sunday|day\s*\d+)\b/i.test(h);
  const isWeekName = (h: string) => /^week\s*\d+/i.test(h.replace(/[:\s]+$/, ""));

  for (const raw of diet.split("\n")) {
    const line = raw.trim();
    if (!line || line === "---") continue;

    if (/^#\s/.test(line)) { if (!title) title = clean(line.replace(/^#\s*/, "")); continue; }

    // ## headings — but day/week names at this level belong to the meal plan
    if (/^##\s/.test(line)) {
      const h = clean(line.replace(/^##\s*/, "").replace(/:$/, ""));
      if (isWeekName(h)) { ensureWeek(dpTitleCase(h)); continue; }
      if (isDayName(h)) { startDay(h); continue; }
      pushSec(classify(h));
      continue;
    }
    if (/^#{3,4}\s/.test(line)) {
      const h = clean(line.replace(/^#{3,4}\s*/, "").replace(/:$/, ""));
      if (isWeekName(h)) { ensureWeek(dpTitleCase(h)); continue; }
      if (isDayName(h)) { startDay(h); continue; }
      if (sec?.kind === "days") {
        // "Meal total: ~575 kcal" written as a subheading → total for the previous meal
        if (/^(meal\s*)?total|^macros|^approx/i.test(h)) {
          if (meal) meal.options[meal.options.length - 1].total = dpParseMacros(h);
          continue;
        }
        // Non-day subheading inside the meal plan → it's a meal (### Breakfast)
        const timeM = h.match(/\(([^)]+)\)\s*$/);
        startMeal(timeM ? h.slice(0, h.indexOf("(")).trim() : h, timeM ? timeM[1] : undefined);
        continue;
      }
      if (sec?.kind === "targets") {
        if (/macro|goal|target/i.test(h) && !/timing|strategy|distribution/i.test(h)) block = null;
        else { block = { title: h, lines: [] }; (sec as any).blocks.push(block); }
      } else if (sec?.kind === "group") { block = { title: h, lines: [] }; sec.blocks.push(block); }
      else if (sec?.kind === "note") { sec.lines.push(h); }
      continue;
    }

    // Plain-text structure (no markdown headings):
    // "Daily Targets:" → targets section
    if (/^daily targets?:?\s*$/i.test(line)) { pushSec({ kind: "targets", cards: [], extras: [], blocks: [] }); continue; }
    // "MEAL 3 - LUNCH (1:00 PM):" → meal header
    const plainMeal = line.match(/^meal\s*\d+\s*[-–:]\s*(.+?)\s*(?:\(([^)]+)\))?\s*:?\s*$/i);
    if (plainMeal) { ensureDays(); startMeal(dpTitleCase(plainMeal[1].trim()), plainMeal[2]); continue; }
    // Bare meal-name line inside a day: "Breakfast (7:30 AM):" / "Mid-Morning Snack:"
    if (sec && sec.kind === "days" && day) {
      const mealName = line.match(/^((?:pre|post)[- ]?workout|breakfast|brunch|lunch|dinner|supper|(?:mid[- ]?)?(?:morning|afternoon|evening)(?:\s+snack)?|snack(?:\s*\d*)?)\s*(?:\(([^)]+)\))?\s*:\s*$/i);
      if (mealName) { startMeal(dpTitleCase(mealName[1].trim()), mealName[2]); continue; }
    }
    // "WEEK 2" / "MONDAY" as bare caps lines
    if (/^[A-Z][A-Z\s\d&/()-]{2,40}:?$/.test(line)) {
      const h = clean(line.replace(/:$/, ""));
      if (isWeekName(h)) { ensureWeek(dpTitleCase(h)); continue; }
      if (isDayName(h)) { startDay(dpTitleCase(h)); continue; }
      // "SUPPLEMENTS:" style caps heading → its own section
      if (line.endsWith(":")) { pushSec(classify(dpTitleCase(h))); continue; }
    }

    if (!sec) {
      // Preamble before any structure: first non-heading line becomes the title
      if (!title) { title = clean(line); continue; }
      pushSec({ kind: "group", group: "other", title: "", blocks: [] });
    }

    // Daily totals line (checked before bold meal headers — it is often bold)
    if (/^\*?\*?daily totals?\b/i.test(clean(line))) {
      if (sec.kind === "days" && day) day.total = dpParseMacros(line);
      continue;
    }

    // Bold whole line → meal header inside a days context
    const boldOnly = line.match(/^\*\*(.+?)\*\*:?$/);
    if (boldOnly && sec.kind === "days") {
      const h = boldOnly[1].replace(/:$/, "").trim();
      if (/^(meal\s*)?total|^macros|^approx/i.test(h)) {
        if (meal) meal.options[meal.options.length - 1].total = dpParseMacros(h);
        continue;
      }
      const timeM = h.match(/\(([^)]+)\)\s*$/);
      startMeal(timeM ? h.slice(0, h.indexOf("(")).trim() : h, timeM ? timeM[1] : undefined);
      continue;
    }

    // Macros/total line → total for the current meal option
    const noBullet = line.replace(/^[-–•]\s*/, "");
    const looksMacroTotal =
      (/^\*[^*].*\*$/.test(noBullet) && /cal|protein|carb|fat/i.test(noBullet)) ||
      /^\**\s*(macros|approx\.?(imate)? (meal )?total|meal total)/i.test(noBullet) ||
      (/^(protein|calories|kcal|carbs?|fats?)\s*:/i.test(noBullet) && noBullet.includes("|"));
    if (looksMacroTotal && sec.kind === "days" && meal) {
      meal.options[meal.options.length - 1].total = dpParseMacros(noBullet);
      continue;
    }

    // Alternative separators inside a meal: bare "OR" or "Option 2 / Alternative"
    if (meal && (/^\*?\*?\s*or\s*\*?\*?:?$/i.test(line) || /^(option|alternative)\s*\d/i.test(clean(line)))) {
      meal.options.push({ items: [] });
      continue;
    }

    let c = clean(line);
    if (!c) continue;

    // Inline alternative: "OR banana with peanut butter" → new option
    if (meal && /^or\s+/i.test(c)) {
      meal.options.push({ items: [c.replace(/^or\s+/i, "")] });
      continue;
    }

    if (sec.kind === "meta") {
      const kv = c.match(/^([^:]{2,30}):\s*(.+)$/);
      if (kv) sec.pairs.push([kv[1].trim(), kv[2].trim()]);
      continue;
    }
    if (sec.kind === "targets") {
      if (block) { block.lines.push(c); continue; }
      const kv = c.match(/^([^:]{2,30}):\s*(.+)$/);
      if (kv) {
        const label = kv[1].trim(); const valRaw = kv[2].trim();
        const core = { calories: "Daily calories", protein: "Protein", carb: "Carbs", fat: "Fat" };
        const coreKey = Object.keys(core).find(k => label.toLowerCase().includes(k));
        if (coreKey) {
          const parenM = valRaw.match(/\(([^)]+)\)/);
          const bare = valRaw.replace(/\([^)]*\)/, "").trim();
          const vm = bare.match(/^([\d,.\s–-]+g?)\s*(.*)$/);
          const value = vm && vm[1].trim() ? vm[1].trim() : bare;
          const tail = vm ? vm[2].trim() : "";
          sec.cards.push({ label: (core as any)[coreKey], value, sub: parenM ? parenM[1] : (tail || undefined) });
        } else {
          sec.extras.push([label, valRaw]);
        }
      }
      continue;
    }
    if (sec.kind === "days") {
      if (meal) meal.options[meal.options.length - 1].items.push(c);
      else if (day) day.loose.push(c);
      continue;
    }
    if (sec.kind === "note") { sec.lines.push(c); continue; }
    if (sec.kind === "group") {
      if (!block) { block = { title: "", lines: [] }; sec.blocks.push(block); }
      block.lines.push(c);
      continue;
    }
  }
  return { title, sections };
}

// Accessible accordion header — real button, visible focus ring, large target
function DpAccordionButton({ open, onClick, ariaLabel, children, style }: { open: boolean; onClick: () => void; ariaLabel: string; children: React.ReactNode; style?: React.CSSProperties }) {
  const T = React.useContext(ThemeCtx);
  const [hov, setHov] = React.useState(false);
  const [foc, setFoc] = React.useState(false);
  return (
    <button
      type="button" aria-expanded={open} aria-label={ariaLabel} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onFocus={() => setFoc(true)} onBlur={() => setFoc(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12, textAlign: "left",
        padding: "14px 18px", cursor: "pointer", border: "none", outline: "none",
        background: hov ? T.surfaceMid : "transparent", transition: "background 0.12s",
        boxShadow: foc ? `inset 0 0 0 2px ${T.accentBorder}` : "none",
        fontFamily: "inherit", color: T.textPrimary,
        ...style,
      }}
    >{children}</button>
  );
}
const dpCaret = (open: boolean, T: typeof C) => (
  <span aria-hidden="true" style={{ color: T.textDim, fontSize: 9, width: 10, flexShrink: 0 }}>{open ? "▼" : "▶"}</span>
);
const dpChip = (T: typeof C): React.CSSProperties => ({ fontSize: 11, fontWeight: 600, color: T.textSecondary, background: T.surfaceMid, border: `1px solid ${T.border}`, borderRadius: 6, padding: "3px 9px", fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" });
const dpGroupLabel = (label: string, T: typeof C) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "26px 0 10px" }}>
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", color: T.textDim, whiteSpace: "nowrap" }}>{label}</span>
    <div style={{ flex: 1, height: 1, background: T.borderSubtle }} />
  </div>
);

function DpMealCard({ meal, isLast }: { meal: DpMeal; isLast: boolean }) {
  const T = React.useContext(ThemeCtx);
  const [open, setOpen] = React.useState(false);
  // A lone totals line after the alternatives applies to the whole meal
  const headMacros = meal.options[0]?.total ?? meal.options[meal.options.length - 1]?.total;
  const headSummary = headMacros ? [headMacros.kcal ? `≈ ${headMacros.kcal} kcal` : null, headMacros.protein ? `${headMacros.protein}g protein` : null].filter(Boolean).join(" · ") : "";
  const realOptions = meal.options.filter(o => o.items.length > 0);
  return (
    <div style={{ borderBottom: isLast ? "none" : `1px solid ${T.borderSubtle}` }}>
      <DpAccordionButton open={open} onClick={() => setOpen(o => !o)} ariaLabel={`${meal.title}${meal.time ? ", " + meal.time : ""}${headSummary ? ", " + headSummary : ""}`}>
        {dpCaret(open, T)}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: T.textPrimary }}>{meal.title}</span>
          {meal.time && <span style={{ fontSize: 11.5, color: T.textMuted, marginLeft: 10 }}>{meal.time}</span>}
        </span>
        {headSummary && <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" }}>{headSummary}</span>}
      </DpAccordionButton>
      {open && (
        <div style={{ padding: "4px 18px 16px 40px", background: T.bg }}>
          {realOptions.length > 1 && (
            <div style={{ fontSize: 11.5, color: T.textMuted, margin: "6px 0 10px", fontStyle: "italic" }}>Choose one complete option — not all of them.</div>
          )}
          {realOptions.map((opt, oi) => (
            <div key={oi} style={oi > 0 ? { marginTop: 12, border: `1px solid ${T.border}`, borderRadius: R.md, padding: "10px 14px" } : { marginTop: 4 }}>
              {realOptions.length > 1 && (
                <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.8, textTransform: "uppercase", color: oi === 0 ? T.textSecondary : T.textDim, marginBottom: 6 }}>
                  {oi === 0 ? "Main option" : `Option ${oi + 1}`}
                </div>
              )}
              {opt.items.map((item, ii) => (
                <div key={ii} style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.7, padding: "2px 0" }}>{item}</div>
              ))}
              {opt.total && dpMacroChips(opt.total).length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                  <span style={{ fontSize: 10.5, color: T.textDim, marginRight: 2 }}>Approx. total</span>
                  {dpMacroChips(opt.total).map(chip => <span key={chip} style={dpChip(T)}>{chip}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DpMealList({ day }: { day: DpDay }) {
  const T = React.useContext(ThemeCtx);
  return (
    <>
      {day.loose.length > 0 && (
        <div style={{ padding: "10px 18px", borderBottom: `1px solid ${T.borderSubtle}` }}>
          {day.loose.map((l, i) => <div key={i} style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.7 }}>{l}</div>)}
        </div>
      )}
      {day.meals.map((meal, mi) => (
        <DpMealCard key={mi} meal={meal} isLast={mi === day.meals.length - 1 && !day.total} />
      ))}
      {day.total && dpMacroChips(day.total).length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", padding: "12px 18px", borderTop: `1px solid ${T.borderSubtle}`, background: T.surfaceRaised }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: T.textMuted, marginRight: 4 }}>Day total</span>
          {dpMacroChips(day.total).map(chip => <span key={chip} style={dpChip(T)}>{chip}</span>)}
        </div>
      )}
    </>
  );
}

function DpDayCard({ day }: { day: DpDay }) {
  const T = React.useContext(ThemeCtx);
  const [open, setOpen] = React.useState(false);
  const mealCount = day.meals.length;
  const meta = [mealCount ? `${mealCount} ${mealCount === 1 ? "meal" : "meals"}` : null, day.total?.kcal ? `≈ ${day.total.kcal} kcal` : null].filter(Boolean).join(" · ");
  const descM = day.title.match(/^([^(]+?)\s*\(([^)]+)\)\s*$/);
  const dayName = descM ? descM[1].trim() : day.title;
  const dayDesc = descM ? descM[2].trim() : "";
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: R.lg, overflow: "hidden" }}>
      <DpAccordionButton open={open} onClick={() => setOpen(o => !o)} ariaLabel={`${dayName}${dayDesc ? ", " + dayDesc : ""}${meta ? ", " + meta : ""}`} style={{ padding: "15px 18px", background: open ? T.surfaceRaised : "transparent" }}>
        {dpCaret(open, T)}
        <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontFamily: F_SANS, fontSize: 12, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase" as const, color: T.textPrimary }}>{dayName}</span>
          {dayDesc && <span style={{ fontSize: 11.5, color: T.textMuted }}>{dayDesc}</span>}
        </span>
        {meta && <span style={{ fontSize: 10.5, color: T.textDim, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" }}>{meta}</span>}
      </DpAccordionButton>
      {open && <div style={{ borderTop: `1px solid ${T.borderSubtle}` }}><DpMealList day={day} /></div>}
    </div>
  );
}

function DpWeekCard({ week }: { week: DpWeek }) {
  const T = React.useContext(ThemeCtx);
  const [open, setOpen] = React.useState(false);
  const meta = `${week.days.length} ${week.days.length === 1 ? "day" : "days"}`;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: R.lg, overflow: "hidden" }}>
      <DpAccordionButton open={open} onClick={() => setOpen(o => !o)} ariaLabel={`${week.title}, ${meta}`} style={{ padding: "16px 18px", background: open ? T.surfaceRaised : "transparent" }}>
        {dpCaret(open, T)}
        <span style={{ flex: 1, fontFamily: F_SERIF, fontSize: 18, letterSpacing: 0.4, color: T.textPrimary }}>{week.title}</span>
        <span style={{ fontSize: 10.5, color: T.textDim, fontFamily: "'JetBrains Mono', monospace" }}>{meta}</span>
      </DpAccordionButton>
      {open && (
        <div style={{ borderTop: `1px solid ${T.borderSubtle}`, padding: 10, display: "flex", flexDirection: "column", gap: 8, background: T.bg }}>
          {week.days.map((day, di) => <DpDayCard key={di} day={day} />)}
        </div>
      )}
    </div>
  );
}

// Note card — collapsible when long, so reference sections open compact
function DpNoteCard({ title, lines, startOpen }: { title?: string; lines: string[]; startOpen?: boolean }) {
  const T = React.useContext(ThemeCtx);
  // Meals stay visible — a client checking "what's for lunch" shouldn't need a
  // click; only long reference lists (grocery list, tracking) open compact
  const isMeal = !!title && /breakfast|lunch|dinner|snack|meal\b/i.test(title);
  const collapsible = lines.length > 4 && !!title && !isMeal;
  const [open, setOpen] = React.useState(startOpen ?? !collapsible);
  if (lines.length === 0) return null;
  const body = lines.map((l, i) => {
    const kv = l.match(/^([^:]{2,40}):\s*(.+)$/);
    return kv
      ? <div key={i} style={{ fontSize: 13, lineHeight: 1.7 }}><span style={{ color: T.textPrimary, fontWeight: 600 }}>{kv[1]}: </span><span style={{ color: T.textSecondary }}>{kv[2]}</span></div>
      : <div key={i} style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.7 }}>{l}</div>;
  });
  if (!collapsible) return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.indigo}`, borderRadius: R.md, padding: "14px 18px" }}>
      {title && <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1.8, textTransform: "uppercase", color: T.textMuted, marginBottom: 6 }}>{title}</div>}
      {body}
    </div>
  );
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.indigo}`, borderRadius: R.md, overflow: "hidden" }}>
      <DpAccordionButton open={open} onClick={() => setOpen(o => !o)} ariaLabel={`${title}, ${lines.length} items`} style={{ padding: "12px 18px" }}>
        {dpCaret(open, T)}
        <span style={{ flex: 1, fontSize: 10.5, fontWeight: 700, letterSpacing: 1.8, textTransform: "uppercase", color: T.textMuted }}>{title}</span>
        <span style={{ fontSize: 10.5, color: T.textDim, fontFamily: "'JetBrains Mono', monospace" }}>{lines.length} items</span>
      </DpAccordionButton>
      {open && <div style={{ padding: "2px 18px 14px 40px" }}>{body}</div>}
    </div>
  );
}

function DietPlanView({ diet }: { diet: string }) {
  const T = React.useContext(ThemeCtx);
  const { sections } = React.useMemo(() => dpParse(diet), [diet]);

  const meta    = sections.filter(s => s.kind === "meta") as Extract<DpSection, { kind: "meta" }>[];
  const targets = sections.filter(s => s.kind === "targets") as Extract<DpSection, { kind: "targets" }>[];
  const notes   = sections.filter(s => s.kind === "note") as Extract<DpSection, { kind: "note" }>[];
  const dayss   = sections.filter(s => s.kind === "days") as Extract<DpSection, { kind: "days" }>[];
  const flexG   = sections.filter(s => s.kind === "group" && s.group === "flex") as Extract<DpSection, { kind: "group" }>[];
  const planG   = sections.filter(s => s.kind === "group" && s.group === "plan") as Extract<DpSection, { kind: "group" }>[];
  const otherG  = sections.filter(s => s.kind === "group" && s.group === "other") as Extract<DpSection, { kind: "group" }>[];

  const metaPairs = metaFlat(meta);
  const allWeeks = dayss.flatMap(d => d.weeks);
  const namedWeeks = allWeeks.filter(w => w.title);
  const looseDays = allWeeks.filter(w => !w.title).flatMap(w => w.days);
  // A single untitled day whose meals stand alone (plain-text plans) → flat meal list
  const flatDay = looseDays.length === 1 && !looseDays[0].title ? looseDays[0] : null;

  const renderGroup = (label: string, groups: Extract<DpSection, { kind: "group" }>[]) => (
    groups.some(g => g.blocks.some(b => b.lines.length > 0)) && (
      <>
        {dpGroupLabel(label, T)}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {groups.map((g, gi) => (
            <React.Fragment key={gi}>
              {g.blocks.filter(b => b.lines.length > 0).map((b, bi) => <DpNoteCard key={bi} title={b.title || g.title} lines={b.lines} />)}
            </React.Fragment>
          ))}
        </div>
      </>
    )
  );

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", width: "100%" }}>
      {metaPairs.length > 0 && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
          {metaPairs.map(([k, v]) => (
            <div key={k} style={{ fontSize: 12, lineHeight: 1.5 }}>
              <span style={{ color: T.textDim }}>{k}: </span>
              <span style={{ color: T.textSecondary }}>{v}</span>
            </div>
          ))}
        </div>
      )}

      {targets.map((t, ti) => (
        <div key={ti} style={{ marginBottom: 16 }}>
          {t.cards.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: t.extras.length || t.blocks.length ? 10 : 0 }}>
              {t.cards.map(card => (
                <div key={card.label} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: R.lg, padding: "16px 16px 14px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.8, textTransform: "uppercase", color: T.textMuted, marginBottom: 8 }}>{card.label}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 19, fontWeight: 700, color: card.label === "Daily calories" ? T.accent : T.textPrimary, lineHeight: 1.2, marginBottom: card.sub ? 4 : 0 }}>{card.value}</div>
                  {card.sub && <div style={{ fontSize: 11, color: T.textDim }}>{card.sub}</div>}
                </div>
              ))}
            </div>
          )}
          {t.extras.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: t.blocks.length ? 10 : 0 }}>
              {t.extras.map(([k, v]) => <span key={k} style={dpChip(T)}>{k} {v}</span>)}
            </div>
          )}
          {t.blocks.map((b, bi) => <div key={bi} style={{ marginBottom: 8 }}><DpNoteCard title={b.title} lines={b.lines} /></div>)}
        </div>
      ))}

      {notes.map((n, ni) => <div key={ni} style={{ marginBottom: 10 }}><DpNoteCard title={n.title} lines={n.lines} /></div>)}

      {(namedWeeks.length > 0 || looseDays.length > 0) && (
        <>
          {dpGroupLabel("Daily meals", T)}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {flatDay ? (
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: R.lg, overflow: "hidden" }}>
                <DpMealList day={flatDay} />
              </div>
            ) : (
              <>
                {looseDays.map((day, di) => <DpDayCard key={`d${di}`} day={day} />)}
                {namedWeeks.map((week, wi) => <DpWeekCard key={`w${wi}`} week={week} />)}
              </>
            )}
          </div>
        </>
      )}

      {renderGroup("Flexibility", flexG)}
      {renderGroup("Planning", planG)}
      {otherG.some(g => g.blocks.some(b => b.lines.length > 0)) && (
        <div style={{ marginTop: 16 }}>{renderGroup("More", otherG)}</div>
      )}
    </div>
  );
}
function metaFlat(meta: Extract<DpSection, { kind: "meta" }>[]): [string, string][] {
  return meta.flatMap(m => m.pairs);
}

function SessionToughnessRating({ clientId, log, jwt, onUpdate }: {
  clientId: string; log: any; jwt: string; onUpdate: (updatedLog: any) => void;
}) {
  const T = React.useContext(ThemeCtx);
  const [rpe, setRpe] = React.useState<number | null>(log.sessionRPE ?? null);
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const patch = async (val: number) => {
    const prev = rpe;
    setRpe(val);
    setEditing(false);
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/clients/${clientId}/workout-logs/${log.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ sessionRPE: val }),
      });
      if (!r.ok) throw new Error();
      onUpdate({ ...log, sessionRPE: val });
    } catch {
      setRpe(prev);
    }
    setSaving(false);
  };

  return (
    <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 8, paddingTop: 12, paddingBottom: 2 }}>
      {!editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: T.textSecondary, textTransform: "uppercase" as const }}>Session Toughness</span>
          {rpe !== null ? (
            <span style={{ fontSize: 15, fontWeight: 700, color: T.accent, letterSpacing: 0.5 }}>
              {rpe}<span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400 }}>/10</span>
            </span>
          ) : (
            <span style={{ fontSize: 12, color: T.textMuted }}>Not rated</span>
          )}
          <button onClick={() => setEditing(true)}
            style={{ marginLeft: "auto", background: T.surfaceRaised, border: `1px solid ${T.border}`, borderRadius: 5, color: T.textSecondary, fontSize: 11, fontWeight: 600, cursor: "pointer", padding: "4px 10px", letterSpacing: 0.5 }}>
            {rpe !== null ? "Edit" : "Rate"}
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" as const, gap: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: T.textSecondary, textTransform: "uppercase" as const }}>How tough was this session?</span>
          <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" as const }}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} onClick={() => patch(n)} disabled={saving}
                style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${rpe === n ? T.accentBorder : T.border}`, background: rpe === n ? T.accentBg : T.surfaceRaised, color: rpe === n ? T.accent : T.textSecondary, fontSize: 13, fontWeight: rpe === n ? 700 : 500, cursor: saving ? "default" : "pointer", padding: 0 }}>
                {n}
              </button>
            ))}
            <button onClick={() => setEditing(false)}
              style={{ marginLeft: 4, background: "none", border: "none", color: T.textDim, fontSize: 14, cursor: "pointer", padding: "0 4px" }}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

function getCalendarWeek(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7)); // ISO: week starts Monday
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

// Auth gate for the client portal: /portal (login, forgot, reset) and legacy
// /client/<portalToken> links (first-visit password setup, then login redirect).
function PortalAuth({ legacyToken }: { legacyToken?: string }) {
  const [jwt, setJwt] = useState<string | null>(getPortalJwt());
  const resetToken = new URLSearchParams(window.location.search).get("reset");
  const [view, setView] = useState<"login" | "register" | "setup" | "forgot" | "forgotSent" | "reset" | "checking" | "invalid" | "ready">(
    resetToken ? "reset" : legacyToken ? "checking" : "login"
  );
  const [linkName, setLinkName] = useState("");
  const [linkNote, setLinkNote] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Legacy link: ask the server who this token belongs to and whether it needs setup.
  // A /client/<token> link must always land on THAT client — so if a portal session
  // for a *different* client is stored, drop it and honour the link instead of
  // silently showing the previously logged-in account.
  useEffect(() => {
    if (!legacyToken || resetToken) return;
    fetch(`${API}/api/clients/token/${legacyToken}`)
      .then(async r => {
        const data = await r.json().catch(() => ({} as any));
        const tokenClientId = data.id == null ? null : String(data.id);
        if (jwt) {
          // Same client already logged in (or id unknown from an older server) → keep the session.
          if (!tokenClientId || portalJwtClientId(jwt) === tokenClientId) { setView("ready"); return; }
          // Different client → the link wins: clear the stale session and continue below.
          clearPortalJwt(); setJwt(null);
        }
        if (r.ok && data.needsSetup) { setLinkName(data.name || ""); setView("setup"); }
        else if (r.status === 401 && data.requiresLogin) {
          setLinkName(data.name || "");
          setLinkNote("Your portal now has secure login. Sign in with your email and password.");
          setView("login");
        } else setView("invalid");
      })
      .catch(() => setView("invalid"));
  }, [legacyToken]);

  // Anyone logged out had an account, so login is always the right screen
  const logout = () => { clearPortalJwt(); setJwt(null); setView("login"); setPassword(""); setError(""); };
  const finishAuth = (t: string) => {
    setPortalJwt(t);
    // Drop the token/reset from the address bar — the session lives in localStorage now
    try { window.history.replaceState(null, "", "/portal"); } catch {}
    setJwt(t);
  };

  const doLogin = async () => {
    if (!email.trim() || !password) { setError("Enter your email and password."); return; }
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/api/portal/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim(), password }) });
      const data = await r.json().catch(() => ({} as any));
      if (r.ok && data.token) finishAuth(data.token);
      else setError(data.error || "Login failed. Please try again.");
    } catch { setError("Could not connect. Please try again."); }
    setBusy(false);
  };

  const doRegister = async () => {
    if (!name.trim() || !email.trim() || !password) { setError("Enter your name, email and password."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== password2) { setError("Passwords don't match."); return; }
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/api/portal/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password }),
      });
      const data = await r.json().catch(() => ({} as any));
      if (r.ok && data.token) finishAuth(data.token);
      else setError(data.error || "Could not create your profile. Please try again.");
    } catch { setError("Could not connect. Please try again."); }
    setBusy(false);
  };

  const doSetPassword = async () => {
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== password2) { setError("Passwords don't match."); return; }
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/api/portal/set-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: resetToken || legacyToken, password }) });
      const data = await r.json().catch(() => ({} as any));
      if (r.ok && data.token) finishAuth(data.token);
      else setError(data.error || "Could not set password. Please try again.");
    } catch { setError("Could not connect. Please try again."); }
    setBusy(false);
  };

  const doForgot = async () => {
    if (!email.trim()) { setError("Enter your email address."); return; }
    setBusy(true); setError("");
    try {
      await fetch(`${API}/api/portal/forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim() }) });
      setView("forgotSent");
    } catch { setError("Could not connect. Please try again."); }
    setBusy(false);
  };

  // Coach preview: short-lived client JWT in the URL, never stored — takes priority
  // over (and leaves untouched) any portal session in localStorage
  const previewJwt = new URLSearchParams(window.location.search).get("preview");
  if (previewJwt) return <ClientPortal jwt={previewJwt} onLogout={() => { window.close(); window.location.href = "/"; }} preview />;

  // While a /client/<token> link is still being verified (view "checking"), hold off
  // rendering the portal so we never flash the previously logged-in client first.
  if (jwt && view !== "checking") return <ClientPortal jwt={jwt} onLogout={logout} />;

  const inputStyle: React.CSSProperties = { width: "100%", background: IV.surface, border: `1px solid ${IV.borderInput}`, borderRadius: R.md, padding: "13px 14px", color: IV.textPrimary, fontSize: 15, outline: "none", boxSizing: "border-box" as const };
  const labelStyle: React.CSSProperties = { fontSize: 10, fontWeight: 600, color: IV.textMuted, letterSpacing: 2, textTransform: "uppercase" as const, marginBottom: 6 };
  const buttonStyle: React.CSSProperties = { width: "100%", background: IV.textPrimary, color: IV.surface, fontWeight: 600, fontSize: 14, letterSpacing: 1.5, border: "none", borderRadius: R.md, padding: "14px 20px", cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1, textTransform: "uppercase" as const, marginTop: 4 };
  const onEnter = (fn: () => void) => (e: React.KeyboardEvent) => { if (e.key === "Enter") fn(); };

  return (
    <div style={{ minHeight: "100vh", background: IV.bg, color: IV.textPrimary, fontFamily: "'Inter', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center" as const, marginBottom: 28 }}>
          <MetaLogo size={40} color={IV.gold} style={{ margin: "0 auto 14px" }} />
          <div style={{ fontFamily: F_SERIF, fontSize: 26, color: IV.textPrimary, letterSpacing: 2 }}>JOEY ROUHANA PT</div>
          <div style={{ fontSize: 10, fontWeight: 600, color: IV.textMuted, letterSpacing: 3, textTransform: "uppercase" as const, marginTop: 6 }}>Client Portal</div>
        </div>

        {view === "checking" && (
          <div style={{ textAlign: "center" as const, color: IV.accent, fontFamily: F_SERIF, fontSize: 20, letterSpacing: 2 }}>Loading…</div>
        )}

        {view === "invalid" && (
          <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: 14, padding: "28px 24px", textAlign: "center" as const }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Invalid or expired link</div>
            <div style={{ fontSize: 13, color: IV.textMuted, lineHeight: 1.6, marginBottom: 18 }}>This portal link isn't valid anymore. If you already have an account, log in below.</div>
            <button onClick={() => { setView("login"); setError(""); }} style={buttonStyle}>Go to login</button>
          </div>
        )}

        {(view === "setup" || view === "reset") && (
          <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: 14, padding: "28px 24px" }}>
            <div style={{ fontFamily: F_SERIF, fontSize: 22, letterSpacing: 0.4, marginBottom: 6 }}>
              {view === "setup" ? (linkName ? `Welcome, ${linkName.split(" ")[0]}` : "Secure your account") : "Choose a new password"}
            </div>
            <div style={{ fontSize: 13, color: IV.textMuted, lineHeight: 1.6, marginBottom: 20 }}>
              {view === "setup"
                ? "Create a password to secure your training portal. From now on you'll log in with your email and this password."
                : "Enter a new password for your portal account."}
            </div>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
              <div>
                <div style={labelStyle}>Password (min. 8 characters)</div>
                <div style={{ position: "relative" as const }}>
                  <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={onEnter(doSetPassword)} autoComplete="new-password" style={{ ...inputStyle, paddingRight: 64 }} />
                  <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? "Hide password" : "Show password"} style={{ position: "absolute" as const, right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: IV.indigo, fontSize: 13, cursor: "pointer", padding: "4px 6px" }}>{showPassword ? "Hide" : "Show"}</button>
                </div>
              </div>
              <div>
                <div style={labelStyle}>Repeat password</div>
                <input type={showPassword ? "text" : "password"} value={password2} onChange={e => setPassword2(e.target.value)} onKeyDown={onEnter(doSetPassword)} autoComplete="new-password" style={inputStyle} />
              </div>
              {error && <div style={{ color: IV.danger, fontSize: 13 }}>{error}</div>}
              <button onClick={doSetPassword} disabled={busy} style={buttonStyle}>{busy ? "Saving…" : view === "setup" ? "Create account" : "Save new password"}</button>
            </div>
          </div>
        )}

        {view === "register" && (
          <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: 14, padding: "28px 24px" }}>
            <div style={{ fontFamily: F_SERIF, fontSize: 22, letterSpacing: 0.4, marginBottom: 6 }}>Create your profile</div>
            <div style={{ fontSize: 13, color: IV.textMuted, lineHeight: 1.6, marginBottom: 20 }}>Set up your Metaphysique account now. You can choose a program whenever you are ready.</div>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
              <div>
                <div style={labelStyle}>Full name</div>
                <input value={name} onChange={e => setName(e.target.value)} autoComplete="name" placeholder="Your name" style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>Email</div>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" placeholder="your@email.com" style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>Password (min. 8 characters)</div>
                <div style={{ position: "relative" as const }}>
                  <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" style={{ ...inputStyle, paddingRight: 64 }} />
                  <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? "Hide password" : "Show password"} style={{ position: "absolute" as const, right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: IV.indigo, fontSize: 13, cursor: "pointer", padding: "4px 6px" }}>{showPassword ? "Hide" : "Show"}</button>
                </div>
              </div>
              <div>
                <div style={labelStyle}>Repeat password</div>
                <input type={showPassword ? "text" : "password"} value={password2} onChange={e => setPassword2(e.target.value)} onKeyDown={onEnter(doRegister)} autoComplete="new-password" style={inputStyle} />
              </div>
              {error && <div style={{ color: IV.danger, fontSize: 13 }}>{error}</div>}
              <button onClick={doRegister} disabled={busy} style={buttonStyle}>{busy ? "Creating profile..." : "Create profile"}</button>
              <button onClick={() => { setView("login"); setError(""); }} style={{ background: "none", border: "none", color: IV.indigo, fontSize: 13, cursor: "pointer", padding: 0 }}>Already have an account? Log in</button>
            </div>
          </div>
        )}

        {view === "login" && (
          <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: 14, padding: "28px 24px" }}>
            <div style={{ fontFamily: F_SERIF, fontSize: 22, letterSpacing: 0.4, marginBottom: 6 }}>
              {linkName ? `Welcome back, ${linkName.split(" ")[0]}` : "Log in"}
            </div>
            {linkNote && <div style={{ fontSize: 13, color: IV.textMuted, lineHeight: 1.6, marginBottom: 14 }}>{linkNote}</div>}
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 14, marginTop: linkNote ? 0 : 14 }}>
              <div>
                <div style={labelStyle}>Email</div>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={onEnter(doLogin)} autoComplete="email" placeholder="your@email.com" style={inputStyle} />
              </div>
              <div>
                <div style={labelStyle}>Password</div>
                <div style={{ position: "relative" as const }}>
                  <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={onEnter(doLogin)} autoComplete="current-password" style={{ ...inputStyle, paddingRight: 64 }} />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    style={{ position: "absolute" as const, right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: IV.indigo, fontSize: 13, cursor: "pointer", padding: "4px 6px" }}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              {error && <div style={{ color: IV.danger, fontSize: 13 }}>{error}</div>}
              <button onClick={doLogin} disabled={busy} style={buttonStyle}>{busy ? "Logging in…" : "Log in"}</button>
              <button onClick={() => { setView("forgot"); setError(""); }} style={{ background: "none", border: "none", color: IV.indigo, fontSize: 13, cursor: "pointer", padding: 0 }}>Forgot password?</button>
              <button onClick={() => { setView("register"); setError(""); setPassword(""); setPassword2(""); }} style={{ background: "none", border: "none", color: IV.indigo, fontSize: 13, cursor: "pointer", padding: 0 }}>New here? Create your profile</button>
            </div>
          </div>
        )}

        {view === "forgot" && (
          <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: 14, padding: "28px 24px" }}>
            <div style={{ fontFamily: F_SERIF, fontSize: 22, letterSpacing: 0.4, marginBottom: 6 }}>Reset password</div>
            <div style={{ fontSize: 13, color: IV.textMuted, lineHeight: 1.6, marginBottom: 20 }}>Enter your email and we'll send you a link to reset your password.</div>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
              <div>
                <div style={labelStyle}>Email</div>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={onEnter(doForgot)} autoComplete="email" placeholder="your@email.com" style={inputStyle} />
              </div>
              {error && <div style={{ color: IV.danger, fontSize: 13 }}>{error}</div>}
              <button onClick={doForgot} disabled={busy} style={buttonStyle}>{busy ? "Sending…" : "Send reset link"}</button>
              <button onClick={() => { setView("login"); setError(""); }} style={{ background: "none", border: "none", color: IV.indigo, fontSize: 13, cursor: "pointer", padding: 0 }}>← Back to login</button>
            </div>
          </div>
        )}

        {view === "forgotSent" && (
          <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: 14, padding: "28px 24px", textAlign: "center" as const }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>📬</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Check your email</div>
            <div style={{ fontSize: 13, color: IV.textMuted, lineHeight: 1.6, marginBottom: 18 }}>If an account exists for {email.trim() || "that address"}, a password link is on its way. It works for 1 hour.</div>
            <button onClick={() => { setView("login"); setError(""); }} style={buttonStyle}>Back to login</button>
          </div>
        )}
      </div>
    </div>
  );
}

class PortalErrorBoundary extends React.Component<{ children: React.ReactNode }, { crashed: boolean }> {
  state = { crashed: false };
  static getDerivedStateFromError() { return { crashed: true }; }
  render() {
    if (this.state.crashed) return (
      <div style={{ minHeight: "100vh", background: IV.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" as const, gap: 12 }}>
        <MetaLogo size={36} color={IV.gold} />
        <div style={{ fontFamily: F_SERIF, fontSize: 22, color: IV.textPrimary, letterSpacing: 2 }}>JOEY ROUHANA PT</div>
        <div style={{ color: IV.textSecondary, fontSize: 14 }}>Something went wrong. Please refresh the page.</div>
        <button onClick={() => window.location.reload()} style={{ marginTop: 8, background: IV.textPrimary, border: "none", borderRadius: R.md, color: IV.surface, fontWeight: 600, fontSize: 13, padding: "8px 20px", cursor: "pointer" }}>Refresh</button>
      </div>
    );
    return this.props.children;
  }
}

// Icons for the mobile bottom navigation bar (lucide-style stroke SVGs).
function PortalTabIcon({ tabKey, size = 22, color = "currentColor" }: { tabKey: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (tabKey) {
    case "training": // dumbbell
      return (<svg {...p}><path d="M6.5 6.5 17.5 17.5"/><path d="m21 21-1-1"/><path d="m3 3 1 1"/><path d="m18 22 4-4"/><path d="m2 6 4-4"/><path d="m3 10 7-7"/><path d="m14 21 7-7"/></svg>);
    case "diet": // apple / nutrition
      return (<svg {...p}><path d="M12 20.94c1.5 0 2.75-.42 3.9-1.28.85-.62 1.75-1.68 2.35-3.16.63-1.56.75-3.32.34-5.02-.4-1.66-1.32-2.98-2.55-3.63-1.03-.55-2.2-.58-3.14-.13-.6.29-1.2.29-1.8 0-.94-.45-2.11-.42-3.14.13-1.23.65-2.15 1.97-2.55 3.63-.41 1.7-.29 3.46.34 5.02.6 1.48 1.5 2.54 2.35 3.16 1.15.86 2.4 1.28 3.9 1.28Z"/><path d="M12 7c0-1.5.5-3 2-4"/></svg>);
    case "inbody": // body composition / activity
      return (<svg {...p}><path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"/></svg>);
    case "message": // chat bubble
      return (<svg {...p}><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>);
    case "invoices": // receipt
      return (<svg {...p}><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M8 7h8"/><path d="M8 11h8"/><path d="M8 15h5"/></svg>);
    case "shop": // shopping bag
      return (<svg {...p}><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>);
    case "more": // ellipsis
      return (<svg {...p}><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>);
    case "logout": // log out
      return (<svg {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>);
    default:
      return (<svg {...p}><circle cx="12" cy="12" r="9"/></svg>);
  }
}

// Chrome fires beforeinstallprompt early (often on the login screen), so catch it at module
// load and hand it to the banner once the portal mounts.
let portalInstallPrompt: any = null;
const portalInstallListeners = new Set<() => void>();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e: any) => {
    e.preventDefault();
    portalInstallPrompt = e;
    portalInstallListeners.forEach(fn => fn());
  });
  window.addEventListener("appinstalled", () => {
    portalInstallPrompt = null;
    try { localStorage.setItem("portal-install-dismissed", "1"); } catch {}
    portalInstallListeners.forEach(fn => fn());
  });
}

// "Add Metaphysique to your home screen" strip for phones. Android/Chrome gets a real Install
// button; iPhone (no install API) gets Share → Add to Home Screen instructions.
function PortalInstallBanner() {
  const [, rerender] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("portal-install-dismissed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    const fn = () => rerender(n => n + 1);
    portalInstallListeners.add(fn);
    return () => { portalInstallListeners.delete(fn); };
  }, []);
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isPhone = isIOS || /Android/.test(ua);
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || (navigator as any).standalone === true;
  if (!isPhone || standalone || dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem("portal-install-dismissed", "1"); } catch {}
    setDismissed(true);
  };
  const install = async () => {
    const p = portalInstallPrompt;
    if (!p) return;
    p.prompt();
    const choice = await p.userChoice.catch(() => null);
    portalInstallPrompt = null;
    if (choice?.outcome === "accepted") dismiss(); else rerender(n => n + 1);
  };
  const hint = isIOS
    ? <>Tap <b>Share</b> (square with ↑ arrow), then <b>Add to Home Screen</b>.</>
    : /SamsungBrowser/.test(ua)
      ? <>Tap the <b>⋮</b> menu, then <b>Add page to</b> → <b>Home screen</b>.</>
      : <>Tap the <b>⋮</b> menu, then <b>Add to home screen</b>.</>;

  return (
    <div style={{ background: IV.indigoBg, borderBottom: `1px solid ${IV.indigoBorder}`, padding: "9px 14px", display: "flex", alignItems: "center", gap: 10 }}>
      <img src="/portal-icon-192.png" alt="" aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: IV.indigo }}>Get the Metaphysique app</div>
        <div style={{ fontSize: 11, color: IV.textMuted, marginTop: 1, lineHeight: 1.4 }}>
          {portalInstallPrompt ? "Open your portal straight from your home screen." : hint}
        </div>
      </div>
      {portalInstallPrompt && (
        <button onClick={install}
          style={{ fontFamily: F_SANS, fontSize: 11, fontWeight: 600, letterSpacing: 1.5, color: IV.surface, background: IV.indigoDeep, border: "none", padding: "6px 12px", borderRadius: R.sm, cursor: "pointer", whiteSpace: "nowrap" as const }}>
          INSTALL
        </button>
      )}
      <button onClick={dismiss} aria-label="Dismiss"
        style={{ background: "none", border: "none", color: IV.textMuted, fontSize: 18, lineHeight: 1, padding: "2px 4px", cursor: "pointer" }}>×</button>
    </div>
  );
}

function ClientPortal({ jwt, onLogout, preview }: { jwt: string; onLogout: () => void; preview?: boolean }) {
  const [client, setClient] = useState<Client | null | undefined>(undefined);
  const authHeader = { Authorization: `Bearer ${jwt}` };
  const [planPdfBusy, setPlanPdfBusy] = useState(false);
  const downloadPlanPdf = async () => {
    if (!client) return;
    setPlanPdfBusy(true);
    try {
      const res = await fetch(`${API}/api/clients/${client.id}/program-pdf`, { headers: authHeader });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.error || "Could not prepare your PDF — please try again.");
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `Metaphysique_Program_${(client.name || "Client").replace(/[^\w]+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Could not prepare your PDF — please try again."); }
    setPlanPdfBusy(false);
  };
  const [tab, setTab] = useState<"training" | "diet" | "inbody" | "message" | "invoices" | "shop">("training");
  const [moreOpen, setMoreOpen] = useState(false); // mobile "More" bottom sheet
  const [expandedDay, setExpandedDay] = useState<number | null>(0);
  const [msgText, setMsgText] = useState("");
  const [msgSent, setMsgSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [collapsedPortalWeeks, setCollapsedPortalWeeks] = useState<Set<number>>(new Set([0,1,2,3,4,5,6,7,8,9]));
  const [portalNotesOpen, setPortalNotesOpen] = useState(false);
  const togglePortalWeek = (wi: number) => setCollapsedPortalWeeks(prev => { const s = new Set(prev); s.has(wi) ? s.delete(wi) : s.add(wi); return s; });

  // ── Workout logging state ──
  const [loggingDay, setLoggingDay] = useState<number | null>(null);
  const [logSets, setLogSets] = useState<{[ex: string]: {weight: string; reps: string}[]}>({});
  const [sessionNote, setSessionNote] = useState('');
  const [savingLog, setSavingLog] = useState(false);
  const [logSaved, setLogSaved] = useState(false);
  const [savedGdi, setSavedGdi] = useState<number | null>(null);
  const [workoutLogs, setWorkoutLogs] = useState<any[]>([]);
  const [prs, setPrs] = useState<{[ex: string]: {weight: number; reps: number; date: string; estimated1RM: number}}>({});
  const [chartExercise, setChartExercise] = useState<string | null>(null);

  // ── Weekly check-in state ──
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkInWeekStart, setCheckInWeekStart] = useState("");
  const [checkInStep, setCheckInStep] = useState(0);
  const [checkInDone, setCheckInDone] = useState(false);
  const [checkInSaving, setCheckInSaving] = useState(false);
  const [checkInAnswers, setCheckInAnswers] = useState({
    overallFeel: 0,
    energyRecovery: 0,
    sessionsCompleted: "" as "" | "all" | "most" | "some" | "none",
    hardOrEasy: "",
    notesForCoach: "",
  });

  // Returns the ISO date of the Monday of the current week
  const getWeekStart = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1 - day);
    d.setDate(d.getDate() + diff);
    return d.toISOString().split("T")[0];
  };

  // Returns Monday of the previous calendar week
  const getPrevWeekStart = () => {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1) - 7);
    return d.toISOString().split("T")[0];
  };

  // On Sunday the current week is "over"; Mon-Sat we ask about the previous week
  const getWeekToCheckIn = () => new Date().getDay() === 0 ? getWeekStart() : getPrevWeekStart();

  // True if any workout log falls within the given week (identified by its Monday date)
  const weekHasLogs = (weekStart: string) => workoutLogs.some(log => {
    if (!log.date) return false;
    const d = new Date(log.date + "T12:00:00");
    const dy = d.getDay();
    d.setDate(d.getDate() - (dy === 0 ? 6 : dy - 1));
    return d.toISOString().split("T")[0] === weekStart;
  });

  // Dismissing the modal silences it for the rest of the week (per device)
  const checkInDismissKey = (weekStart: string) => `checkin-dismissed-${client?.id}-${weekStart}`;
  const wasCheckInDismissed = (weekStart: string) => {
    try { return !!localStorage.getItem(checkInDismissKey(weekStart)); } catch { return false; }
  };
  const dismissCheckIn = () => {
    try { localStorage.setItem(checkInDismissKey(checkInWeekStart || getWeekToCheckIn()), "1"); } catch {}
    setCheckInOpen(false);
  };

  // Show check-in modal only once the week is over and the client has logged training that week
  useEffect(() => {
    if (!client) return;
    const weekToAsk = getWeekToCheckIn();
    const alreadyDone = (client.weeklyCheckIns || []).some(c => c.weekStart === weekToAsk);
    if (alreadyDone) return;
    if (wasCheckInDismissed(weekToAsk)) return;
    if (!weekHasLogs(weekToAsk)) return;
    const t = setTimeout(() => { setCheckInWeekStart(weekToAsk); setCheckInOpen(true); }, 1500);
    return () => clearTimeout(t);
  }, [client, workoutLogs]);

  const submitCheckIn = async () => {
    if (!client) return;
    setCheckInSaving(true);
    try {
      await fetch(`${API}/api/clients/${client.id}/checkins`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ ...checkInAnswers, weekStart: checkInWeekStart || getWeekToCheckIn() }),
      });
      setCheckInDone(true);
      setTimeout(() => { setCheckInOpen(false); setCheckInDone(false); setCheckInStep(0); }, 2000);
    } catch { /* silent */ }
    setCheckInSaving(false);
  };

  // ── Shop state ──
  const [shopModal, setShopModal] = useState<{ title: string; price: string; amount: string; description: string; recurring?: boolean } | null>(null);
  const [shopForm, setShopForm] = useState({ name: "", email: "", phone: "", termsAgreed: false });
  const [shopLoading, setShopLoading] = useState(false);
  const [shopError, setShopError] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);

  const openShopModal = (product: any) => { setShopModal(product); setShopForm({ name: "", email: "", phone: "", termsAgreed: false }); setShopError(""); };

  const handleShopCheckout = async () => {
    if (!shopForm.name.trim() || !shopForm.email.trim()) { setShopError("Name and email are required."); return; }
    setShopLoading(true); setShopError("");
    const endpoint = shopModal!.recurring ? `${API}/api/shop/membership-checkout` : `${API}/api/shop/checkout`;
    try {
      const res = await fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: shopForm.name, email: shopForm.email, phone: shopForm.phone, amount: shopModal!.amount, description: shopModal!.description }) });
      const data = await res.json();
      if (data.checkoutUrl) { window.location.href = data.checkoutUrl; }
      else { setShopError(data.error || "Something went wrong. Please try again."); setShopLoading(false); }
    } catch { setShopError("Could not connect. Please try again."); setShopLoading(false); }
  };

  useEffect(() => {
    fetch(`${API}/api/portal/me`, { headers: authHeader })
      .then(r => {
        if (r.status === 401) { onLogout(); return null; } // expired session → back to login
        return r.ok ? r.json() : null;
      })
      .then(data => setClient(data))
      .catch(() => setClient(null));
  }, [jwt]);

  useEffect(() => {
    if (!client) return;
    fetch(`${API}/api/clients/${client.id}/workout-logs`, { headers: authHeader }).then(r => r.ok ? r.json() : Promise.reject()).then(data => { if (Array.isArray(data)) setWorkoutLogs(data); }).catch(() => {});
    fetch(`${API}/api/clients/${client.id}/prs`, { headers: authHeader }).then(r => r.ok ? r.json() : Promise.reject()).then(data => { if (data && typeof data === 'object' && !Array.isArray(data)) setPrs(data); }).catch(() => {});
  }, [client]);

  // ── Client InBody upload: file → server reads it → client reviews numbers → save ──
  const blankInBody = () => ({ date: new Date().toISOString().split("T")[0], weight: "", muscleMass: "", bodyFat: "", bmi: "", visceralFat: "" });
  const [inbodyDraft, setInbodyDraft] = useState<Record<string, string> | null>(null);
  const [inbodyBusy, setInbodyBusy] = useState<"" | "reading" | "saving">("");
  const [inbodyError, setInbodyError] = useState("");
  const [inbodySaved, setInbodySaved] = useState(false);
  // Photos are shrunk to a ~2000px JPEG so phone pictures stay under the upload limit
  const fileToUpload = (file: File) => new Promise<{ data: string; mediaType: string }>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (file.type === "application/pdf") return resolve({ data: dataUrl.split(",")[1], mediaType: "application/pdf" });
      const img = new Image();
      img.onerror = () => reject(new Error("image failed"));
      img.onload = () => {
        const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale); canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ data: canvas.toDataURL("image/jpeg", 0.88).split(",")[1], mediaType: "image/jpeg" });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
  const readInBodyFile = async (file: File) => {
    if (!client) return;
    if (file.type === "application/pdf" && file.size > 7 * 1024 * 1024) { setInbodyError("That PDF is too large (max 7 MB)."); return; }
    setInbodyError(""); setInbodySaved(false); setInbodyBusy("reading");
    try {
      const payload = await fileToUpload(file);
      const res = await fetch(`${API}/api/clients/${client.id}/inbody/extract`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeader }, body: JSON.stringify(payload) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setInbodyError(d.error || "Couldn't read that file."); setInbodyDraft(blankInBody()); }
      else {
        const v = (x: any) => (x || x === 0) && Number(x) !== 0 ? String(x) : "";
        setInbodyDraft({ date: /^\d{4}-\d{2}-\d{2}$/.test(d.date || "") ? d.date : blankInBody().date, weight: v(d.weight), muscleMass: v(d.muscleMass), bodyFat: v(d.bodyFat), bmi: v(d.bmi), visceralFat: v(d.visceralFat) });
      }
    } catch { setInbodyError("Couldn't read that file. Try a clearer photo, or enter the numbers by hand."); setInbodyDraft(blankInBody()); }
    setInbodyBusy("");
  };
  const saveInBody = async () => {
    if (!client || !inbodyDraft) return;
    setInbodyError(""); setInbodyBusy("saving");
    try {
      const res = await fetch(`${API}/api/clients/${client.id}/inbody`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeader }, body: JSON.stringify(inbodyDraft) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) setInbodyError(d.error || "Could not save your scan.");
      else { setClient({ ...client, inbody: d.inbody }); setInbodyDraft(null); setInbodySaved(true); }
    } catch { setInbodyError("Could not save your scan. Please try again."); }
    setInbodyBusy("");
  };

  const [openLogExercises, setOpenLogExercises] = useState<Set<string>>(new Set());

  // ── Workout draft persistence: mobile browsers kill background tabs mid-workout ──
  const workoutDraftKey = () => `workout-draft-${client?.id}-${new Date().toISOString().split('T')[0]}`;
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    if (!client || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const todayKey = workoutDraftKey();
      const prefix = `workout-draft-${client.id}-`;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix) && k !== todayKey) localStorage.removeItem(k); // stale drafts from earlier days
      }
      const raw = localStorage.getItem(todayKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft && typeof draft === 'object' && draft.logSets && typeof draft.logSets === 'object') {
        setLogSets(draft.logSets);
        setSessionNote(typeof draft.sessionNote === 'string' ? draft.sessionNote : '');
        setLoggingDay(typeof draft.loggingDay === 'number' ? draft.loggingDay : null);
        setOpenLogExercises(new Set(Array.isArray(draft.openLogExercises) ? draft.openLogExercises : []));
        if (typeof draft.expandedDay === 'number') setExpandedDay(draft.expandedDay);
      }
    } catch { /* corrupt draft or storage unavailable — start fresh */ }
  }, [client]);

  useEffect(() => {
    if (!client || !draftRestoredRef.current) return;
    const t = setTimeout(() => {
      try {
        const hasContent = Object.values(logSets).some(sets => sets.some(s => s.weight || s.reps)) || sessionNote.trim() !== '';
        if (hasContent) {
          localStorage.setItem(workoutDraftKey(), JSON.stringify({
            loggingDay, logSets, sessionNote,
            openLogExercises: Array.from(openLogExercises),
            expandedDay,
          }));
        } else {
          localStorage.removeItem(workoutDraftKey());
        }
      } catch { /* storage full/unavailable — logging still works without a draft */ }
    }, 400);
    return () => clearTimeout(t);
  }, [client, loggingDay, logSets, sessionNote, openLogExercises, expandedDay]);

  const isNoteMode = (ex: any) =>
    /carry|sled|prowler|farmer|sprint|jog|march|walk(?:ing)?|run(?:ning)?|ski erg|rowing machine|bike|cycling|treadmill|stair|bound|jump rope|plank|hollow|deadbug|crawl/i.test(ex.name) ||
    /\d+\s*(sec|min|m\b|km|ft|yard|meter)/i.test(ex.reps);

  const toggleExerciseLog = (ex: any, gdi: number, ei: number) => {
    const key = `${gdi}-${ei}`;
    setOpenLogExercises(prev => {
      const s = new Set(prev);
      if (s.has(key)) { s.delete(key); return s; }
      s.add(key);
      if (!logSets[key] || logSets[key].length === 0) {
        const numSets = isNoteMode(ex) ? 1 : (parseInt(ex.sets) || 3);
        const currentWeek = getCalendarWeek();
        const lastLog = [...workoutLogs].reverse().find(log =>
          log.exercises?.some((e: any) => e.name === ex.name) &&
          log.calendarWeek === currentWeek
        );
        const lastSets = lastLog?.exercises?.find((e: any) => e.name === ex.name)?.sets || [];
        const prefilled = Array.from({ length: numSets }, (_, i) => ({
          weight: lastSets[i]?.weight || '',
          reps: lastSets[i]?.reps || '',
        }));
        setLogSets(p => ({ ...p, [key]: prefilled }));
      }
      setLoggingDay(gdi);
      setLogSaved(false);
      return s;
    });
  };

  const startLogging = (day: any, gdi: number) => {
    const initial: {[ex: string]: {weight: string; reps: string}[]} = {};
    for (const ex of day.exercises) {
      const numSets = parseInt(ex.sets) || 3;
      initial[ex.name] = Array.from({length: numSets}, () => ({ weight: '', reps: '' }));
    }
    setLogSets(initial);
    setLoggingDay(gdi);
    setLogSaved(false);
    setSessionNote('');
  };

  const addSet = (exName: string) => setLogSets(prev => ({ ...prev, [exName]: [...(prev[exName] || []), { weight: '', reps: '' }] }));
  const removeSet = (exName: string, idx: number) => setLogSets(prev => { const arr = [...(prev[exName] || [])]; arr.splice(idx, 1); return { ...prev, [exName]: arr }; });
  const updateSet = (exName: string, idx: number, field: 'weight' | 'reps', val: string) => setLogSets(prev => { const arr = [...(prev[exName] || [])]; arr[idx] = { ...arr[idx], [field]: val }; return { ...prev, [exName]: arr }; });

  const saveLog = async (day: any, weekName: string, gdi: number) => {
    if (!client) return;
    setSavingLog(true);
    const exercises = day.exercises
      .map((ex: any, ei: number) => ({ name: ex.name, sets: (logSets[`${loggingDay}-${ei}`] || []).filter((s: any) => s.weight || s.reps) }))
      .filter((ex: any) => ex.sets.length > 0);
    if (exercises.length === 0) { setSavingLog(false); return; }
    const draftBackup = { logSets, sessionNote, openLogExercises }; // restore if save fails
    const optimisticLog = { id: Date.now().toString(), date: new Date().toISOString().split('T')[0], dayName: day.day, weekName, calendarWeek: getCalendarWeek(), exercises, sessionNote };
    setWorkoutLogs(prev => [...prev, optimisticLog]);
    setLogSaved(true);
    setLoggingDay(null);
    setLogSets({});
    setOpenLogExercises(new Set());
    setSessionNote('');
    setSavedGdi(gdi);
    setTimeout(() => setSavedGdi(null), 3000);
    try {
      const saveRes = await fetch(`${API}/api/clients/${client.id}/workout-logs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ date: optimisticLog.date, dayName: day.day, weekName, calendarWeek: getCalendarWeek(), exercises, sessionNote }),
      });
      if (!saveRes.ok) throw new Error('Save failed');
      try { localStorage.removeItem(workoutDraftKey()); } catch {}
      const [newLogs, newPrs] = await Promise.all([
        fetch(`${API}/api/clients/${client.id}/workout-logs`, { headers: authHeader }).then(r => r.ok ? r.json() : Promise.reject(new Error('Fetch failed'))),
        fetch(`${API}/api/clients/${client.id}/prs`, { headers: authHeader }).then(r => r.ok ? r.json() : Promise.reject(new Error('Fetch failed'))),
      ]);
      if (Array.isArray(newLogs)) setWorkoutLogs(newLogs);
      if (newPrs && typeof newPrs === 'object' && !Array.isArray(newPrs)) setPrs(newPrs);
    } catch {
      setWorkoutLogs(prev => prev.filter(l => l.id !== optimisticLog.id));
      setLogSaved(false);
      setLoggingDay(gdi);
      setLogSets(draftBackup.logSets);
      setOpenLogExercises(draftBackup.openLogExercises);
      setSessionNote(draftBackup.sessionNote);
      alert('Could not save. Please try again.');
    }
    setSavingLog(false);
  };

  const getExerciseHistory = (exName: string) =>
    workoutLogs
      .filter(log => log.exercises?.some((ex: any) => ex.name === exName))
      .slice(-10)
      .map(log => {
        const ex = log.exercises.find((e: any) => e.name === exName);
        const topSet = (ex?.sets || []).reduce((best: any, s: any) => (parseFloat(s.weight)||0) > (parseFloat(best?.weight)||0) ? s : best, null);
        return { date: log.date, topSet, allSets: ex?.sets || [] };
      });

  // Once logs load, auto-expand the "current" program week: the first week with
  // an unfinished day (or the last week if everything is done), so the client
  // lands on what to do now while completed weeks stay collapsed-but-clearly-done.
  // MUST stay above the early returns below — a hook after a conditional return
  // changes the hook count between renders (React error #310). Self-contained so
  // it doesn't depend on values computed after the client guard.
  const didAutoExpandRef = useRef(false);
  useEffect(() => {
    if (didAutoExpandRef.current || !client || workoutLogs.length === 0) return;
    let inProg = false;
    const weeks = parseTrainingScheme(client.trainingScheme).reduce((acc: any[], day: any) => {
      if (day.day.match(/^WEEK:/)) {
        const wt = day.day.replace("WEEK:", "");
        inProg = /progression\s*rules?/i.test(wt);
        if (!inProg) acc.push({ week: wt, days: [] });
      } else {
        if (acc.length === 0) acc.push({ week: "Training Plan", days: [] });
        if (!inProg) acc[acc.length - 1].days.push(day);
      }
      return acc;
    }, []);
    const vw = weeks.filter((w: any) => w.days.length > 0);
    if (vw.length === 0) return;
    didAutoExpandRef.current = true;
    const done = (wl: string, dt: string) => workoutLogs.some((l: any) => l.dayName === dt && (l.weekName ? l.weekName === wl : true));
    let active = vw.findIndex((w: any) => w.days.some((d: any) => !done(w.week, d.day)));
    if (active < 0) active = vw.length - 1;
    setCollapsedPortalWeeks(prev => { const s = new Set(prev); s.delete(active); return s; });
  }, [client, workoutLogs]);

  if (client === undefined) return (
    <div style={{ minHeight: "100vh", background: IV.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: IV.accent, fontFamily: F_SERIF, fontSize: 22, letterSpacing: 2 }}>Loading…</div>
    </div>
  );

  if (!client) return (
    <div style={{ minHeight: "100vh", background: IV.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" as const }}>
        <MetaLogo size={36} color={IV.gold} style={{ margin: "0 auto 12px" }} />
        <div style={{ fontFamily: F_SERIF, fontSize: 32, color: IV.textPrimary, marginBottom: 8 }}>JOEY ROUHANA PT</div>
        <div style={{ color: IV.textMuted, fontSize: 14 }}>Invalid or expired link.</div>
      </div>
    </div>
  );

  const latestInBody = client.inbody[client.inbody.length - 1];
  const parsedDays = parseTrainingScheme(client.trainingScheme);
  const isCoachingNoteLine = (ex: any) => !ex.sets && !ex.reps && !ex.weight && !/^###\s*/.test(ex.name) && !/same.?day\s+lighter\s+option/i.test(ex.name) && !/^-{2,}$/.test((ex.name||"").trim());
  // Fold "Kettlebell Progression Rules" (and similar) weeks into the previous week.
  // Coaching notes from those sections are hidden from the client portal entirely.
  let _inProgRules = false;
  const parsedWeeks = parsedDays.reduce((acc: {week: string, days: typeof parsedDays}[], day) => {
    if (day.day.match(/^WEEK:/)) {
      const weekTitle = day.day.replace("WEEK:", "");
      _inProgRules = /progression\s*rules?/i.test(weekTitle);
      if (!_inProgRules) acc.push({week: weekTitle, days: []});
    } else {
      if (acc.length === 0) acc.push({week: "Training Plan", days: []});
      if (_inProgRules) {
        // Absorb into previous week: strip coaching notes, deduplicate by day name
        const filtered = { ...day, exercises: day.exercises.filter((e: any) => e.sets || e.reps || e.weight || /^###/.test(e.name || "")) };
        const prev = acc[acc.length - 1].days;
        if (!prev.some((d: any) => d.day === day.day)) prev.push(filtered);
      } else {
        acc[acc.length - 1].days.push(day);
      }
    }
    return acc;
  }, []);
  let _inProgRulesNotes = false;
  const allCoachingNotes = parsedDays.reduce((acc: {dayLabel: string; lines: string[]}[], day) => {
    if (day.day.match(/^WEEK:/)) { _inProgRulesNotes = /progression\s*rules?/i.test(day.day.replace("WEEK:","")); return acc; }
    if (_inProgRulesNotes) return acc;
    const notes = day.exercises.filter(isCoachingNoteLine).map(ex => ex.name);
    if (notes.length > 0) acc.push({ dayLabel: day.day.replace(/^Day\s*\d+\s*[-–]?\s*/i, "").trim() || day.day, lines: notes });
    return acc;
  }, []);
  const isMobile = window.innerWidth < 600;

  // A program day is "done" if there is ANY saved log for that week + day —
  // permanently, not only within the current calendar week. Returns the most
  // recent matching log (for the completion date + toughness rating). This is
  // what keeps checkmarks on past weeks from disappearing as time moves on.
  const findDayLog = (weekLabel: string, dayTitle: string) =>
    [...workoutLogs].reverse().find((l: any) =>
      l.dayName === dayTitle && (l.weekName ? l.weekName === weekLabel : true)
    );

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    setSending(true);
    try {
      const now = new Date();
      const message = { id: Date.now().toString(), from: "client", text: msgText, date: now.toISOString().split("T")[0], time: now.toTimeString().slice(0, 5) };
      await fetch(`${API}/api/clients/${client.id}/messages`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeader }, body: JSON.stringify(message) });
      await fetch(`https://coachagent-server-production.up.railway.app/api/send-email`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ to: "joey.metaphysique@gmail.com", subject: `Message from client: ${client.name}`, body: `${client.name} sent you a message via the client portal:\n\n${msgText}` }) });
      setMsgSent(true); setMsgText("");
    } catch { alert("Could not send message. Please try again."); }
    setSending(false);
  };

  // Portal-specific tab labels
  const PORTAL_TAB_LABELS: Record<string, string> = { training: "Training", diet: "Diet", inbody: "InBody", message: "Message", invoices: "Invoices", shop: "Shop" };

  return (
    <ThemeCtx.Provider value={IV}>
    <div style={{ minHeight: "100vh", background: IV.bg, color: IV.textPrimary, fontFamily: F_SANS, position: "relative", paddingBottom: isMobile ? "calc(66px + env(safe-area-inset-bottom, 0px))" : 0 }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap" rel="stylesheet" />

      {/* ── Coach preview bar ── */}
      {preview && (
        <div style={{ background: IV.indigoDeep, padding: "7px 16px", display: "flex", alignItems: "center", justifyContent: "center", gap: 14, position: "relative", zIndex: 2 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#F3EFE6" }}>COACH PREVIEW — {client.name.toUpperCase()}</span>
          <button onClick={onLogout} style={{ background: "transparent", border: "1px solid rgba(243,239,230,0.45)", color: "#F3EFE6", borderRadius: 6, padding: "2px 10px", fontSize: 10, fontWeight: 600, letterSpacing: 1.5, cursor: "pointer" }}>EXIT</button>
        </div>
      )}

      {/* Silhouette */}
      <div style={{ position: "fixed", bottom: 0, right: 0, width: "48vw", maxWidth: 500, height: "82vh", zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
        <img src="/hero-silhouette.png" alt="" aria-hidden="true" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "left top", opacity: 0.06 }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to right, ${IV.bg} 0%, rgba(243,239,230,0.45) 45%, transparent 80%), linear-gradient(to top, ${IV.bg} 0%, transparent 35%)` }} />
      </div>

      {/* ── Weekly Check-in Modal ── */}
      {checkInOpen && (() => {
        // Mon–Sat the check-in asks about the previous week; on Sunday about the week ending today
        const weekWord = (checkInWeekStart || getWeekToCheckIn()) === getWeekStart() ? "this week" : "last week";
        const questions = [
          { key: "overallFeel", label: `How did ${weekWord} feel overall?`, type: "scale10" },
          { key: "energyRecovery", label: `How was your energy and recovery ${weekWord}?`, type: "scale5" },
          { key: "sessionsCompleted", label: `Did you complete your training sessions ${weekWord}?`, type: "sessions" },
          { key: "hardOrEasy", label: "Anything that felt particularly hard or easy?", type: "text", optional: true },
          { key: "notesForCoach", label: "Anything you want your coach to know?", type: "text", optional: true },
        ];
        const q = questions[checkInStep];
        const isAnswered = () => {
          const v = (checkInAnswers as any)[q.key];
          if (q.optional) return true;
          if (q.type === "scale10" || q.type === "scale5") return v > 0;
          if (q.type === "sessions") return v !== "";
          return true;
        };
        return (
          <div style={{ position: "fixed", inset: 0, background: IV.overlay, zIndex: 1000, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center" }}>
            <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: isMobile ? "16px 16px 0 0" : 16, padding: isMobile ? "28px 20px 36px" : "36px 40px", width: "100%", maxWidth: isMobile ? "100%" : 480, boxSizing: "border-box" as const }}>
              {checkInDone ? (
                <div style={{ textAlign: "center" as const, padding: "20px 0" }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
                  <div style={{ fontFamily: F_SERIF, fontSize: 22, letterSpacing: 0.4, color: IV.accent }}>Thanks!</div>
                  <div style={{ fontSize: 14, color: IV.textSecondary, marginTop: 8 }}>Your coach will see this shortly.</div>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 4 }}>Weekly Check-in</div>
                      <div style={{ fontSize: 13, color: IV.textMuted }}>{checkInStep + 1} of {questions.length}</div>
                    </div>
                    <button onClick={dismissCheckIn} style={{ background: "none", border: "none", cursor: "pointer", color: IV.textDim, fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: 2, background: IV.surfaceRaised, borderRadius: 2, marginBottom: 28 }}>
                    <div style={{ height: "100%", width: `${((checkInStep + 1) / questions.length) * 100}%`, background: IV.accent, borderRadius: 2, transition: "width 0.3s" }} />
                  </div>
                  {/* Question */}
                  <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, color: IV.textPrimary, lineHeight: 1.4, marginBottom: 24 }}>
                    {q.label}
                    {q.optional && <span style={{ fontSize: 12, color: IV.textDim, fontWeight: 400, marginLeft: 8 }}>(optional)</span>}
                  </div>
                  {/* Answer input */}
                  {q.type === "scale10" && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as const }}>
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <button key={n} onClick={() => setCheckInAnswers(a => ({ ...a, overallFeel: n }))}
                          style={{ width: isMobile ? 40 : 44, height: isMobile ? 40 : 44, borderRadius: 8, border: `1px solid ${checkInAnswers.overallFeel === n ? IV.accent : IV.border}`, background: checkInAnswers.overallFeel === n ? IV.accentBg : IV.surfaceRaised, color: checkInAnswers.overallFeel === n ? IV.accent : IV.textSecondary, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                  {q.type === "scale5" && (() => {
                    const labels = ["Poor", "Low", "Okay", "Good", "Great"];
                    return (
                      <div style={{ display: "flex", flexDirection: "column" as const, gap: 8 }}>
                        {labels.map((label, i) => (
                          <button key={i} onClick={() => setCheckInAnswers(a => ({ ...a, energyRecovery: i + 1 }))}
                            style={{ padding: "11px 16px", borderRadius: 8, border: `1px solid ${checkInAnswers.energyRecovery === i + 1 ? IV.accent : IV.border}`, background: checkInAnswers.energyRecovery === i + 1 ? IV.accentBg : IV.surfaceRaised, color: checkInAnswers.energyRecovery === i + 1 ? IV.accent : IV.textSecondary, fontSize: 14, fontWeight: 500, cursor: "pointer", textAlign: "left" as const, display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontFamily: F_SANS, fontSize: 12, fontWeight: 600, letterSpacing: 0.5, color: IV.textDim, minWidth: 14 }}>{i + 1}</span>
                            {label}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {q.type === "sessions" && (() => {
                    const opts: { value: "all"|"most"|"some"|"none"; label: string }[] = [
                      { value: "all", label: "All of them" },
                      { value: "most", label: "Most of them" },
                      { value: "some", label: "Some of them" },
                      { value: "none", label: "None" },
                    ];
                    return (
                      <div style={{ display: "flex", flexDirection: "column" as const, gap: 8 }}>
                        {opts.map(o => (
                          <button key={o.value} onClick={() => setCheckInAnswers(a => ({ ...a, sessionsCompleted: o.value }))}
                            style={{ padding: "11px 16px", borderRadius: 8, border: `1px solid ${checkInAnswers.sessionsCompleted === o.value ? IV.accent : IV.border}`, background: checkInAnswers.sessionsCompleted === o.value ? IV.accentBg : IV.surfaceRaised, color: checkInAnswers.sessionsCompleted === o.value ? IV.accent : IV.textSecondary, fontSize: 14, fontWeight: 500, cursor: "pointer", textAlign: "left" as const }}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {q.type === "text" && (
                    <textarea
                      value={(checkInAnswers as any)[q.key]}
                      onChange={e => setCheckInAnswers(a => ({ ...a, [q.key]: e.target.value }))}
                      placeholder="Type here…"
                      rows={3}
                      style={{ width: "100%", background: IV.surfaceRaised, border: `1px solid ${IV.borderInput}`, borderRadius: 8, padding: "11px 14px", color: IV.textPrimary, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: "none", resize: "none" as const, boxSizing: "border-box" as const }}
                    />
                  )}
                  {/* Nav buttons */}
                  <div style={{ display: "flex", gap: 10, marginTop: 28 }}>
                    {checkInStep > 0 && (
                      <Btn variant="ghost" onClick={() => setCheckInStep(s => s - 1)} style={{ flex: 1, justifyContent: "center" }}>Back</Btn>
                    )}
                    {checkInStep < questions.length - 1 ? (
                      <Btn variant="primary" onClick={() => setCheckInStep(s => s + 1)} disabled={!isAnswered()} style={{ flex: 2, justifyContent: "center" }}>Next</Btn>
                    ) : (
                      <Btn variant="primary" onClick={submitCheckIn} disabled={checkInSaving || !isAnswered()} style={{ flex: 2, justifyContent: "center" }}>
                        {checkInSaving ? "Saving…" : "Submit"}
                      </Btn>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      <div style={{ position: "relative", zIndex: 1 }}>

        {/* ── Header ── */}
        <div style={{ background: IV.surface, borderBottom: `1px solid ${IV.border}`, padding: isMobile ? "10px 14px" : "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12 }}>
            <MetaLogo size={isMobile ? 26 : 32} color={IV.gold} />
            <div>
              <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 18 : 22, color: IV.textPrimary, letterSpacing: 2 }}>JOEY ROUHANA PT</div>
              <div style={{ fontSize: 9, fontWeight: 600, color: IV.textMuted, letterSpacing: 2.5, textTransform: "uppercase" as const, marginTop: 2 }}>Client Portal</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 16 }}>
            <div style={{ textAlign: "right" as const }}>
              <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: IV.textPrimary }}>{client.name}</div>
              {client.goalSummary && <div style={{ fontSize: 11, color: IV.textMuted, marginTop: 2 }}>{client.goalSummary}</div>}
            </div>
            <button onClick={onLogout} title="Log out"
              style={{ background: "none", border: `1px solid ${IV.border}`, borderRadius: 8, color: IV.textDim, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" as const, padding: isMobile ? "6px 8px" : "7px 12px", cursor: "pointer", whiteSpace: "nowrap" as const }}>
              Log out
            </button>
          </div>
        </div>

        {!preview && <PortalInstallBanner />}

        {/* ── Trial banner ── */}
        {client.isTrial && client.trialEndDate && (() => {
          const daysLeft = Math.max(0, Math.ceil((new Date(client.trialEndDate).getTime() - Date.now()) / 86400000));
          const expired  = daysLeft === 0;
          return (
            <div style={{ background: expired ? IV.dangerBg : IV.indigoBg, borderBottom: `1px solid ${expired ? IV.dangerBorder : IV.indigoBorder}`, padding: isMobile ? '7px 14px' : '10px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' as const }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontFamily: F_SANS, fontSize: 11, fontWeight: 700, letterSpacing: 2, color: expired ? IV.danger : IV.indigo }}>{expired ? 'TRIAL EXPIRED' : 'FREE TRIAL'}</div>
                <div style={{ fontSize: 12, color: IV.textMuted }}>{expired ? 'Your trial has ended.' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`}</div>
              </div>
              <a href="/shop" style={{ fontFamily: F_SANS, fontSize: 11, fontWeight: 600, letterSpacing: 1.5, color: IV.surface, background: expired ? IV.danger : IV.textPrimary, padding: '5px 14px', borderRadius: R.sm, textDecoration: 'none', whiteSpace: 'nowrap' as const }}>
                UPGRADE NOW
              </a>
            </div>
          );
        })()}

        {/* ── Latest InBody strip ── */}
        {latestInBody && (
          <div style={{ padding: isMobile ? "12px 16px 0" : "16px 32px 0" }}>
            <div style={{ background: IV.surfaceMid, border: `1px solid ${IV.border}`, borderRadius: R.lg, padding: isMobile ? "8px 12px" : "14px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))", gap: isMobile ? 8 : 12 }}>
              {[{ label: "Weight", value: latestInBody.weight + " kg" }, { label: "Muscle", value: latestInBody.muscleMass + " kg" }, { label: "Body Fat", value: latestInBody.bodyFat + "%" }, { label: "BMI", value: String(latestInBody.bmi) }, { label: "Visceral", value: String(latestInBody.visceralFat) }].map(item => (
                <div key={item.label}>
                  <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 20 : 26, color: IV.accent, lineHeight: 1 }}>{item.value}</div>
                  <div style={{ fontSize: 9, color: IV.textDim, letterSpacing: 1.5, textTransform: "uppercase" as const, marginTop: 3 }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Tab bar + content ── */}
        <div style={{ padding: isMobile ? "8px 12px 0" : "16px 32px 0" }}>

          {/* Tab bar — desktop/tablet only; mobile uses the fixed bottom nav */}
          {!isMobile && (
            <div style={{ display: "flex", gap: 2, marginBottom: 20, overflowX: "auto" as const, paddingBottom: 8, borderBottom: `1px solid ${IV.border}`, msOverflowStyle: "none" as any, scrollbarWidth: "none" as any }}>
              {(["training", "diet", "inbody", "message", "invoices", "shop"] as const).map(t => (
                <ProfileTab key={t} tabKey={t} active={tab === t} onClick={() => setTab(t)} />
              ))}
            </div>
          )}

          {/* ── Training ── */}
          {tab === "training" && (
            <div style={{ paddingBottom: 32, display: "flex", flexDirection: "column", gap: 8 }}>
              {/* Hero header */}
              {client.trainingScheme && (() => {
                const weekCount = parsedWeeks.filter((w: any) => w.days.length > 0).length;
                const daysPerWeek = weekCount > 0 ? Math.round(parsedWeeks.filter((w: any) => w.days.length > 0).reduce((s: number, w: any) => s + w.days.length, 0) / weekCount) : 0;
                return (
                  <div style={{ position: "relative", background: IV.surfaceRaised, border: `1px solid ${IV.border}`, borderRadius: R.xl, padding: isMobile ? "14px 14px" : "24px 28px", marginBottom: 8, overflow: "hidden" }}>
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(176,138,74,0.06) 0%, transparent 60%)", pointerEvents: "none" }} />
                    <div style={{ position: "absolute", top: 0, right: 0, width: 120, height: "100%", background: "linear-gradient(to left, rgba(176,138,74,0.05), transparent)", pointerEvents: "none" }} />
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase" as const, color: IV.accent, marginBottom: 4 }}>Training Plan</div>
                    <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 22 : 36, letterSpacing: 0.4, color: IV.textPrimary, lineHeight: 1, marginBottom: isMobile ? 8 : 12 }}>
                      {parsedWeeks[0]?.week?.trim() && parsedWeeks[0].week.trim() !== "Training Plan" ? parsedWeeks[0].week.trim() : "Your Program"}
                    </div>
                    <div style={{ display: "flex", gap: SP.sm, flexWrap: "wrap" as const }}>
                      {weekCount > 0 && <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: IV.accentBg, border: `1px solid ${IV.accentBorder}`, borderRadius: R.md, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: IV.accent }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        {weekCount} {weekCount === 1 ? "Week" : "Weeks"}
                      </div>}
                      {daysPerWeek > 0 && <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: IV.surfaceMid, border: `1px solid ${IV.border}`, borderRadius: R.md, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: IV.textMuted }}>
                        {daysPerWeek} Days/Week
                      </div>}
                      {(client.programStart || client.programEnd) && (() => {
                        const M = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const f = (s?: string) => { if (!s) return "…"; const d = new Date(s + "T12:00:00"); return isNaN(d.getTime()) ? s : `${d.getDate()} ${M[d.getMonth()]}`; };
                        return (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: IV.surfaceMid, border: `1px solid ${IV.border}`, borderRadius: R.md, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: IV.textMuted }}>
                            {f(client.programStart)} – {f(client.programEnd)}
                          </div>
                        );
                      })()}
                      <button onClick={downloadPlanPdf} disabled={planPdfBusy} style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "transparent", border: `1px solid ${IV.accentBorder}`, borderRadius: R.md,
                        padding: "4px 10px", fontSize: 11, fontWeight: 600, color: IV.accent,
                        cursor: planPdfBusy ? "default" : "pointer", opacity: planPdfBusy ? 0.6 : 1,
                        fontFamily: "'Inter', sans-serif",
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        {planPdfBusy ? "Preparing…" : "Download PDF"}
                      </button>
                    </div>
                  </div>
                );
              })()}
              {parsedWeeks.every((w: any) => w.days.length === 0) ? (
                client.category === "prospect" ? (
                  <div style={{ background: IV.surfaceRaised, border: `1px solid ${IV.border}`, borderRadius: R.lg, padding: isMobile ? "22px 18px" : "28px 26px", maxWidth: 620 }}>
                    <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 22 : 28, color: IV.textPrimary, marginBottom: 8 }}>Your profile is ready</div>
                    <div style={{ color: IV.textMuted, fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>Choose a Metaphysique program when you are ready and it will appear here, together with your nutrition guidance and training notes.</div>
                    <button onClick={() => { window.location.href = "/program-access"; }} style={{ background: IV.textPrimary, color: IV.surface, border: "none", borderRadius: R.md, padding: "12px 16px", fontSize: 13, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>Choose a program</button>
                  </div>
                ) : (
                  <div style={{ color: IV.textMuted, fontSize: 14, padding: "20px 0" }}>No training plan yet. Your coach will add one soon!</div>
                )
              ) : parsedWeeks.filter((w: any) => w.days.length > 0).map((week: any, wi: number) => {
                const weekStartDi = parsedWeeks.filter((w: any) => w.days.length > 0).slice(0, wi).reduce((acc: number, w: any) => acc + w.days.length, 0);
                const isCollapsed = collapsedPortalWeeks.has(wi);
                const doneCount = week.days.filter((d: any) => findDayLog(week.week, d.day)).length;
                const weekComplete = doneCount === week.days.length && week.days.length > 0;
                return (
                  <div key={wi}>
                    {/* Week header */}
                    <div onClick={() => togglePortalWeek(wi)} style={{ cursor: "pointer", userSelect: "none" as const, display: "flex", alignItems: "center", gap: 12, padding: isMobile ? "10px 14px" : "13px 18px", background: IV.surfaceRaised, border: `1px solid ${IV.border}`, borderRadius: isCollapsed ? R.lg : `${R.lg}px ${R.lg}px 0 0` }}>
                      <span style={{ color: IV.accent, fontSize: 9, opacity: 0.8, width: 10, flexShrink: 0 }}>{isCollapsed ? "▶" : "▼"}</span>
                      <span style={{ fontFamily: F_SANS, fontSize: isMobile ? 12 : 13, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase" as const, color: IV.textPrimary }}>{week.week}</span>
                      <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {doneCount > 0 && (
                          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, color: weekComplete ? IV.accent : IV.textSecondary, textTransform: "uppercase" as const }}>
                            {weekComplete ? "✓ Complete" : `${doneCount} / ${week.days.length} done`}
                          </span>
                        )}
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: IV.textDim, textTransform: "uppercase" as const }}>{week.days.length} days</span>
                      </span>
                    </div>

                    {/* Days */}
                    {!isCollapsed && (
                      <div style={{ border: `1px solid ${IV.border}`, borderTop: "none", borderRadius: `0 0 ${R.lg}px ${R.lg}px`, overflow: "hidden" }}>
                        {week.days.map((day: any, di: number) => {
                          const gdi = weekStartDi + di;
                          const isOpen = expandedDay === gdi;
                          const dayLog = findDayLog(week.week, day.day);
                          const dayDone = !!dayLog;
                          const doneDateLabel = dayLog?.date
                            ? new Date(dayLog.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
                            : "";
                          return (
                            <div key={di} style={{ borderBottom: di < week.days.length - 1 ? `1px solid ${IV.border}` : "none" }}>
                              {/* Day header */}
                              <div onClick={() => setExpandedDay(isOpen ? null : gdi)} style={{ background: dayDone ? "rgba(81,107,74,0.07)" : isOpen ? IV.surfaceMid : IV.surface, padding: isMobile ? "10px 14px" : "13px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  {dayDone && (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: IV.accent, whiteSpace: "nowrap" as const }}>
                                      <span style={{ fontSize: 13, lineHeight: 1 }}>✅</span>
                                      {doneDateLabel && <span>Done · {doneDateLabel}</span>}
                                    </span>
                                  )}
                                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, color: dayDone ? IV.accent : IV.textDim, textTransform: "uppercase" as const, flexShrink: 0 }}>Day {di + 1}</span>
                                  {(() => { const m = day.day?.match(/\(([^)]+)\)/); const label = m ? m[1] : day.day?.replace(/^Day\s+\d+\s*[-–]\s*\w+\s*/i, '').trim(); return label ? <span style={{ fontSize: isMobile ? 12 : 11, color: dayDone ? IV.textSecondary : IV.textMuted, fontWeight: 500 }}>{label}</span> : null; })()}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  {!dayDone && (() => { const c = day.exercises.filter((e: any) => (e.sets || e.reps || e.weight) && !/^###\s*/.test(e.name)).length; return c > 0 ? <span style={{ fontSize: 10, color: IV.textDim }}>{c} ex</span> : null; })()}
                                  <span style={{ fontSize: 10, color: IV.textDim }}>{isOpen ? "▲" : "▼"}</span>
                                </div>
                              </div>

                              {/* Exercise list */}
                              {isOpen && (
                                <div style={{ padding: isMobile ? "8px 10px 10px" : "12px 14px 14px", background: IV.bg, display: "flex", flexDirection: "column", gap: isMobile ? 4 : 6 }}>
                                  {day.exercises.length === 0 && <div style={{ color: IV.textDim, fontSize: 13, padding: "6px 0" }}>Rest day — active recovery</div>}

                                  {(() => {
                                    // Pre-process: skip coaching notes (moved to Program Notes) + separators
                                    const grouped: any[] = [];
                                    for (let ei = 0; ei < day.exercises.length; ei++) {
                                      const ex = day.exercises[ei];
                                      const isSeparator = /^-{2,}$/.test((ex.name||"").trim());
                                      if (isSeparator || isCoachingNoteLine(ex)) continue;
                                      grouped.push({ ...ex, _origIdx: ei, _type: "exercise" });
                                    }
                                    return grouped.map((item: any, gi: number) => {
                                      const ex = item;
                                      const ei = item._origIdx;
                                      const isBlockHeader = /^###\s*/.test(ex.name);
                                      const isSameDayOption = /same.?day\s+lighter\s+option/i.test(ex.name);
                                      if (isBlockHeader) {
                                        return (
                                          <div key={gi} style={{ paddingTop: gi > 0 ? 10 : 4, paddingBottom: 4 }}>
                                            <span style={{ fontFamily: F_SANS, fontSize: 12, fontWeight: 600, letterSpacing: 2, color: IV.accent, textTransform: "uppercase" as const }}>
                                              {ex.name.replace(/^###\s*/, "")}
                                            </span>
                                          </div>
                                        );
                                      }
                                      if (isSameDayOption) {
                                        return (
                                          <div key={gi} style={{ padding: "8px 14px", background: IV.indigoBg, border: `1px dashed ${IV.indigoBorder}`, borderRadius: R.md, marginTop: 6 }}>
                                            <span style={{ fontSize: 11, fontStyle: "italic", color: IV.indigo, letterSpacing: 0.5 }}>{ex.name}</span>
                                          </div>
                                        );
                                      }
                                    const exKey = `${gdi}-${ei}`;
                                    const pr = prs[ex.name];
                                    const history = getExerciseHistory(ex.name);
                                    const last = history[history.length - 1];
                                    const isShowingChart = chartExercise === ex.name;
                                    const isLoggingThis = openLogExercises.has(exKey);
                                    const noteMode = isNoteMode(ex);
                                    const prescParts = [ex.sets, ex.reps, ex.weight, ex.rest ? `Rest ${ex.rest}` : ""].filter(Boolean);
                                    const presc = prescParts.join("  ·  ");
                                    const currentBlock = day.exercises.slice(0, ei).reverse().find((e: any) => /^###\s*/.test(e.name));
                                    const isInPrepBlock = currentBlock && /prep|warm/i.test(currentBlock.name);

                                    return (
                                      <div key={gi} style={{ background: IV.surfaceRaised, border: `1px solid ${IV.border}`, borderRadius: R.md, padding: isMobile ? "9px 11px" : "12px 14px" }}>

                                        {/* Row 1: Name + badges + log button */}
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: presc || ex.notes ? 4 : 0, flexWrap: "wrap" as const }}>
                                          <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: IV.textPrimary, flex: 1, lineHeight: 1.3 }}>{ex.name}</span>
                                          {ex.isSuperset && <span style={dsBadge(IV.accent)}>SS</span>}
                                          {pr && <span style={{ ...dsBadge(IV.gold), whiteSpace: "nowrap" as const }}>🏆 {pr.weight}kg×{pr.reps}</span>}
                                          {!isInPrepBlock && (ex.sets || ex.reps || ex.weight) && (
                                            <button
                                              onClick={() => toggleExerciseLog(ex, gdi, ei)}
                                              style={{ background: isLoggingThis ? IV.accentBg : "transparent", border: `1px solid ${isLoggingThis ? IV.accentBorder : IV.border}`, borderRadius: R.sm, padding: "3px 10px", color: isLoggingThis ? IV.accent : IV.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer", letterSpacing: 0.5, flexShrink: 0 }}
                                            >
                                              {isLoggingThis ? "▲ close" : "+ log"}
                                            </button>
                                          )}
                                        </div>

                                        {/* Row 2: Prescription */}
                                        {presc && (
                                          <div style={{ fontSize: 12, color: IV.textSecondary, fontFamily: "'JetBrains Mono', monospace", marginBottom: ex.notes ? 4 : last ? 6 : 0, lineHeight: 1.4 }}>
                                            {presc}
                                          </div>
                                        )}

                                        {/* Row 3: Coaching note */}
                                        {ex.notes && (
                                          <div style={{ fontSize: 11, color: IV.textMuted, fontStyle: "italic" as const, lineHeight: 1.5, marginBottom: last ? 6 : 0 }}>
                                            {ex.notes}
                                          </div>
                                        )}

                                        {/* Row 4: Last session + chart toggle */}
                                        {last && (
                                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" as const }}>
                                            <span style={{ fontSize: 11, color: IV.textDim }}>Last ({last.date}): {last.allSets.map((s: any) => s.weight ? `${s.weight}kg×${s.reps || "?"}` : (s.reps || "?")).join(" · ")}</span>
                                            {history.length >= 2 && (
                                              <button onClick={() => setChartExercise(isShowingChart ? null : ex.name)} style={{ background: "none", border: "none", color: isShowingChart ? IV.accent : IV.textDim, fontSize: 11, cursor: "pointer", padding: 0, letterSpacing: 0.3 }}>
                                                {isShowingChart ? "▲ hide" : "📈 progress"}
                                              </button>
                                            )}
                                          </div>
                                        )}

                                        {/* Progress chart */}
                                        {isShowingChart && history.length >= 2 && (() => {
                                          const weights = history.map(h => parseFloat(h.topSet?.weight || "0")).filter(w => w > 0);
                                          if (weights.length < 2) return null;
                                          const min = Math.min(...weights), max = Math.max(...weights), range = max - min || 1;
                                          const W = 280, H = 56, PL = 28, PR = 8, PT = 6, PB = 16;
                                          const cW = W - PL - PR, cH = H - PT - PB;
                                          const pts = weights.map((w, i) => `${PL + (i / (weights.length - 1)) * cW},${PT + (1 - (w - min) / range) * cH}`).join(" ");
                                          return (
                                            <div style={{ marginTop: 8, background: IV.bg, borderRadius: R.sm, padding: "6px 8px" }}>
                                              <svg width={W} height={H} style={{ display: "block", overflow: "visible" }}>
                                                <polyline points={pts} fill="none" stroke={IV.accent} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                                                {weights.map((w, i) => (
                                                  <g key={i}>
                                                    <circle cx={PL + (i / (weights.length - 1)) * cW} cy={PT + (1 - (w - min) / range) * cH} r="2.5" fill={IV.accent} />
                                                    <text x={PL + (i / (weights.length - 1)) * cW} y={H - 2} textAnchor="middle" fontSize="9" fill={IV.textDim}>{history[i].date.slice(5)}</text>
                                                  </g>
                                                ))}
                                                <text x={0} y={PT + 5} fontSize="9" fill={IV.textMuted}>{max}kg</text>
                                                <text x={0} y={PT + cH + 4} fontSize="9" fill={IV.textMuted}>{min}kg</text>
                                              </svg>
                                            </div>
                                          );
                                        })()}

                                        {/* Inline log panel */}
                                        {isLoggingThis && (
                                          <div style={{ marginTop: 10, background: IV.bg, borderRadius: R.md, padding: "12px 12px 10px", border: `1px solid ${IV.accentBorder}` }}>
                                            {/* Last week + target suggestion */}
                                            {last && !noteMode && (() => {
                                              const repsNums = (ex.reps || '').match(/\d+/g)?.map(Number) || [];
                                              const maxPrescReps = repsNums.length ? Math.max(...repsNums) : null;
                                              const lastReps = parseInt(last.topSet?.reps) || 0;
                                              const lastWeight = parseFloat(last.topSet?.weight) || 0;
                                              const atTop = maxPrescReps && lastReps >= maxPrescReps;
                                              const targetWeight = lastWeight > 0 ? (atTop ? lastWeight + 2.5 : lastWeight) : null;
                                              const lastSetsStr = last.allSets.map((s: any) => s.weight ? `${s.weight}kg×${s.reps || '?'}` : (s.reps || '?')).join(' · ');
                                              return (
                                                <div style={{ marginBottom: 10, padding: "7px 10px", background: IV.accentBg, borderRadius: R.sm, border: `1px solid ${IV.goldBorder}` }}>
                                                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: IV.textDim, textTransform: "uppercase" as const, marginBottom: 3 }}>Last session</div>
                                                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" as const }}>
                                                    <span style={{ fontSize: 12, color: IV.textMuted }}>{lastSetsStr}</span>
                                                    {targetWeight && (
                                                      <>
                                                        <span style={{ fontSize: 10, color: IV.textDim }}>→</span>
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: IV.accent }}>
                                                          Target: {targetWeight % 1 === 0 ? targetWeight : targetWeight.toFixed(1)}kg{atTop ? " ↑" : ""}
                                                        </span>
                                                      </>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })()}
                                            {noteMode ? (
                                              <input
                                                type="text"
                                                placeholder="e.g. 40kg / 20m, 2 rounds, 30 sec…"
                                                value={(logSets[exKey] || [{ weight: '', reps: '' }])[0].reps}
                                                onChange={e => updateSet(exKey, 0, "reps", e.target.value)}
                                                style={{ width: "100%", background: IV.surfaceRaised, border: `1px solid ${IV.borderInput}`, borderRadius: R.sm, padding: "7px 10px", color: IV.textPrimary, fontSize: 13, outline: "none", boxSizing: "border-box" as const }}
                                              />
                                            ) : (
                                              <>
                                                {(logSets[exKey] || []).map((set, si) => (
                                                  <div key={si} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                                                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: IV.textDim, width: 36, flexShrink: 0, textTransform: "uppercase" as const }}>Set {si + 1}</span>
                                                    <input type="number" inputMode="decimal" placeholder="kg" value={set.weight} onChange={e => updateSet(exKey, si, "weight", e.target.value)}
                                                      style={{ width: 62, background: IV.surfaceRaised, border: `1px solid ${IV.borderInput}`, borderRadius: R.sm, padding: "6px 8px", color: IV.textPrimary, fontSize: 13, textAlign: "center" as const, outline: "none" }} />
                                                    <span style={{ color: IV.textDim, fontSize: 11 }}>×</span>
                                                    <input type="number" inputMode="numeric" placeholder="reps" value={set.reps} onChange={e => updateSet(exKey, si, "reps", e.target.value)}
                                                      style={{ width: 62, background: IV.surfaceRaised, border: `1px solid ${IV.borderInput}`, borderRadius: R.sm, padding: "6px 8px", color: IV.textPrimary, fontSize: 13, textAlign: "center" as const, outline: "none" }} />
                                                    {(logSets[exKey] || []).length > 1 && (
                                                      <button onClick={() => removeSet(exKey, si)} style={{ background: "none", border: "none", color: IV.textDim, cursor: "pointer", fontSize: 13, padding: "0 4px" }}>✕</button>
                                                    )}
                                                  </div>
                                                ))}
                                                <button onClick={() => addSet(exKey)} style={{ background: "none", border: "none", color: IV.textMuted, fontSize: 11, cursor: "pointer", padding: "2px 0", marginLeft: 44 }}>+ add set</button>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  });
                                  })()}

                                  {/* Save session */}
                                  {loggingDay === gdi && Object.values(logSets).some(sets => sets.some(s => s.weight || s.reps)) && (
                                    <div style={{ marginTop: 8, borderTop: `1px solid ${IV.border}`, paddingTop: 12 }}>
                                      <textarea placeholder="Session note (optional)…" value={sessionNote} onChange={e => setSessionNote(e.target.value)} rows={2}
                                        style={{ ...ivInputStyle, resize: "none" as const, marginBottom: 10 }} />
                                      {logSaved && <div style={{ color: IV.accent, fontSize: 12, marginBottom: 8, textAlign: "center" as const }}>✅ Session saved!</div>}
                                      <div style={{ display: "flex", gap: 8 }}>
                                        <Btn variant="primary" onClick={() => saveLog(day, week.week, gdi)} disabled={savingLog} style={{ flex: 1, justifyContent: "center" }}>
                                          {savingLog ? "Saving…" : "Save Session"}
                                        </Btn>
                                        <Btn variant="secondary" onClick={() => { setLoggingDay(null); setOpenLogExercises(new Set()); setLogSets({}); }}>Cancel</Btn>
                                      </div>
                                    </div>
                                  )}

                                  {/* Save confirmation banner */}
                                  {savedGdi === gdi && (
                                    <div style={{ color: IV.accent, fontSize: 13, fontWeight: 600, textAlign: "center" as const, padding: "8px 0" }}>✅ Session saved!</div>
                                  )}

                                  {/* Session toughness rating */}
                                  {(() => {
                                    const existingLog = findDayLog(week.week, day.day);
                                    if (!existingLog) return null;
                                    return (
                                      <SessionToughnessRating
                                        key={existingLog.id}
                                        clientId={client!.id}
                                        log={existingLog}
                                        jwt={jwt}
                                        onUpdate={updated => setWorkoutLogs(prev => prev.map(l => l.id === updated.id ? updated : l))}
                                      />
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Program Notes */}
              {client.trainingNotes && (
                <div style={{ marginTop: 8, background: IV.surfaceRaised, border: `1px solid ${IV.border}`, borderRadius: R.lg, overflow: "hidden" }}>
                  <div onClick={() => setPortalNotesOpen(!portalNotesOpen)} style={{ padding: `10px ${SP.md}px`, borderBottom: portalNotesOpen ? `1px solid ${IV.border}` : "none", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" as const }}>
                    <span style={{ color: IV.textDim, fontSize: 9 }}>{portalNotesOpen ? "▼" : "▶"}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: IV.textMuted }}>Program Notes</span>
                  </div>
                  {portalNotesOpen && (
                    <div style={{ padding: SP.md }}>
                      <div style={{ display: "flex", flexDirection: "column" as const, gap: 6 }}>
                        {(() => {
                          let tn = 0;
                          return client.trainingNotes.split("\n").filter((l: string) => l.trim()).map((line: string, i: number) => {
                            const bm = line.trim().match(/^\*{1,2}([^*]+)\*{1,2}:?\s*(.*)/);
                            const nm = !bm && line.trim().match(/^(\d+)[\.\)]\s*(.*)/);
                            if (bm || nm) {
                              tn++;
                              const title = bm ? bm[1].trim() : (nm ? nm[2] : "");
                              const sub = bm && bm[2] ? ": " + bm[2].trim() : "";
                              return (
                                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginTop: i > 0 ? 14 : 0 }}>
                                  <span style={{ color: IV.accent, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, minWidth: 24, flexShrink: 0, lineHeight: 1.6 }}>{tn}.</span>
                                  <span style={{ fontSize: 14, color: IV.textPrimary, fontWeight: 700, lineHeight: 1.6 }}>{title}<span style={{ color: IV.textSecondary, fontWeight: 400, fontSize: 13 }}>{sub}</span></span>
                                </div>
                              );
                            }
                            return (
                              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, paddingLeft: 36 }}>
                                <span style={{ color: IV.accent, fontSize: 6, marginTop: 9, flexShrink: 0 }}>●</span>
                                <span style={{ fontSize: 13, color: IV.textSecondary, lineHeight: 1.7 }}>{line.replace(/^[-•*]\s*/, "").replace(/\*{1,2}/g, "")}</span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Diet ── */}
          {tab === "diet" && (
            <div style={{ paddingBottom: 32 }}>
              {client.diet && (
                <div style={{ maxWidth: 1000, margin: "0 auto 18px", padding: isMobile ? "4px 0 0" : "8px 0 0" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: IV.textMuted, marginBottom: 6 }}>Nutrition Plan</div>
                  <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 26 : 32, letterSpacing: 0.4, color: IV.textPrimary, lineHeight: 1.05, marginBottom: 6 }}>
                    Personalised for {client.name.split(" ")[0]}
                  </div>
                  <div style={{ fontSize: 12.5, color: IV.textMuted }}>
                    {client.goal ? `Goal: ${client.goal}` : "Built around your goals, training and preferences."}
                  </div>
                </div>
              )}
              {client.diet
                ? <DietPlanView diet={client.diet} />
                : <div style={{ color: IV.textMuted, fontSize: 14, padding: "20px 0" }}>No diet plan yet. Your coach will add one soon!</div>
              }
            </div>
          )}

          {/* ── InBody ── */}
          {tab === "inbody" && (
            <div style={{ marginBottom: 32, display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Upload your own scan */}
              <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: R.lg, padding: isMobile ? 16 : 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" as const }}>
                  <div>
                    <div style={{ fontFamily: F_SERIF, fontSize: 18, color: IV.indigo, letterSpacing: 0.4 }}>Add an InBody scan</div>
                    <div style={{ fontSize: 12, color: IV.textMuted, marginTop: 2 }}>Upload the PDF or a photo of your printout — we'll read the numbers for you.</div>
                  </div>
                  {!inbodyDraft && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <label style={{ ...dsBtn("primary", IV), cursor: inbodyBusy ? "default" : "pointer", opacity: inbodyBusy ? 0.6 : 1 }}>
                        {inbodyBusy === "reading" ? "Reading scan…" : "Upload scan"}
                        <input type="file" accept="application/pdf,image/*" style={{ display: "none" }} disabled={!!inbodyBusy}
                          onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) readInBodyFile(f); }} />
                      </label>
                      <Btn variant="secondary" disabled={!!inbodyBusy} onClick={() => { setInbodyError(""); setInbodySaved(false); setInbodyDraft(blankInBody()); }}>Enter manually</Btn>
                    </div>
                  )}
                </div>
                {inbodySaved && !inbodyDraft && <div style={{ marginTop: 12, fontSize: 13, color: IV.success, fontWeight: 600 }}>✓ Scan saved — Joey has been notified.</div>}
                {inbodyError && <div style={{ marginTop: 12, fontSize: 13, color: IV.danger }}>{inbodyError}</div>}
                {inbodyDraft && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, color: IV.textMuted, marginBottom: 12 }}>Check the numbers against your report, correct anything that's off, then save.</div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 12 }}>
                      {([["date", "Scan date", "date"], ["weight", "Weight (kg)", "number"], ["muscleMass", "Muscle mass — SMM (kg)", "number"], ["bodyFat", "Body fat (%)", "number"], ["bmi", "BMI", "number"], ["visceralFat", "Visceral fat", "number"]] as const).map(([key, label, type]) => (
                        <div key={key}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: IV.textDim, letterSpacing: 1.2, textTransform: "uppercase" as const, marginBottom: 4 }}>{label}</div>
                          <input type={type} inputMode={type === "number" ? "decimal" : undefined} step="0.1" value={inbodyDraft[key]}
                            onChange={e => setInbodyDraft({ ...inbodyDraft, [key]: e.target.value })}
                            style={{ width: "100%", background: IV.surfaceRaised, border: `1px solid ${IV.borderInput}`, borderRadius: 8, padding: "9px 12px", color: IV.textPrimary, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: "none", boxSizing: "border-box" as const }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
                      <Btn variant="ghost" disabled={inbodyBusy === "saving"} onClick={() => { setInbodyDraft(null); setInbodyError(""); }}>Cancel</Btn>
                      <Btn variant="primary" disabled={inbodyBusy === "saving"} onClick={saveInBody}>{inbodyBusy === "saving" ? "Saving…" : "Save scan"}</Btn>
                    </div>
                  </div>
                )}
              </div>
              {client.inbody.length === 0 ? (
                <div style={{ color: IV.textMuted, fontSize: 14, padding: "4px 0" }}>No InBody results yet.</div>
              ) : (
                <>
                  <InBodyChart inbody={client.inbody} />
                  <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: R.lg, overflow: "hidden" }}>
                    <div style={{ overflowX: "auto" as const }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(80px, 1fr))", gap: 8, padding: "10px 16px", color: IV.textDim, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" as const, borderBottom: `1px solid ${IV.border}`, minWidth: 480, fontWeight: 700 }}>
                        <div>Date</div><div>Weight</div><div>Muscle</div><div>Body Fat</div><div>BMI</div><div>Visceral</div>
                      </div>
                      {[...client.inbody].sort((a, b) => b.date.localeCompare(a.date)).map((r, i) => (
                        <div key={i} style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(80px, 1fr))", gap: 8, padding: "12px 16px", borderBottom: `1px solid ${IV.border}`, fontSize: 13, minWidth: 480 }}>
                          <div style={{ color: IV.accent, fontWeight: 600 }}>{r.date}</div>
                          <div style={{ color: IV.textPrimary }}>{r.weight} kg</div>
                          <div style={{ color: IV.textPrimary }}>{r.muscleMass} kg</div>
                          <div style={{ color: IV.textPrimary }}>{r.bodyFat}%</div>
                          <div style={{ color: IV.textPrimary }}>{r.bmi}</div>
                          <div style={{ color: IV.textPrimary }}>{r.visceralFat}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Message ── */}
          {tab === "message" && (
            <div style={{ maxWidth: 540, paddingBottom: 32 }}>
              <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: R.lg, padding: isMobile ? 16 : 24 }}>
                <div style={{ fontFamily: F_SERIF, fontSize: 18, color: IV.indigo, letterSpacing: 0.4, marginBottom: 4 }}>Message Your Coach</div>
                <div style={{ fontSize: 12, color: IV.textMuted, marginBottom: 20 }}>Send a message directly to Joey. He'll reply via email.</div>
                {msgSent ? (
                  <div style={{ background: IV.surfaceMid, border: `1px solid ${IV.border}`, borderRadius: R.md, padding: 24, textAlign: "center" as const }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
                    <div style={{ color: IV.success, fontWeight: 600, marginBottom: 4 }}>Message sent!</div>
                    <div style={{ fontSize: 13, color: IV.textMuted }}>Joey will get back to you soon.</div>
                    <Btn variant="secondary" onClick={() => setMsgSent(false)} style={{ marginTop: 16 }}>Send another</Btn>
                  </div>
                ) : (
                  <>
                    <textarea value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Type your message here…"
                      style={{ ...ivInputStyle, resize: "vertical" as const, minHeight: 140, lineHeight: 1.6, marginBottom: 12 }} />
                    <Btn variant="primary" onClick={sendMessage} disabled={sending || !msgText.trim()} style={{ width: "100%", justifyContent: "center" }}>
                      {sending ? "Sending…" : "Send Message"}
                    </Btn>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Invoices ── */}
          {tab === "invoices" && (
            <div style={{ maxWidth: 620, paddingBottom: 32 }}>
              <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: R.lg, padding: isMobile ? 16 : 24 }}>
                <div style={{ fontFamily: F_SERIF, fontSize: 18, color: IV.accent, letterSpacing: 0.4, marginBottom: 4 }}>Your Invoices</div>
                <div style={{ fontSize: 12, color: IV.textMuted, marginBottom: 20 }}>Download PDF invoices for all your payments.</div>
                {(!client.payments || client.payments.filter((p: any) => p.status === "paid" && p.id).length === 0) ? (
                  <div style={{ color: IV.textDim, fontSize: 13, padding: "16px 0" }}>No invoices yet.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" as const }}>
                    {[...client.payments].filter((p: any) => p.status === "paid" && p.id).reverse().map((p: any, i: number, arr: any[]) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 0", borderBottom: i < arr.length - 1 ? `1px solid ${IV.border}` : "none" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: IV.textPrimary, marginBottom: 2 }}>{p.description}</div>
                          <div style={{ fontSize: 11, color: IV.textMuted }}>{p.date}</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: IV.accent }}>€{p.amount}</div>
                          <a href={`https://coachagent-server-production.up.railway.app/api/invoices/${p.id}`} target="_blank" rel="noopener noreferrer"
                            style={{ ...dsBtn("primary", IV), textDecoration: "none", fontSize: 12, padding: "6px 14px" }}>
                            PDF
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Shop ── */}
          {tab === "shop" && (() => {
            const memberships = [
              { title: "1 Session / Week", price: "€380 / month", amount: "380.00", description: "PT Membership — 1 Session/Week", features: ["Structured weekly training", "Clear progression plan", "Ongoing coaching support"], highlight: false, recurring: true },
              { title: "2 Sessions / Week", price: "€720 / month", amount: "720.00", description: "PT Membership — 2 Sessions/Week", features: ["Faster progress through increased frequency", "Strength, conditioning, and movement focus", "Continuous adjustments based on performance"], highlight: true, badge: "Most Popular", recurring: true },
              { title: "3 Sessions / Week", price: "€1020 / month", amount: "1020.00", description: "PT Membership — 3 Sessions/Week", features: ["High-frequency coaching for accelerated results", "Maximum accountability and structure", "Ideal for serious transformation"], highlight: false, recurring: true },
            ];
            const onlineTraining = [
              {
                title: "Personalized Training Plan", price: "€34.95 / month", amount: "34.95",
                description: "Personalized Training Plan — Online Programming",
                tagline: "A personalized training program without the cost or commitment of full online coaching.",
                commitment: "No minimum commitment",
                features: [
                  "Access to the training app",
                  "Personalized program built around your goals, experience, schedule, equipment and limitations",
                  "Exercise demonstrations, workout tracking and structured progression",
                  "New or updated program every 4–6 weeks",
                  "Limited support for exercise clarification or app-related questions",
                ],
                notIncluded: ["Regular coaching check-ins or weekly follow-up", "Ongoing accountability", "Nutrition coaching", "Video calls or technique video analysis", "Frequent program adjustments"],
                cta: "Get My Training Plan",
                highlight: false, recurring: true,
              },
              {
                title: "Full Online Coaching", price: "€100 / month", amount: "100.00",
                description: "Full Online Coaching",
                tagline: "Full online coaching with ongoing follow-up, accountability and adjustments.",
                features: [
                  SHOW_BASIC_ONLINE_PLAN ? "Everything in the Personalized Training Plan" : "Personalized program built around your goals, schedule, equipment and limitations",
                  "Regular progress check-ins, follow-up and accountability",
                  "Program adjustments based on progress, recovery and feedback",
                  "Direct communication with your coach and technique feedback",
                  "Nutrition and lifestyle guidance where relevant",
                ],
                cta: "Start Online Coaching",
                highlight: true, badge: "Full Coaching", recurring: true,
              },
            ].filter(m => SHOW_BASIC_ONLINE_PLAN || m.title !== "Personalized Training Plan");
            const packages = [
              { title: "8 Sessions",  price: "€800",   amount: "800.00",  description: "PT Package — 8 Sessions",  tagline: "A focused starting point to build structure and momentum", validity: "3 months" },
              { title: "12 Sessions", price: "€1,140", amount: "1140.00", description: "PT Package — 12 Sessions", tagline: "Build consistency and establish measurable progress", validity: "5 months" },
              { title: "24 Sessions", price: "€2,160", amount: "2160.00", description: "PT Package — 24 Sessions", tagline: "Longer commitment for deeper results and progression", validity: "9 months" },
            ];
            const inp: React.CSSProperties = { width: "100%", background: IV.surfaceRaised, border: `1px solid ${IV.borderInput}`, borderRadius: 8, padding: "11px 14px", color: IV.textPrimary, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: "none", boxSizing: "border-box" };
            return (
              <div style={{ paddingBottom: isMobile ? 24 : 40 }}>
                {/* Memberships */}
                <div style={{ marginBottom: isMobile ? 24 : 40 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 4 }}>Main Offer</div>
                  <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 22 : 30, letterSpacing: 0.4, color: IV.textPrimary, marginBottom: 3 }}>Coaching Memberships</div>
                  <div style={{ fontSize: 12, color: IV.textSecondary, marginBottom: isMobile ? 14 : 24 }}>Designed for consistency and long-term results</div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(260px, 1fr))", gap: isMobile ? 10 : 18 }}>
                    {memberships.map(m => <MembershipCard key={m.title} m={m} onSelect={openShopModal} isMobile={isMobile} />)}
                  </div>
                </div>

                <div style={{ height: 1, background: IV.border, marginBottom: isMobile ? 24 : 40 }} />

                {/* Online Training */}
                <div style={{ marginBottom: isMobile ? 24 : 40 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 4 }}>Remote Coaching</div>
                  <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 22 : 30, letterSpacing: 0.4, color: IV.textPrimary, marginBottom: 3 }}>Online Training</div>
                  <div style={{ fontSize: 12, color: IV.textSecondary, marginBottom: isMobile ? 14 : 24 }}>{SHOW_BASIC_ONLINE_PLAN ? "Two levels of remote support — standalone programming, or full coaching with follow-up" : "Remote coaching with a personal program, follow-up and accountability — train anywhere"}</div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))", gap: isMobile ? 10 : 18 }}>
                    {onlineTraining.map(m => <MembershipCard key={m.title} m={m} onSelect={openShopModal} isMobile={isMobile} />)}
                  </div>
                </div>

                <div style={{ height: 1, background: IV.border, marginBottom: isMobile ? 24 : 40 }} />

                {/* Packages */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 4 }}>Session Packages</div>
                  <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 22 : 30, letterSpacing: 0.4, color: IV.textPrimary, marginBottom: 3 }}>Training Packages</div>
                  <div style={{ fontSize: 12, color: IV.textSecondary, marginBottom: isMobile ? 14 : 24 }}>Flexible options for shorter commitments</div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(240px, 1fr))", gap: isMobile ? 10 : 18 }}>
                    {packages.map(p => <PackageCard key={p.title} p={p} onSelect={openShopModal} isMobile={isMobile} />)}
                  </div>
                </div>

                {/* Checkout modal */}
                {shopModal && (
                  <div onClick={e => { if (e.target === e.currentTarget) setShopModal(null); }}
                    style={{ position: "fixed" as const, inset: 0, background: IV.overlay, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 1000, padding: isMobile ? 0 : 24 }}>
                    <div style={{ background: IV.surface, border: `1px solid ${IV.borderInput}`, borderRadius: isMobile ? "16px 16px 0 0" : 16, padding: isMobile ? "24px 18px 32px" : 36, maxWidth: 440, width: "100%", boxShadow: "0 40px 80px rgba(28,24,20,0.25)" }}>
                      <div style={{ marginBottom: isMobile ? 18 : 28 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 6 }}>Apply Now</div>
                        <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 24 : 30, letterSpacing: 0.4, color: IV.textPrimary, lineHeight: 1 }}>{shopModal.title}</div>
                        <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 20 : 24, color: IV.accent, marginTop: 4 }}>{shopModal.price}</div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
                        {([
                          { label: "Full Name", key: "name", type: "text", placeholder: "Your name" },
                          { label: "Email", key: "email", type: "email", placeholder: "your@email.com" },
                          { label: "Phone (optional)", key: "phone", type: "tel", placeholder: "+31 6 ..." },
                        ] as const).map(f => (
                          <div key={f.key}>
                            <label style={{ display: "block", fontSize: 12, color: IV.textMuted, marginBottom: 6, fontWeight: 500 }}>{f.label}</label>
                            <input type={f.type} placeholder={f.placeholder} value={shopForm[f.key]} onChange={e => setShopForm(prev => ({ ...prev, [f.key]: e.target.value }))} style={inp} />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 16 }}>
                        <input
                          type="checkbox"
                          id="terms-agree"
                          checked={shopForm.termsAgreed}
                          onChange={e => setShopForm(prev => ({ ...prev, termsAgreed: e.target.checked }))}
                          style={{ marginTop: 2, accentColor: IV.accent, cursor: "pointer", flexShrink: 0 }}
                        />
                        <label htmlFor="terms-agree" style={{ fontSize: 12, color: IV.textMuted, lineHeight: 1.5, cursor: "pointer" }}>
                          I agree to the{" "}
                          <span onClick={() => setTermsOpen(true)} style={{ color: IV.indigo, textDecoration: "underline", cursor: "pointer" }}>Terms & Conditions</span>
                          {" "}and waive my 14-day right of withdrawal upon accessing the service.
                        </label>
                      </div>
                      {shopError && <div style={{ color: IV.danger, fontSize: 13, marginTop: 12 }}>{shopError}</div>}
                      <div style={{ display: "flex", gap: 10, marginTop: 28 }}>
                        <Btn variant="ghost" onClick={() => setShopModal(null)} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
                        <Btn variant="primary" onClick={handleShopCheckout} disabled={shopLoading || !shopForm.termsAgreed} style={{ flex: 2, justifyContent: "center", fontSize: 14 }}>
                          {shopLoading ? "Redirecting…" : "Proceed to Payment →"}
                        </Btn>
                      </div>
                      <div style={{ textAlign: "center" as const, fontSize: 11, color: IV.textDim, marginTop: 16 }}>Secure payment via Mollie · All prices include VAT</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

        </div>

        {/* ── T&C footer ── */}
        <div style={{ textAlign: "center" as const, padding: "20px 0 28px", marginTop: 8 }}>
          <span
            onClick={() => setTermsOpen(true)}
            style={{ fontSize: 11, color: IV.indigo, cursor: "pointer", letterSpacing: 0.3, opacity: 0.8 }}
          >
            Terms &amp; Conditions · Privacy Policy
          </span>
        </div>

        {/* ── Terms modal ── */}
        {termsOpen && (
          <div onClick={e => { if (e.target === e.currentTarget) setTermsOpen(false); }}
            style={{ position: "fixed" as const, inset: 0, background: IV.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 24 }}>
            <div style={{ background: IV.surface, border: `1px solid ${IV.borderInput}`, borderRadius: 16, padding: "32px 36px", maxWidth: 640, width: "100%", maxHeight: "80vh", overflowY: "auto" as const, boxShadow: "0 40px 80px rgba(28,24,20,0.25)", position: "relative" as const }}>
              <button onClick={() => setTermsOpen(false)} style={{ position: "absolute" as const, top: 16, right: 20, background: "none", border: "none", color: IV.textMuted, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 6 }}>Legal</div>
              <div style={{ fontFamily: F_SERIF, fontSize: 26, letterSpacing: 0.4, color: IV.textPrimary, marginBottom: 24 }}>Terms &amp; Conditions</div>
              {([
                { heading: "1. Introduction", body: "These Terms and Conditions govern your purchase and use of products and services offered through the Metaphysique platform, operated by PT by Joey Rouhana, KvK 86163795, Roomtuintjes 204, 1093TB Amsterdam. By completing a purchase you agree to these Terms in full." },
                { heading: "2. Products and Services", body: "We offer Personal Training Memberships (monthly subscription), Training Packages (one-time), and Training Programs (digital content) as described on the relevant product pages." },
                { heading: "3. Prices and Payment", body: "All prices are in euros (€) and include VAT where applicable. Payment is processed securely at checkout. We do not store your payment details. Memberships are billed monthly and any price changes will be communicated at least 30 days in advance." },
                { heading: "4. Memberships", body: "Memberships are billed monthly from the purchase date and require a minimum commitment of 3 months, except for subscriptions expressly marked as having no minimum commitment (such as the Personalized Training Plan), which may be cancelled at any time. After any applicable minimum period, you may cancel by emailing joey.metaphysique@gmail.com. Cancellation takes effect at the end of the current billing period. No partial refunds are issued for unused days." },
                { heading: "5. Packages", body: "Packages are one-time purchases. Sessions must be used within the validity period stated at the time of purchase (Foundation Block: 3 months, Progress Block: 5 months, Transformation Block: 9 months). Unused sessions after the validity period are forfeited." },
                { heading: "6. Right of Withdrawal", body: "EU law grants a 14-day right of withdrawal. For digital programs, by accessing the content you expressly request immediate delivery and waive this right (Art. 6:230p(f) BW). For memberships and packages, by consenting to immediate commencement you waive this right once the service begins (Art. 6:230p(a) BW). If you have not accessed content or begun the service within 14 days, contact joey.metaphysique@gmail.com to withdraw." },
                { heading: "7. Your Responsibilities", body: "You agree to provide accurate information, use content for personal non-commercial use only, not redistribute or resell any materials, and consult a medical professional before beginning any training program. Metaphysique is not liable for injury arising from failure to do so." },
                { heading: "8. Intellectual Property", body: "All content is the exclusive property of PT by Joey Rouhana. You receive a personal, non-transferable licence for your own training only." },
                { heading: "9. Limitation of Liability", body: "Metaphysique provides fitness content for informational purposes and is not a medical provider. Our total liability shall not exceed the amount you paid in the 12 months preceding a claim. Nothing limits liability for death or injury caused by gross negligence or fraud." },
                { heading: "10. Privacy", body: "Your data is processed in accordance with our Privacy Policy and the GDPR. Contact joey.metaphysique@gmail.com for any data requests." },
                { heading: "11. Governing Law", body: "These Terms are governed by Dutch law. Disputes shall be submitted to the competent court in Amsterdam." },
                { heading: "12. Contact", body: "Metaphysique · Roomtuintjes 204, 1093TB Amsterdam · KvK 86163795 · joey.metaphysique@gmail.com · +31 6 49 53 67 06" },
              ] as { heading: string; body: string }[]).map(s => (
                <div key={s.heading} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: IV.textPrimary, marginBottom: 4, letterSpacing: 0.3 }}>{s.heading}</div>
                  <div style={{ fontSize: 13, color: IV.textMuted, lineHeight: 1.65 }}>{s.body}</div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: IV.textDim, marginTop: 24, paddingTop: 16, borderTop: `1px solid ${IV.border}` }}>Last updated: 3 May 2026</div>
            </div>
          </div>
        )}

      </div>

      {/* ── Mobile "More" bottom sheet (Message / Invoices / Shop / Log out) ── */}
      {isMobile && moreOpen && (
        <div
          onClick={() => setMoreOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 950, background: IV.overlay, display: "flex", alignItems: "flex-end" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: "100%", background: IV.surface, borderTop: `1px solid ${IV.border}`,
              borderRadius: "18px 18px 0 0", padding: "10px 12px calc(16px + env(safe-area-inset-bottom, 0px))",
              boxShadow: "0 -10px 40px rgba(0,0,0,0.18)",
            }}
          >
            <div style={{ width: 38, height: 4, borderRadius: 4, background: IV.border, margin: "2px auto 12px" }} />
            {([
              { key: "message" as const },
              { key: "invoices" as const },
              { key: "shop" as const },
            ]).map(({ key }) => {
              const isActive = tab === key;
              return (
                <button
                  key={key}
                  onClick={() => { setTab(key); setMoreOpen(false); window.scrollTo({ top: 0 }); }}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 14,
                    background: isActive ? IV.accentBg : "transparent",
                    border: `1px solid ${isActive ? IV.accentBorder : "transparent"}`, borderRadius: R.md,
                    padding: "13px 14px", cursor: "pointer", color: isActive ? IV.accent : IV.textPrimary,
                    fontSize: 15, fontWeight: 600, fontFamily: "'Inter', sans-serif", textAlign: "left" as const,
                    WebkitTapHighlightColor: "transparent" as any,
                  }}
                >
                  <PortalTabIcon tabKey={key} size={22} color={isActive ? IV.accent : IV.textMuted} />
                  {PORTAL_TAB_LABELS[key] || key}
                </button>
              );
            })}
            <div style={{ height: 1, background: IV.border, margin: "8px 4px" }} />
            <button
              onClick={() => { setMoreOpen(false); onLogout(); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 14, background: "transparent",
                border: "1px solid transparent", borderRadius: R.md, padding: "13px 14px", cursor: "pointer",
                color: IV.danger, fontSize: 15, fontWeight: 600, fontFamily: "'Inter', sans-serif", textAlign: "left" as const,
                WebkitTapHighlightColor: "transparent" as any,
              }}
            >
              <PortalTabIcon tabKey="logout" size={22} color={IV.danger} />
              Log out
            </button>
          </div>
        </div>
      )}

      {/* ── Mobile bottom navigation (native-app style) ── */}
      {isMobile && (() => {
        const primary = ["training", "diet", "inbody"] as const;
        const moreActive = tab === "message" || tab === "invoices" || tab === "shop";
        return (
          <nav style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 900,
            background: IV.surface, borderTop: `1px solid ${IV.border}`,
            display: "flex", alignItems: "stretch",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
          }}>
            {primary.map(t => {
              const isActive = tab === t && !moreOpen;
              return (
                <button
                  key={t}
                  onClick={() => { setMoreOpen(false); setTab(t); window.scrollTo({ top: 0 }); }}
                  aria-label={PORTAL_TAB_LABELS[t] || t}
                  aria-current={isActive ? "page" : undefined}
                  style={{
                    flex: 1, minWidth: 0, background: "transparent", border: "none",
                    display: "flex", flexDirection: "column" as const, alignItems: "center", justifyContent: "center", gap: 3,
                    padding: "9px 2px 8px", cursor: "pointer",
                    color: isActive ? IV.accent : IV.textDim,
                    WebkitTapHighlightColor: "transparent" as any, position: "relative",
                  }}
                >
                  {isActive && <span style={{ position: "absolute", top: 0, left: "26%", right: "26%", height: 2, borderRadius: 2, background: IV.accent }} />}
                  <PortalTabIcon tabKey={t} size={23} />
                  <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: 0.2, lineHeight: 1, whiteSpace: "nowrap" as const }}>
                    {PORTAL_TAB_LABELS[t] || t}
                  </span>
                </button>
              );
            })}
            {/* More */}
            {(() => {
              const isActive = moreOpen || moreActive;
              return (
                <button
                  onClick={() => setMoreOpen(o => !o)}
                  aria-label="More"
                  aria-expanded={moreOpen}
                  style={{
                    flex: 1, minWidth: 0, background: "transparent", border: "none",
                    display: "flex", flexDirection: "column" as const, alignItems: "center", justifyContent: "center", gap: 3,
                    padding: "9px 2px 8px", cursor: "pointer",
                    color: isActive ? IV.accent : IV.textDim,
                    WebkitTapHighlightColor: "transparent" as any, position: "relative",
                  }}
                >
                  {isActive && <span style={{ position: "absolute", top: 0, left: "26%", right: "26%", height: 2, borderRadius: 2, background: IV.accent }} />}
                  <PortalTabIcon tabKey="more" size={23} />
                  <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: 0.2, lineHeight: 1, whiteSpace: "nowrap" as const }}>
                    More
                  </span>
                </button>
              );
            })()}
          </nav>
        );
      })()}
    </div>
    </ThemeCtx.Provider>
  );
}

function InBodyOverviewChart({ inbody }: { inbody: InBodyResult[] }) {
  const T = React.useContext(ThemeCtx);
  const metrics = [
    { key: "weight" as const, label: "Weight", unit: "kg", color: T.gold },
    { key: "muscleMass" as const, label: "Muscle", unit: "kg", color: T.indigo },
    { key: "bodyFat" as const, label: "Body Fat", unit: "%", color: T.textSecondary },
  ];
  const [active, setActive] = useState<Set<string>>(new Set(["weight", "bodyFat", "muscleMass"]));
  const toggle = (key: string) => setActive(prev => {
    const s = new Set(prev); s.has(key) ? (s.size > 1 && s.delete(key)) : s.add(key); return s;
  });
  const sorted = [...inbody].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sorted[sorted.length - 1];
  const W = 520, H = 110, PL = 8, PR = 8, PT = 8, PB = 22;
  const chartW = W - PL - PR, chartH = H - PT - PB;

  if (!latest) return <div style={{ fontSize: 14, color: T.textDim }}>No InBody data yet</div>;

  return (
    <div style={{ flex: 2, minWidth: 280 }}>
      {/* Toggle buttons + current values */}
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 10, flexWrap: "wrap" as const }}>
        {metrics.map(m => {
          const isOn = active.has(m.key);
          const prev = sorted.length >= 2 ? sorted[sorted.length - 2] : null;
          const delta = prev !== null ? Number(latest[m.key]) - Number(prev[m.key]) : null;
          const deltaStr = delta !== null ? ((delta >= 0 ? "+" : "") + delta.toFixed(1) + m.unit) : null;
          const isGood = delta !== null && delta > 0;
          return (
            <button key={m.key} onClick={() => toggle(m.key)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" as const, opacity: isOn ? 1 : 0.3, transition: "opacity 0.15s" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: m.color, display: "inline-block", flexShrink: 0 }} />
                <span style={{ fontSize: 9, color: T.textMuted, letterSpacing: 1.5, textTransform: "uppercase" as const }}>{m.label}</span>
              </div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 700, color: m.color, lineHeight: 1 }}>
                {Number(latest[m.key])}<span style={{ fontSize: 10, color: T.textMuted }}>{m.unit}</span>
              </div>
              {deltaStr && <div style={{ fontSize: 10, color: isGood ? T.accent : T.danger, marginTop: 2 }}>{deltaStr}</div>}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      {sorted.length >= 2 ? (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 90, display: "block" }}>
          {metrics.filter(m => active.has(m.key)).map(m => {
            const vals = sorted.map(r => Number(r[m.key]));
            const min = Math.min(...vals), max = Math.max(...vals);
            const range = max - min || 1;
            const pts = vals.map((v, i) => {
              const x = PL + (sorted.length === 1 ? chartW / 2 : (i / (vals.length - 1)) * chartW);
              const y = PT + (1 - (v - min) / range) * chartH;
              return `${x},${y}`;
            }).join(" ");
            return (
              <g key={m.key}>
                <defs><linearGradient id={`ov-${m.key}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={m.color} stopOpacity="0.15" /><stop offset="100%" stopColor={m.color} stopOpacity="0" /></linearGradient></defs>
                <polyline points={pts} fill={`url(#ov-${m.key})`} stroke="none" />
                <polyline points={pts} fill="none" stroke={m.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                {sorted.map((_, i) => {
                  const x = PL + (i / (vals.length - 1)) * chartW;
                  const y = PT + (1 - (vals[i] - min) / range) * chartH;
                  return <circle key={i} cx={x} cy={y} r="3" fill={m.color} />;
                })}
              </g>
            );
          })}
          {sorted.map((r, i) => (
            <text key={i} x={PL + (i / (sorted.length - 1)) * chartW} y={H - 4} textAnchor="middle" fontSize="9" fill={T.textDim}>{r.date.slice(5)}</text>
          ))}
        </svg>
      ) : (
        <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>Add more scans to see trend</div>
      )}
    </div>
  );
}

function InBodyChart({ inbody }: { inbody: InBodyResult[] }) {
  const T = React.useContext(ThemeCtx);
  if (inbody.length < 2) return null;
  const sorted = [...inbody].sort((a, b) => a.date.localeCompare(b.date));
  const metrics = [
    { key: "weight"    as const, label: "Weight",   unit: "kg", color: T.gold },
    { key: "muscleMass"as const, label: "Muscle",   unit: "kg", color: T.indigo },
    { key: "bodyFat"   as const, label: "Body Fat", unit: "%",  color: T.textSecondary },
  ];
  const W = 340, H = 120, PL = 32, PR = 10, PT = 10, PB = 24;
  const chartW = W - PL - PR, chartH = H - PT - PB;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
      {metrics.map(({ key, label, unit, color }) => {
        const vals   = sorted.map(r => Number(r[key]));
        const min    = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
        const pts    = vals.map((v, i) => `${PL + (i / (vals.length - 1)) * chartW},${PT + (1 - (v - min) / range) * chartH}`).join(" ");
        const latest = vals[vals.length - 1];
        const prev   = vals[vals.length - 2];
        const delta  = latest - prev;
        const deltaColor = delta === 0 ? T.textDim : delta > 0 ? T.accent : T.danger;
        return (
          <div key={key} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: R.lg, padding: "14px 16px", overflow: "hidden" }}>
            {/* Header: label + delta vs prev scan */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block" }} />
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: T.textMuted }}>{label}</span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: deltaColor }}>
                {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}${unit}`}
              </span>
            </div>

            {/* Chart */}
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H * 0.75, display: "block" }}>
              <defs>
                <linearGradient id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
              </defs>
              {[0, 0.5, 1].map(t => (
                <line key={t} x1={PL} y1={PT + t * chartH} x2={W - PR} y2={PT + t * chartH} stroke={T.border} strokeWidth="1" />
              ))}
              <text x={PL - 4} y={PT + 4}       textAnchor="end" fontSize="8" fill={T.textDim}>{max.toFixed(0)}</text>
              <text x={PL - 4} y={PT + chartH + 4} textAnchor="end" fontSize="8" fill={T.textDim}>{min.toFixed(0)}</text>
              <polyline points={pts} fill={`url(#grad-${key})`} stroke="none" />
              <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
              {sorted.map((r, i) => {
                const x = PL + (i / (vals.length - 1)) * chartW;
                const y = PT + (1 - (vals[i] - min) / range) * chartH;
                return (
                  <g key={i}>
                    <circle cx={x} cy={y} r="2.5" fill={color} />
                    <text x={x} y={H - 5} textAnchor="middle" fontSize="8" fill={T.textDim}>{r.date.slice(5)}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        );
      })}
    </div>
  );
}

const TQ_QDATA = [
  { key:"q1",  label:"What is your actual goal — and why does it matter to you right now?",       hint:"Be specific. Not just 'lose weight' — what does achieving this actually change for you?", tall:true,  required:true },
  { key:"q2",  label:"What does your current weekly training look like?",                          hint:"Days, type of training, session length, typical week structure.", tall:true,  required:true },
  { key:"q3",  label:"What injuries, pain, tightness, or recurring issues do you currently have?", hint:"Location, when it happens, what makes it better or worse, how long.", tall:true,  required:true },
  { key:"q4",  label:"Which movements or exercises feel strongest and weakest for you?",           hint:"Compound lifts, unilateral work, overhead, hinge, squat — what's solid and what isn't.", tall:false, required:false },
  { key:"q5",  label:"What does your work/life stress and sleep currently look like?",             hint:"Hours of sleep, quality, work demands, general stress level.", tall:false, required:false },
  { key:"q6",  label:"How many days can you realistically train consistently?",                    hint:"Not the ideal — the realistic number given your actual life.", tall:false, required:false },
  { key:"q7",  label:"What type of training do you actually enjoy?",                               hint:"What keeps you engaged vs what feels like a chore?", tall:false, required:false },
  { key:"q8",  label:"What are your biggest limitations right now?",                               hint:"Time, energy, injuries, technique, equipment, motivation — be honest.", tall:false, required:false },
  { key:"q9",  label:"How do you respond to intensity and volume?",                                hint:"Do you recover well? Do you tend to do too much or too little?", tall:false, required:false },
  { key:"q10", label:"What kind of athlete or person do you want to become?",                      hint:"Describe the version of yourself this training is building toward.", tall:false, required:false },
];

function TrainingQuestionnaire() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState({ name: "", email: "" });
  const [answers, setAnswers] = useState({ q1:"",q2:"",q3:"",q4:"",q5:"",q6:"",q7:"",q8:"",q9:"",q10:"" });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const portalToken = new URLSearchParams(window.location.search).get("token") || undefined;
  const setA = (k: string, v: string) => { setAnswers(a => ({ ...a, [k]: v })); setErrs(prev => { const { [k]: _, ...rest } = prev; return rest; }); };

  const handleSubmit = async () => {
    const e: Record<string, string> = {};
    if (!info.name.trim()) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.email.trim())) e.email = "Please enter a valid email address.";
    TQ_QDATA.filter(q => q.required && !(answers as any)[q.key].trim()).forEach(q => { e[q.key] = "Please answer this question."; });
    if (Object.keys(e).length) { setErrs(e); window.scrollTo(0, 0); return; }
    setLoading(true);
    try {
      await fetch(`${API}/api/training-questionnaire`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ name: info.name, email: info.email, answers, portalToken }),
      });
    } catch {}
    setLoading(false); setSubmitted(true);
  };

  if (submitted) return <NiDone title="Answers received" body="I'll review your answers and build a personalised training analysis based on what you've shared." />;

  return (
    <NiPage
      title="Training Questionnaire"
      intro="Take your time. The more honest and specific your answers, the more accurate your training analysis will be. There are no wrong answers — these questions build your coaching profile, they don't judge you."
      footer={niSubmitBtn(loading ? "Sending…" : "Submit questionnaire", handleSubmit, loading)}
    >
      {Object.keys(errs).length > 0 && (
        <div role="alert" style={{ fontSize: 13.5, color: NP.danger, background: "#F6ECE6", border: "1px solid #DFC5BB", borderRadius: 10, padding: "12px 14px", marginBottom: 16, lineHeight: 1.5 }}>
          A few answers are still needed — they're marked below.
        </div>
      )}
      <div style={niCard}>
        <NiQ label="Full name" error={errs.name} id="tq-name"><NiText id="tq-name" value={info.name} onChange={v => { setInfo(i => ({ ...i, name: v })); setErrs(p => { const { name, ...r } = p; return r; }); }} /></NiQ>
        <NiQ label="Email" error={errs.email} id="tq-email"><NiText id="tq-email" type="email" value={info.email} onChange={v => { setInfo(i => ({ ...i, email: v })); setErrs(p => { const { email, ...r } = p; return r; }); }} /></NiQ>
      </div>
      <div style={niCard}>
        {TQ_QDATA.map((q, i) => (
          <NiQ key={q.key} label={`${i + 1}. ${q.label}`} help={q.hint} optional={!q.required} error={errs[q.key]} id={`tq-${q.key}`}>
            <NiArea id={`tq-${q.key}`} value={(answers as any)[q.key]} onChange={v => setA(q.key, v)} tall={q.tall} />
          </NiQ>
        ))}
      </div>
      <p style={{ fontSize: 12.5, color: NP.inkMuted, lineHeight: 1.6, textAlign: "center" as const }}>Your answers go directly to your coach. A personalised analysis will follow.</p>
    </NiPage>
  );
}

// ═══ Nutrition Intake — /nutrition ════════════════════════════════════════════
// Light "paper" variant of the design system: health forms read better on a calm
// light surface, and the same palette drives the printable PDF. Structure, spacing
// and component shapes mirror the app; only the surface colors differ.
const NP = {
  bg:        "#F3EFE6",  // plaster
  card:      "#FFFDF8",  // paper
  ink:       "#1C1814",
  inkSoft:   "#4F463D",
  inkMuted:  "#8A8175",  // taupe
  line:      "#D8CFC0",  // stone
  lineSoft:  "#E4DCCE",
  green:     "#876737",  // deep bronze accent (selection/focus)
  greenDark: "#6B4F28",
  greenSoft: "#F1EADA",  // soft gold wash
  danger:    "#9B3F32",  // oxblood
  navy:      "#33477A",  // lapis — informational/neutral-status accent (never selection; that stays bronze)
  navyInk:   "#23324F",  // text-legible navy for small copy
  navyBg:    "rgba(35,50,79,0.07)",
  navyBorder:"rgba(51,71,122,0.30)",
};
const NP_SANS  = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const NP_SERIF = "'Playfair Display', Didot, Georgia, 'Times New Roman', serif";
const niInput: React.CSSProperties = { width: "100%", background: NP.card, borderWidth: 1, borderStyle: "solid", borderColor: NP.line, borderRadius: 10, padding: "13px 14px", color: NP.ink, fontSize: 16, outline: "none", boxSizing: "border-box", fontFamily: NP_SANS, WebkitAppearance: "none" as any, appearance: "none" as any };

function NiQ({ label, help, optional, error, children, id }: { label: string; help?: string; optional?: boolean; error?: string; children: React.ReactNode; id?: string }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <label htmlFor={id} style={{ display: "block", fontSize: 15, fontWeight: 600, color: NP.ink, lineHeight: 1.45, marginBottom: help ? 4 : 10 }}>
        {label}{optional && <span style={{ fontWeight: 400, fontSize: 12, color: NP.inkMuted, marginLeft: 8 }}>Optional</span>}
      </label>
      {help && <div style={{ fontSize: 13, color: NP.inkMuted, lineHeight: 1.5, marginBottom: 10 }}>{help}</div>}
      {children}
      {error && <div role="alert" style={{ fontSize: 12.5, color: NP.danger, marginTop: 7, fontWeight: 500 }}>{error}</div>}
    </div>
  );
}
function NiText({ id, value, onChange, type, placeholder, suffix }: { id?: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; suffix?: string }) {
  const [focus, setFocus] = useState(false);
  const el = (
    <input id={id} type={type || "text"} value={value} placeholder={placeholder}
      inputMode={type === "number" ? "decimal" as const : undefined}
      onChange={e => onChange(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ ...niInput, ...(type === "number" ? { MozAppearance: "textfield" as any } : {}), ...(focus ? { borderColor: NP.green, boxShadow: `0 0 0 3px ${NP.greenSoft}` } : {}) }} />
  );
  if (!suffix) return el;
  return (
    <div style={{ position: "relative" }}>
      {el}
      <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: NP.inkMuted, pointerEvents: "none" }}>{suffix}</span>
    </div>
  );
}
function NiArea({ id, value, onChange, placeholder, tall }: { id?: string; value: string; onChange: (v: string) => void; placeholder?: string; tall?: boolean }) {
  const [focus, setFocus] = useState(false);
  return (
    <textarea id={id} value={value} placeholder={placeholder} rows={tall ? 4 : 2}
      onChange={e => { onChange(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight + 2, 320) + "px"; }}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ ...niInput, resize: "vertical", lineHeight: 1.55, minHeight: tall ? 104 : 60, ...(focus ? { borderColor: NP.green, boxShadow: `0 0 0 3px ${NP.greenSoft}` } : {}) }} />
  );
}
function NiRadio({ name, options, value, onChange }: { name: string; options: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div role="radiogroup" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {options.map(o => {
        const sel = value === o;
        return (
          <label key={o} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, cursor: "pointer", background: sel ? NP.greenSoft : NP.card, border: `1px solid ${sel ? NP.green : NP.line}`, transition: "border-color 0.12s, background 0.12s" }}>
            <input type="radio" name={name} checked={sel} onChange={() => onChange(o)} style={{ accentColor: NP.green, width: 18, height: 18, margin: 0, flexShrink: 0 }} />
            <span style={{ fontSize: 15, color: NP.ink, lineHeight: 1.4 }}>{o}</span>
          </label>
        );
      })}
    </div>
  );
}
function NiChecks({ options, value, onChange }: { options: string[]; value: string; onChange: (v: string) => void }) {
  const selected = value ? value.split(", ").filter(Boolean) : [];
  const toggle = (o: string) => {
    const next = selected.includes(o) ? selected.filter(s => s !== o) : [...selected, o];
    onChange(options.filter(x => next.includes(x)).join(", "));
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {options.map(o => {
        const sel = selected.includes(o);
        return (
          <label key={o} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, cursor: "pointer", background: sel ? NP.greenSoft : NP.card, border: `1px solid ${sel ? NP.green : NP.line}`, transition: "border-color 0.12s, background 0.12s" }}>
            <input type="checkbox" checked={sel} onChange={() => toggle(o)} style={{ accentColor: NP.green, width: 18, height: 18, margin: 0, flexShrink: 0 }} />
            <span style={{ fontSize: 15, color: NP.ink, lineHeight: 1.4 }}>{o}</span>
          </label>
        );
      })}
    </div>
  );
}
function NiScale({ value, onChange, low, high }: { value: string; onChange: (v: string) => void; low: string; high: string }) {
  return (
    <div>
      <div role="radiogroup" aria-label={`${low} to ${high}`} style={{ display: "flex", gap: 5 }}>
        {Array.from({ length: 10 }, (_, i) => String(i + 1)).map(n => {
          const sel = value === n;
          return (
            <button key={n} type="button" role="radio" aria-checked={sel} onClick={() => onChange(n)}
              style={{ flex: 1, minWidth: 0, height: 46, borderRadius: 10, fontSize: 14, fontWeight: sel ? 700 : 500, cursor: "pointer", fontFamily: NP_SANS, background: sel ? NP.green : NP.card, color: sel ? NP.card : NP.inkSoft, border: `1px solid ${sel ? NP.green : NP.line}`, transition: "background 0.12s, border-color 0.12s", padding: 0 }}>
              {n}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7 }}>
        <span style={{ fontSize: 12, color: NP.inkMuted }}>{low}</span>
        <span style={{ fontSize: 12, color: NP.inkMuted }}>{high}</span>
      </div>
    </div>
  );
}

// Shared confirmation screen for the public forms (intake, questionnaire, nutrition)
function NiDone({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ minHeight: "100vh", background: NP.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 32, fontFamily: NP_SANS }}>
      <div style={{ maxWidth: 440, textAlign: "center" as const }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: NP.greenSoft, border: `1px solid ${NP.green}`, color: NP.green, fontSize: 26, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>✓</div>
        <h2 style={{ fontFamily: NP_SERIF, fontSize: 30, fontWeight: 600, color: NP.ink, margin: "0 0 12px" }}>{title}</h2>
        <p style={{ fontSize: 15, color: NP.inkSoft, lineHeight: 1.7, margin: 0 }}>{body}</p>
        <MetaLogo size={40} color={NP.green} style={{ margin: "36px auto 10px" }} />
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, color: NP.inkMuted, textTransform: "uppercase" as const }}>Joey Rouhana Coaching</div>
      </div>
    </div>
  );
}
// Shared page chrome for the single-page public forms
function NiPage({ title, intro, children, footer }: { title: string; intro: string; children: React.ReactNode; footer: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", background: NP.bg, color: NP.ink, fontFamily: NP_SANS }}>
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "28px 20px 130px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 26 }}>
          <MetaLogo size={22} color={NP.green} />
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, color: NP.inkMuted, textTransform: "uppercase" as const }}>Joey Rouhana Coaching</div>
        </div>
        <h1 style={{ fontFamily: NP_SERIF, fontSize: 34, fontWeight: 600, color: NP.ink, lineHeight: 1.2, margin: "0 0 14px" }}>{title}</h1>
        <p style={{ fontSize: 14.5, color: NP.inkSoft, lineHeight: 1.7, margin: "0 0 26px" }}>{intro}</p>
        {children}
      </div>
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: NP.card, borderTop: `1px solid ${NP.line}`, padding: "14px 20px calc(14px + env(safe-area-inset-bottom))" }}>
        <div style={{ maxWidth: 620, margin: "0 auto", display: "flex", gap: 10, alignItems: "center" }}>{footer}</div>
      </div>
    </div>
  );
}
function niSubmitBtn(label: string, onClick: () => void, disabled?: boolean): React.ReactNode {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{ flex: 1, background: disabled ? NP.line : NP.ink, color: disabled ? NP.inkMuted : NP.card, border: "none", borderRadius: 11, padding: "15px 0", fontSize: 16, fontWeight: 600, cursor: disabled ? "default" : "pointer", fontFamily: NP_SANS }}>{label}</button>
  );
}
const niCard: React.CSSProperties = { background: NP.card, border: `1px solid ${NP.line}`, borderRadius: 14, padding: "24px 20px 6px", marginBottom: 16 };

const NI_SECTIONS = [
  "Personal details", "Your goals", "Health & medical", "Diet history", "A typical day of eating",
  "Drinks & supplements", "Food preferences", "Lifestyle & recovery", "Habits & relationship with food", "Readiness & practical",
];

function NutritionIntakeForm() {
  const portalToken = new URLSearchParams(window.location.search).get("token") || undefined;
  const draftKey = `nutrition-intake-draft-${portalToken || "anon"}`;
  const [step, setStep] = useState(0); // 0 welcome · 1–10 sections · 11 review · 12 done
  const [a, setA] = useState<Record<string, string>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [restored, setRestored] = useState(false);
  const savedOnce = useRef(false);

  const set = (k: string, v: string) => { setA(prev => ({ ...prev, [k]: v })); setErrs(prev => { const { [k]: _, ...rest } = prev; return rest; }); };
  const g = (k: string) => a[k] || "";

  // Restore draft once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.a && typeof d.a === "object") { setA(d.a); if (typeof d.step === "number" && d.step > 0 && d.step <= 11) setStep(d.step); setRestored(true); }
      }
    } catch {}
  }, []);
  // Autosave on every change
  useEffect(() => {
    if (Object.keys(a).length === 0 && step === 0) return;
    try { localStorage.setItem(draftKey, JSON.stringify({ a, step: Math.min(step, 11), savedAt: Date.now() })); savedOnce.current = true; } catch {}
  }, [a, step, draftKey]);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const isFemale = g("sex") === "Female";
  const validators: Record<number, () => Record<string, string>> = {
    0: () => {
      const e: Record<string, string> = {};
      if (!g("name").trim()) e.name = "Please enter your name.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(g("email").trim())) e.email = "Please enter a valid email address.";
      return e;
    },
    1: () => {
      const e: Record<string, string> = {};
      if (!g("dob")) e.dob = "Please enter your date of birth.";
      if (!g("heightCm").trim() || isNaN(Number(g("heightCm")))) e.heightCm = "Please enter your height as a number.";
      if (!g("weightKg").trim() || isNaN(Number(g("weightKg")))) e.weightKg = "Please enter your weight as a number.";
      return e;
    },
    2: () => {
      const e: Record<string, string> = {};
      if (!g("goalPrimary")) e.goalPrimary = "Please choose your primary goal.";
      if (g("goalPrimary") === "Other" && !g("goalPrimaryOther").trim()) e.goalPrimaryOther = "Please describe your goal.";
      return e;
    },
    10: () => {
      const e: Record<string, string> = {};
      if (!g("motivation")) e.motivation = "Please pick a number from 1 to 10.";
      if (!g("confidence")) e.confidence = "Please pick a number from 1 to 10.";
      return e;
    },
  };
  const next = () => {
    const v = validators[step] ? validators[step]() : {};
    if (Object.keys(v).length) { setErrs(v); return; }
    setErrs({});
    setStep(s => Math.min(s + 1, 11));
  };
  const back = () => { setErrs({}); setStep(s => Math.max(s - 1, 0)); };

  // Ordered question-label → answer map (only answered + currently-relevant questions)
  const labeledAnswers = (): Record<string, string> => {
    const out: Record<string, string> = {};
    const put = (label: string, key: string, cond = true) => { if (cond && g(key).trim()) out[label] = g(key).trim(); };
    put("Date of birth", "dob");
    put("Height (cm)", "heightCm");
    put("Weight (kg)", "weightKg");
    put("Sex", "sex");
    put("Occupation & working hours", "occupation");
    put("Primary goal", "goalPrimary");
    put("Primary goal — other", "goalPrimaryOther", g("goalPrimary") === "Other");
    put("What success looks like in 3–6 months", "goalSuccess");
    put("Target weight or look", "goalTarget");
    put("Why this goal matters now", "goalWhy");
    put("Medical conditions", "medConditions");
    put("Medical conditions — details", "medConditionsDetail", g("medConditions") === "Yes");
    put("Taking medication", "meds");
    put("Medication — details", "medsDetail", g("meds") === "Yes");
    put("Allergies or intolerances", "allergies");
    put("Allergies — details", "allergiesDetail", g("allergies") === "Yes");
    put("Digestion", "digestion");
    put("Digestion — other", "digestionOther", g("digestion").includes("Other"));
    put("Cycle / pregnancy / menopause notes", "femaleHealth", isFemale);
    put("Followed diets before", "dietsBefore");
    put("Which diets", "dietsBeforeDetail", g("dietsBefore") === "Yes");
    put("What worked and what didn't", "dietsWorked", g("dietsBefore") === "Yes");
    put("Weight over past 12 months", "weightTrend");
    put("Tracked calories or macros before", "tracked");
    put("Tracking experience", "trackedDetail", g("tracked") === "Yes");
    put("Typical breakfast", "mealBreakfast");
    put("Typical morning snack", "mealSnackAm");
    put("Typical lunch", "mealLunch");
    put("Typical afternoon snack", "mealSnackPm");
    put("Typical dinner", "mealDinner");
    put("Typical evening / late snack", "mealLate");
    put("Weekends vs weekdays", "weekendDiff");
    put("Meals out or ordered in per week", "eatOut");
    put("Who cooks & shops", "cooking");
    put("Water per day", "water");
    put("Coffee / tea / energy drinks", "caffeine");
    put("Alcoholic drinks per week", "alcohol");
    put("Takes supplements", "supplements");
    put("Supplements — details", "supplementsDetail", g("supplements") === "Yes");
    put("Style of eating", "eatingStyle");
    put("Style of eating — other", "eatingStyleOther", g("eatingStyle") === "Other");
    put("Foods loved", "foodsLove");
    put("Foods disliked", "foodsHate");
    put("Favourite cuisines / go-to meals", "cuisines");
    put("Sleep hours & quality", "sleep");
    put("Stress level (1–10)", "stress");
    put("Daily activity outside training", "activity");
    put("Training per week", "training");
    put("Eats from emotion or boredom", "emotionalEating");
    put("When most likely to snack or overeat", "snackTriggers");
    put("Biggest thing holding nutrition back", "biggestObstacle");
    put("Motivation to change (1–10)", "motivation");
    put("Confidence to stick to a plan (1–10)", "confidence");
    put("Time for meal prep per week", "prepTime");
    put("Plan preference", "planPref");
    put("Anything else", "anythingElse");
    return out;
  };

  const handleSubmit = async () => {
    setSaving(true); setSubmitError("");
    try {
      const r = await fetch(`${API}/api/nutrition-intake`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: g("name").trim(), email: g("email").trim(), portalToken, answers: labeledAnswers() }),
      });
      if (!r.ok) throw new Error();
      try { localStorage.removeItem(draftKey); } catch {}
      setStep(12);
    } catch {
      setSubmitError("Your answers couldn't be sent. Please check your connection and try again — nothing has been lost.");
    }
    setSaving(false);
  };

  // ── Section bodies ──
  const sectionBody = (s: number): React.ReactNode => {
    switch (s) {
      case 1: return (<>
        <NiQ label="Date of birth" error={errs.dob} id="ni-dob"><NiText id="ni-dob" type="date" value={g("dob")} onChange={v => set("dob", v)} /></NiQ>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <NiQ label="Height" error={errs.heightCm} id="ni-height"><NiText id="ni-height" type="number" value={g("heightCm")} onChange={v => set("heightCm", v)} suffix="cm" /></NiQ>
          <NiQ label="Current weight" error={errs.weightKg} id="ni-weight"><NiText id="ni-weight" type="number" value={g("weightKg")} onChange={v => set("weightKg", v)} suffix="kg" /></NiQ>
        </div>
        <NiQ label="Sex" help="Used only to tailor nutrition guidance." optional><NiRadio name="sex" options={["Male", "Female", "Prefer not to say"]} value={g("sex")} onChange={v => set("sex", v)} /></NiQ>
        <NiQ label="Occupation & typical working hours" help="e.g. office job, 9–6, one evening shift a week." optional id="ni-occ"><NiText id="ni-occ" value={g("occupation")} onChange={v => set("occupation", v)} /></NiQ>
      </>);
      case 2: return (<>
        <NiQ label="What is your primary goal?" error={errs.goalPrimary}><NiRadio name="goalPrimary" options={["Fat loss", "Muscle gain", "Body recomposition", "Performance", "Health & energy", "Other"]} value={g("goalPrimary")} onChange={v => set("goalPrimary", v)} /></NiQ>
        {g("goalPrimary") === "Other" && <NiQ label="Tell me more about your goal" error={errs.goalPrimaryOther} id="ni-goalother"><NiText id="ni-goalother" value={g("goalPrimaryOther")} onChange={v => set("goalPrimaryOther", v)} /></NiQ>}
        <NiQ label="In your own words, what would success look like in 3–6 months?" optional id="ni-success"><NiArea id="ni-success" value={g("goalSuccess")} onChange={v => set("goalSuccess", v)} tall /></NiQ>
        <NiQ label="Do you have a target weight or look in mind?" optional id="ni-target"><NiText id="ni-target" value={g("goalTarget")} onChange={v => set("goalTarget", v)} /></NiQ>
        <NiQ label="Why is this goal important to you right now?" optional id="ni-why"><NiArea id="ni-why" value={g("goalWhy")} onChange={v => set("goalWhy", v)} /></NiQ>
      </>);
      case 3: return (<>
        <NiQ label="Do you have any medical conditions I should know about?" help="e.g. diabetes, thyroid, blood pressure."><NiRadio name="medConditions" options={["No", "Yes"]} value={g("medConditions")} onChange={v => set("medConditions", v)} /></NiQ>
        {g("medConditions") === "Yes" && <NiQ label="Which conditions?" id="ni-medc"><NiArea id="ni-medc" value={g("medConditionsDetail")} onChange={v => set("medConditionsDetail", v)} /></NiQ>}
        <NiQ label="Are you currently taking any medication?"><NiRadio name="meds" options={["No", "Yes"]} value={g("meds")} onChange={v => set("meds", v)} /></NiQ>
        {g("meds") === "Yes" && <NiQ label="Which medication?" id="ni-meds"><NiArea id="ni-meds" value={g("medsDetail")} onChange={v => set("medsDetail", v)} /></NiQ>}
        <NiQ label="Do you have any food allergies or intolerances?"><NiRadio name="allergies" options={["No", "Yes"]} value={g("allergies")} onChange={v => set("allergies", v)} /></NiQ>
        {g("allergies") === "Yes" && <NiQ label="Which foods?" help="e.g. lactose, gluten, nuts." id="ni-allergy"><NiArea id="ni-allergy" value={g("allergiesDetail")} onChange={v => set("allergiesDetail", v)} /></NiQ>}
        <NiQ label="How would you describe your digestion?" help="Select all that apply."><NiChecks options={["No issues", "Bloating", "Irregular", "Reflux", "Other"]} value={g("digestion")} onChange={v => set("digestion", v)} /></NiQ>
        {g("digestion").includes("Other") && <NiQ label="Tell me more about your digestion" id="ni-digother"><NiText id="ni-digother" value={g("digestionOther")} onChange={v => set("digestionOther", v)} /></NiQ>}
        {isFemale && <NiQ label="Anything relevant around your cycle, pregnancy, or menopause?" optional id="ni-fem"><NiArea id="ni-fem" value={g("femaleHealth")} onChange={v => set("femaleHealth", v)} /></NiQ>}
      </>);
      case 4: return (<>
        <NiQ label="Have you followed any diets or nutrition plans before?"><NiRadio name="dietsBefore" options={["No", "Yes"]} value={g("dietsBefore")} onChange={v => set("dietsBefore", v)} /></NiQ>
        {g("dietsBefore") === "Yes" && (<>
          <NiQ label="Which ones?" id="ni-diets"><NiText id="ni-diets" value={g("dietsBeforeDetail")} onChange={v => set("dietsBeforeDetail", v)} /></NiQ>
          <NiQ label="What worked well for you — and what didn't?" optional id="ni-dietsworked"><NiArea id="ni-dietsworked" value={g("dietsWorked")} onChange={v => set("dietsWorked", v)} tall /></NiQ>
        </>)}
        <NiQ label="How has your weight changed over the past 12 months?"><NiRadio name="weightTrend" options={["Stable", "Gained", "Lost", "Up and down"]} value={g("weightTrend")} onChange={v => set("weightTrend", v)} /></NiQ>
        <NiQ label="Have you ever tracked calories or macros?"><NiRadio name="tracked" options={["No", "Yes"]} value={g("tracked")} onChange={v => set("tracked", v)} /></NiQ>
        {g("tracked") === "Yes" && <NiQ label="How did you find tracking?" help="Did it work for you? Would you want it as part of your plan?" optional id="ni-tracked"><NiArea id="ni-tracked" value={g("trackedDetail")} onChange={v => set("trackedDetail", v)} /></NiQ>}
      </>);
      case 5: return (<>
        <div style={{ fontSize: 13.5, color: NP.inkSoft, lineHeight: 1.6, background: NP.greenSoft, border: `1px solid ${NP.lineSoft}`, borderRadius: 10, padding: "12px 14px", marginBottom: 24 }}>
          Describe an average weekday. Don't clean it up — the real version is the useful version. Include drinks and rough portions where you can.
        </div>
        {([["mealBreakfast", "Breakfast"], ["mealSnackAm", "Morning snack"], ["mealLunch", "Lunch"], ["mealSnackPm", "Afternoon snack"], ["mealDinner", "Dinner"], ["mealLate", "Evening / late snack"]] as const).map(([k, label]) => (
          <NiQ key={k} label={label} optional={k === "mealSnackAm" || k === "mealSnackPm" || k === "mealLate"} id={`ni-${k}`}>
            <NiArea id={`ni-${k}`} value={g(k)} onChange={v => set(k, v)} placeholder="What do you typically eat & drink?" />
          </NiQ>
        ))}
        <NiQ label="How different are your weekends from weekdays?" optional id="ni-weekend"><NiArea id="ni-weekend" value={g("weekendDiff")} onChange={v => set("weekendDiff", v)} /></NiQ>
        <NiQ label="How many meals do you eat out or order in per week?"><NiRadio name="eatOut" options={["0–1", "2–3", "4–6", "7 or more"]} value={g("eatOut")} onChange={v => set("eatOut", v)} /></NiQ>
        <NiQ label="Who does the cooking and grocery shopping in your household?" optional id="ni-cooking"><NiText id="ni-cooking" value={g("cooking")} onChange={v => set("cooking", v)} /></NiQ>
      </>);
      case 6: return (<>
        <NiQ label="How much water do you drink per day, roughly?"><NiRadio name="water" options={["Less than 1 litre", "1–2 litres", "2–3 litres", "More than 3 litres"]} value={g("water")} onChange={v => set("water", v)} /></NiQ>
        <NiQ label="Coffee, tea or energy drinks — how many per day, and with sugar or milk?" optional id="ni-caffeine"><NiText id="ni-caffeine" value={g("caffeine")} onChange={v => set("caffeine", v)} placeholder="e.g. 3 coffees with milk, 1 energy drink" /></NiQ>
        <NiQ label="How many alcoholic drinks in a typical week?"><NiRadio name="alcohol" options={["None", "1–3", "4–7", "8 or more"]} value={g("alcohol")} onChange={v => set("alcohol", v)} /></NiQ>
        <NiQ label="Do you currently take any supplements?"><NiRadio name="supplements" options={["No", "Yes"]} value={g("supplements")} onChange={v => set("supplements", v)} /></NiQ>
        {g("supplements") === "Yes" && <NiQ label="Which supplements?" help="e.g. protein, creatine, vitamins." id="ni-supps"><NiArea id="ni-supps" value={g("supplementsDetail")} onChange={v => set("supplementsDetail", v)} /></NiQ>}
      </>);
      case 7: return (<>
        <NiQ label="Do you follow a specific style of eating?"><NiRadio name="eatingStyle" options={["No restrictions", "Vegetarian", "Vegan", "Halal", "Other"]} value={g("eatingStyle")} onChange={v => set("eatingStyle", v)} /></NiQ>
        {g("eatingStyle") === "Other" && <NiQ label="Which style?" id="ni-styleother"><NiText id="ni-styleother" value={g("eatingStyleOther")} onChange={v => set("eatingStyleOther", v)} /></NiQ>}
        <NiQ label="Foods you love and could eat every day" optional id="ni-love"><NiArea id="ni-love" value={g("foodsLove")} onChange={v => set("foodsLove", v)} /></NiQ>
        <NiQ label="Foods you dislike or refuse to eat" optional id="ni-hate"><NiArea id="ni-hate" value={g("foodsHate")} onChange={v => set("foodsHate", v)} /></NiQ>
        <NiQ label="Favourite cuisines or go-to meals" optional id="ni-cuisines"><NiText id="ni-cuisines" value={g("cuisines")} onChange={v => set("cuisines", v)} /></NiQ>
      </>);
      case 8: return (<>
        <NiQ label="How many hours do you sleep on average, and how is the quality?" optional id="ni-sleep"><NiText id="ni-sleep" value={g("sleep")} onChange={v => set("sleep", v)} placeholder="e.g. 7 hours, wake up once" /></NiQ>
        <NiQ label="How would you rate your stress level right now?"><NiScale value={g("stress")} onChange={v => set("stress", v)} low="Fully relaxed" high="Maxed out" /></NiQ>
        <NiQ label="Outside of training, how active is your day?" help="Steps, desk job, physical work." optional id="ni-activity"><NiArea id="ni-activity" value={g("activity")} onChange={v => set("activity", v)} /></NiQ>
        <NiQ label="How many times per week do you currently train, and what type?" optional id="ni-training"><NiText id="ni-training" value={g("training")} onChange={v => set("training", v)} placeholder="e.g. 3× gym, 1× padel" /></NiQ>
      </>);
      case 9: return (<>
        <NiQ label="Do you ever eat out of boredom, stress, or emotion rather than hunger?"><NiRadio name="emotionalEating" options={["Rarely", "Sometimes", "Often"]} value={g("emotionalEating")} onChange={v => set("emotionalEating", v)} /></NiQ>
        <NiQ label="When are you most likely to snack or overeat?" help="Time of day, situation, mood." optional id="ni-triggers"><NiArea id="ni-triggers" value={g("snackTriggers")} onChange={v => set("snackTriggers", v)} /></NiQ>
        <NiQ label="What do you think is the single biggest thing holding your nutrition back?" optional id="ni-obstacle"><NiArea id="ni-obstacle" value={g("biggestObstacle")} onChange={v => set("biggestObstacle", v)} tall /></NiQ>
      </>);
      case 10: return (<>
        <NiQ label="How motivated are you to change your nutrition right now?" error={errs.motivation}><NiScale value={g("motivation")} onChange={v => set("motivation", v)} low="Not at all" high="Extremely" /></NiQ>
        <NiQ label="How confident are you that you can stick to a plan?" error={errs.confidence}><NiScale value={g("confidence")} onChange={v => set("confidence", v)} low="Not confident" high="Very confident" /></NiQ>
        <NiQ label="How much time can you realistically spend on meal prep per week?"><NiRadio name="prepTime" options={["Almost none", "1–2 hours", "3–5 hours", "As much as needed"]} value={g("prepTime")} onChange={v => set("prepTime", v)} /></NiQ>
        <NiQ label="What would work best for you?"><NiRadio name="planPref" options={["A flexible plan with guidelines", "A structured meal plan", "Macro targets to track"]} value={g("planPref")} onChange={v => set("planPref", v)} /></NiQ>
        <NiQ label="Anything else you want me to know before we start?" optional id="ni-else"><NiArea id="ni-else" value={g("anythingElse")} onChange={v => set("anythingElse", v)} /></NiQ>
      </>);
      default: return null;
    }
  };

  const page = (content: React.ReactNode, nav: React.ReactNode) => (
    <div style={{ minHeight: "100vh", background: NP.bg, color: NP.ink, fontFamily: NP_SANS }}>
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "28px 20px 120px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 26 }}>
          <MetaLogo size={22} color={NP.green} />
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, color: NP.inkMuted, textTransform: "uppercase" as const }}>Joey Rouhana Coaching</div>
        </div>
        {content}
      </div>
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: NP.card, borderTop: `1px solid ${NP.line}`, padding: "14px 20px calc(14px + env(safe-area-inset-bottom))" }}>
        <div style={{ maxWidth: 620, margin: "0 auto", display: "flex", gap: 10, alignItems: "center" }}>{nav}</div>
      </div>
    </div>
  );
  const primaryBtn = (label: string, onClick: () => void, disabled?: boolean): React.ReactNode => (
    <button type="button" onClick={onClick} disabled={disabled} style={{ flex: 1, background: disabled ? NP.line : NP.ink, color: disabled ? NP.inkMuted : NP.card, border: "none", borderRadius: 11, padding: "15px 0", fontSize: 16, fontWeight: 600, cursor: disabled ? "default" : "pointer", fontFamily: NP_SANS }}>{label}</button>
  );
  const backBtn = (
    <button type="button" onClick={back} style={{ background: "transparent", color: NP.inkSoft, border: `1px solid ${NP.line}`, borderRadius: 11, padding: "15px 22px", fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: NP_SANS }}>Back</button>
  );
  const progress = (label: string, frac: number) => (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: NP.green }}>{label}</span>
        {savedOnce.current && <span style={{ fontSize: 11.5, color: NP.navy }}>Saved automatically ✓</span>}
      </div>
      <div style={{ height: 4, background: NP.lineSoft, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.round(frac * 100)}%`, background: NP.green, borderRadius: 2, transition: "width 0.25s" }} />
      </div>
    </div>
  );

  // ── Done ──
  if (step === 12) return (
    <div style={{ minHeight: "100vh", background: NP.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 32, fontFamily: NP_SANS }}>
      <div style={{ maxWidth: 440, textAlign: "center" as const }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: NP.greenSoft, border: `1px solid ${NP.green}`, color: NP.green, fontSize: 26, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>✓</div>
        <h2 style={{ fontFamily: NP_SERIF, fontSize: 30, fontWeight: 600, color: NP.ink, margin: "0 0 12px" }}>Thank you</h2>
        <p style={{ fontSize: 15, color: NP.inkSoft, lineHeight: 1.7, margin: 0 }}>Your nutrition intake has been received. I'll review your answers and we'll go through them together in your intake session.</p>
        <MetaLogo size={40} color={NP.green} style={{ margin: "36px auto 10px" }} />
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, color: NP.inkMuted, textTransform: "uppercase" as const }}>Joey Rouhana Coaching</div>
      </div>
    </div>
  );

  // ── Welcome ──
  if (step === 0) return page(
    <>
      <h1 style={{ fontFamily: NP_SERIF, fontSize: 34, fontWeight: 600, color: NP.ink, lineHeight: 1.2, margin: "0 0 14px" }}>Nutrition Intake</h1>
      <p style={{ fontSize: 15, color: NP.inkSoft, lineHeight: 1.7, margin: "0 0 8px" }}>
        This questionnaire helps me understand how you currently eat, live and recover, so your nutrition plan fits your real life — not an ideal version of it.
      </p>
      <p style={{ fontSize: 13.5, color: NP.inkMuted, lineHeight: 1.7, margin: "0 0 28px" }}>
        10 short sections · about 10 minutes · your answers save automatically on this device, so you can leave and come back.
      </p>
      {restored && <div style={{ fontSize: 13, color: NP.navyInk, background: NP.navyBg, border: `1px solid ${NP.navyBorder}`, borderRadius: 10, padding: "10px 14px", marginBottom: 22 }}>Welcome back — your earlier answers were restored.</div>}
      <div style={{ background: NP.card, border: `1px solid ${NP.line}`, borderRadius: 14, padding: "22px 20px" }}>
        <NiQ label="Full name" error={errs.name} id="ni-name"><NiText id="ni-name" value={g("name")} onChange={v => set("name", v)} /></NiQ>
        <div style={{ marginBottom: -18 }}>
          <NiQ label="Email" error={errs.email} id="ni-email"><NiText id="ni-email" type="email" value={g("email")} onChange={v => set("email", v)} /></NiQ>
        </div>
      </div>
      <p style={{ fontSize: 12.5, color: NP.inkMuted, lineHeight: 1.6, marginTop: 18 }}>
        There are no wrong answers — this is about where you are right now, not where you think you should be.
      </p>
    </>,
    primaryBtn("Start", next)
  );

  // ── Review ──
  if (step === 11) {
    const la = labeledAnswers();
    const sectionKeys: [string, string[]][] = [
      ["Personal details", ["Date of birth", "Height (cm)", "Weight (kg)", "Sex", "Occupation & working hours"]],
      ["Your goals", ["Primary goal", "Primary goal — other", "What success looks like in 3–6 months", "Target weight or look", "Why this goal matters now"]],
      ["Health & medical", ["Medical conditions", "Medical conditions — details", "Taking medication", "Medication — details", "Allergies or intolerances", "Allergies — details", "Digestion", "Digestion — other", "Cycle / pregnancy / menopause notes"]],
      ["Diet history", ["Followed diets before", "Which diets", "What worked and what didn't", "Weight over past 12 months", "Tracked calories or macros before", "Tracking experience"]],
      ["A typical day of eating", ["Typical breakfast", "Typical morning snack", "Typical lunch", "Typical afternoon snack", "Typical dinner", "Typical evening / late snack", "Weekends vs weekdays", "Meals out or ordered in per week", "Who cooks & shops"]],
      ["Drinks & supplements", ["Water per day", "Coffee / tea / energy drinks", "Alcoholic drinks per week", "Takes supplements", "Supplements — details"]],
      ["Food preferences", ["Style of eating", "Style of eating — other", "Foods loved", "Foods disliked", "Favourite cuisines / go-to meals"]],
      ["Lifestyle & recovery", ["Sleep hours & quality", "Stress level (1–10)", "Daily activity outside training", "Training per week"]],
      ["Habits & relationship with food", ["Eats from emotion or boredom", "When most likely to snack or overeat", "Biggest thing holding nutrition back"]],
      ["Readiness & practical", ["Motivation to change (1–10)", "Confidence to stick to a plan (1–10)", "Time for meal prep per week", "Plan preference", "Anything else"]],
    ];
    return page(
      <>
        {progress("Review", 1)}
        <h1 style={{ fontFamily: NP_SERIF, fontSize: 28, fontWeight: 600, color: NP.ink, lineHeight: 1.25, margin: "0 0 8px" }}>Review your answers</h1>
        <p style={{ fontSize: 14, color: NP.inkSoft, lineHeight: 1.6, margin: "0 0 24px" }}>Check everything looks right. You can go back to any section before sending.</p>
        {sectionKeys.map(([title, keys], i) => {
          const rows = keys.filter(k => la[k]);
          return (
            <div key={title} style={{ background: NP.card, border: `1px solid ${NP.line}`, borderRadius: 14, padding: "18px 20px", marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: rows.length ? 12 : 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: NP.ink }}>{i + 1}. {title}</div>
                <button type="button" onClick={() => setStep(i + 1)} style={{ background: "transparent", border: "none", color: NP.green, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "4px 0", fontFamily: NP_SANS }}>Edit</button>
              </div>
              {rows.length === 0
                ? <div style={{ fontSize: 13, color: NP.inkMuted }}>No answers in this section.</div>
                : rows.map(k => (
                  <div key={k} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: NP.inkMuted, textTransform: "uppercase" as const, letterSpacing: 0.6, marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 14, color: NP.ink, lineHeight: 1.55, whiteSpace: "pre-wrap" as const }}>{la[k]}</div>
                  </div>
                ))}
            </div>
          );
        })}
        <div style={{ background: NP.greenSoft, border: `1px solid ${NP.line}`, borderRadius: 14, padding: "16px 18px", marginTop: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: NP.ink, marginBottom: 6 }}>Privacy</div>
          <div style={{ fontSize: 13, color: NP.inkSoft, lineHeight: 1.6 }}>
            Your medical and nutrition information is confidential. It's used only to personalise your coaching and is never shared. This questionnaire supports coaching — it isn't a medical or diagnostic assessment.
          </div>
        </div>
        {submitError && <div role="alert" style={{ fontSize: 13.5, color: NP.danger, background: "#F6ECE6", border: `1px solid #DFC5BB`, borderRadius: 10, padding: "12px 14px", marginTop: 14, lineHeight: 1.5 }}>{submitError}</div>}
      </>,
      <>
        {backBtn}
        {primaryBtn(saving ? "Sending…" : "Submit", handleSubmit, saving)}
      </>
    );
  }

  // ── Sections 1–10 ──
  return page(
    <>
      {progress(`Step ${step} of 10`, step / 10)}
      <h1 style={{ fontFamily: NP_SERIF, fontSize: 27, fontWeight: 600, color: NP.ink, lineHeight: 1.25, margin: "0 0 22px" }}>{NI_SECTIONS[step - 1]}</h1>
      <div style={{ background: NP.card, border: `1px solid ${NP.line}`, borderRadius: 14, padding: "24px 20px 6px" }}>
        {sectionBody(step)}
      </div>
    </>,
    <>
      {backBtn}
      {primaryBtn(step === 10 ? "Review answers" : "Continue", next)}
    </>
  );
}

function CopyIntakeLink() {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText("https://app.joeyrouhana.com/intake");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "9px 10px", borderRadius: R.md, cursor: "pointer",
        background: copied ? C.accentBg : "transparent",
        color: copied ? C.accent : C.textMuted,
        fontSize: 13, fontWeight: copied ? 600 : 400,
        border: copied ? `1px solid ${C.accentBorder}` : "1px solid transparent",
        transition: "all 0.15s",
      }}
    >
      <span style={{ fontSize: 14, width: 18, textAlign: "center" as const, flexShrink: 0, opacity: copied ? 1 : 0.6 }}>⊞</span>
      <span>{copied ? "Link Copied!" : "Copy Intake Link"}</span>
    </button>
  );
}

function SendFormsSection({ selectedClient }: { selectedClient?: Client }) {
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState<"intake"|"checkin"|"questionnaire"|"nutrition"|null>(null);

  const copy = (type: "intake"|"checkin"|"questionnaire"|"nutrition") => {
    const urls = {
      intake: "https://app.joeyrouhana.com/intake",
      questionnaire: selectedClient?.portalToken ? `https://app.joeyrouhana.com/questionnaire?token=${selectedClient.portalToken}` : "https://app.joeyrouhana.com/questionnaire",
      nutrition: selectedClient?.portalToken ? `https://app.joeyrouhana.com/nutrition?token=${selectedClient.portalToken}` : "https://app.joeyrouhana.com/nutrition",
      checkin: selectedClient?.portalToken ? `https://app.joeyrouhana.com/client/${selectedClient.portalToken}` : null,
    };
    const url = urls[type];
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const btnBase: React.CSSProperties = {
    width: "100%", display: "flex", alignItems: "center", gap: 10,
    padding: "9px 10px", borderRadius: R.md, cursor: "pointer",
    background: "transparent", fontSize: 13, fontWeight: 400,
    border: "1px solid transparent", transition: "all 0.15s", textAlign: "left" as const,
  };

  const forms = [
    { key: "intake" as const,        icon: "📋", label: "Intake Form",           sub: "New client registration" },
    { key: "checkin" as const,       icon: "📊", label: "Weekly Assessment",     sub: selectedClient ? selectedClient.name : "Select a client first", needsClient: true },
    { key: "questionnaire" as const, icon: "🎯", label: "Training Questionnaire", sub: "10-question deep analysis" },
    { key: "nutrition" as const,     icon: "🥗", label: "Nutrition Intake",       sub: "Full nutrition & lifestyle intake" },
  ];

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ ...btnBase, color: open ? C.accent : C.textMuted, background: open ? C.accentBg : "transparent", border: open ? `1px solid ${C.accentBorder}` : "1px solid transparent", fontWeight: open ? 600 : 400 }}
      >
        <span style={{ fontSize: 14, width: 18, textAlign: "center" as const, flexShrink: 0, opacity: open ? 1 : 0.6 }}>⊞</span>
        <span>Send Forms</span>
        <span style={{ marginLeft: "auto", fontSize: 10, opacity: 0.5 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{ marginTop: 4, paddingLeft: 10, display: "flex", flexDirection: "column" as const, gap: 2 }}>
          {forms.map(f => {
            const isCopied = copied === f.key;
            const disabled = f.needsClient && !selectedClient?.portalToken;
            return (
              <button
                key={f.key}
                onClick={() => copy(f.key)}
                disabled={disabled}
                style={{ ...btnBase, color: disabled ? C.textDim : isCopied ? C.accent : C.textSecondary, background: isCopied ? C.accentBg : C.surfaceRaised, border: `1px solid ${isCopied ? C.accentBorder : C.border}`, fontWeight: isCopied ? 600 : 400, opacity: disabled ? 0.4 : 1, cursor: disabled ? "default" : "pointer" }}
              >
                <span style={{ fontSize: 13, width: 18, textAlign: "center" as const, flexShrink: 0 }}>{f.icon}</span>
                <div style={{ flex: 1 }}>
                  <div>{isCopied ? "Link Copied!" : f.label}</div>
                  <div style={{ fontSize: 10, color: C.textDim, fontWeight: 400, marginTop: 1 }}>{f.sub}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Login Page ──────────────────────────────────────────────────────────────
function LoginPage({ onLogin }: { onLogin: () => void }) {
  const [password,   setPassword]   = React.useState("");
  const [loading,    setLoading]    = React.useState(false);
  const [error,      setError]      = React.useState("");
  const [showPass,   setShowPass]   = React.useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.token) { setToken(data.token); onLogin(); }
      else setError("Incorrect password.");
    } catch { setError("Could not connect to server."); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ width: 360, padding: "40px 36px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, boxShadow: "0 24px 48px rgba(0,0,0,0.4)" }}>
        <div style={{ textAlign: "center" as const, marginBottom: 36 }}>
          <img src="/metaphysique-emblem.png" alt="Metaphysique" style={{ width: 64, height: 64, objectFit: "contain", marginBottom: 14 }} />
          <div style={{ fontFamily: F_SERIF, fontSize: 26, color: C.accent, letterSpacing: 3, marginBottom: 4 }}>JOEY ROUHANA</div>
          <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 2.5, textTransform: "uppercase" as const }}>Personal Training OS</div>
        </div>
        <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, color: C.textMuted, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" as const, marginBottom: 8 }}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoFocus
                style={{ width: "100%", background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: R.md, padding: "11px 42px 11px 14px", color: C.textPrimary, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: "none", boxSizing: "border-box" as const }}
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, color: C.textDim, display: "flex", alignItems: "center" }}
              >
                {showPass ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>
          {error && <div style={{ fontSize: 13, color: C.danger }}>{error}</div>}
          <Btn variant="primary" type="submit" disabled={loading} style={{ width: "100%", justifyContent: "center", padding: "12px 20px", fontSize: 14, marginTop: 4 }}>
            {loading ? "Signing in…" : "Sign In"}
          </Btn>
        </form>
      </div>
    </div>
  );
}

// ─── Shop Page ───────────────────────────────────────────────────────────────

function MembershipCard({ m, onSelect, isMobile }: { m: any; onSelect: (m: any) => void; isMobile?: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: m.highlight ? "rgba(176,138,74,0.06)" : IV.surface,
        border: m.highlight ? `1px solid rgba(176,138,74,0.35)` : `1px solid ${hov ? IV.borderInput : IV.border}`,
        borderRadius: 16,
        padding: isMobile ? "22px 18px 20px" : "40px 30px 32px",
        display: "flex",
        flexDirection: "column" as const,
        position: "relative" as const,
        transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
        transform: hov ? "translateY(-3px)" : "translateY(0)",
        boxShadow: m.highlight
          ? `0 0 40px rgba(176,138,74,0.14), 0 2px 20px rgba(28,24,20,0.08)`
          : hov ? "0 4px 24px rgba(28,24,20,0.10)" : "none",
        height: "100%",
        boxSizing: "border-box" as const,
      }}>
      {m.badge && (
        <div style={{ position: "absolute" as const, top: -12, left: "50%", transform: "translateX(-50%)", background: IV.accent, color: IV.textOnAccent, fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, padding: "4px 18px", borderRadius: 20, whiteSpace: "nowrap" as const }}>
          {m.badge}
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: isMobile ? 10 : 16 }}>{m.title}</div>
      <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 34 : 42, color: m.highlight ? IV.accent : IV.textPrimary, lineHeight: 1, marginBottom: 6 }}>{m.price}</div>
      <div style={{ fontSize: 12, color: IV.textDim, marginBottom: 6 }}>Billed monthly · VAT included</div>
      <div style={{ fontSize: 12, color: IV.textDim, marginBottom: isMobile ? 14 : 24 }}>{m.commitment !== undefined ? m.commitment : "Minimum commitment: 3 months"}</div>
      {m.tagline && (
        <div style={{ fontSize: 13, color: IV.textSecondary, fontStyle: "italic", marginBottom: m.blurb ? 10 : isMobile ? 14 : 24, paddingBottom: m.blurb ? 0 : isMobile ? 14 : 24, borderBottom: m.blurb ? "none" : `1px solid ${IV.border}` }}>{m.tagline}</div>
      )}
      {m.blurb && (
        <div style={{ fontSize: 12.5, color: IV.textDim, lineHeight: 1.65, marginBottom: isMobile ? 14 : 24, paddingBottom: isMobile ? 14 : 24, borderBottom: `1px solid ${IV.border}` }}>{m.blurb}</div>
      )}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" as const, marginBottom: isMobile ? 20 : 36 }}>
        <div style={{ display: "flex", flexDirection: "column" as const, gap: isMobile ? 10 : 14 }}>
          {m.features.map((f: string) => {
            const appFeature = f.toLowerCase().includes("app") && !f.toLowerCase().includes("limited");
            return (
              <div key={f} style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                  <circle cx="7" cy="7" r="6.5" stroke={appFeature ? "rgba(176,138,74,0.5)" : m.highlight ? "rgba(176,138,74,0.5)" : IV.borderInput} />
                  <path d="M4 7l2 2 4-4" stroke={appFeature ? IV.accent : m.highlight ? IV.accent : IV.textDim} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontSize: isMobile ? 13 : 14, color: appFeature ? IV.accent : IV.textSecondary, lineHeight: 1.6, fontWeight: appFeature ? 500 : 400 }}>{f}</span>
              </div>
            );
          })}
        </div>
        {m.notIncluded && m.notIncluded.length > 0 && (
          <div style={{ marginTop: isMobile ? 14 : 20, paddingTop: isMobile ? 14 : 20, borderTop: `1px solid ${IV.border}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: IV.textMuted, marginBottom: isMobile ? 8 : 12 }}>Not Included</div>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: isMobile ? 8 : 10 }}>
              {m.notIncluded.map((f: string) => (
                <div key={f} style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                    <circle cx="7" cy="7" r="6.5" stroke={IV.border} />
                    <path d="M4.8 4.8l4.4 4.4M9.2 4.8l-4.4 4.4" stroke={IV.textMuted} strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                  <span style={{ fontSize: isMobile ? 12.5 : 13, color: IV.textMuted, lineHeight: 1.6 }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {m.bestFor && m.bestFor.length > 0 && (
          <div style={{ marginTop: isMobile ? 14 : 20, paddingTop: isMobile ? 14 : 20, borderTop: `1px solid ${IV.border}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: isMobile ? 8 : 12 }}>Best For Clients Who</div>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: isMobile ? 7 : 9 }}>
              {m.bestFor.map((f: string) => (
                <div key={f} style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: m.highlight ? IV.accent : IV.textDim, flexShrink: 0, marginTop: 8 }} />
                  <span style={{ fontSize: isMobile ? 12.5 : 13, color: IV.textSecondary, lineHeight: 1.6 }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Btn
        variant={m.highlight ? "primary" : "secondary"}
        onClick={() => onSelect(m)}
        style={{ width: "100%", justifyContent: "center", padding: isMobile ? "11px 16px" : "13px 20px", fontSize: 14, borderRadius: 10 }}>
        {m.cta || "Begin Your Coaching Plan"}
      </Btn>
    </div>
  );
}

function PackageCard({ p, onSelect, isMobile }: { p: any; onSelect: (p: any) => void; isMobile?: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: IV.surface,
        border: `1px solid ${hov ? IV.borderInput : IV.border}`,
        borderRadius: 16,
        padding: isMobile ? "18px 16px 18px" : "34px 28px 28px",
        display: "flex",
        flexDirection: "column" as const,
        transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
        transform: hov ? "translateY(-3px)" : "translateY(0)",
        boxShadow: hov ? "0 4px 24px rgba(28,24,20,0.10)" : "none",
        height: "100%",
        boxSizing: "border-box" as const,
      }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: isMobile ? 8 : 12 }}>{p.title}</div>
      <div style={{ fontSize: isMobile ? 13 : 14, color: IV.textSecondary, marginBottom: isMobile ? 12 : 20 }}>{p.sessionCount}</div>
      <div style={{ fontFamily: F_SERIF, fontSize: isMobile ? 28 : 36, color: IV.accent, lineHeight: 1, marginBottom: 6 }}>{p.price}</div>
      <div style={{ fontSize: 12, color: IV.textDim, marginBottom: isMobile ? 12 : 20 }}>One-time payment · VAT included</div>
      <div style={{ fontSize: isMobile ? 13 : 14, color: IV.textSecondary, lineHeight: 1.7, flex: 1, marginBottom: isMobile ? 12 : 16 }}>{p.tagline}</div>
      {p.validity && <div style={{ fontSize: 11, color: IV.textMuted, marginBottom: isMobile ? 16 : 28 }}>Valid for {p.validity}</div>}
      <Btn variant="secondary" onClick={() => onSelect(p)} style={{ width: "100%", justifyContent: "center", padding: isMobile ? "10px 16px" : "12px 20px", borderRadius: 10 }}>
        Apply for Coaching
      </Btn>
    </div>
  );
}

function PublicBlogPage() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 820);
  const articleSlug = window.location.pathname.replace(/^\/blog\/?/, "").replace(/\/$/, "");
  const [selectedPostTitle, setSelectedPostTitle] = useState<string | null>(
    articleSlug === "health-is-not-an-identity" ? "Health Is Not An Identity" : null
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const navigate = (path: string) => { window.location.href = path; };
  const posts = [
    {
      label: "Philosophy",
      title: "Health Is Not An Identity",
      slug: "health-is-not-an-identity",
      date: "Metaphysique Notes",
      excerpt: "The point of training is not to become a person who performs health. It is to build enough capacity, confidence and physical honesty that the rest of life has ground to stand on.",
      body: [
        "Health is easiest to distort when it becomes a performance of who we are. The food, the workouts, the sleep score, the morning routine, the supplements: all of it can become a costume that asks to be defended instead of a practice that helps us live.",
        "This does not mean health is unimportant. Quite the opposite.",
        "It is hard to live a good life when you are frequently in pain, exhausted, or unable to do the ordinary tasks. A body that feels unreliable can make life smaller. It can make work harder, sleep shallower, and simple pleasures more difficult to enjoy.",
        "Health matters because it gives you access to the rest of life. It gives you the energy to work, the patience to be with people and the resilience to meet stress in day to day life.",
        "But something strange happens when health becomes a business.",
        "The Health Industrial Complex",
        "For people like me, health is part of the work. I study it, coach it, explain it, and sell it. That comes with responsibility, because the health professional exists inside a system that does not always reward simplicity.",
        "A market needs attention. It needs insecurity. It needs the feeling that something is missing.",
        "The supplement company must create urgency around something you were not thinking about last week. A vitamin you had never heard of suddenly becomes the missing piece. Our constant search for an answer to why we are tired, why we do not sleep well is met by definite answers from companies claiming with certainty that the solution is in hand.",
        "This is not always fake. Deficiencies exist. Supplements can help. Expert guidance can be valuable. But the business model often depends on making ordinary people feel slightly incomplete, and slightly at risk of missing the thing that would FINALLY make them healthy.",
        "Health becomes a shopping list you can never finish.",
        "The wellness industry does something similar with stress.",
        "Modern life is genuinely demanding. Many people are overworked and under-rested. There is a real need for practices that help us slow down, breathe, sleep, and return to the body.",
        "But wellness often sells relief from pressure by giving people more pressure to manage.",
        "Now relaxation has rules and metrics and recovery becomes another thing to optimize.",
        "The promise is peace, but the method often adds one more layer of self-monitoring to a life already drowning in it.",
        "Food follows the same logic.",
        "Nutrition matters. What you eat affects your energy, digestion, sleep, and long-term health. It would be dishonest to pretend otherwise.",
        "But obsessing over every bite is not health.",
        "A person can make conscious food choices, eat enough protein, include fruits and vegetables, pay attention to how food makes them feel, and still live a normal life.",
        "If your way of eating makes you afraid of a drink with friends or a cinnamon bun on a Sunday, something has gone wrong.",
        "The point of nutrition is not to make food morally pure. The point is to support a body and a life that can handle flexibility.",
        "A good coach should not want to become the center of your life. A good coach should help health become simpler, and more livable.",
        "That is the aim of Metaphysique: build a body you can trust, stay steady, and adjust without judging yourself.",
        "The goal is not to think about health all the time.",
        "The goal is to build a body you can trust enough to stop making health the center of your life.",
        "Health should not become the thing you perform.",
        "It should become the ground you stand on while you live.",
      ],
    },
    {
      label: "Training",
      title: "A Capable Body Beats An Optimized One",
      slug: "a-capable-body-beats-an-optimized-one",
      date: "Training Practice",
      excerpt: "Optimization often asks for more attention than life can realistically give. Capability is quieter: clear strength, useful conditioning, joints that tolerate work, and a plan you can repeat.",
      body: [
        "Optimization is attractive because it promises precision. It can also become fragile. The more exact the system needs to be, the more easily it collapses when travel, stress, sleep or a busy week enters the room.",
        "Capability is more useful. A capable body can hinge, squat, press, carry, breathe, recover and repeat. It does not require the week to be perfect before the work can count.",
        "A good training plan builds that kind of resilience. It gives you priorities, enough progression to keep moving forward, and enough room to remain a person with a full life.",
      ],
    },
    {
      label: "Coaching",
      title: "Structure Is A Form Of Respect",
      slug: "structure-is-a-form-of-respect",
      date: "CoachAgent Method",
      excerpt: "A good plan reduces negotiation. The work is still demanding, but the system holds the decisions: what to train, how to progress, when to adjust, and what deserves patience.",
      body: [
        "Structure is not there to control the client. It is there to protect attention. When the plan is clear, less energy is spent asking what to do next and more energy can go into the quality of the work.",
        "Good coaching gives decisions a place to live: assessment, programming, check-ins, feedback and adjustment. The client still does the hard part, but they do not have to carry every variable alone.",
        "That respect matters. It turns training from a weekly act of willpower into a system that can hold ambition, constraint, recovery and real life at the same time.",
      ],
    },
  ];
  const selectedPost = posts.find(post => post.title === selectedPostTitle) || null;
  const openPost = (title: string, slug: string) => {
    setSelectedPostTitle(title);
    window.history.pushState(null, "", `/blog/${slug}`);
    window.setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
  };

  return (
    <ThemeCtx.Provider value={IV}>
      <div style={{ minHeight: "100vh", background: IV.bg, color: IV.textPrimary, fontFamily: F_SANS }}>
        <header style={{ position: "sticky", top: 0, zIndex: 30, background: "rgba(243,239,230,0.92)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${IV.border}` }}>
          <div style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "12px 16px" : "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <button onClick={() => navigate("/website")} style={{ display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", cursor: "pointer", padding: 0, color: IV.textPrimary }}>
              <MetaLogo size={28} color={IV.gold} />
              <div style={{ textAlign: "left" as const }}>
                <div style={{ fontFamily: F_SERIF, fontSize: 17, letterSpacing: 3, lineHeight: 1 }}>METAPHYSIQUE</div>
                <div style={{ fontSize: 9, letterSpacing: 2.3, color: IV.textDim, textTransform: "uppercase" as const, marginTop: 3 }}>Joey Rouhana</div>
              </div>
            </button>
            <nav style={{ display: "flex", alignItems: "center", gap: isMobile ? 4 : 8, flexWrap: "wrap" as const, justifyContent: "flex-end" }}>
              <button onClick={() => navigate("/website")} style={{ background: "transparent", border: "none", color: IV.textSecondary, fontSize: 13, cursor: "pointer", padding: "8px 9px" }}>Home</button>
              <button onClick={() => navigate("/blog")} style={{ background: "transparent", border: "none", color: IV.textPrimary, fontSize: 13, cursor: "pointer", padding: "8px 9px" }}>Blog</button>
              <Btn variant="primary" onClick={() => navigate("/trial")} style={{ padding: isMobile ? "9px 12px" : "10px 16px", borderRadius: 8 }}>Start Free</Btn>
            </nav>
          </div>
        </header>

        <main>
          <section style={{ padding: isMobile ? "62px 18px 42px" : "92px 24px 70px", borderBottom: `1px solid ${IV.border}` }}>
            <div style={{ maxWidth: 1120, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 0.95fr) minmax(300px, 0.65fr)", gap: isMobile ? 34 : 68, alignItems: "end" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: IV.accent, textTransform: "uppercase" as const, marginBottom: 20 }}>Journal</div>
                <h1 style={{ fontFamily: F_SERIF, fontSize: "clamp(46px, 7vw, 82px)", lineHeight: 1.02, letterSpacing: 0.2, margin: 0 }}>
                  Notes on training, health and the life around it.
                </h1>
              </div>
              <p style={{ margin: 0, color: IV.textSecondary, fontSize: 16, lineHeight: 1.85 }}>
                Short essays from the Metaphysique practice: strength without identity performance, structure without obsession, and coaching that keeps the body in service of a meaningful life.
              </p>
            </div>
          </section>

          <section style={{ padding: isMobile ? "42px 18px 72px" : "70px 24px 104px", background: IV.surface }}>
            <div style={{ maxWidth: selectedPost ? 820 : 1120, margin: "0 auto" }}>
              {selectedPost ? (
                <article style={{ background: IV.surfaceRaised, border: `1px solid ${IV.border}`, borderRadius: 8, padding: isMobile ? "28px 22px" : "46px 50px" }}>
                  <button
                    onClick={() => {
                      setSelectedPostTitle(null);
                      window.history.pushState(null, "", "/blog");
                    }}
                    style={{ background: "transparent", border: "none", color: IV.accent, fontSize: 12, fontWeight: 700, letterSpacing: 1.8, textTransform: "uppercase" as const, cursor: "pointer", padding: 0, marginBottom: 28 }}
                  >
                    Back to essays
                  </button>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", marginBottom: 24 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.3, textTransform: "uppercase" as const, color: IV.accent }}>{selectedPost.label}</span>
                    <span style={{ fontSize: 11, color: IV.textDim }}>{selectedPost.date}</span>
                  </div>
                  <h2 style={{ fontFamily: F_SERIF, fontSize: isMobile ? 38 : 56, lineHeight: 1.03, letterSpacing: 0.2, margin: "0 0 24px" }}>{selectedPost.title}</h2>
                  <p style={{ color: IV.textSecondary, fontSize: isMobile ? 16 : 18, lineHeight: 1.85, margin: "0 0 28px" }}>{selectedPost.excerpt}</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                    {selectedPost.body.map((paragraph, idx) => paragraph === "The Health Industrial Complex" ? (
                      <h3 key={idx} style={{ fontFamily: F_SERIF, color: IV.textPrimary, fontSize: isMobile ? 28 : 34, lineHeight: 1.1, margin: "14px 0 2px" }}>{paragraph}</h3>
                    ) : (
                      <p key={idx} style={{ color: IV.textSecondary, fontSize: 16, lineHeight: 1.9, margin: 0 }}>{paragraph}</p>
                    ))}
                  </div>
                  <div style={{ marginTop: 36, paddingTop: 26, borderTop: `1px solid ${IV.border}` }}>
                    <Btn variant="secondary" onClick={() => navigate("/trial")} style={{ borderRadius: 8 }}>Start With Practice</Btn>
                  </div>
                </article>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
                  {posts.map(post => (
                    <article key={post.title} style={{ background: IV.surfaceRaised, border: `1px solid ${IV.border}`, borderRadius: 8, padding: isMobile ? "26px 22px" : "34px 30px", minHeight: 300, display: "flex", flexDirection: "column" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", marginBottom: 28 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.3, textTransform: "uppercase" as const, color: IV.accent }}>{post.label}</span>
                        <span style={{ fontSize: 11, color: IV.textDim }}>{post.date}</span>
                      </div>
                      <h2 style={{ fontFamily: F_SERIF, fontSize: 31, lineHeight: 1.08, letterSpacing: 0.2, margin: "0 0 18px" }}>{post.title}</h2>
                      <p style={{ color: IV.textSecondary, fontSize: 14.5, lineHeight: 1.8, margin: 0 }}>{post.excerpt}</p>
                      <div style={{ marginTop: "auto", paddingTop: 34 }}>
                        <Btn variant="secondary" onClick={() => openPost(post.title, post.slug)} style={{ borderRadius: 8 }}>Read More</Btn>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </ThemeCtx.Provider>
  );
}

function PublicLandingPage() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 820);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const navigate = (path: string) => { window.location.href = path; };
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const pillars = [
    { title: "A capable body", text: "Training that supports how you live, work, move and recover." },
    { title: "A clear system", text: "Assessment, programming, check-ins and progression in one connected coaching process." },
    { title: "A grounded practice", text: "No identity performance. No endless optimization. Just deliberate work that compounds." },
  ];
  const pathways = [
    { title: "Free 4-week program", text: "Start with a personalized training plan and unlock your portal.", action: "Start Free", onClick: () => navigate("/trial") },
    { title: "Program library", text: "Download structured Metaphysique programs for efficient training, nutrition direction and habit building.", action: "Explore Library", onClick: () => navigate("/programs") },
    { title: "Online coaching", text: "Train anywhere with structured programming, follow-up and accountability.", action: "View Options", onClick: () => navigate("https://app.joeyrouhana.com/shop#shop-online") },
    { title: "In-person coaching", text: "Amsterdam-based coaching for technique, strength and long-term consistency.", action: "See Memberships", onClick: () => navigate("https://app.joeyrouhana.com/shop#shop-memberships") },
  ];

  return (
    <ThemeCtx.Provider value={IV}>
      <div style={{ minHeight: "100vh", background: IV.bg, color: IV.textPrimary, fontFamily: F_SANS }}>
        <header style={{ position: "sticky", top: 0, zIndex: 30, background: "rgba(243,239,230,0.92)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${IV.border}` }}>
          <div style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "12px 16px" : "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <button onClick={() => scrollTo("landing-hero")} style={{ display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", cursor: "pointer", padding: 0, color: IV.textPrimary }}>
              <MetaLogo size={28} color={IV.gold} />
              <div style={{ textAlign: "left" as const }}>
                <div style={{ fontFamily: F_SERIF, fontSize: 17, letterSpacing: 3, lineHeight: 1 }}>METAPHYSIQUE</div>
                <div style={{ fontSize: 9, letterSpacing: 2.3, color: IV.textDim, textTransform: "uppercase" as const, marginTop: 3 }}>Joey Rouhana</div>
              </div>
            </button>
            <nav style={{ display: "flex", alignItems: "center", gap: isMobile ? 4 : 8, flexWrap: "wrap" as const, justifyContent: "flex-end" }}>
              {[
                { label: "Approach", action: () => scrollTo("landing-approach") },
                { label: "Coaching", action: () => scrollTo("landing-pathways") },
                { label: "Programs", action: () => navigate("/programs") },
                { label: "Blog", action: () => navigate("/blog") },
                { label: "Portal", action: () => navigate("/portal") },
              ].map(item => (
                <button key={item.label} onClick={item.action} style={{ display: isMobile && !["Programs", "Blog", "Portal"].includes(item.label) ? "none" : "inline-flex", background: "transparent", border: "none", color: IV.textSecondary, fontSize: 13, cursor: "pointer", padding: "8px 9px" }}>
                  {item.label}
                </button>
              ))}
              <Btn variant="primary" onClick={() => navigate("/trial")} style={{ padding: isMobile ? "9px 12px" : "10px 16px", borderRadius: 8 }}>Start Free</Btn>
            </nav>
          </div>
        </header>

        <main>
          <section id="landing-hero" style={{ minHeight: isMobile ? "auto" : "calc(100vh - 64px)", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 0.92fr) minmax(320px, 1.08fr)", alignItems: "stretch", borderBottom: `1px solid ${IV.border}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "54px 18px 42px" : "72px 24px", minHeight: isMobile ? 0 : 620 }}>
              <div style={{ maxWidth: 560 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: IV.accent, textTransform: "uppercase" as const, marginBottom: 24 }}>Beyond the physique</div>
                <h1 style={{ fontFamily: F_SERIF, fontSize: "clamp(44px, 6.2vw, 76px)", lineHeight: 1.02, letterSpacing: 0.2, margin: 0, color: IV.textPrimary }}>
                  Health is not an identity.
                </h1>
                <p style={{ fontFamily: F_SERIF, fontSize: "clamp(34px, 4.9vw, 58px)", lineHeight: 1.08, letterSpacing: 0.2, margin: "18px 0 34px", color: IV.textPrimary }}>
                  It is the ground on which everything else stands.
                </p>
                <p style={{ fontSize: 17, lineHeight: 1.85, color: IV.textSecondary, maxWidth: 480, margin: "0 0 38px" }}>
                  I work with people who want a capable body in service of a meaningful life, not another thing to optimize endlessly.
                </p>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" as const }}>
                  <Btn variant="primary" onClick={() => navigate("/trial")} style={{ padding: "14px 24px", borderRadius: 8 }}>Start Free Program</Btn>
                  <Btn variant="secondary" onClick={() => navigate("/programs")} style={{ padding: "14px 24px", borderRadius: 8 }}>View Program Library</Btn>
                  <Btn variant="secondary" onClick={() => scrollTo("landing-approach")} style={{ padding: "14px 24px", borderRadius: 8 }}>Read The Philosophy</Btn>
                </div>
              </div>
            </div>
            <div style={{ position: "relative" as const, minHeight: isMobile ? 420 : 620, background: `linear-gradient(180deg, ${IV.surface} 0%, ${IV.bg} 100%)`, borderLeft: isMobile ? "none" : `1px solid ${IV.border}`, borderTop: isMobile ? `1px solid ${IV.border}` : "none", overflow: "hidden" }}>
              <img src="/hero-studio-portrait.jpg" alt="Joey Rouhana coaching" style={{ position: "absolute" as const, inset: 0, width: "100%", height: "100%", objectFit: "contain", objectPosition: "center bottom", filter: "sepia(0.14) contrast(0.92) brightness(0.98)", opacity: 0.9 }} />
              <div style={{ position: "absolute" as const, inset: 0, background: isMobile
                ? "linear-gradient(180deg, rgba(243,239,230,0.18) 0%, rgba(243,239,230,0.24) 72%, rgba(243,239,230,0.46) 100%), rgba(176,138,74,0.05)"
                : "linear-gradient(90deg, rgba(243,239,230,0.62) 0%, rgba(243,239,230,0.18) 18%, rgba(243,239,230,0.10) 72%, rgba(243,239,230,0.44) 100%), rgba(176,138,74,0.06)" }} />
            </div>
          </section>

          <section id="landing-approach" style={{ padding: "96px 24px", background: IV.surface }}>
            <div style={{ maxWidth: 1120, margin: "0 auto" }}>
              <div style={{ maxWidth: 690, marginBottom: isMobile ? 34 : 50 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: IV.textDim, textTransform: "uppercase" as const, marginBottom: 14 }}>The Method</div>
                <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(36px, 5vw, 58px)", lineHeight: 1.08, margin: "0 0 18px" }}>
                  A training practice for strength, structure and self-respect.
                </h2>
                <p style={{ color: IV.textSecondary, fontSize: 16, lineHeight: 1.8, margin: 0 }}>
                  Metaphysique combines thoughtful coaching with the CoachAgent portal: your plan, sessions, check-ins, nutrition intake and progress live together, so the work stays clear.
                </p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }}>
                {pillars.map((pillar, index) => (
                  <div key={pillar.title} style={{ background: IV.surfaceRaised, border: `1px solid ${IV.border}`, borderRadius: 8, padding: "30px 26px" }}>
                    <div style={{ fontFamily: F_SERIF, fontSize: 42, color: "rgba(176,138,74,0.32)", marginBottom: 16 }}>0{index + 1}</div>
                    <h3 style={{ margin: "0 0 10px", fontSize: 17, color: IV.textPrimary }}>{pillar.title}</h3>
                    <p style={{ margin: 0, color: IV.textSecondary, fontSize: 14, lineHeight: 1.7 }}>{pillar.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="landing-pathways" style={{ padding: "96px 24px", borderTop: `1px solid ${IV.border}`, borderBottom: `1px solid ${IV.border}` }}>
            <div style={{ maxWidth: 1120, margin: "0 auto", textAlign: "center" as const }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: IV.textDim, textTransform: "uppercase" as const, marginBottom: 14 }}>Start Here</div>
              <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(36px, 5vw, 56px)", lineHeight: 1.08, margin: "0 0 18px" }}>Clear next steps.</h2>
              <p style={{ color: IV.textSecondary, fontSize: 16, lineHeight: 1.75, margin: "0 auto 48px", maxWidth: 610 }}>
                Whether you train online or in person, the same system carries the process: plan, execute, log, adjust.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18, textAlign: "left" as const }}>
                {pathways.map(pathway => (
                  <div key={pathway.title} style={{ background: IV.surface, border: `1px solid ${IV.borderInput}`, borderRadius: 8, padding: "28px 26px", display: "flex", flexDirection: "column" as const, minHeight: 250 }}>
                    <h3 style={{ fontFamily: F_SERIF, fontSize: 25, margin: "0 0 12px", color: IV.textPrimary }}>{pathway.title}</h3>
                    <p style={{ color: IV.textSecondary, fontSize: 14, lineHeight: 1.7, margin: "0 0 24px" }}>{pathway.text}</p>
                    <Btn variant={pathway.title === "Free 4-week program" ? "primary" : "secondary"} onClick={pathway.onClick} style={{ marginTop: "auto", justifyContent: "center", borderRadius: 8, padding: "12px 16px" }}>
                      {pathway.action}
                    </Btn>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section style={{ padding: "86px 24px", background: IV.textPrimary, color: IV.surface }}>
            <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", justifyContent: "space-between", gap: 26, alignItems: "center", flexWrap: "wrap" as const }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: "rgba(255,253,248,0.58)", textTransform: "uppercase" as const, marginBottom: 12 }}>Client Portal</div>
                <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(34px, 5vw, 54px)", lineHeight: 1.08, margin: 0 }}>Already coaching?</h2>
                <p style={{ color: "rgba(255,253,248,0.72)", margin: "16px 0 0", lineHeight: 1.7, maxWidth: 520 }}>
                  Your training plan, weekly check-ins, nutrition forms and session history live in the app.
                </p>
              </div>
              <Btn variant="primary" onClick={() => navigate("/portal")} style={{ background: IV.surface, color: IV.textPrimary, padding: "14px 24px", borderRadius: 8 }}>Open Portal</Btn>
            </div>
          </section>
        </main>
      </div>
    </ThemeCtx.Provider>
  );
}

type ProgramLibraryItem = {
  title: string;
  status: "Ready" | "Coming Next";
  price: string;
  length: string;
  schedule: string;
  level: string;
  session: string;
  goal: string;
  includes: string[];
  bestFor: string[];
  href?: string;
};

function ProgramLibraryPage() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 820);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const navigate = (path: string) => { window.location.href = path; };
  const registerInterest = (programTitle: string) => {
    const subject = encodeURIComponent(`Program interest: ${programTitle}`);
    const body = encodeURIComponent(`Hi Joey,\n\nI am interested in the ${programTitle} download from The Metaphysique program library.\n\nPlease let me know when it is available.\n\nName:\nGoal:\n`);
    window.location.href = `mailto:joey.metaphysique@gmail.com?subject=${subject}&body=${body}`;
  };
  const programs: ProgramLibraryItem[] = [
    {
      title: "Metaphysique Foundation",
      status: "Coming Next",
      price: "Flagship download",
      length: "8 weeks",
      schedule: "3 or 4 days/week",
      level: "Beginner to intermediate",
      session: "Warm up, mobility, training, stretching",
      goal: "Build the base: strength, movement quality, nutrition structure and repeatable habits.",
      includes: ["Training plan with efficient rest pairings", "Nutrition guidelines and plate-building rules", "Weekly habit tracker", "Exercise substitutions for busy gyms"],
      bestFor: ["Returning to training", "Building consistency", "Wanting one complete structure"],
    },
    {
      title: "45-Minute Physique Plan",
      status: "Coming Next",
      price: "Time-efficient download",
      length: "6 weeks",
      schedule: "3 days/week",
      level: "Intermediate",
      session: "Heavy work protected, accessories superset, mobility during low-fatigue rest",
      goal: "Make the best use of limited gym time without turning every session into rushed conditioning.",
      includes: ["Session timers and priorities", "Superset map", "30-minute fallback version", "Simple protein, steps and sleep targets"],
      bestFor: ["Busy schedules", "Lunch-break training", "People who want a clear gym script"],
    },
    {
      title: "Strong & Mobile",
      status: "Coming Next",
      price: "Strength and mobility block",
      length: "8 weeks",
      schedule: "4 days/week",
      level: "Intermediate",
      session: "Strength lifts paired with joint-specific mobility between appropriate sets",
      goal: "Build strength while keeping hips, ankles, shoulders and spine prepared for real movement.",
      includes: ["Squat, hinge, push and pull progression", "Mobility pairings by lift", "Stretching menu", "Recovery and soreness guidelines"],
      bestFor: ["Strength-focused clients", "Stiff lifters", "People who hate wasting rest periods"],
    },
    {
      title: "Lean Lifestyle Blueprint",
      status: "Coming Next",
      price: "Nutrition and habits guide",
      length: "4 weeks",
      schedule: "Works with any program",
      level: "All levels",
      session: "Lifestyle support for the training week",
      goal: "Create the food, sleep, hydration and daily movement structure that makes training results easier to maintain.",
      includes: ["Nutrition guidelines", "Meal structure examples", "Habit scorecard", "Weekly reflection and adjustment prompts"],
      bestFor: ["Fat loss phases", "Habit rebuilding", "People who need structure outside the gym"],
    },
  ];
  const methodSteps = ["Warm up", "Mobility", "Training", "Stretching"];

  return (
    <ThemeCtx.Provider value={IV}>
      <div style={{ minHeight: "100vh", background: IV.bg, color: IV.textPrimary, fontFamily: F_SANS }}>
        <header style={{ position: "sticky", top: 0, zIndex: 30, background: "rgba(243,239,230,0.92)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${IV.border}` }}>
          <div style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "12px 16px" : "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <button onClick={() => navigate("/website")} style={{ display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", cursor: "pointer", padding: 0, color: IV.textPrimary }}>
              <MetaLogo size={28} color={IV.gold} />
              <div style={{ textAlign: "left" as const }}>
                <div style={{ fontFamily: F_SERIF, fontSize: 17, letterSpacing: 3, lineHeight: 1 }}>METAPHYSIQUE</div>
                <div style={{ fontSize: 9, letterSpacing: 2.3, color: IV.textDim, textTransform: "uppercase" as const, marginTop: 3 }}>Joey Rouhana</div>
              </div>
            </button>
            <nav style={{ display: "flex", alignItems: "center", gap: isMobile ? 4 : 8, flexWrap: "wrap" as const, justifyContent: "flex-end" }}>
              {[
                { label: "Home", action: () => navigate("/website") },
                { label: "Shop", action: () => navigate("/shop") },
                { label: "Blog", action: () => navigate("/blog") },
              ].map(item => (
                <button key={item.label} onClick={item.action} style={{ background: "transparent", border: "none", color: IV.textSecondary, fontSize: 13, cursor: "pointer", padding: "8px 9px" }}>
                  {item.label}
                </button>
              ))}
              <Btn variant="primary" onClick={() => navigate("/trial")} style={{ padding: isMobile ? "9px 12px" : "10px 16px", borderRadius: 8 }}>Start Free</Btn>
            </nav>
          </div>
        </header>

        <main>
          <section style={{ padding: isMobile ? "58px 18px 46px" : "92px 24px 76px", borderBottom: `1px solid ${IV.border}` }}>
            <div style={{ maxWidth: 1120, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 0.92fr) minmax(320px, 0.72fr)", gap: isMobile ? 34 : 62, alignItems: "end" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: IV.accent, textTransform: "uppercase" as const, marginBottom: 20 }}>Downloadable Programs</div>
                <h1 style={{ fontFamily: F_SERIF, fontSize: "clamp(44px, 7vw, 78px)", lineHeight: 1.02, letterSpacing: 0.2, margin: "0 0 24px" }}>
                  Strength, structure and habits for a body that performs as good as it looks.
                </h1>
                <p style={{ margin: 0, color: IV.textSecondary, fontSize: 17, lineHeight: 1.85, maxWidth: 620 }}>
                  Pre-designed Metaphysique programs built for people who want efficient sessions, intelligent rest periods, clear nutrition guidelines and lifestyle practices they can maintain.
                </p>
              </div>
              <div style={{ background: IV.surface, border: `1px solid ${IV.borderInput}`, borderRadius: 8, padding: isMobile ? "24px 20px" : "30px 28px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.4, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 18 }}>The Session Flow</div>
                <div style={{ display: "grid", gap: 10 }}>
                  {methodSteps.map((step, index) => (
                    <div key={step} style={{ display: "flex", alignItems: "center", gap: 12, color: IV.textPrimary }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, border: `1px solid ${IV.goldBorder}`, background: IV.goldBg, color: IV.accent, display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700 }}>{index + 1}</div>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{step}</span>
                    </div>
                  ))}
                </div>
                <p style={{ color: IV.textSecondary, fontSize: 13.5, lineHeight: 1.75, margin: "20px 0 0" }}>
                  Heavy lifts keep enough real rest to perform well. Low-fatigue mobility and accessory supersets are used when they improve the session instead of stealing from it.
                </p>
              </div>
            </div>
          </section>

          <section style={{ padding: isMobile ? "42px 18px 72px" : "72px 24px 104px", background: IV.surface }}>
            <div style={{ maxWidth: 1120, margin: "0 auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(285px, 1fr))", gap: 18 }}>
                {programs.map(program => (
                  <article key={program.title} style={{ background: IV.surfaceRaised, border: `1px solid ${IV.border}`, borderRadius: 8, padding: isMobile ? "24px 20px" : "30px 28px", minHeight: 520, display: "flex", flexDirection: "column" as const }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, marginBottom: 22 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.2, color: IV.accent, textTransform: "uppercase" as const, marginBottom: 10 }}>{program.status}</div>
                        <h2 style={{ fontFamily: F_SERIF, fontSize: 30, lineHeight: 1.08, margin: 0, color: IV.textPrimary }}>{program.title}</h2>
                      </div>
                      <span style={{ border: `1px solid ${IV.borderInput}`, borderRadius: 8, padding: "7px 9px", color: IV.textMuted, fontSize: 11, whiteSpace: "nowrap" as const }}>{program.length}</span>
                    </div>
                    <p style={{ color: IV.textSecondary, fontSize: 14.5, lineHeight: 1.75, margin: "0 0 22px" }}>{program.goal}</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 22 }}>
                      {[
                        ["Schedule", program.schedule],
                        ["Level", program.level],
                        ["Format", program.price],
                        ["Session", program.session],
                      ].map(([label, value]) => (
                        <div key={label} style={{ borderTop: `1px solid ${IV.border}`, paddingTop: 10, minWidth: 0 }}>
                          <div style={{ fontSize: 10, letterSpacing: 1.7, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 5 }}>{label}</div>
                          <div style={{ fontSize: 13, color: IV.textPrimary, lineHeight: 1.45 }}>{value}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginBottom: 22 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 12 }}>Includes</div>
                      <div style={{ display: "grid", gap: 9 }}>
                        {program.includes.map(item => (
                          <div key={item} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <div style={{ width: 5, height: 5, borderRadius: "50%", background: IV.accent, marginTop: 8, flexShrink: 0 }} />
                            <span style={{ fontSize: 13.5, color: IV.textSecondary, lineHeight: 1.55 }}>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginTop: "auto", borderTop: `1px solid ${IV.border}`, paddingTop: 18 }}>
                      <div style={{ color: IV.textMuted, fontSize: 12.5, lineHeight: 1.6, marginBottom: 16 }}>
                        Best for {program.bestFor.join(", ").toLowerCase()}.
                      </div>
                      <Btn variant={program.href ? "primary" : "secondary"} onClick={() => program.href ? navigate(program.href) : navigate("/program-access")} style={{ width: "100%", justifyContent: "center", borderRadius: 8, padding: "12px 16px" }}>
                        {program.href ? "Download Program" : "Get Program Access"}
                      </Btn>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section style={{ padding: "82px 24px", background: IV.textPrimary, color: IV.surface }}>
            <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", justifyContent: "space-between", gap: 26, alignItems: "center", flexWrap: "wrap" as const }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: "rgba(255,253,248,0.58)", textTransform: "uppercase" as const, marginBottom: 12 }}>Start Immediately</div>
                <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(34px, 5vw, 52px)", lineHeight: 1.08, margin: 0 }}>Want the personalized version?</h2>
                <p style={{ color: "rgba(255,253,248,0.72)", margin: "16px 0 0", lineHeight: 1.7, maxWidth: 550 }}>
                  Answer a few questions and get a program built around your current goal, equipment, schedule and limitations.
                </p>
              </div>
              <Btn variant="primary" onClick={() => navigate("/program-access")} style={{ background: IV.surface, color: IV.textPrimary, padding: "14px 24px", borderRadius: 8 }}>Get Program Access</Btn>
            </div>
          </section>
        </main>
      </div>
    </ThemeCtx.Provider>
  );
}

function ProgramAccessPage() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 820);
  type ProgramAccessType = "subscription" | "one-time" | "test";
  const isTestOffer = new URLSearchParams(window.location.search).get("testOffer") === "1";
  const [selected, setSelected] = useState<ProgramAccessType>(isTestOffer ? "test" : "subscription");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [answers, setAnswers] = useState<Record<string, string>>({ goal: "muscle", frequency: "3", experience: "intermediate", equipment: "full_gym", sessionLength: "45" });
  const [focus, setFocus] = useState<string[]>(["Strength", "Mobility"]);
  const [injuries, setInjuries] = useState<string[]>(["None"]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const navigate = (path: string) => { window.location.href = path; };
  const products = {
    subscription: {
      title: "The Metaphysique Program Path",
      price: "€14.95 / month",
      amount: "14.95",
      cta: "Start Program Path",
      sub: "Downloadable programs created in the Metaphysique style, with clear progression, nutrition guidelines and monthly updates.",
    },
    "one-time": {
      title: "Metaphysique Program Download",
      price: "€39.95 one time",
      amount: "39.95",
      cta: "Buy Downloadable Program",
      sub: "A complete downloadable program built from the Metaphysique training structure, with nutrition and lifestyle guidance included.",
    },
    test: {
      title: "Metaphysique Test Program",
      price: "€1 test",
      amount: "1.00",
      cta: "Test Checkout",
      sub: "A private test offer for checking payment, email, PDF download and portal access before sharing the program path.",
    },
  };
  const productKeys: ProgramAccessType[] = isTestOffer ? ["test", "subscription", "one-time"] : ["subscription", "one-time"];
  const pill = (active: boolean): React.CSSProperties => ({
    border: `1px solid ${active ? IV.accent : IV.borderInput}`,
    background: active ? IV.accentBg : IV.surface,
    color: active ? IV.accent : IV.textSecondary,
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    cursor: "pointer",
    textAlign: "center" as const,
  });
  const toggle = (value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev.filter(v => v !== "None"), value]);
  };
  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { setError("Name and email are required."); return; }
    setLoading(true);
    setError("");
    const p = products[selected];
    try {
      const res = await fetch(`${API}/api/program-library/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          productId: selected === "subscription" ? "metaphysique-program-access" : selected === "test" ? "metaphysique-program-test" : "metaphysique-program-download",
          productName: p.title,
          accessType: selected === "test" ? "one-time" : selected,
          amount: p.amount,
          answers: { ...answers, focus, injuries },
        }),
      });
      const data = await res.json();
      if (data.checkoutUrl) window.location.href = data.checkoutUrl;
      else { setError(data.error || "Could not start checkout."); setLoading(false); }
    } catch {
      setError("Could not connect to checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <ThemeCtx.Provider value={IV}>
      <div style={{ minHeight: "100vh", background: IV.bg, color: IV.textPrimary, fontFamily: F_SANS }}>
        <header style={{ position: "sticky", top: 0, zIndex: 30, background: "rgba(243,239,230,0.92)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${IV.border}` }}>
          <div style={{ maxWidth: 1180, margin: "0 auto", padding: isMobile ? "12px 16px" : "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <button onClick={() => navigate("/website")} style={{ display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", cursor: "pointer", padding: 0, color: IV.textPrimary }}>
              <MetaLogo size={28} color={IV.gold} />
              <div style={{ textAlign: "left" as const }}>
                <div style={{ fontFamily: F_SERIF, fontSize: 17, letterSpacing: 3, lineHeight: 1 }}>METAPHYSIQUE</div>
                <div style={{ fontSize: 9, letterSpacing: 2.3, color: IV.textDim, textTransform: "uppercase" as const, marginTop: 3 }}>Self-Guided Programs</div>
              </div>
            </button>
            <nav style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={() => navigate("/programs")} style={{ background: "transparent", border: "none", color: IV.textSecondary, fontSize: 13, cursor: "pointer", padding: "8px 9px" }}>Programs</button>
              <button onClick={() => navigate("/portal")} style={{ background: "transparent", border: "none", color: IV.textSecondary, fontSize: 13, cursor: "pointer", padding: "8px 9px" }}>Portal</button>
            </nav>
          </div>
        </header>

        <main>
          <section style={{ padding: isMobile ? "52px 18px 34px" : "86px 24px 58px", borderBottom: `1px solid ${IV.border}` }}>
            <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 0.9fr) minmax(340px, 0.65fr)", gap: isMobile ? 34 : 66, alignItems: "start" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: IV.accent, textTransform: "uppercase" as const, marginBottom: 20 }}>Metaphysique Program Library</div>
                <h1 style={{ fontFamily: F_SERIF, fontSize: "clamp(42px, 6.5vw, 76px)", lineHeight: 1.02, margin: "0 0 24px" }}>
                  Train on your own time, with my structure behind every session.
                </h1>
                <p style={{ color: IV.textSecondary, fontSize: 17, lineHeight: 1.85, margin: 0, maxWidth: 620 }}>
                  Downloadable programs created and overseen by Joey Rouhana, built around the Metaphysique session flow: warm up, mobility, training and stretching. You train independently, with clear guidance for exercise order, rest, supersets, nutrition and habits.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 30, maxWidth: 720 }}>
                  {["Warm up", "Mobility", "Training", "Stretching"].map(step => (
                    <div key={step} style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: 8, padding: "16px 14px", color: IV.textPrimary, fontSize: 13, fontWeight: 700 }}>{step}</div>
                  ))}
                </div>
              </div>

              <div style={{ background: IV.surface, border: `1px solid ${IV.borderInput}`, borderRadius: 8, padding: isMobile ? "22px 18px" : "28px 26px" }}>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${productKeys.length}, minmax(0, 1fr))`, gap: 10, marginBottom: 22 }}>
                  {productKeys.map(key => (
                    <button key={key} onClick={() => setSelected(key)} style={{ ...pill(selected === key), borderRadius: 8 }}>
                      <div>{products[key].price}</div>
                      <div style={{ fontSize: 11, color: selected === key ? IV.accent : IV.textMuted, fontWeight: 500, marginTop: 5 }}>{key === "subscription" ? "Updated monthly" : key === "test" ? "Private test" : "Single purchase"}</div>
                    </button>
                  ))}
                </div>
                <h2 style={{ fontFamily: F_SERIF, fontSize: 28, lineHeight: 1.08, margin: "0 0 10px" }}>{products[selected].title}</h2>
                <p style={{ color: IV.textSecondary, fontSize: 14, lineHeight: 1.7, margin: "0 0 22px" }}>{products[selected].sub}</p>

                <div style={{ display: "grid", gap: 14 }}>
                  {[["name", "Full name"], ["email", "Email address"], ["phone", "Phone optional"]].map(([key, label]) => (
                    <input key={key} value={(form as any)[key]} onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))} placeholder={label} style={ivInputStyle} />
                  ))}
                  <select value={answers.goal} onChange={e => setAnswers(prev => ({ ...prev, goal: e.target.value }))} style={ivInputStyle}>
                    <option value="muscle">Build muscle</option>
                    <option value="strength">Get stronger</option>
                    <option value="fat_loss">Lose fat</option>
                    <option value="general">General fitness</option>
                  </select>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <select value={answers.frequency} onChange={e => setAnswers(prev => ({ ...prev, frequency: e.target.value }))} style={ivInputStyle}>
                      <option value="3">3 days/week</option>
                      <option value="4">4 days/week</option>
                      <option value="5">5 days/week</option>
                    </select>
                    <select value={answers.sessionLength} onChange={e => setAnswers(prev => ({ ...prev, sessionLength: e.target.value }))} style={ivInputStyle}>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                    </select>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <select value={answers.experience} onChange={e => setAnswers(prev => ({ ...prev, experience: e.target.value }))} style={ivInputStyle}>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                    <select value={answers.equipment} onChange={e => setAnswers(prev => ({ ...prev, equipment: e.target.value }))} style={ivInputStyle}>
                      <option value="full_gym">Full gym</option>
                      <option value="home">Home gym</option>
                      <option value="dumbbells">Dumbbells only</option>
                      <option value="bodyweight">Bodyweight</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 10 }}>Focus</div>
                  <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                    {["Strength", "Mobility", "Muscle", "Conditioning", "Core"].map(x => <button key={x} onClick={() => toggle(x, setFocus)} style={pill(focus.includes(x))}>{x}</button>)}
                  </div>
                </div>
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 10 }}>Limitations</div>
                  <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                    {["None", "Shoulder", "Back", "Hip", "Knee", "Ankle"].map(x => <button key={x} onClick={() => x === "None" ? setInjuries(["None"]) : toggle(x, setInjuries)} style={pill(injuries.includes(x))}>{x}</button>)}
                  </div>
                </div>

                {error && <div style={{ marginTop: 16, color: IV.danger, fontSize: 13 }}>{error}</div>}
                <Btn variant="primary" onClick={submit} disabled={loading} style={{ marginTop: 22, width: "100%", justifyContent: "center", borderRadius: 8, padding: "14px 18px", fontSize: 14 }}>
                  {loading ? "Opening Checkout..." : products[selected].cta}
                </Btn>
                <div style={{ textAlign: "center" as const, color: IV.textMuted, fontSize: 11, marginTop: 14 }}>Secure payment via Mollie. Download link, PDF attachment and portal access after payment.</div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </ThemeCtx.Provider>
  );
}

// Flip to true when the €34.95 Personalized Training Plan should go live (shop page + portal upgrade tab)
const SHOW_BASIC_ONLINE_PLAN = false;

function ShopPage() {
  const [modal, setModal] = useState<{ title: string; price: string; amount: string; description: string; recurring?: boolean } | null>(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoState, setPromoState] = useState<{ valid: boolean; discount: string; finalAmount: string; type: string; value: number } | null>(null);
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [showPromo, setShowPromo] = useState(false);

  // Deep links like /shop#shop-online and /shop#shop-memberships: the browser's
  // native anchor jump fires before React has mounted the sections, so scroll
  // manually once they exist — and again once webfonts settle the layout.
  useEffect(() => {
    const scrollToHash = (behavior: ScrollBehavior) => {
      const id = window.location.hash.slice(1);
      if (!id) return;
      document.getElementById(id)?.scrollIntoView({ behavior });
    };

    // On a reload the browser restores the old scroll position after our jump,
    // which would undo it — but only take over restoration when there is a hash.
    const prevRestoration = window.history.scrollRestoration;
    if (window.location.hash) window.history.scrollRestoration = "manual";

    let userScrolled = false;
    const markUserScrolled = () => { userScrolled = true; };
    ["wheel", "touchstart", "keydown"].forEach(e => window.addEventListener(e, markUserScrolled, { passive: true }));

    const raf = requestAnimationFrame(() => scrollToHash("auto"));
    (document as any).fonts?.ready?.then(() => { if (!userScrolled) scrollToHash("auto"); });

    const onHashChange = () => scrollToHash("smooth");
    window.addEventListener("hashchange", onHashChange);
    return () => {
      cancelAnimationFrame(raf);
      window.history.scrollRestoration = prevRestoration;
      ["wheel", "touchstart", "keydown"].forEach(e => window.removeEventListener(e, markUserScrolled));
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  const openModal = (product: any) => {
    setModal(product);
    setForm({ name: "", email: "", phone: "" });
    setError("");
    setPromoCode("");
    setPromoState(null);
    setPromoError("");
    setShowPromo(false);
  };

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError("");
    setPromoState(null);
    try {
      const res = await fetch(`${API}/api/discount/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: promoCode.trim(), amount: modal!.amount }),
      });
      const data = await res.json();
      if (data.valid) { setPromoState(data); }
      else { setPromoError(data.error || "Invalid code"); }
    } catch { setPromoError("Could not validate code"); }
    setPromoLoading(false);
  };

  const handleCheckout = async () => {
    if (!form.name.trim() || !form.email.trim()) { setError("Name and email are required."); return; }
    setLoading(true);
    setError("");
    const endpoint = modal!.recurring ? `${API}/api/shop/membership-checkout` : `${API}/api/shop/checkout`;
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone, amount: modal!.amount, description: modal!.description, discountCode: promoState ? promoCode.trim().toUpperCase() : undefined, discountedAmount: promoState ? promoState.finalAmount : undefined }),
      });
      const data = await res.json();
      if (data.checkoutUrl) { window.location.href = data.checkoutUrl; }
      else { setError(data.error || "Something went wrong. Please try again."); setLoading(false); }
    } catch { setError("Could not connect. Please try again."); setLoading(false); }
  };

  const memberships = [
    {
      title: "1 Session / Week",
      price: "€380 / month",
      amount: "380.00",
      description: "PT Membership — 1 Session/Week",
      tagline: "Best for building structure and consistency.",
      features: [
        "Weekly coached training session",
        "Structured progression plan",
        "Ongoing support and adjustments",
        "Personal program delivered in the app",
      ],
      highlight: false, recurring: true,
    },
    {
      title: "2 Sessions / Week",
      price: "€720 / month",
      amount: "720.00",
      description: "PT Membership — 2 Sessions/Week",
      tagline: "Best for faster progress and stronger accountability.",
      features: [
        "Two coached sessions per week",
        "Strength, conditioning, and movement focus",
        "Continuous adjustments based on performance",
        "Personal program delivered in the app",
      ],
      highlight: true, badge: "Most Popular", recurring: true,
    },
    {
      title: "3 Sessions / Week",
      price: "€1020 / month",
      amount: "1020.00",
      description: "PT Membership — 3 Sessions/Week",
      tagline: "Best for serious transformation and high-frequency coaching.",
      features: [
        "Three coached sessions per week",
        "Maximum structure and accountability",
        "Ideal for body composition, strength, and performance goals",
        "Personal program delivered in the app",
      ],
      highlight: false, recurring: true,
    },
  ];

  const onlineTrainingPlans = [
    {
      title: "Personalized Training Plan",
      price: "€34.95 / month",
      amount: "34.95",
      description: "Personalized Training Plan — Online Programming",
      tagline: "A personalized training program without the cost or commitment of full online coaching.",
      blurb: "Your program is designed around your goals, experience, schedule, available equipment and physical limitations. Train independently while following a clear, structured program created by a professional coach. Your program will be reviewed and updated every 4–6 weeks.",
      commitment: "No minimum commitment",
      features: [
        "Access to the training app",
        "Personalized program built around your goals, experience, schedule, equipment and limitations",
        "Exercise demonstrations and instructions",
        "Workout tracking inside the app",
        "Structured progression",
        "New or updated program every 4–6 weeks",
        "Limited support for exercise clarification or app-related questions",
      ],
      notIncluded: [
        "Regular coaching check-ins or weekly follow-up",
        "Ongoing accountability",
        "Nutrition coaching",
        "Video calls or technique video analysis",
        "Frequent program adjustments",
      ],
      bestFor: [
        "Want a personalized program",
        "Prefer to train independently",
        "Don't need regular follow-up",
        "Want affordable access to the app and professional programming",
      ],
      cta: "Get My Training Plan",
      highlight: false, recurring: true,
    },
    {
      title: "Full Online Coaching",
      price: "€100 / month",
      amount: "100.00",
      description: "Full Online Coaching",
      tagline: "Full online coaching with ongoing follow-up, accountability and adjustments.",
      blurb: "Train anywhere with a program built specifically for you, backed by continuous coaching follow-up, feedback and accountability from your coach.",
      features: [
        SHOW_BASIC_ONLINE_PLAN ? "Everything in the Personalized Training Plan" : "Personalized program built around your goals, schedule, equipment and limitations",
        "Regular progress check-ins",
        "Ongoing follow-up and accountability",
        "Program adjustments based on progress, recovery and feedback",
        "Direct communication with your coach",
        "Technique feedback",
        "Nutrition and lifestyle guidance where relevant",
      ],
      bestFor: [
        "Want closer guidance",
        "Need accountability",
        "Want regular program adjustments",
        "Want coaching, feedback and follow-up",
      ],
      cta: "Start Online Coaching",
      highlight: true, badge: "Full Coaching", recurring: true,
    },
  ].filter(m => SHOW_BASIC_ONLINE_PLAN || m.title !== "Personalized Training Plan");

  const packages = [
    {
      title: "Foundation Block",
      sessionCount: "8 coached sessions",
      price: "€800",
      amount: "800.00",
      description: "PT Package — 8 Sessions",
      tagline: "A focused starting point to build structure, assess your baseline, and create momentum.",
      validity: "3 months",
    },
    {
      title: "Progress Block",
      sessionCount: "12 coached sessions",
      price: "€1,140",
      amount: "1140.00",
      description: "PT Package — 12 Sessions",
      tagline: "A stronger commitment for building consistency, improving technique, and tracking measurable progress.",
      validity: "5 months",
    },
    {
      title: "Transformation Block",
      sessionCount: "24 coached sessions",
      price: "€2,160",
      amount: "2160.00",
      description: "PT Package — 24 Sessions",
      tagline: "A longer coaching block for deeper progression, better accountability, and more complete results.",
      validity: "9 months",
    },
  ];

  const systemCards = [
    {
      title: "Assessment",
      text: "Understand your current level, goals, limitations, and movement quality.",
    },
    {
      title: "Training Plan",
      text: "Build a structured approach based on your body, schedule, and goal.",
    },
    {
      title: "Coaching Sessions",
      text: "Guided training with technical feedback, appropriate intensity, and progression.",
    },
    {
      title: "Progression",
      text: "Adjust the plan based on performance, recovery, and measurable results.",
    },
  ];

  const investments = [
    "Personal assessment and goal setting",
    "Structured training plan",
    "Coached training sessions",
    "Progression tracking",
    "Technique feedback",
    "Adjustments based on recovery and performance",
    "Optional nutrition direction",
    "App-based structure and communication",
  ];

  const inp: React.CSSProperties = { width: "100%", background: IV.surfaceRaised, border: `1px solid ${IV.borderInput}`, borderRadius: 8, padding: "11px 14px", color: IV.textPrimary, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: "none", boxSizing: "border-box" };

  return (
    <ThemeCtx.Provider value={IV}>
    <div style={{ minHeight: "100vh", background: IV.bg, color: IV.textPrimary, fontFamily: F_SANS }}>

      {/* ── HERO ── */}
      <section style={{ padding: "120px 24px 100px", textAlign: "center" as const, maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 0, marginBottom: 44 }}>
          <MetaLogo size={90} color={IV.gold} style={{ margin: "0 auto 18px" }} />
          <div style={{ fontFamily: F_SERIF, fontSize: 20, letterSpacing: 5, color: IV.textPrimary, lineHeight: 1 }}>METAPHYSIQUE</div>
          <div style={{ fontSize: 10, letterSpacing: 3.5, color: IV.textDim, textTransform: "uppercase" as const, marginTop: 7 }}>Personal Training System</div>
        </div>
        <h1 style={{ fontFamily: F_SERIF, fontSize: "clamp(48px, 8vw, 80px)", letterSpacing: 0.5, lineHeight: 1.05, margin: "0 0 28px", color: IV.textPrimary }}>
          Structured Coaching.<br />Measurable Progress.
        </h1>
        <p style={{ fontSize: 17, color: IV.textSecondary, lineHeight: 1.8, margin: "0 auto 44px", maxWidth: 520 }}>
          Personal coaching built around assessment, planning, execution, and progression — not random workouts.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" as const }}>
          <Btn variant="primary" style={{ fontSize: 15, padding: "14px 36px", borderRadius: 10 }}
            onClick={() => document.getElementById("shop-memberships")?.scrollIntoView({ behavior: "smooth" })}>
            In-Person Coaching
          </Btn>
          <Btn variant="secondary" style={{ fontSize: 15, padding: "14px 36px", borderRadius: 10 }}
            onClick={() => document.getElementById("shop-online")?.scrollIntoView({ behavior: "smooth" })}>
            Online Training
          </Btn>
          <Btn variant="secondary" style={{ fontSize: 15, padding: "14px 36px", borderRadius: 10 }}
            onClick={() => { window.location.href = "/programs"; }}>
            Program Library
          </Btn>
        </div>
        <div style={{ marginTop: 20, fontSize: 12, color: IV.textDim, letterSpacing: 0.4 }}>
          Clear plan · Progressive training · Ongoing coaching support
        </div>
      </section>

      {/* ── WHY THIS IS DIFFERENT ── */}
      <section style={{ background: IV.surface, borderTop: `1px solid ${IV.border}`, borderBottom: `1px solid ${IV.border}`, padding: "96px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center" as const, marginBottom: 64 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 16 }}>The Approach</div>
            <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(36px, 5vw, 52px)", letterSpacing: 0.4, margin: "0 0 20px" }}>Not Just Sessions. A Coaching System.</h2>
            <p style={{ color: IV.textSecondary, fontSize: 16, lineHeight: 1.75, margin: "0 auto", maxWidth: 560 }}>
              Most people do not need more random workouts. They need a clear plan, consistent execution, and someone adjusting the process as they progress.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 20 }}>
            {systemCards.map((card, i) => (
              <div key={card.title} style={{
                background: IV.surfaceRaised,
                border: `1px solid ${IV.borderInput}`,
                borderRadius: 16,
                padding: "32px 26px",
                position: "relative" as const,
              }}>
                <div style={{ fontFamily: F_SERIF, fontSize: 52, color: "rgba(176,138,74,0.28)", lineHeight: 1, marginBottom: 20, userSelect: "none" as const }}>
                  0{i + 1}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: IV.textPrimary, marginBottom: 10, letterSpacing: 0.3 }}>{card.title}</div>
                <div style={{ fontSize: 14, color: IV.textSecondary, lineHeight: 1.7 }}>{card.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MEMBERSHIPS ── */}
      <section id="shop-memberships" style={{ padding: "96px 24px 100px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center" as const, marginBottom: 64 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 16 }}>Main Offer</div>
          <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(36px, 5vw, 52px)", letterSpacing: 0.4, margin: "0 0 16px" }}>Monthly Coaching Memberships</h2>
          <p style={{ color: IV.textSecondary, fontSize: 16, margin: 0 }}>For clients who want consistency, accountability, and long-term progress.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 22, alignItems: "stretch" }}>
          {memberships.map(m => <MembershipCard key={m.title} m={m} onSelect={openModal} />)}
        </div>
      </section>

      <div style={{ height: 1, background: IV.border, maxWidth: 1100, margin: "0 auto" }} />

      {/* ── ONLINE TRAINING ── */}
      <section id="shop-online" style={{ padding: "96px 24px 100px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center" as const, marginBottom: 64 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 16 }}>Remote Coaching</div>
          <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(36px, 5vw, 52px)", letterSpacing: 0.4, margin: "0 0 16px" }}>Online Training</h2>
          <p style={{ color: IV.textSecondary, fontSize: 16, margin: 0 }}>{SHOW_BASIC_ONLINE_PLAN ? "Two levels of remote support — standalone personalized programming, or full coaching with follow-up and accountability." : "Remote coaching with a personal program, continuous follow-up and accountability — train anywhere."}</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 22, alignItems: "stretch", maxWidth: 860, margin: "0 auto" }}>
          {onlineTrainingPlans.map(m => <MembershipCard key={m.title} m={m} onSelect={openModal} />)}
        </div>
      </section>

      <div style={{ height: 1, background: IV.border, maxWidth: 1100, margin: "0 auto" }} />

      {/* ── PACKAGES ── */}
      <section style={{ padding: "96px 24px 100px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center" as const, marginBottom: 64 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 16 }}>Flexible Option</div>
          <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(36px, 5vw, 52px)", letterSpacing: 0.4, margin: "0 0 16px" }}>Session Packages</h2>
          <p style={{ color: IV.textSecondary, fontSize: 16, margin: 0 }}>Flexible coaching blocks for shorter commitments.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 22, alignItems: "stretch" }}>
          {packages.map(p => <PackageCard key={p.title} p={p} onSelect={openModal} />)}
        </div>
      </section>

      {/* ── WHAT YOU'RE INVESTING IN ── */}
      <section style={{ background: IV.surface, borderTop: `1px solid ${IV.border}`, borderBottom: `1px solid ${IV.border}`, padding: "96px 24px" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <div style={{ textAlign: "center" as const, marginBottom: 56 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 16 }}>The Value</div>
            <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(36px, 5vw, 52px)", letterSpacing: 0.4, margin: 0 }}>What You're Investing In</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
            {investments.map(inv => (
              <div key={inv} style={{ display: "flex", alignItems: "center", gap: 16, background: IV.surfaceRaised, border: `1px solid ${IV.borderInput}`, borderRadius: 12, padding: "15px 20px" }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: IV.accent, flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: IV.textPrimary, lineHeight: 1.5 }}>{inv}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARISON ── */}
      <section style={{ padding: "96px 24px", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center" as const, marginBottom: 64 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 16 }}>The Difference</div>
          <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(32px, 5vw, 48px)", letterSpacing: 0.4, margin: 0 }}>Why Not Just Buy Random PT Sessions?</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {/* Standard PT */}
          <div style={{ background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: 16, padding: "32px 28px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 20 }}>Standard PT Sessions</div>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
              {[
                "Pay per workout",
                "Less continuity between sessions",
                "Progress depends mainly on attendance",
                "Often reactive rather than planned",
              ].map(item => (
                <div key={item} style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", border: `1px solid ${IV.border}`, flexShrink: 0, marginTop: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 6, height: 1, background: IV.textDim, borderRadius: 1 }} />
                  </div>
                  <span style={{ fontSize: 14, color: IV.textSecondary, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Joey Rouhana */}
          <div style={{ background: "rgba(176,138,74,0.05)", border: `1px solid ${IV.goldBorder}`, borderRadius: 16, padding: "32px 28px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const, color: IV.accent, marginBottom: 20 }}>Joey Rouhana Coaching</div>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
              {[
                "Assessment-led plan from session one",
                "Clear progression structure over time",
                "Coaching that extends beyond the session",
                "Adjusted based on performance and recovery",
              ].map(item => (
                <div key={item} style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                    <circle cx="8" cy="8" r="7.5" stroke="rgba(176,138,74,0.45)" />
                    <path d="M5 8l2.2 2.2L11 5.5" stroke={IV.accent} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span style={{ fontSize: 14, color: IV.textPrimary, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ background: IV.surface, borderTop: `1px solid ${IV.border}`, padding: "112px 24px", textAlign: "center" as const }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <h2 style={{ fontFamily: F_SERIF, fontSize: "clamp(40px, 7vw, 68px)", letterSpacing: 0.5, margin: "0 0 20px", lineHeight: 1.05 }}>
            Ready to Train With Structure?
          </h2>
          <p style={{ color: IV.textSecondary, fontSize: 17, lineHeight: 1.75, margin: "0 auto 48px", maxWidth: 480 }}>
            Start with an assessment so we can define your goal, current level, and the right coaching option for you.
          </p>
          <Btn variant="primary" style={{ fontSize: 15, padding: "15px 40px", borderRadius: 10 }}
            onClick={() => document.getElementById("shop-memberships")?.scrollIntoView({ behavior: "smooth" })}>
            Apply for Coaching
          </Btn>
          <div style={{ marginTop: 24, fontSize: 12, color: IV.textDim, letterSpacing: 0.4 }}>
            All prices include VAT · Coaching options depend on availability
          </div>
        </div>
      </section>

      {/* ── CHECKOUT MODAL ── */}
      {modal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}
          style={{ position: "fixed" as const, inset: 0, background: IV.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24 }}>
          <div style={{ background: IV.surface, border: `1px solid ${IV.borderInput}`, borderRadius: 16, padding: 36, maxWidth: 440, width: "100%", boxShadow: "0 40px 80px rgba(28,24,20,0.25)" }}>
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: IV.textDim, marginBottom: 8 }}>Apply for Coaching</div>
              <div style={{ fontFamily: F_SERIF, fontSize: 28, letterSpacing: 0.4, color: IV.textPrimary, lineHeight: 1.1 }}>{modal.title}</div>
              <div style={{ fontFamily: F_SERIF, fontSize: 24, color: IV.accent, marginTop: 6 }}>{modal.price}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
              {([
                { label: "Full Name",         key: "name",  type: "text",  placeholder: "Your name" },
                { label: "Email",             key: "email", type: "email", placeholder: "your@email.com" },
                { label: "Phone (optional)",  key: "phone", type: "tel",   placeholder: "+31 6 ..." },
              ] as const).map(f => (
                <div key={f.key}>
                  <label style={{ display: "block", fontSize: 12, color: IV.textMuted, marginBottom: 6, fontWeight: 500 }}>{f.label}</label>
                  <input
                    type={f.type}
                    placeholder={f.placeholder}
                    value={form[f.key]}
                    onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    style={inp}
                  />
                </div>
              ))}
            </div>
            {/* Promo code */}
            <div style={{ marginTop: 20 }}>
              {!promoState && (
                <button
                  onClick={() => setShowPromo(v => !v)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: IV.textMuted, textDecoration: "underline", textDecorationStyle: "dotted" as const, textUnderlineOffset: 3 }}>
                  {showPromo ? "Hide promo code" : "Have a promo code?"}
                </button>
              )}
              {(showPromo || promoState) && (
                <>
                  <label style={{ display: "block", fontSize: 12, color: IV.textMuted, marginBottom: 6, fontWeight: 500, marginTop: promoState ? 0 : 10 }}>Promo Code</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="text"
                      placeholder="e.g. WELCOME20"
                      value={promoCode}
                      onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoState(null); setPromoError(""); }}
                      onKeyDown={e => e.key === "Enter" && applyPromo()}
                      style={{ ...inp, flex: 1, textTransform: "uppercase" as const, letterSpacing: 1 }}
                    />
                    <button
                      onClick={applyPromo}
                      disabled={!promoCode.trim() || promoLoading}
                      style={{ padding: "0 16px", background: IV.surface, border: `1px solid ${IV.border}`, borderRadius: 8, color: IV.textSecondary, fontSize: 13, cursor: "pointer", flexShrink: 0 }}
                    >{promoLoading ? "…" : "Apply"}</button>
                  </div>
                  {promoError && <div style={{ color: IV.danger, fontSize: 12, marginTop: 6 }}>{promoError}</div>}
                  {promoState && (
                    <div style={{ marginTop: 8, padding: "10px 12px", background: IV.accentBg, border: `1px solid ${IV.goldBorder}`, borderRadius: 8 }}>
                      <div style={{ fontSize: 12, color: IV.accent, fontWeight: 600 }}>
                        {promoState.type === "percent" ? `${promoState.value}% off` : `€${promoState.value} off`} applied
                      </div>
                      <div style={{ fontSize: 12, color: IV.textMuted, marginTop: 2 }}>
                        <span style={{ textDecoration: "line-through", marginRight: 8 }}>€{modal!.amount}</span>
                        <span style={{ color: IV.textPrimary, fontWeight: 600 }}>→ €{promoState.finalAmount}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {error && <div style={{ color: IV.danger, fontSize: 13, marginTop: 12 }}>{error}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <Btn variant="ghost" onClick={() => setModal(null)} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
              <Btn variant="primary" onClick={handleCheckout} disabled={loading} style={{ flex: 2, justifyContent: "center", fontSize: 14, borderRadius: 10 }}>
                {loading ? "Redirecting…" : `Pay ${promoState ? `€${promoState.finalAmount}` : modal!.price} →`}
              </Btn>
            </div>
            <div style={{ textAlign: "center" as const, fontSize: 11, color: IV.textDim, marginTop: 16 }}>
              Secure payment via Mollie · All prices include VAT
            </div>
          </div>
        </div>
      )}
    </div>
    </ThemeCtx.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// ── Coach app — all hooks declared before any conditional return ──────────────
function DiscountCodes() {
  const [codes, setCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", type: "percent", value: "", maxUses: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    const r = await authFetch("https://coachagent-server-production.up.railway.app/api/discount-codes");
    if (r.ok) { const d = await r.json(); setCodes(d.codes || []); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim() || !form.value) { setError("Code and value are required"); return; }
    setSaving(true); setError("");
    const r = await authFetch("https://coachagent-server-production.up.railway.app/api/discount-codes", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: form.code.toUpperCase(), type: form.type, value: parseFloat(form.value), maxUses: form.maxUses ? parseInt(form.maxUses) : null }),
    });
    const d = await r.json();
    if (r.ok) { setForm({ code: "", type: "percent", value: "", maxUses: "" }); load(); }
    else { setError(d.error || "Failed to create"); }
    setSaving(false);
  };

  const toggle = async (code: string, active: boolean) => {
    await authFetch(`https://coachagent-server-production.up.railway.app/api/discount-codes/${code}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ active }),
    });
    load();
  };

  const remove = async (code: string) => {
    if (!confirm(`Delete code ${code}?`)) return;
    await authFetch(`https://coachagent-server-production.up.railway.app/api/discount-codes/${code}`, { method: "DELETE" });
    load();
  };

  const inp2: React.CSSProperties = { background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: 8, padding: "10px 12px", color: C.textPrimary, fontSize: 13, fontFamily: "'Inter', sans-serif", outline: "none", width: "100%" };

  return (
    <div style={{ padding: "24px 48px", maxWidth: 720 }}>
      {/* Create form */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 32 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 16 }}>New Code</div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Code</div>
            <input style={inp2} placeholder="WELCOME20" value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Type</div>
            <select style={{ ...inp2, cursor: "pointer" }} value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
              <option value="percent">% off</option>
              <option value="fixed">€ off</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Value</div>
            <input style={inp2} type="number" placeholder="20" value={form.value} onChange={e => setForm(p => ({ ...p, value: e.target.value }))} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Max uses</div>
            <input style={inp2} type="number" placeholder="∞" value={form.maxUses} onChange={e => setForm(p => ({ ...p, maxUses: e.target.value }))} />
          </div>
        </div>
        {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <button onClick={create} disabled={saving} style={{ padding: "9px 20px", background: C.accent, color: "#000", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          {saving ? "Saving…" : "+ Create Code"}
        </button>
      </div>

      {/* Codes list */}
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 12 }}>Active Codes</div>
      {loading ? <div style={{ color: C.textDim, fontSize: 13 }}>Loading…</div> : codes.length === 0 ? (
        <div style={{ color: C.textDim, fontSize: 13 }}>No discount codes yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" as const, gap: 8 }}>
          {codes.map((c: any) => (
            <div key={c.code} style={{ background: C.surface, border: `1px solid ${c.active ? C.border : C.borderInput}`, borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 16, opacity: c.active ? 1 : 0.5 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700, color: C.textPrimary, minWidth: 120 }}>{c.code}</div>
              <div style={{ fontSize: 13, color: C.accent, fontWeight: 600, minWidth: 70 }}>
                {c.type === "percent" ? `${c.value}% off` : `€${c.value} off`}
              </div>
              <div style={{ fontSize: 12, color: C.textDim, flex: 1 }}>
                {c.usedCount} used{c.maxUses !== null ? ` / ${c.maxUses} max` : ""}
              </div>
              <button onClick={() => toggle(c.code, !c.active)} style={{ padding: "5px 12px", background: "none", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontSize: 12, cursor: "pointer" }}>
                {c.active ? "Disable" : "Enable"}
              </button>
              <button onClick={() => remove(c.code)} style={{ padding: "5px 10px", background: "none", border: `1px solid ${C.danger}`, borderRadius: 6, color: C.danger, fontSize: 12, cursor: "pointer" }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── B2B Factuur Generator ────────────────────────────────────────────────────
type LineItem = { date: string; description: string; quantity: number; unitPrice: number };

function B2BInvoice() {
  const blankItem = (): LineItem => ({ date: "", description: "", quantity: 1, unitPrice: 0 });
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
  });
  const [clientName, setClientName] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [lineItems, setLineItems] = useState<LineItem[]>([blankItem()]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const subtotal = lineItems.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  const tax = subtotal * 0.21;
  const total = subtotal + tax;

  const updateItem = (idx: number, field: keyof LineItem, val: string | number) =>
    setLineItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));

  const generate = async () => {
    if (!invoiceNumber.trim() || !clientName.trim()) { setError("Invoice number and client name are required"); return; }
    setGenerating(true); setError("");
    try {
      const res = await authFetch("https://coachagent-server-production.up.railway.app/api/invoices/b2b", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invoiceNumber, invoiceDate, clientName, clientAddress, lineItems }),
      });
      if (!res.ok) { const d = await res.json(); setError(d.error || "Failed to generate"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${invoiceNumber}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { setError(e.message); }
    setGenerating(false);
  };

  const inp: React.CSSProperties = { background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: 8, padding: "9px 12px", color: C.textPrimary, fontSize: 13, fontFamily: "'Inter', sans-serif", outline: "none", width: "100%" };
  const lbl: React.CSSProperties = { fontSize: 11, color: C.textMuted, marginBottom: 4 };
  const dimRow: React.CSSProperties = { fontSize: 12, color: C.textMuted, lineHeight: 1.9 };

  return (
    <div style={{ padding: "24px 48px", maxWidth: 800 }}>

      {/* Your details (read-only, shown on invoice) */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 14 }}>Your Details</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 24px" }}>
          {[
            ["Name", "PT by Joey Rouhana"],
            ["Address", "Roomtuintjes 204, 1093TB, Amsterdam"],
            ["KvK", "86163795"],
            ["BTW / VAT", "NL004200965B48"],
            ["IBAN", "NL94INGB0111714974"],
            ["Phone", "+31649536706"],
            ["Email", "joey_rouhana@hotmail.com"],
          ].map(([label, value]) => (
            <div key={label} style={dimRow}><span style={{ color: C.textDim, marginRight: 6 }}>{label}:</span>{value}</div>
          ))}
        </div>
      </div>

      {/* Invoice meta */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 16 }}>Invoice Details</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <div style={lbl}>Invoice Number</div>
            <input style={inp} placeholder="43" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} />
          </div>
          <div>
            <div style={lbl}>Invoice Date</div>
            <input style={inp} placeholder="25/02/2026" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={lbl}>Client / Company</div>
          <input style={inp} placeholder="Conservatorium Hotel" value={clientName} onChange={e => setClientName(e.target.value)} />
        </div>
        <div>
          <div style={lbl}>Client Address</div>
          <input style={inp} placeholder="Van Baerlestraat 27 - 1071 AN - Amsterdam - The Netherlands" value={clientAddress} onChange={e => setClientAddress(e.target.value)} />
        </div>
      </div>

      {/* Line items */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 16 }}>Line Items</div>
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 70px 100px 28px", gap: 8, marginBottom: 8 }}>
          {["Date", "Description", "Qty", "Unit Price (€)", ""].map(h => (
            <div key={h} style={{ fontSize: 10, color: C.textDim, fontWeight: 600, letterSpacing: 1 }}>{h}</div>
          ))}
        </div>
        {lineItems.map((item, idx) => (
          <div key={idx} style={{ display: "grid", gridTemplateColumns: "120px 1fr 70px 100px 28px", gap: 8, marginBottom: 8, alignItems: "center" }}>
            <input style={inp} placeholder="1/2/26" value={item.date} onChange={e => updateItem(idx, "date", e.target.value)} />
            <input style={inp} placeholder="HIIT Class" value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} />
            <input style={{ ...inp, textAlign: "center" as const }} type="number" min={1} value={item.quantity} onChange={e => updateItem(idx, "quantity", parseFloat(e.target.value) || 1)} />
            <input style={{ ...inp, textAlign: "right" as const }} type="number" min={0} step={0.01} value={item.unitPrice === 0 ? "" : item.unitPrice} placeholder="0.00" onChange={e => updateItem(idx, "unitPrice", parseFloat(e.target.value) || 0)} />
            <button onClick={() => setLineItems(prev => prev.filter((_, i) => i !== idx))} disabled={lineItems.length === 1}
              style={{ background: "none", border: `1px solid ${C.danger}`, borderRadius: 6, color: C.danger, cursor: "pointer", width: 28, height: 34, fontSize: 14, lineHeight: 1 }}>×</button>
          </div>
        ))}
        <button onClick={() => setLineItems(prev => [...prev, blankItem()])}
          style={{ marginTop: 4, padding: "7px 14px", background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, fontSize: 12, cursor: "pointer" }}>
          + Add Line
        </button>
      </div>

      {/* Totals preview */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 24px", marginBottom: 20, display: "flex", flexDirection: "column" as const, alignItems: "flex-end", gap: 6 }}>
        <div style={{ display: "flex", gap: 32, fontSize: 13, color: C.textMuted }}>
          <span>Subtotal excl. VAT</span><span style={{ fontWeight: 500, color: C.textPrimary }}>€{subtotal.toFixed(2)}</span>
        </div>
        <div style={{ display: "flex", gap: 32, fontSize: 13, color: C.textMuted }}>
          <span>VAT (21%)</span><span style={{ fontWeight: 500, color: C.textPrimary }}>€{tax.toFixed(2)}</span>
        </div>
        <div style={{ display: "flex", gap: 32, fontSize: 15, fontWeight: 700, color: C.textPrimary, borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
          <span>Total Due</span><span>€{total.toFixed(2)}</span>
        </div>
      </div>

      {error && <div style={{ color: C.danger, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      <button onClick={generate} disabled={generating}
        style={{ padding: "11px 28px", background: C.accent, color: "#000", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
        {generating ? "Generating…" : "Download Invoice as PDF"}
      </button>
    </div>
  );
}

type LedgerInvoice = {
  invoiceNumber: string; paymentId: string; clientName: string; clientEmail: string;
  description: string; sessions: number; subtotal: string; btw: string; total: string;
  dateISO: string; issuedAt: string;
};
type LedgerResponse = { count: number; totals: { subtotal: string; btw: string; total: string }; invoices: LedgerInvoice[] };

function InvoicesReport() {
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(nowYear);
  const [quarter, setQuarter] = useState<0 | 1 | 2 | 3 | 4>(0); // 0 = whole year
  const [data, setData] = useState<LedgerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const periodLabel = quarter ? `Q${quarter} ${year}` : `${year}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const q = quarter ? `&quarter=${quarter}` : "";
        const res = await authFetch(`${API}/api/invoices?year=${year}${q}`);
        if (!res.ok) { const d = await res.json().catch(() => ({})); if (!cancelled) { setError(d.error || `Failed to load (${res.status})`); setData(null); } return; }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e: any) { if (!cancelled) { setError(e.message); setData(null); } }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [year, quarter]);

  const esc = (s: string) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

  const exportCSV = () => {
    if (!data) return;
    const rows: (string | number)[][] = [
      ["Invoice", "Date", "Client", "Description", "Excl. BTW", "BTW 21%", "Total (incl.)"],
      ...data.invoices.map(i => [i.invoiceNumber, i.dateISO, i.clientName, i.description, i.subtotal, i.btw, i.total]),
      [],
      ["", "", "", `TOTAL ${periodLabel}`, data.totals.subtotal, data.totals.btw, data.totals.total],
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `invoices-${periodLabel.replace(/\s/g, "-")}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!data) return;
    const rowsHtml = data.invoices.map(i => `<tr>
      <td>${esc(i.invoiceNumber)}</td><td>${esc(i.dateISO)}</td><td>${esc(i.clientName)}</td>
      <td>${esc(i.description)}</td><td class="n">€${esc(i.subtotal)}</td><td class="n">€${esc(i.btw)}</td><td class="n">€${esc(i.total)}</td>
    </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Invoices ${esc(periodLabel)}</title>
      <style>@page{margin:18mm;} body{font-family:Arial,Helvetica,sans-serif;color:#222;}
      h1{font-size:20px;margin:0 0 4px;} .sub{color:#666;margin-bottom:22px;font-size:12px;}
      table{width:100%;border-collapse:collapse;font-size:12px;}
      th,td{text-align:left;padding:7px 8px;border-bottom:1px solid #e2e2e2;}
      th{font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#33477A;border-bottom:2px solid #33477A;}
      td.n,th.n{text-align:right;} tfoot td{font-weight:700;border-top:2px solid #33477A;border-bottom:none;padding-top:10px;}
      </style></head><body>
      <h1>Metaphysique — Sales Invoices</h1>
      <div class="sub">Period ${esc(periodLabel)} · ${data.count} invoice(s) · BTW-ID NL004200965B48 · KvK 86163795</div>
      <table><thead><tr><th>Invoice</th><th>Date</th><th>Client</th><th>Description</th><th class="n">Excl. BTW</th><th class="n">BTW 21%</th><th class="n">Total</th></tr></thead>
      <tbody>${rowsHtml || '<tr><td colspan="7" style="color:#999">No invoices in this period.</td></tr>'}</tbody>
      <tfoot><tr><td colspan="4">Totals — ${esc(periodLabel)}</td><td class="n">€${esc(data.totals.subtotal)}</td><td class="n">€${esc(data.totals.btw)}</td><td class="n">€${esc(data.totals.total)}</td></tr></tfoot>
      </table></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Allow pop-ups to export the PDF report."); return; }
    w.document.write(html); w.document.close();
    w.onload = () => { w.focus(); w.print(); };
  };

  const card: React.CSSProperties = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 20px", flex: 1 };
  const qBtn = (active: boolean): React.CSSProperties => ({ padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: active ? 700 : 500, background: active ? C.accentBg : "transparent", color: active ? C.accent : C.textMuted, border: `1px solid ${active ? C.accentBorder : C.border}` });
  const th: React.CSSProperties = { fontSize: 10, color: C.textDim, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" as const, padding: "0 8px 10px", textAlign: "left" as const, borderBottom: `1px solid ${C.border}` };
  const td: React.CSSProperties = { fontSize: 13, color: C.textPrimary, padding: "10px 8px", borderBottom: `1px solid ${C.border}` };
  const years = Array.from({ length: 4 }, (_, i) => nowYear - i);

  if (creating) return (
    <div>
      <div style={{ padding: "24px 48px 0" }}>
        <button onClick={() => setCreating(false)} style={{ padding: "8px 14px", background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, fontSize: 12, cursor: "pointer" }}>← Back to invoices</button>
      </div>
      <B2BInvoice />
    </div>
  );

  return (
    <div style={{ padding: "24px 48px", maxWidth: 1000 }}>
      {/* Controls */}
      <div style={{ display: "flex", flexWrap: "wrap" as const, alignItems: "center", gap: 10, marginBottom: 20 }}>
        <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))}
          style={{ background: C.surfaceRaised, border: `1px solid ${C.borderInput}`, borderRadius: 8, padding: "8px 12px", color: C.textPrimary, fontSize: 13, outline: "none" }}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {([0, 1, 2, 3, 4] as const).map(q => (
          <button key={q} onClick={() => setQuarter(q)} style={qBtn(quarter === q)}>{q === 0 ? "Full year" : `Q${q}`}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={{ padding: "8px 16px", background: C.accentBg, border: `1px solid ${C.accentBorder}`, borderRadius: 8, color: C.accent, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Create invoice</button>
        <button onClick={exportCSV} disabled={!data || data.count === 0} style={{ padding: "8px 16px", background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, fontSize: 12, cursor: data?.count ? "pointer" : "not-allowed", opacity: data?.count ? 1 : 0.5 }}>Export CSV</button>
        <button onClick={exportPDF} disabled={!data || data.count === 0} style={{ padding: "8px 16px", background: C.accent, color: "#000", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: data?.count ? "pointer" : "not-allowed", opacity: data?.count ? 1 : 0.5 }}>Export PDF</button>
      </div>

      {/* BTW totals */}
      <div style={{ display: "flex", gap: 16, marginBottom: 22, flexWrap: "wrap" as const }}>
        <div style={card}><div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 6 }}>Revenue excl. BTW</div><div style={{ fontSize: 22, fontWeight: 700, color: C.textPrimary }}>€{data?.totals.subtotal ?? "0.00"}</div></div>
        <div style={card}><div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 6 }}>BTW to declare</div><div style={{ fontSize: 22, fontWeight: 700, color: C.accent }}>€{data?.totals.btw ?? "0.00"}</div></div>
        <div style={card}><div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" as const, color: C.textDim, marginBottom: 6 }}>Gross received</div><div style={{ fontSize: 22, fontWeight: 700, color: C.textPrimary }}>€{data?.totals.total ?? "0.00"}</div></div>
      </div>

      {error && <div style={{ color: C.danger, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "8px 16px 16px", overflowX: "auto" as const }}>
        {loading ? (
          <div style={{ padding: 32, color: C.textMuted, fontSize: 13 }}>Loading…</div>
        ) : !data || data.count === 0 ? (
          <div style={{ padding: 32, color: C.textMuted, fontSize: 13 }}>No invoices for {periodLabel}.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" as const, minWidth: 720 }}>
            <thead><tr>
              <th style={th}>Invoice</th><th style={th}>Date</th><th style={th}>Client</th><th style={th}>Description</th>
              <th style={{ ...th, textAlign: "right" as const }}>Excl. BTW</th><th style={{ ...th, textAlign: "right" as const }}>BTW</th><th style={{ ...th, textAlign: "right" as const }}>Total</th>
            </tr></thead>
            <tbody>
              {data.invoices.map(i => (
                <tr key={i.paymentId}>
                  <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: C.accent }}>{i.invoiceNumber}</td>
                  <td style={td}>{i.dateISO}</td>
                  <td style={td}>{i.clientName}</td>
                  <td style={{ ...td, color: C.textMuted }}>{i.description}</td>
                  <td style={{ ...td, textAlign: "right" as const }}>€{i.subtotal}</td>
                  <td style={{ ...td, textAlign: "right" as const, color: C.textMuted }}>€{i.btw}</td>
                  <td style={{ ...td, textAlign: "right" as const, fontWeight: 600 }}>€{i.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const MORE_NAV_IDS = ["newsletter", "discounts", "invoices"];

function CoachApp() {
  const [authed, setAuthed]         = useState<boolean>(() => !!getToken());
  const [clients, setClients]       = useState<Client[]>([]);
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState<string>("dashboard");
  const [chatMessages, setChatMessages] = useState<{ role: string; content: string }[]>(() => {
    try { return JSON.parse(localStorage.getItem(CHAT_KEY) || "[]"); } catch { return []; }
  });
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [isMobile, setIsMobile]     = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Newsletter / Discounts / Invoices sit behind "Show more"; they stay visible while one is open
  const [showMoreNav, setShowMoreNav] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set(["__declined__"]));
  const toggleCategory = (cat: string) => setCollapsedCategories(prev => { const s = new Set(prev); s.has(cat) ? s.delete(cat) : s.add(cat); return s; });
  const [clientSearch, setClientSearch] = useState("");
  const [clientSearchFocused, setClientSearchFocused] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  useEffect(() => { if (isMobile) setSidebarOpen(false); }, [view]);

  // Load clients — only runs once authed is true
  useEffect(() => {
    if (!authed) { setLoading(false); return; }
    const normalize = (c: any) => ({
      ...c,
      name: c.name || '',
      sessions: c.sessions || [],
      inbody: c.inbody || [],
      payments: c.payments || [],
      portalToken: c.portalToken || Math.random().toString(36).slice(2, 10),
    });
    loadClientsFromServer().then(loaded => {
      if (loaded === null) { clearToken(); setAuthed(false); return; }
      setClients(loaded.map(normalize));
      setLoading(false);
      // Reconcile session records against Google Calendar (calendar is source of truth)
      authFetch(`${API}/api/sessions/reconcile`, { method: "POST" })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d?.success && d.summary?.length > 0 && Array.isArray(d.clients)) {
            setClients(d.clients.map(normalize));
          }
        })
        .catch(() => { /* reconcile is best-effort; counts fix on next load */ });
    });
  }, [authed]);

  useEffect(() => {
    const trimmed = chatMessages.slice(-10);
    localStorage.setItem(CHAT_KEY, JSON.stringify(trimmed));
  }, [chatMessages]);

  // ── Auth gate — all hooks declared above, safe to return early here ──
  if (!authed) return <LoginPage onLogin={() => setAuthed(true)} />;

  const updateClient = (updated: Client) => {
    setClients(prev => prev.map(c => c.id === updated.id ? updated : c));
    // Serialize saves per client so each save carries the rev from the previous response
    const prevSave = saveQueue[updated.id] || Promise.resolve();
    saveQueue[updated.id] = prevSave.then(async () => {
      const saved = await saveClientToServer(updated);
      if (saved === "conflict") {
        alert("This client was updated on another device or tab, so your last change was NOT saved. Reloading the latest data now — please re-apply that change.");
        const loaded = await loadClientsFromServer();
        if (loaded) setClients(loaded.map(c => ({ ...c, name: c.name || "", sessions: c.sessions || [], inbody: c.inbody || [], payments: c.payments || [] })));
      } else if (saved && typeof saved.rev === "number") {
        setClients(prev => prev.map(c => c.id === updated.id ? { ...c, rev: saved.rev } : c));
      }
    });
  };
  const deleteClient = (id: string) => {
    setClients(prev => prev.filter(c => c.id !== id));
    deleteClientFromServer(id);
    setView("dashboard");
  };
  const addClient = () => {
    if (!newClientName.trim()) return;
    const c = newClient(newClientName.trim());
    setClients(prev => [...prev, c]);
    createClientOnServer(c);
    setView(c.id); setNewClientName(""); setShowAddClient(false);
  };
  const selectedClient = clients.find(c => c.id === view);
  const getPageTitle = () => {
    if (view === "dashboard") return "Dashboard";
    if (view === "ai") return "AI Assistant";
    if (view === "newsletter") return "Newsletter";
    if (view === "intake") return "Intake Form";
    if (view === "calendar") return "Calendar";
    if (view === "discounts") return "Discount Codes";
    if (view === "invoices") return "Invoices";
    return selectedClient?.name || "";
  };

  return (
    <>
      {loading && <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ color: C.accent, fontFamily: F_SERIF, fontSize: 24, letterSpacing: 2 }}>Loading…</div></div>}
      {!loading && <>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap" rel="stylesheet" />
      <style>{`body { min-width: ${isMobile ? "0" : "860px"}; }`}</style>
      <div style={S.app}>
        {isMobile && sidebarOpen && (
          <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 199 }} />
        )}
        {/* ── Sidebar ── */}
        <div style={isMobile
          ? { ...S.sidebar, position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 200, transform: sidebarOpen ? "translateX(0)" : "translateX(-280px)", transition: "transform 0.25s ease", boxShadow: sidebarOpen ? "4px 0 32px rgba(0,0,0,0.6)" : "none", overflowY: "auto" as const, WebkitOverflowScrolling: "touch" as const }
          : S.sidebar
        }>

          {/* Brand */}
          <div style={{ padding: "20px 20px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <MetaLogo size={28} color={C.accent} />
              <div>
                <div style={{ fontFamily: F_SERIF, fontSize: 20, color: C.accent, letterSpacing: 2, lineHeight: 1 }}>JOEY ROUHANA</div>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 2.5, textTransform: "uppercase" as const, marginTop: 5 }}>Personal Training OS</div>
              </div>
            </div>
            <button
              title="Sign out"
              onClick={() => { clearToken(); setAuthed(false); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: C.textDim, fontSize: 16, padding: 4, lineHeight: 1 }}
            >⏻</button>
          </div>

          {/* Nav section */}
          <div style={{ padding: "20px 14px 12px" }}>
            <div style={{ fontSize: 9, color: C.textDim, letterSpacing: 2, textTransform: "uppercase" as const, paddingLeft: 6, marginBottom: 8, fontWeight: 600 }}>Menu</div>
            {[
              { id: "dashboard",  label: "Dashboard",    icon: "▦" },
              { id: "ai",         label: "AI Assistant", icon: "◈" },
              { id: "calendar",   label: "Calendar",     icon: "⊡" },
              ...(showMoreNav || MORE_NAV_IDS.includes(view) ? [
                { id: "newsletter", label: "Newsletter",   icon: "✉" },
                { id: "discounts",  label: "Discounts",    icon: "%" },
                { id: "invoices",   label: "Invoices",     icon: "€" },
              ] : []),
            ].map(item => {
              const active = view === item.id;
              return (
                <div
                  key={item.id}
                  onClick={() => { setView(item.id); setSidebarOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 10px", borderRadius: R.md, cursor: "pointer", marginBottom: 1,
                    background: active ? C.accentBg : "transparent",
                    color: active ? C.accent : C.textMuted,
                    fontSize: 13, fontWeight: active ? 600 : 400,
                    border: active ? `1px solid ${C.accentBorder}` : "1px solid transparent",
                    transition: "all 0.15s",
                  }}
                >
                  <span style={{ fontSize: 14, width: 18, textAlign: "center" as const, flexShrink: 0, opacity: active ? 1 : 0.6 }}>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              );
            })}
            {!MORE_NAV_IDS.includes(view) && (
              <button
                onClick={() => setShowMoreNav(v => !v)}
                aria-expanded={showMoreNav}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: R.md, cursor: "pointer", background: "transparent", color: C.textMuted, fontSize: 13, border: "1px solid transparent", textAlign: "left" as const, fontFamily: "inherit" }}
              >
                <span style={{ fontSize: 10, width: 18, textAlign: "center" as const, flexShrink: 0, opacity: 0.6 }}>{showMoreNav ? "▲" : "▼"}</span>
                <span>{showMoreNav ? "Show less" : "Show more"}</span>
              </button>
            )}
          </div>

          {/* Send Forms */}
          <div style={{ padding: "0 14px 8px" }}>
            <SendFormsSection selectedClient={selectedClient} />
          </div>

          {/* Shop link */}
          <div style={{ padding: "8px 14px" }}>
            <a
              href="/shop"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 10px", borderRadius: R.md, cursor: "pointer",
                background: "transparent", color: C.accent,
                fontSize: 13, fontWeight: 500, textDecoration: "none",
                border: `1px solid ${C.accentBorder}`,
                transition: "all 0.15s",
              }}
            >
              <span style={{ fontSize: 14, width: 18, textAlign: "center" as const, flexShrink: 0 }}>↗</span>
              <span>View Shop</span>
            </a>
          </div>

          {/* Clients section */}
          <div style={{ borderTop: `1px solid ${C.border}`, padding: "14px 14px 8px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 9, color: C.textDim, letterSpacing: 2, textTransform: "uppercase" as const, fontWeight: 600 }}>Clients</span>
              <button
                onClick={() => { setShowAddClient(true); setSidebarOpen(false); }}
                style={{ background: C.accentBg, border: `1px solid ${C.accentBorder}`, borderRadius: R.sm, padding: "3px 9px", color: C.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: 0.5 }}
              >
                + Add
              </button>
            </div>
            <div style={{ position: "relative" as const }}>
              <span style={{ position: "absolute" as const, left: 12, top: "50%", transform: "translateY(-50%)", display: "flex", color: clientSearchFocused ? C.accent : C.textDim, pointerEvents: "none" as const, transition: "color 0.15s" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              </span>
              <input
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                onFocus={() => setClientSearchFocused(true)}
                onBlur={() => setClientSearchFocused(false)}
                placeholder="Search clients…"
                style={{ width: "100%", background: C.surfaceRaised, border: `1px solid ${clientSearchFocused ? C.accent : C.border}`, borderRadius: R.md, padding: "10px 34px 10px 36px", color: C.textPrimary, fontSize: 13.5, outline: "none", boxSizing: "border-box" as const, boxShadow: clientSearchFocused ? `0 0 0 3px ${C.accentBg}` : "none", transition: "border-color 0.15s, box-shadow 0.15s" }}
              />
              {clientSearch && (
                <button
                  onClick={() => setClientSearch("")}
                  aria-label="Clear search"
                  style={{ position: "absolute" as const, right: 7, top: "50%", transform: "translateY(-50%)", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", background: C.surfaceMid, border: `1px solid ${C.border}`, borderRadius: "50%", color: C.textDim, fontSize: 14, lineHeight: 1, cursor: "pointer", padding: 0 }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Client list grouped by category — on mobile it flows into the drawer's own scroll (see sidebar overflowY); on desktop it scrolls independently */}
          <div style={{ flex: isMobile ? "none" : 1, overflowY: isMobile ? "visible" as const : "auto" as const, padding: "0 14px 16px" }}>
            {(() => {
              const q = clientSearch.trim().toLowerCase();
              const byName = (a: Client, b: Client) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
              const filtered = q ? clients.filter(c =>
                c.name.toLowerCase().includes(q)
                || (c.goal || "").toLowerCase().includes(q)
                || (c.package || "").toLowerCase().includes(q)
                || (c.category || "").toLowerCase().includes(q)
                || (c.email || "").toLowerCase().includes(q)
                || (c.phone || "").toLowerCase().includes(q)
              ).sort(byName) : null;
              if (filtered) return (
                <>
                  <div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1.5, textTransform: "uppercase" as const, fontWeight: 600, padding: "2px 6px 8px" }}>
                    {filtered.length === 0 ? "No matches" : `${filtered.length} match${filtered.length === 1 ? "" : "es"}`}
                  </div>
                  {filtered.length === 0
                    ? <div style={{ fontSize: 12, color: C.textDim, padding: "2px 10px", fontStyle: "italic" as const }}>No clients found</div>
                    : filtered.map(c => {
                    const active = view === c.id;
                    return (
                      <div key={c.id} onClick={() => { setView(c.id); setSidebarOpen(false); }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: R.md, cursor: "pointer", marginBottom: 2, background: active ? C.accentBg : "transparent", border: active ? `1px solid ${C.accentBorder}` : "1px solid transparent", transition: "all 0.15s" }}>
                        <div style={S.avatar(30)}>{(c.name || '?').slice(0, 2).toUpperCase()}</div>
                        <div style={{ overflow: "hidden", flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: active ? C.accent : C.textPrimary, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
                          <div style={{ fontSize: 10, color: C.textDim, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 }}>{c.category || "Uncategorized"}</div>
                        </div>
                      </div>
                    );
                  })}
                </>
              );
              // Free-program signups waiting to be vetted sit in their own group at the top; declined ones go to the bottom.
              const toVet = clients.filter(c => c.vetStatus === "pending").sort((a, b) => (b.joinDate || "").localeCompare(a.joinDate || "") || byName(a, b));
              const declined = clients.filter(c => c.vetStatus === "declined").sort(byName);
              const vetted = clients.filter(c => c.vetStatus !== "pending" && c.vetStatus !== "declined");
              const categories = Array.from(new Set(vetted.map(c => c.category || ""))).filter(Boolean).sort();
              const uncategorized = vetted.filter(c => !c.category).sort(byName);
              const groups: { label: string; key: string; items: Client[] }[] = [
                ...(toVet.length > 0 ? [{ label: "Free signups · to vet", key: "__tovet__", items: toVet }] : []),
                ...categories.map(cat => ({ label: cat, key: cat, items: vetted.filter(c => c.category === cat).sort(byName) })),
                ...(uncategorized.length > 0 ? [{ label: "Uncategorized", key: "__none__", items: uncategorized }] : []),
                ...(declined.length > 0 ? [{ label: "Declined", key: "__declined__", items: declined }] : []),
              ];
              if (groups.length === 0) return <div style={{ fontSize: 12, color: C.textDim, padding: "10px 10px", fontStyle: "italic" as const }}>No clients yet</div>;
              return groups.map(group => {
                const collapsed = collapsedCategories.has(group.key);
                return (
                  <div key={group.key} style={{ marginBottom: 4 }}>
                    {(categories.length > 0 || uncategorized.length > 0) && (
                      <div
                        onClick={() => toggleCategory(group.key)}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 6px", cursor: "pointer", borderRadius: R.sm, marginBottom: 2, userSelect: "none" as const }}
                      >
                        <span style={{ fontSize: 8, color: C.textDim, opacity: 0.7, width: 8, flexShrink: 0 }}>{collapsed ? "▶" : "▼"}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" as const, color: group.key === "__tovet__" ? C.accent : C.textDim }}>{group.label}</span>
                        <span style={{ marginLeft: "auto", fontSize: 9, ...(group.key === "__tovet__" ? { color: C.textOnAccent, background: C.accent, borderRadius: 8, padding: "0 6px", fontWeight: 700 } : { color: C.textDim, opacity: 0.6 }) }}>{group.items.length}</span>
                      </div>
                    )}
                    {!collapsed && group.items.map(c => {
                      const active = view === c.id;
                      return (
                        <div
                          key={c.id}
                          onClick={() => { setView(c.id); setSidebarOpen(false); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "8px 10px", borderRadius: R.md, cursor: "pointer", marginBottom: 2,
                            background: active ? C.accentBg : "transparent",
                            border: active ? `1px solid ${C.accentBorder}` : "1px solid transparent",
                            transition: "all 0.15s",
                          }}
                        >
                          <div style={S.avatar(30)}>{(c.name || '?').slice(0, 2).toUpperCase()}</div>
                          <div style={{ overflow: "hidden", flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: active ? C.accent : C.textPrimary, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>{c.name}</div>
                            <div style={{ fontSize: 10, color: C.textDim, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis", marginTop: 2, lineHeight: 1.3 }}>{c.goal || c.package || "—"}</div>
                          </div>
                          {(() => {
                            const pkgMatch = c.package?.match(/(\d+)\s*session/i);
                            if (!pkgMatch) return active ? <span style={{ width: 4, height: 4, borderRadius: "50%", background: C.accent, flexShrink: 0 }} /> : null;
                            const adjustment = c.creditAdjustment || 0;
                            const pocketInfo = calcPocketSessions(c.package, c.sessions, c.membershipStart || c.joinDate, adjustment);
                            const total = pocketInfo ? pocketInfo.monthly : parseInt(pkgMatch[1]);
                            const remaining = pocketInfo ? pocketInfo.pocket : parseInt(pkgMatch[1]) - blockUsedSessions(c.sessions, c.packageStartSessions) + adjustment;
                            const low = remaining <= 2;
                            const leftPill: React.CSSProperties = low
                              ? { color: C.danger, background: "rgba(239,68,68,0.12)", border: `1px solid ${C.danger}` }
                              : pocketInfo
                                ? { color: C.textOnAccent, background: C.accent, border: `1px solid ${C.accent}` }
                                : { color: C.accent, background: C.accentBg, border: `1px solid ${C.accentBorder}` };
                            return (
                              <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, ...leftPill, borderRadius: 4, padding: "1px 6px", minWidth: 20, textAlign: "center" as const }}>{remaining}</span>
                                <span style={{ fontSize: 11, color: C.textDim, background: C.surfaceMid, border: `1px solid ${C.border}`, borderRadius: 4, padding: "1px 6px", minWidth: 20, textAlign: "center" as const }}>{total}</span>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        </div>
        <div style={S.main}>
          <div style={{ ...S.topbar, padding: isMobile ? "14px 16px" : "16px 48px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {isMobile && (
                <button onClick={() => setSidebarOpen(v => !v)} style={{ background: "none", border: "none", color: C.accent, fontSize: 22, cursor: "pointer", padding: "4px", lineHeight: 1, flexShrink: 0 }}>☰</button>
              )}
              <div style={{ ...S.pageTitle, fontSize: isMobile ? 20 : undefined }}>{getPageTitle()}</div>
            </div>
            {view === "dashboard" && <Btn variant="primary" onClick={() => setShowAddClient(true)}>{isMobile ? "+ Add" : "+ Add Client"}</Btn>}
          </div>
          {view === "calendar" ? <CalendarView clients={clients} onClientUpdate={updateClient} />
            : view === "intake" ? <IntakeForm />
            : view === "newsletter" ? <Newsletter clients={clients} />
            : view === "ai" ? <AIAssistant clients={clients} onClientsChange={(updated) => { setClients(updated); }} chatMessages={chatMessages} setChatMessages={setChatMessages} />
            : view === "discounts" ? <div style={{ flex: 1, overflowY: "auto" as const }}><DiscountCodes /></div>
            : view === "invoices" ? <div style={{ flex: 1, overflowY: "auto" as const }}><InvoicesReport /></div>
            : (
              <div style={{ ...S.content, padding: isMobile ? "16px" : "24px 48px" }}>
                {view === "dashboard" && <Dashboard clients={clients} isMobile={isMobile} onSelectClient={(id) => setView(id)} />}
                {selectedClient && <ClientProfile client={selectedClient} onUpdate={updateClient} onDelete={deleteClient} isMobile={isMobile} allCategories={Array.from(new Set(clients.map(c => c.category || "").filter(Boolean))).sort()} />}
              </div>
            )}
        </div>
        {showAddClient && (
          <div style={S.modal} onClick={() => setShowAddClient(false)}>
            <div style={S.modalBox} onClick={e => e.stopPropagation()}>
              <div style={{ fontFamily: F_SERIF, fontSize: 26, marginBottom: 20, letterSpacing: 0.4 }}>New Client</div>
              <label style={S.label}>Client Name</label>
              <input style={S.input} value={newClientName} onChange={e => setNewClientName(e.target.value)} onKeyDown={e => e.key === "Enter" && addClient()} placeholder="Full name..." autoFocus />
              <div style={{ display: "flex", gap: SP.sm, justifyContent: "flex-end", marginTop: SP.lg }}>
                <Btn variant="ghost" onClick={() => setShowAddClient(false)}>Cancel</Btn>
                <Btn variant="primary" onClick={addClient}>Create Client</Btn>
              </div>
            </div>
          </div>
        )}
      </div>
    </>}
  </>
  );
}

function TrialPage() {
  const [started, setStarted] = useState(false);
  const [step, setStep]     = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [multiAnswers, setMultiAnswers] = useState<Record<string, string[]>>({});
  const [name, setName]     = useState('');
  const [email, setEmail]   = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [result, setResult] = useState<{ portalUrl: string; pdfUrl?: string; trialEndDate: string; portalJwt?: string } | null>(null);
  // Where the visitor came from: referring site + any utm_* / ref tags on the link they clicked.
  // Captured once on first load (kept for the browser tab, so it survives in-app navigation).
  const [attribution] = useState<Record<string, string>>(() => {
    try {
      const saved = sessionStorage.getItem('trialAttribution');
      if (saved) return JSON.parse(saved);
    } catch {}
    const out: Record<string, string> = {};
    try {
      const qs = new URLSearchParams(window.location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref', 'fbclid', 'gclid'].forEach(k => {
        const v = qs.get(k); if (v) out[k] = (k === 'fbclid' || k === 'gclid') ? 'yes' : v.slice(0, 120);
      });
      if (document.referrer && !document.referrer.startsWith(window.location.origin)) out.referrer = document.referrer.slice(0, 300);
      out.landingPage = (window.location.pathname + window.location.search).slice(0, 300);
      sessionStorage.setItem('trialAttribution', JSON.stringify(out));
    } catch {}
    return out;
  });

  const steps: { key: string; question: string; multi?: boolean; options: { value: string; label: string; sub: string }[] }[] = [
    {
      key: 'goal',
      question: "What's your main goal?",
      options: [
        { value: 'muscle',   label: 'Build Muscle',     sub: 'Hypertrophy & size' },
        { value: 'strength', label: 'Get Stronger',     sub: 'Powerlifting & strength' },
        { value: 'fat_loss', label: 'Lose Fat',         sub: 'Cut & conditioning' },
        { value: 'general',  label: 'General Fitness',  sub: 'Health & athleticism' },
      ]
    },
    {
      key: 'frequency',
      question: 'How many days can you train per week?',
      options: [
        { value: '3', label: '3 Days',  sub: 'Mon / Wed / Fri' },
        { value: '4', label: '4 Days',  sub: 'Mon / Tue / Thu / Fri' },
        { value: '5', label: '5 Days',  sub: 'Mon – Fri' },
      ]
    },
    {
      key: 'experience',
      question: 'What is your training experience?',
      options: [
        { value: 'beginner',     label: 'Beginner',     sub: '0 – 1 year' },
        { value: 'intermediate', label: 'Intermediate', sub: '1 – 3 years' },
        { value: 'advanced',     label: 'Advanced',     sub: '3+ years' },
      ]
    },
    {
      key: 'equipment',
      question: 'What equipment do you have access to?',
      options: [
        { value: 'gym',        label: 'Full Gym',       sub: 'Barbells, machines & cables' },
        { value: 'home',       label: 'Home Gym',       sub: 'Dumbbells & pull-up bar' },
        { value: 'bodyweight', label: 'Bodyweight',     sub: 'No equipment needed' },
      ]
    },
    {
      key: 'sessionLength',
      question: 'How long can your workouts be?',
      options: [
        { value: '30', label: '30 Minutes',  sub: 'Short & efficient' },
        { value: '45', label: '45 Minutes',  sub: 'The sweet spot' },
        { value: '60', label: '60+ Minutes', sub: 'Full training sessions' },
      ]
    },
    {
      key: 'focus',
      question: 'Which areas do you want to focus on?',
      multi: true,
      options: [
        { value: 'Glutes & Legs',       label: 'Glutes & Legs',    sub: 'Lower body priority' },
        { value: 'Chest',               label: 'Chest',            sub: 'Pressing power' },
        { value: 'Back',                label: 'Back',             sub: 'Width & thickness' },
        { value: 'Shoulders & Arms',    label: 'Shoulders & Arms', sub: 'Delts, biceps & triceps' },
        { value: 'Core & Abs',          label: 'Core & Abs',       sub: 'Midsection strength' },
        { value: 'Balanced',            label: 'Balanced',         sub: 'A bit of everything' },
      ]
    },
    {
      key: 'injuries',
      question: 'Any injuries or limitations?',
      multi: true,
      options: [
        { value: 'None',              label: 'None',              sub: 'All clear, ready to go' },
        { value: 'Lower back',        label: 'Lower Back',        sub: "We'll train around it" },
        { value: 'Knees',             label: 'Knees',             sub: "We'll train around it" },
        { value: 'Shoulders',         label: 'Shoulders',         sub: "We'll train around it" },
        { value: 'Neck / upper back', label: 'Neck / Upper Back', sub: "We'll train around it" },
      ]
    },
    {
      key: 'heardFrom',
      question: 'How did you hear about me?',
      options: [
        { value: 'Instagram',        label: 'Instagram',        sub: 'A post, reel or story' },
        { value: 'Google',           label: 'Google',           sub: 'Search or Maps' },
        { value: 'Friend / referral', label: 'Friend or Referral', sub: 'Someone recommended me' },
        { value: 'In person',        label: 'In Person',        sub: 'At the gym or an event' },
        { value: 'Other',            label: 'Other',            sub: 'Somewhere else' },
      ]
    },
    { key: 'about', question: 'A little about you', options: [] },
  ];

  const totalSteps = steps.length + 1; // +1 for contact form
  const progress   = Math.round(((step) / totalSteps) * 100);

  const selectOption = (key: string, value: string) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    setStep(s => s + 1);
  };

  const toggleMulti = (key: string, value: string) => {
    setMultiAnswers(prev => {
      const cur = prev[key] || [];
      let next: string[];
      if (value === 'None') next = cur.includes('None') ? [] : ['None'];
      else if (cur.includes(value)) next = cur.filter(v => v !== value);
      else next = [...cur.filter(v => v !== 'None'), value];
      return { ...prev, [key]: next };
    });
  };

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) { setError('Please enter your name and email.'); return; }
    if (password.length < 8) { setError('Please choose a password of at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/trial-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          password,
          goal: answers.goal,
          frequency: answers.frequency,
          experience: answers.experience,
          equipment: answers.equipment,
          sessionLength: answers.sessionLength,
          age: answers.age,
          gender: answers.gender,
          focus: (multiAnswers.focus || []),
          injuries: (multiAnswers.injuries || []).filter(v => v !== 'None'),
          heardFrom: answers.heardFrom,
          attribution,
        })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong.'); setLoading(false); return; }
      if (data.portalJwt) setPortalJwt(data.portalJwt); // signup doubles as first login
      setResult(data);
    } catch { setError('Could not connect. Please try again.'); }
    setLoading(false);
  };

  const accent  = '#876737';   // deep bronze — text-safe accent on ivory
  const gold    = '#B08A4A';   // linework / marks only
  const bg      = '#F3EFE6';
  const surface = '#FFFDF8';
  const border  = '#D8CFC0';
  const dim     = '#8A8175';
  const text    = '#1C1814';

  const cardBase: React.CSSProperties = {
    background: surface,
    border: `1px solid ${border}`,
    borderRadius: 14,
    padding: '18px 20px',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
    textAlign: 'left'
  };

  if (result) {
    return (
      <div style={{ minHeight: '100vh', background: bg, color: text, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 20px', fontFamily: "'Inter', sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontFamily: F_SERIF, fontSize: 14, letterSpacing: 3, color: accent, marginBottom: 24 }}>METAPHYSIQUE</div>
          <MetaLogo size={64} color={gold} style={{ margin: "0 auto 20px" }} />
          <div style={{ fontFamily: F_SERIF, fontSize: 32, letterSpacing: 0.5, marginBottom: 8 }}>YOUR 4-WEEK PROGRAM IS READY</div>
          <div style={{ fontSize: 14, color: dim, marginBottom: 28, lineHeight: 1.7 }}>
            Your personalized program has been built from your answers.<br />
            Portal access until <strong style={{ color: text }}>{result.trialEndDate}</strong>. Check your email — your PDF program is attached.
          </div>
          <a href={result.portalUrl} style={{ display: 'block', background: text, color: surface, fontWeight: 600, fontSize: 15, letterSpacing: 1.5, textDecoration: 'none', padding: '16px 24px', borderRadius: 10, marginBottom: 12, textTransform: 'uppercase' as const }}>
            Open My Training Portal →
          </a>
          {result.pdfUrl && (
            <a href={result.pdfUrl} target="_blank" rel="noreferrer" style={{ display: 'block', background: 'transparent', color: accent, fontWeight: 700, fontSize: 14, letterSpacing: 1, textDecoration: 'none', padding: '14px 24px', borderRadius: 10, marginBottom: 20, border: `1px solid ${accent}`, textTransform: 'uppercase' as const, textAlign: 'center' as const }}>
              ↓ Download My Program PDF
            </a>
          )}
          <div style={{ fontSize: 12, color: dim }}>
            Log in anytime at <span style={{ color: accent, wordBreak: 'break-all' as const }}>{result.portalUrl}</span> with your email and password.
          </div>
        </div>
      </div>
    );
  }

  if (!started) {
    return (
      <div style={{ minHeight: '100vh', background: bg, color: text, display: 'flex', flexDirection: 'column', fontFamily: "'Inter', sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ height: 4, background: gold }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center' as const }}>
          <div style={{ width: '100%', maxWidth: 520 }}>
            <MetaLogo size={84} color={gold} style={{ margin: "0 auto 16px" }} />
            <div style={{ fontFamily: F_SERIF, fontSize: 19, letterSpacing: 5, color: text, lineHeight: 1, marginBottom: 6 }}>METAPHYSIQUE</div>
            <div style={{ fontSize: 10, letterSpacing: 3.5, color: dim, textTransform: 'uppercase' as const, marginBottom: 28 }}>Beyond the physique</div>
            <div style={{ fontFamily: F_SERIF, fontSize: 'clamp(34px, 10.5vw, 52px)', letterSpacing: 0.5, lineHeight: 1.05, marginBottom: 8 }}>
              FREE 4-WEEK<br /><span style={{ color: accent }}>TRAINING PROGRAM</span>
            </div>
            <div style={{ fontSize: 15, color: '#4F463D', lineHeight: 1.7, margin: '18px 0 30px' }}>
              Personalized to your goal, schedule, equipment and injuries.<br />
              Answer 8 quick questions — your program and personal training portal are ready in minutes.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' as const, maxWidth: 380, margin: '0 auto 34px' }}>
              {['Built around your goal, focus areas & injuries', '4 weeks of workouts with built-in progression', 'PDF download + your own online training portal', 'No credit card. No strings attached.'].map(line => (
                <div key={line} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, color: text }}>
                  <span style={{ color: accent, fontWeight: 700 }}>✓</span><span>{line}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setStarted(true)}
              style={{ width: '100%', maxWidth: 380, background: text, color: surface, fontWeight: 600, fontSize: 16, letterSpacing: 1.5, border: 'none', borderRadius: 10, padding: '18px 24px', cursor: 'pointer', textTransform: 'uppercase' as const }}
            >
              Get My Free Program →
            </button>
            <div style={{ marginTop: 14, fontSize: 12, color: dim }}>Takes about 60 seconds</div>
          </div>
        </div>
      </div>
    );
  }

  const currentStep = steps[step];

  return (
    <div style={{ minHeight: '100vh', background: bg, color: text, fontFamily: "'Inter', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ background: surface, borderBottom: `2px solid ${gold}`, padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MetaLogo size={24} color={gold} />
          <div style={{ fontFamily: F_SERIF, fontSize: 20, color: text, letterSpacing: 2 }}>METAPHYSIQUE</div>
        </div>
        <div style={{ fontSize: 11, color: dim, letterSpacing: 1.5, textTransform: 'uppercase' as const }}>Free 4-Week Program</div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: border }}>
        <div style={{ height: '100%', width: `${progress}%`, background: gold, transition: 'width 0.4s ease' }} />
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '40px 20px 80px' }}>

        {/* Question steps */}
        {step < steps.length && currentStep && (
          <>
            <div style={{ fontSize: 11, color: dim, letterSpacing: 2, textTransform: 'uppercase' as const, marginBottom: 12 }}>
              Step {step + 1} of {totalSteps}
            </div>
            <div style={{ fontFamily: F_SERIF, fontSize: 26, letterSpacing: 0.4, marginBottom: currentStep.multi ? 8 : 28, lineHeight: 1.2 }}>
              {currentStep.question}
            </div>
            {currentStep.multi && (
              <div style={{ fontSize: 13, color: dim, marginBottom: 24 }}>Select all that apply.</div>
            )}

            {/* About you: age + gender pill groups */}
            {currentStep.key === 'about' && (
              <>
                {([
                  { key: 'gender', label: 'Gender',    values: ['Male', 'Female', 'Other'] },
                  { key: 'age',    label: 'Age group', values: ['Under 25', '25 – 34', '35 – 44', '45+'] },
                ] as const).map(group => (
                  <div key={group.key} style={{ marginTop: 18 }}>
                    <div style={{ fontSize: 11, color: dim, letterSpacing: 1.5, textTransform: 'uppercase' as const, marginBottom: 10 }}>{group.label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 10 }}>
                      {group.values.map(v => (
                        <div
                          key={v}
                          onClick={() => setAnswers(prev => ({ ...prev, [group.key]: v }))}
                          style={{
                            ...cardBase,
                            padding: '12px 18px',
                            borderColor: answers[group.key] === v ? accent : border,
                            boxShadow: answers[group.key] === v ? `inset 0 0 0 1px ${accent}` : 'none',
                            background: answers[group.key] === v ? 'rgba(176,138,74,0.14)' : surface,
                            color: answers[group.key] === v ? accent : text,
                            fontWeight: 600, fontSize: 14
                          }}
                        >
                          {v}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={!answers.gender || !answers.age}
                  style={{ marginTop: 28, width: '100%', background: (!answers.gender || !answers.age) ? border : text, color: (!answers.gender || !answers.age) ? dim : surface, fontWeight: 600, fontSize: 15, letterSpacing: 1.5, border: 'none', borderRadius: 10, padding: '16px 24px', cursor: (!answers.gender || !answers.age) ? 'not-allowed' : 'pointer', textTransform: 'uppercase' as const }}
                >
                  Continue →
                </button>
              </>
            )}

            {/* Multi-select steps */}
            {currentStep.key !== 'about' && currentStep.multi && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {currentStep.options.map(opt => {
                    const selected = (multiAnswers[currentStep.key] || []).includes(opt.value);
                    return (
                      <div
                        key={opt.value}
                        onClick={() => toggleMulti(currentStep.key, opt.value)}
                        style={{
                          ...cardBase,
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          borderColor: selected ? accent : border,
                          background: selected ? 'rgba(176,138,74,0.10)' : surface
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: text, marginBottom: 3 }}>{opt.label}</div>
                          <div style={{ fontSize: 12, color: dim }}>{opt.sub}</div>
                        </div>
                        <div style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, border: `2px solid ${selected ? accent : border}`, background: selected ? accent : 'transparent', color: surface, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>
                          {selected ? '✓' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={(multiAnswers[currentStep.key] || []).length === 0}
                  style={{ marginTop: 24, width: '100%', background: (multiAnswers[currentStep.key] || []).length === 0 ? border : text, color: (multiAnswers[currentStep.key] || []).length === 0 ? dim : surface, fontWeight: 600, fontSize: 15, letterSpacing: 1.5, border: 'none', borderRadius: 10, padding: '16px 24px', cursor: (multiAnswers[currentStep.key] || []).length === 0 ? 'not-allowed' : 'pointer', textTransform: 'uppercase' as const }}
                >
                  Continue →
                </button>
              </>
            )}

            {/* Single-select steps (tap to advance) */}
            {currentStep.key !== 'about' && !currentStep.multi && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {currentStep.options.map(opt => (
                  <div
                    key={opt.value}
                    onClick={() => selectOption(currentStep.key, opt.value)}
                    style={{
                      ...cardBase,
                      borderColor: answers[currentStep.key] === opt.value ? accent : border,
                      background: answers[currentStep.key] === opt.value ? 'rgba(176,138,74,0.10)' : surface
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = accent; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = answers[currentStep.key] === opt.value ? accent : border; }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 15, color: text, marginBottom: 3 }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: dim }}>{opt.sub}</div>
                  </div>
                ))}
              </div>
            )}

            <button onClick={() => (step > 0 ? setStep(s => s - 1) : setStarted(false))} style={{ marginTop: 24, background: 'none', border: 'none', color: dim, fontSize: 13, cursor: 'pointer', padding: 0 }}>← Back</button>
          </>
        )}

        {/* Contact form (last step) */}
        {step === steps.length && (
          <>
            <div style={{ fontSize: 11, color: dim, letterSpacing: 2, textTransform: 'uppercase' as const, marginBottom: 12 }}>
              Step {step + 1} of {totalSteps}
            </div>
            <div style={{ fontFamily: F_SERIF, fontSize: 26, letterSpacing: 0.4, marginBottom: 8 }}>
              Where should we send your program?
            </div>
            <div style={{ fontSize: 13, color: dim, marginBottom: 28 }}>Your personalized 4-week program will be ready in about a minute.</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontSize: 11, color: dim, letterSpacing: 1.5, textTransform: 'uppercase' as const, marginBottom: 6 }}>Full Name</div>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                  style={{ width: '100%', background: surface, border: `1px solid ${border}`, borderRadius: 8, padding: '12px 14px', color: text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                  onFocus={e => { e.target.style.borderColor = accent; }}
                  onBlur={e => { e.target.style.borderColor = border; }}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: dim, letterSpacing: 1.5, textTransform: 'uppercase' as const, marginBottom: 6 }}>Email Address</div>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={{ width: '100%', background: surface, border: `1px solid ${border}`, borderRadius: 8, padding: '12px 14px', color: text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                  onFocus={e => { e.target.style.borderColor = accent; }}
                  onBlur={e => { e.target.style.borderColor = border; }}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: dim, letterSpacing: 1.5, textTransform: 'uppercase' as const, marginBottom: 6 }}>Choose a Password</div>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                  style={{ width: '100%', background: surface, border: `1px solid ${border}`, borderRadius: 8, padding: '12px 14px', color: text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                  onFocus={e => { e.target.style.borderColor = accent; }}
                  onBlur={e => { e.target.style.borderColor = border; }}
                />
                <div style={{ fontSize: 11, color: dim, marginTop: 6 }}>You'll use this to log in to your training portal.</div>
              </div>
            </div>

            {/* Summary of choices */}
            <div style={{ marginTop: 20, background: 'rgba(176,138,74,0.08)', border: `1px solid rgba(176,138,74,0.32)`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: accent, letterSpacing: 1.5, textTransform: 'uppercase' as const, marginBottom: 10 }}>Your Program</div>
              {[
                { label: 'Goal',           value: steps[0].options.find(o => o.value === answers.goal)?.label },
                { label: 'Days per week',  value: steps[1].options.find(o => o.value === answers.frequency)?.label },
                { label: 'Experience',     value: steps[2].options.find(o => o.value === answers.experience)?.label },
                { label: 'Equipment',      value: steps[3].options.find(o => o.value === answers.equipment)?.label },
                { label: 'Session length', value: steps[4].options.find(o => o.value === answers.sessionLength)?.label },
                { label: 'Focus',          value: (multiAnswers.focus || []).join(', ') },
                { label: 'Injuries',       value: (multiAnswers.injuries || []).join(', ') },
                { label: 'About you',      value: [answers.gender, answers.age].filter(Boolean).join(', ') },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, marginBottom: 6 }}>
                  <span style={{ color: dim, flexShrink: 0 }}>{row.label}</span>
                  <span style={{ color: text, fontWeight: 600, textAlign: 'right' as const }}>{row.value || '—'}</span>
                </div>
              ))}
            </div>

            {error && <div style={{ marginTop: 14, color: '#9B3F32', fontSize: 13 }}>{error}</div>}

            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{ marginTop: 20, width: '100%', background: text, color: surface, fontWeight: 600, fontSize: 15, letterSpacing: 1.5, border: 'none', borderRadius: 10, padding: '16px 24px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, textTransform: 'uppercase' as const }}
            >
              {loading ? 'Building your program… this takes up to a minute' : 'Get My 4-Week Program →'}
            </button>

            <button onClick={() => setStep(s => s - 1)} style={{ marginTop: 14, background: 'none', border: 'none', color: dim, fontSize: 13, cursor: 'pointer', padding: 0, width: '100%' }}>← Back</button>

            <div style={{ marginTop: 20, fontSize: 11, color: dim, textAlign: 'center' as const, lineHeight: 1.6 }}>
              No credit card required. No commitment.<br />Your portal unlocks immediately after signup.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const path = window.location.pathname;
  const host = window.location.hostname.toLowerCase();
  const isPublicMarketingHost = host === "joeyrouhana.com" || host === "www.joeyrouhana.com";
  useEffect(() => {
    const routeMeta: Record<string, { title: string; description: string }> = {
      "/": {
        title: "Metaphysique / Joey Rouhana",
        description: "Training, nutrition, check-ins, and progress in one connected coaching process.",
      },
      "/website": {
        title: "Metaphysique / Joey Rouhana",
        description: "Training, nutrition, check-ins, and progress in one connected coaching process.",
      },
      "/blog": {
        title: "Journal / Metaphysique",
        description: "Notes on training, health and the life around it from the Metaphysique practice.",
      },
      "/blog/health-is-not-an-identity": {
        title: "Health Is Not An Identity / Metaphysique",
        description: "The point of training is not to perform health, but to build enough capacity for the rest of life to stand on.",
      },
      "/programs": {
        title: "Programs / Metaphysique",
        description: "Structured Metaphysique programs for efficient training, nutrition direction and habit building.",
      },
      "/program-access": {
        title: "Program Access / Metaphysique",
        description: "Downloadable Metaphysique programs built around warm up, mobility, training and stretching.",
      },
      "/shop": {
        title: "Coaching / Metaphysique",
        description: "Personal coaching built around assessment, planning, execution and progression.",
      },
      "/trial": {
        title: "Free 4-Week Program / Metaphysique",
        description: "Start with a personalized 4-week training program and unlock your portal.",
      },
      "/portal": {
        title: "Portal / Metaphysique",
        description: "Open your Metaphysique training portal.",
      },
    };
    const fallback = path.startsWith("/client/")
      ? {
          title: "Client Portal / Metaphysique",
          description: "Open your Metaphysique training portal.",
        }
      : {
          title: "CoachAgent / Metaphysique",
          description: "Training, client management and coaching operations for Metaphysique.",
        };
    const meta = routeMeta[path] || fallback;
    document.title = meta.title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", meta.description);
  }, [path]);
  // Client-facing pages are the ivory (light) design; only the coach dashboard is dark.
  // Declare color-scheme so phones with auto/force-dark don't invert the light pages to black.
  const isLightPage =
    (isPublicMarketingHost && path === "/") ||
    ["/website", "/blog", "/programs", "/program-access", "/intake", "/questionnaire", "/nutrition", "/shop", "/trial", "/portal"].includes(path) ||
    path.startsWith("/blog/") ||
    path.startsWith("/client/");
  document.documentElement.style.colorScheme = isLightPage ? "light" : "dark";

  // Portal pages only: link the installable app manifest (opens on /portal, full screen),
  // so a client's home-screen icon is their portal — the coach dashboard stays a plain shortcut.
  const isPortalPage = path === "/portal" || path.startsWith("/client/");
  useEffect(() => {
    if (!isPortalPage || document.querySelector('link[rel="manifest"]')) return;
    // Samsung Internet turns a manifest into its own WebAPK, which Play Protect blocks as an
    // "unsafe app" (built for an older Android). There, clients use Add page to → Home screen.
    const samsung = /SamsungBrowser/.test(navigator.userAgent);
    const add = (tag: string, attrs: Record<string, string>) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
    };
    if (!samsung) add("link", { rel: "manifest", href: "/portal.webmanifest" });
    add("meta", { name: "theme-color", content: "#F8F0E1" });
    add("meta", { name: "mobile-web-app-capable", content: "yes" });
    add("meta", { name: "apple-mobile-web-app-capable", content: "yes" });
    add("meta", { name: "apple-mobile-web-app-status-bar-style", content: "default" });
  }, [isPortalPage]);

  if (isPublicMarketingHost && path === "/") return <PublicLandingPage />;
  if (path === "/website")       return <PublicLandingPage />;
  if (path === "/blog" || path.startsWith("/blog/")) return <PublicBlogPage />;
  if (path === "/programs")      return <ProgramLibraryPage />;
  if (path === "/program-access") return <ProgramAccessPage />;
  if (path === "/intake")        return <IntakeForm hideShareLink />;
  if (path === "/questionnaire") return <TrainingQuestionnaire />;
  if (path === "/nutrition")     return <NutritionIntakeForm />;
  if (path === "/shop")          return <ShopPage />;
  if (path === "/trial")  return <TrialPage />;
  if (path === "/portal") return <PortalErrorBoundary><PortalAuth /></PortalErrorBoundary>;
  if (path.startsWith("/client/")) {
    const token = path.split("/client/")[1];
    return <PortalErrorBoundary><PortalAuth legacyToken={token} /></PortalErrorBoundary>;
  }
  return <CoachApp />;
}
